<template>
  <div>
    <div class="container-fluid">
      <div class="row">
        <div class="col-lg-12">
          <div class="d-flex flex-wrap flex-wrap align-items-center justify-content-center">
            <div class="text-center">
              <h4 class="mb-3">키워드 소싱 분석</h4>
              <p class="mb-0">키워의 상품별 트래픽 정보를 수집하여 소싱 분석을 수행합니다.<br>
                실제 Wing API에서 트래픽 키워드와 상품 정보를 수집하여 종합 분석을 제공합니다.
              </p>
            </div>
          </div>
        </div>

        <div class="col-lg-12">
          <!-- 메인 버튼들 -->
          <div class="d-flex flex-wrap justify-content-center align-items-center mb-4" style="gap: 15px;">
            <!-- 키워드그룹 선택 -->
            <b-dropdown
              id="keyword-group-dropdown"
              :text="'키워드그룹: ' + activeKeywordGroup"
              class="me-2"
              variant="outline-primary"
              :disabled="isAnalyzing"
            >
              <b-dropdown-item
                v-for="group in keywordGroups"
                :key="group"
                :active="group === activeKeywordGroup"
                @click="switchKeywordGroup(group)"
              >
                <div class="d-flex justify-content-between align-items-center">
                  <div>{{ group }}</div>
                  <b-badge variant="secondary" v-if="group !== 'Default'" @click.stop="deleteKeywordGroup(group)">Del</b-badge>
                </div>
              </b-dropdown-item>
              <b-dropdown-divider v-if="keywordGroups.length > 0"></b-dropdown-divider>
              <b-dropdown-item @click="createKeywordGroup">새 키워드그룹</b-dropdown-item>
            </b-dropdown>

            <!-- 키워드설정 버튼 -->
            <button type="button" class="btn btn-info" @click="openKeywordModal" :disabled="isAnalyzing">
              키워드설정 ({{ autoList.length }}개)
            </button>

            <!-- 초기화 버튼 -->
            <button
              type="button"
              class="btn btn-secondary"
              @click="resetData"
              :disabled="isAnalyzing"
            >
              초기화
            </button>

            <!-- 카테고리 선택 버튼 (최상단) -->
            <button v-if="categoryAnalysis.length > 0" class="btn btn-outline-success" @click="openCategoryModal" :disabled="isAnalyzing">
              🎯 카테고리 ({{ appliedCategoryNames.length > 0 ? appliedCategoryNames.length + '개 선택' : '전체' }})
            </button>

            <!-- 현재 키워드그룹 메모리 사용량 표시 (항상 표시) -->
            <div class="d-flex align-items-center flex-wrap">
              <small class="me-2" :class="memoryUsage.totalProducts >= maxProducts ? 'text-danger fw-bold' : 'text-muted'">
                🏷️ {{ activeKeywordGroup }}: {{ memoryUsage.totalProducts.toLocaleString() }}개 상품
                | 💾 {{ formatDataSize(memoryUsage.estimatedSize) }}
                <span v-if="memoryUsage.estimatedSize > 30 * 1024 * 1024" class="text-warning">⚠️</span>
              </small>
              <!-- 500개 이상 경고 메시지 -->
              <small v-if="memoryUsage.totalProducts >= maxProducts" class="text-danger fw-bold ml-2">
                ⚠️ 최대 제한 도달 - 신규 수집 불가
              </small>
            </div>
          </div>
        </div>
      </div>



      <!-- 진행 상태 -->
      <div class="row" v-if="isAnalyzing">
        <div class="col-lg-12">
          <div class="card">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <h6 class="mb-1">{{ progressMessage }}</h6>
                  <div class="progress" style="height: 6px;">
                    <div
                      class="progress-bar"
                      role="progressbar"
                      :style="`width: ${progressPercent}%`"
                    ></div>
                  </div>
                </div>
                <div class="text-end">
                  <span class="badge bg-primary">{{ progressPercent.toFixed(0) }}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 트래픽 키워드 미수신 안내 (비면 소싱 후보가 안 만들어진다) -->
      <div class="row" v-if="trafficKeywordNotice">
        <div class="col-lg-12">
          <div class="alert alert-warning d-flex align-items-start" role="alert">
            <i class="fas fa-exclamation-triangle mr-2 mt-1"></i>
            <span>{{ trafficKeywordNotice }}</span>
          </div>
        </div>
      </div>

      <!-- 키워드 수집 실패 안내 (로그인 풀림·응답 없음 — 0건과 구분해 다시 수집하게 한다) -->
      <div class="row" v-for="(notice, idx) in collectFailNotices" :key="'collect-fail-' + idx">
        <div class="col-lg-12">
          <div class="alert alert-danger d-flex align-items-start" role="alert">
            <i class="fas fa-exclamation-circle mr-2 mt-1"></i>
            <span style="white-space: pre-line">{{ notice }}</span>
          </div>
        </div>
      </div>

      <!-- 키워드 상품 소싱 전략 -->
      <div v-if="analysisResults.length > 0 && !isGroupSwitching" class="row mb-4">
        <div class="col-lg-12">
          <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center">
              <h4 class="card-title mb-0">📊 키워드 상품 소싱 전략</h4>
              <button type="button" class="btn btn-outline-info btn-sm"
                      :disabled="!topSourcingProducts || topSourcingProducts.length === 0"
                      @click="addToSourcingBoard">
                <i class="fas fa-clipboard-list mr-1"></i>소싱관리 담기
              </button>
            </div>
            <div class="card-body">
              <div class="row g-4">
                <!-- 상품 소싱 -->
                <div class="col-md-6">
                  <div class="card h-100">
                    <div class="card-header d-flex justify-content-between align-items-center">
                      <h6 class="card-title mb-0">
                        <i class="fas fa-shopping-cart mr-2"></i>상품 소싱 (TOP3)
                      </h6>
                      <b-dropdown v-if="userExcludedList && userExcludedList.length"
                                   size="sm"
                                   variant="outline-secondary"
                                   right>
                        <template #button-content>
                          삭제된 상품 ({{ userExcludedList.length }})
                          <b-spinner small type="border" class="ml-1 align-middle" v-if="isUserOutlierProcessing"></b-spinner>
                        </template>
                        <div class="p-2" style="min-width: 300px;">
                          <div v-for="item in userExcludedList" :key="item.vendorItemId" class="d-flex align-items-center justify-content-between mb-1">
                            <div class="d-flex align-items-center" style="gap: 6px; min-width: 0;">
                              <small v-b-tooltip.hover :title="item.productName" class="text-truncate" style="max-width: 160px;">{{ item.productName }}</small>
                              <span class="badge badge-danger">RPI {{ parseFloat(item.rpi || item.overall || 0).toFixed(2) }}</span>
                            </div>
                            <b-button size="xs" variant="link" class="p-0 text-primary"
                                      :disabled="isUserOutlierProcessing"
                                      @click.stop="restoreExcludedProduct(item.vendorItemId)">
                              <template v-if="isUserOutlierProcessing && processingVendorItemId === item.vendorItemId">
                                <b-spinner small type="grow" class="align-middle"></b-spinner>
                              </template>
                              <span v-else>복원</span>
                            </b-button>
                          </div>
                          <b-dropdown-divider></b-dropdown-divider>
                          <b-button size="sm" variant="outline-primary" class="w-100"
                                    :disabled="isUserOutlierProcessing"
                                    @click.stop="restoreAllExcluded">
                            <template v-if="isUserOutlierProcessing && processingAllUserExcluded">
                              <b-spinner small type="grow" class="align-middle mr-1"></b-spinner>
                            </template>
                            <span v-else>모두 복원</span>
                          </b-button>
                        </div>
                      </b-dropdown>
                    </div>
                    <div class="card-body p-3">
                      <p class="text-muted small mb-0">
                        <strong>기준:</strong> RPI 기준 상위 3개 상품
                      </p>
                      <div v-if="topSourcingProducts && topSourcingProducts.length > 0">
                        <div v-for="(product, index) in topSourcingProducts" :key="index" class="mt-2">
                          <div class="border p-2 rounded d-flex justify-content-between row">
                            <div class="d-flex align-items-center col-11">
                              <div class="mr-2">
                                <span class="badge" :class="index === 0 ? 'bg-warning text-dark' : index === 1 ? 'bg-secondary' : 'bg-dark'">
                                  {{ index + 1 }}
                                </span>
                              </div>
                              <img v-if="product.imgUrl"
                                   :src="product.imgUrl"
                                   alt="상품 이미지"
                                   style="width: 70px; height: 70px; object-fit: cover; border-radius: 4px; cursor: pointer;"
                                   class="mr-2"
                                   @click="openProductPage(product)"
                                   @error="$event.target.src = '/img/no-image.png'"
                                   title="클릭하여 상품페이지 보기">
                              <div class="flex-grow-1">
                                <div class="row mt-1 ml-2" style="font-size: 0.8em;">
                                  <div class="col-12">
                                  {{ product.productName }}
                                  </div>
                                </div>
                                <div class="row text-center ml-2 mt-0 g-1 h-50">
                                  <div class="col-3">
                                    <small class="text-success fw-bold d-block">{{ product.calculatedRPI === null ? '-' : parseFloat(product.calculatedRPI || 0).toFixed(2) }}</small>
                                    <small class="text-muted" style="font-size: 0.6em;">RPI</small>
                                  </div>
                                  <div class="col-3">
                                    <small class="text-info fw-bold d-block">{{ parseFloat(product.calculatedCTR !== undefined ? product.calculatedCTR : (product.ctr || 0)).toFixed(2) }}%</small>
                                    <small class="text-muted" style="font-size: 0.6em;">CTR</small>
                                  </div>
                                  <div class="col-3">
                                    <small class="text-warning fw-bold d-block">{{ product.cvr === null ? '-' : parseFloat(product.cvr || 0).toFixed(2) + '%' }}</small>
                                    <small class="text-muted" style="font-size: 0.6em;">CVR</small>
                                  </div>
                                  <div class="col-3">
                                    <small class="text-primary fw-bold d-block">{{ formatPrice(product.price) }}</small>
                                    <small class="text-muted" style="font-size: 0.6em;">가격</small>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div class="col-1">
                              <button class="btn btn-sm text-center ml-2"
                                      :disabled="isUserOutlierProcessing"
                                      title="이 상품 제외"
                                      @click="excludeTopProduct(product)">
                                <template v-if="isUserOutlierProcessing && processingVendorItemId === product.vendorItemId">
                                  <b-spinner small type="grow" class="align-middle"></b-spinner>
                                </template>
                                <h3 v-else class="text-secondary text-lg">ⓧ</h3>
                              </button>
                            </div>
                          </div>
                        </div>

                        <!-- 비교상품이 TOP3에 포함되어 있는지 표시 -->
                        <div v-if="comparisonProduct && isComparisonInTop3" class="mt-2">
                          <div class="alert alert-success py-1 px-2 mb-0 small">
                            <i class="fas fa-star mr-1"></i>비교상품이 TOP{{ comparisonTop3Rank }}에 포함되어 있습니다!
                          </div>
                        </div>
                      </div>

                      <!-- 비교상품이 TOP3에 없으면 별도 표시 (TOP3와 동일한 스타일) -->
                      <div v-if="comparisonProduct && !isComparisonInTop3 && topSourcingProducts && topSourcingProducts.length > 0" class="mt-2">
                        <div class="border border-info p-2 rounded d-flex justify-content-between row" style="background-color: rgba(13, 202, 240, 0.08);">
                          <div class="d-flex align-items-center col-11">
                            <div class="mr-2">
                              <span class="badge bg-info text-white">⚖️</span>
                            </div>
                            <img :src="comparisonProductImage"
                                 alt="비교상품 이미지"
                                 style="width: 70px; height: 70px; object-fit: cover; border-radius: 4px; cursor: pointer;"
                                 class="mr-2"
                                 @click="openProductPage(comparisonProduct)"
                                 @error="$event.target.src = '/img/no-image.png'"
                                 title="클릭하여 상품페이지 보기">
                            <div class="flex-grow-1">
                              <div class="row mt-1 ml-2" style="font-size: 0.8em;">
                                <div class="col-12">
                                  {{ comparisonProduct.productName || '상품명 없음' }}
                                </div>
                              </div>
                              <div class="row text-center ml-2 mt-0 g-1 h-50">
                                <div class="col-3">
                                  <small class="text-success fw-bold d-block">{{ comparisonProductRPI || (comparisonProduct.rpi === null ? '모름' : '0.00') }}</small>
                                  <small class="text-muted" style="font-size: 0.6em;">RPI</small>
                                </div>
                                <div class="col-3">
                                  <small class="text-info fw-bold d-block">{{ parseFloat(comparisonProduct.ctr || 0).toFixed(2) }}%</small>
                                  <small class="text-muted" style="font-size: 0.6em;">CTR</small>
                                </div>
                                <div class="col-3">
                                  <small class="text-warning fw-bold d-block">{{ comparisonProduct.cvr === null ? '모름' : parseFloat(comparisonProduct.cvr || 0).toFixed(2) + '%' }}</small>
                                  <small class="text-muted" style="font-size: 0.6em;">CVR</small>
                                </div>
                                <div class="col-3">
                                  <small class="text-primary fw-bold d-block">{{ comparisonProduct.price > 0 ? formatPrice(comparisonProduct.price) : '-' }}</small>
                                  <small class="text-muted" style="font-size: 0.6em;">가격</small>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div class="col-1">
                            <button class="btn btn-sm text-center ml-2"
                                    title="비교상품 제거"
                                    @click="clearComparisonProduct">
                              <h3 class="text-secondary text-lg">ⓧ</h3>
                            </button>
                          </div>
                        </div>
                      </div>

                      <div v-else-if="!topSourcingProducts || topSourcingProducts.length === 0" class="text-center text-muted py-3">
                        <i class="fas fa-shopping-cart fa-2x mb-2 opacity-50"></i>
                        <div>상품 소싱 데이터가 없습니다</div>
                        <div v-if="sourcingEmptyReason" class="small text-warning mt-2">
                          {{ sourcingEmptyReason }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <!-- AI 소싱 분석 -->
                <div class="col-md-6">
                  <div class="card h-100">
                    <div class="card-header d-flex justify-content-between align-items-center">
                      <h6 class="card-title mb-0">
                        <i class="fas fa-brain mr-2"></i>AI 소싱 분석
                      </h6>
                      <button
                        class="btn btn-sm btn-outline-primary"
                        @click="generateReviewBasedChecklist"
                        :disabled="isGeneratingChecklist || isCollectingReviews || !topSourcingProducts || topSourcingProducts.length === 0"
                      >
                        <i class="fas fa-rocket mr-1"></i>
                        {{ isCollectingReviews ? '리뷰수집중...' : isGeneratingChecklist ? 'AI분석중...' : 'AI 소싱 분석 시작' }}
                      </button>
                    </div>
                    <div class="card-body p-0 d-flex flex-column" style="max-height: 500px;">
                      <p class="text-muted small mb-2 px-3 pt-3">
                        <strong>기준:</strong> TOP3 상품 실제 리뷰 분석 기반 1688 소싱 가이드
                      </p>
                      <!-- 버튼이 왜 막혔는지 화면에서 보이게 한다 -->
                      <p v-if="!topSourcingProducts || topSourcingProducts.length === 0"
                         class="small text-warning mb-2 px-3">
                        TOP3 소싱 상품이 없어 분석을 시작할 수 없습니다.{{ sourcingEmptyReason ? ' ' + sourcingEmptyReason : '' }}
                      </p>

                      <!-- 체크리스트 로딩 상태 -->
                      <div v-if="isGeneratingChecklist" class="text-center py-4 px-3">
                        <div class="spinner-border text-primary mb-2" role="status">
                          <span class="visually-hidden">Loading...</span>
                        </div>
                        <div class="text-muted small">AI가 체크리스트를 생성하고 있습니다...</div>
                      </div>

                      <!-- AI 분석 로딩 상태 (리뷰 수집 중) -->
                      <div v-else-if="isCollectingReviews" class="text-center py-4 px-3">
                        <div class="spinner-border text-info mb-2" role="status">
                          <span class="visually-hidden">Loading...</span>
                        </div>
                        <div class="text-muted small">TOP3 상품 리뷰를 수집하고 있습니다...</div>
                        <div class="text-muted" style="font-size: 0.65em;">{{ collectedReviews.length }}개 리뷰 수집됨</div>
                      </div>

                      <!-- 리뷰 기반 체크리스트 표시 (카드 형태) -->
                      <div v-else-if="sourcingChecklist && sourcingChecklist.checklistItems && sourcingChecklist.checklistItems.length > 0" class="checklist-container flex-grow-1 overflow-auto px-3 pb-3">

                        <!-- 우선순위별 그룹화된 카드 -->
                        <!-- 필수 항목 (Critical) -->
                        <div v-if="getChecklistByPriority('critical').length > 0" class="mb-3">
                          <div class="d-flex align-items-center mb-2">
                            <span class="badge bg-danger mr-2">필수</span>
                            <small class="text-muted">{{ getChecklistByPriority('critical').length }}개 항목</small>
                          </div>
                          <div v-for="(item, index) in getChecklistByPriority('critical')"
                               :key="'critical-'+index"
                               class="checklist-card critical mb-2">
                            <div class="d-flex justify-content-between align-items-start">
                              <div class="flex-grow-1">
                                <div class="d-flex align-items-center mb-1">
                                  <span class="badge bg-secondary mr-2" style="font-size: 0.7em;">{{ getCategoryShortName(item.category) }}</span>
                                  <strong style="font-size: 0.85em;">{{ item.title }}</strong>
                                  <span v-if="item.mentionCount > 0" class="ms-2 text-info" style="font-size: 0.7em;">📊 {{ item.mentionCount }}건</span>
                                </div>
                                <div class="text-muted mb-1" style="font-size: 0.75em;">{{ item.description }}</div>
                                <!-- 1688 검색 키워드 버튼 -->
                                <div v-if="item.searchKeywords && item.searchKeywords.length > 0" class="mb-1">
                                  <span style="font-size: 0.7em;" class="text-muted mr-1">🔍 1688:</span>
                                  <span v-for="(kw, kwIdx) in item.searchKeywords" :key="kwIdx"
                                        class="badge bg-primary text-white mr-1 mb-1 keyword-link"
                                        style="font-size: 0.7em; cursor: pointer;"
                                        @click="open1688Search(kw)"
                                        :title="'1688에서 ' + kw + ' 검색'">
                                    {{ kw }}
                                  </span>
                                </div>
                                <div class="action-text" style="font-size: 0.8em;">
                                  <i class="fas fa-arrow-right text-danger mr-1"></i>{{ item.actionRequired }}
                                </div>
                              </div>
                              <div class="score-badge critical">{{ item.score }}</div>
                            </div>
                          </div>
                        </div>

                        <!-- 권장 항목 (Recommended) -->
                        <div v-if="getChecklistByPriority('recommended').length > 0" class="mb-3">
                          <div class="d-flex align-items-center mb-2">
                            <span class="badge bg-warning text-dark mr-2">권장</span>
                            <small class="text-muted">{{ getChecklistByPriority('recommended').length }}개 항목</small>
                          </div>
                          <div v-for="(item, index) in getChecklistByPriority('recommended')"
                               :key="'recommended-'+index"
                               class="checklist-card recommended mb-2">
                            <div class="d-flex justify-content-between align-items-start">
                              <div class="flex-grow-1">
                                <div class="d-flex align-items-center mb-1">
                                  <span class="badge bg-secondary mr-2" style="font-size: 0.7em;">{{ getCategoryShortName(item.category) }}</span>
                                  <strong style="font-size: 0.85em;">{{ item.title }}</strong>
                                  <span v-if="item.mentionCount > 0" class="ms-2 text-info" style="font-size: 0.7em;">📊 {{ item.mentionCount }}건</span>
                                </div>
                                <div class="text-muted mb-1" style="font-size: 0.75em;">{{ item.description }}</div>
                                <!-- 1688 검색 키워드 버튼 -->
                                <div v-if="item.searchKeywords && item.searchKeywords.length > 0" class="mb-1">
                                  <span style="font-size: 0.7em;" class="text-muted mr-1">🔍 1688:</span>
                                  <span v-for="(kw, kwIdx) in item.searchKeywords" :key="kwIdx"
                                        class="badge bg-warning text-dark mr-1 mb-1 keyword-link"
                                        style="font-size: 0.7em; cursor: pointer;"
                                        @click="open1688Search(kw)"
                                        :title="'1688에서 ' + kw + ' 검색'">
                                    {{ kw }}
                                  </span>
                                </div>
                                <div class="action-text" style="font-size: 0.8em;">
                                  <i class="fas fa-arrow-right text-warning mr-1"></i>{{ item.actionRequired }}
                                </div>
                              </div>
                              <div class="score-badge recommended">{{ item.score }}</div>
                            </div>
                          </div>
                        </div>

                        <!-- 참고 항목 (Optional) -->
                        <div v-if="getChecklistByPriority('optional').length > 0" class="mb-3">
                          <div class="d-flex align-items-center mb-2">
                            <span class="badge bg-success mr-2">참고</span>
                            <small class="text-muted">{{ getChecklistByPriority('optional').length }}개 항목</small>
                          </div>
                          <div v-for="(item, index) in getChecklistByPriority('optional')"
                               :key="'optional-'+index"
                               class="checklist-card optional mb-2">
                            <div class="d-flex justify-content-between align-items-start">
                              <div class="flex-grow-1">
                                <div class="d-flex align-items-center mb-1">
                                  <span class="badge bg-secondary mr-2" style="font-size: 0.7em;">{{ getCategoryShortName(item.category) }}</span>
                                  <strong style="font-size: 0.85em;">{{ item.title }}</strong>
                                  <span v-if="item.mentionCount > 0" class="ms-2 text-info" style="font-size: 0.7em;">📊 {{ item.mentionCount }}건</span>
                                </div>
                                <div class="text-muted mb-1" style="font-size: 0.75em;">{{ item.description }}</div>
                                <!-- 1688 검색 키워드 버튼 -->
                                <div v-if="item.searchKeywords && item.searchKeywords.length > 0" class="mb-1">
                                  <span style="font-size: 0.7em;" class="text-muted mr-1">🔍 1688:</span>
                                  <span v-for="(kw, kwIdx) in item.searchKeywords" :key="kwIdx"
                                        class="badge bg-success text-white mr-1 mb-1 keyword-link"
                                        style="font-size: 0.7em; cursor: pointer;"
                                        @click="open1688Search(kw)"
                                        :title="'1688에서 ' + kw + ' 검색'">
                                    {{ kw }}
                                  </span>
                                </div>
                                <div class="action-text" style="font-size: 0.8em;">
                                  <i class="fas fa-arrow-right text-success mr-1"></i>{{ item.actionRequired }}
                                </div>
                              </div>
                              <div class="score-badge optional">{{ item.score }}</div>
                            </div>
                          </div>
                        </div>

                        <!-- 보완된 USP -->
                        <div v-if="sourcingChecklist.enhancedUSP" class="mt-3 p-2 bg-primary bg-opacity-10 rounded">
                          <h6 class="small fw-bold mb-2">🌟 1688 소싱 핵심 포인트</h6>
                          <div class="small">
                            <div class="mb-1"><strong>현재 강점:</strong> {{ sourcingChecklist.enhancedUSP.currentUSP }}</div>
                            <div class="mb-1"><strong>차별화:</strong> {{ sourcingChecklist.enhancedUSP.improvedUSP }}</div>
                            <!-- 핵심 검색 키워드 버튼 -->
                            <div v-if="sourcingChecklist.enhancedUSP.searchKeywords && sourcingChecklist.enhancedUSP.searchKeywords.length > 0">
                              <strong>🔍 핵심 검색:</strong>
                              <span v-for="(kw, kwIdx) in sourcingChecklist.enhancedUSP.searchKeywords" :key="kwIdx"
                                    class="badge bg-primary text-white mr-1 mb-1 keyword-link"
                                    style="font-size: 0.75em; cursor: pointer;"
                                    @click="open1688Search(kw)"
                                    :title="'1688에서 ' + kw + ' 검색'">
                                {{ kw }}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- 초기 상태 -->
                      <div v-else class="text-center text-muted py-4 px-3 flex-grow-1 d-flex flex-column justify-content-center">
                        <i class="fas fa-brain fa-2x mb-2 opacity-50"></i>
                        <div>AI 소싱 분석을 시작해보세요</div>
                        <small class="text-muted">TOP3 상품의 실제 리뷰를 분석하여 1688 소싱 가이드를 제공합니다</small>
                      </div>
                    </div>
                  </div>
                </div>


                <!-- AI 다국어 검색어 (소구점 기반) - 주석처리 -->
                <!--
                <div class="col-md-12">
                  <div class="card h-100">
                    <div class="card-header">
                      <h6 class="card-title mb-0">
                        <i class="fas fa-language mr-2"></i>AI 다국어 검색어 (자동생성)
                      </h6>
                    </div>
                    <div class="card-body p-3">
                      <div v-if="isGeneratingMultilingualKeywords" class="text-center py-4">
                        <div class="spinner-border text-primary mb-2" role="status"><span class="visually-hidden">Loading...</span></div>
                        <div class="text-muted small">소구점 기반 다국어 검색어를 생성 중입니다...</div>
                      </div>

                      <div v-else-if="multilingualKeywords && multilingualKeywords.length > 0" class="table-responsive">
                        <table class="table table-sm table-bordered">
                          <thead>
                            <tr class="table-light">
                              <th width="15%" class="text-center">언어</th>
                              <th width="17%" class="text-center">키워드 1</th>
                              <th width="17%" class="text-center">키워드 2</th>
                              <th width="17%" class="text-center">키워드 3</th>
                              <th width="17%" class="text-center">키워드 4</th>
                              <th width="17%" class="text-center">키워드 5</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td class="text-center align-middle">
                                <span class="badge bg-primary">🇰🇷 한국어</span>
                              </td>
                              <td v-for="(keywordSet, idx) in multilingualKeywords" :key="'ko-'+idx" class="text-center">
                                <span v-if="keywordSet.ko"
                                      class="badge bg-primary small"
                                      style="cursor: pointer;"
                                      v-b-tooltip.hover :title="keywordSet.ko.rationale || '클릭하여 복사'"
                                      @click="copyKeyword(keywordSet.ko.keyword)">
                                  {{ keywordSet.ko.keyword }}
                                </span>
                                <span v-else class="text-muted">-</span>
                              </td>
                            </tr>
                            <tr>
                              <td class="text-center align-middle">
                                <span class="badge bg-success">🇨🇳 중국어</span>
                              </td>
                              <td v-for="(keywordSet, idx) in multilingualKeywords" :key="'zh-'+idx" class="text-center">
                                <span v-if="keywordSet.zh"
                                      class="badge bg-success small"
                                      style="cursor: pointer;"
                                      v-b-tooltip.hover :title="keywordSet.zh.rationale || '点击复制'"
                                      @click="copyKeyword(keywordSet.zh.keyword)">
                                  {{ keywordSet.zh.keyword }}
                                </span>
                                <span v-else class="text-muted">-</span>
                              </td>
                            </tr>
                            <tr>
                              <td class="text-center align-middle">
                                <span class="badge bg-info text-dark">🇺🇸 영어</span>
                              </td>
                              <td v-for="(keywordSet, idx) in multilingualKeywords" :key="'en-'+idx" class="text-center">
                                <span v-if="keywordSet.en"
                                      class="badge bg-info text-dark small"
                                      style="cursor: pointer;"
                                      v-b-tooltip.hover :title="keywordSet.en.rationale || 'Click to copy'"
                                      @click="copyKeyword(keywordSet.en.keyword)">
                                  {{ keywordSet.en.keyword }}
                                </span>
                                <span v-else class="text-muted">-</span>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                    <div v-else class="text-center text-muted py-3">
                        <i class="fas fa-language fa-2x mb-2 opacity-50"></i>
                        <div>AI 소싱 분석 완료 시 자동으로 다국어 검색어가 생성됩니다</div>
                      </div>
                    </div>
                  </div>
                </div>
                -->

              <!-- 두 번째 줄: 카테고리 + 판매가 -->

                <!-- 카테고리 -->
                <div class="col-md-6">
                  <div class="card h-100">
                    <div class="card-header">
                      <h6 class="card-title mb-0">
                        <i class="fas fa-tags mr-2"></i>카테고리
                      </h6>
                    </div>
                    <div class="card-body p-3">
                      <p class="text-muted small mb-3">
                        <strong>기준:</strong> 선택된 카테고리
                      </p>
                      <div v-if="effectiveOptimizedCategoryInfo">
                        <div class="border p-3 rounded">
                          <h6 class="text-primary mb-2">🎯 {{ effectiveOptimizedCategoryInfo.categoryName }}</h6>
                          <div class="row text-center g-2">
                            <div class="col-4">
                              <div class="p-2">
                                <div class="fw-bold text-warning">{{ formatNumber(effectiveOptimizedCategoryInfo.totalImpressions) }}</div>
                                <small class="text-muted">총 노출수</small>
                              </div>
                            </div>
                            <div class="col-4">
                              <div class="p-2">
                                <div class="fw-bold text-info">{{ effectiveOptimizedCategoryInfo.productCount }}</div>
                                <small class="text-muted">상품수</small>
                              </div>
                            </div>
                            <div class="col-4">
                              <div class="p-2">
                                <div class="fw-bold text-success">{{ effectiveOptimizedCategoryInfo.keywordCount }}</div>
                                <small class="text-muted">키워드수</small>
                              </div>
                            </div>
                          </div>
                        </div>
                        <!-- 비교상품 카테고리 표시 -->
                        <div v-if="comparisonProduct && comparisonProduct.categoryName" class="mt-2 pt-2 border-top">
                          <div class="d-flex align-items-center justify-content-between">
                            <small class="text-muted"><i class="fas fa-balance-scale mr-1"></i>비교상품:</small>
                            <span v-if="isComparisonCategorySame" class="badge bg-success">
                              <i class="fas fa-check mr-1"></i>동일 카테고리
                            </span>
                            <span v-else class="badge bg-info">{{ comparisonProduct.categoryName }}</span>
                          </div>
                        </div>
                      </div>
                      <div v-else class="text-center text-muted py-3">
                        <i class="fas fa-tags fa-2x mb-2 opacity-50"></i>
                        <div>카테고리 정보가 없습니다</div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 판매가 -->
                <div class="col-md-6">
                  <div class="card h-100">
                    <div class="card-header">
                      <h6 class="card-title mb-0">
                        <i class="fas fa-won-sign mr-2"></i>판매가
                      </h6>
                    </div>
                    <div class="card-body p-3">
                      <p class="text-muted small mb-3">
                        <strong>기준:</strong> 가격대별 분석에서 매출이 가장 높은 가격대
                      </p>
                      <div v-if="bestPriceRanges && bestPriceRanges.length > 0">
                        <div v-for="(range, index) in bestPriceRanges" :key="index" :class="index === 0 ? 'mb-2' : 'mb-1'">
                          <div v-if="index === 0">
                            <strong class="text-success d-block" style="font-size: 0.9em;">
                              🥇 {{ range.priceRangeText }}
                              <span v-if="comparisonProduct && isComparisonInPriceRange(range)" class="badge bg-info ms-1" style="font-size: 0.7em;">비교상품</span>
                            </strong>
                            <small class="text-muted" style="font-size: 0.7em;">
                              매출: {{ formatPrice(range.salesAmount) }}원
                            </small>
                          </div>
                          <div v-else>
                            <small class="text-secondary d-block" style="font-size: 0.75em;">
                              {{ index === 1 ? '🥈' : '🥉' }} {{ range.priceRangeText }}
                              <span v-if="comparisonProduct && isComparisonInPriceRange(range)" class="badge bg-info ms-1" style="font-size: 0.65em;">비교상품</span>
                            </small>
                            <small class="text-muted" style="font-size: 0.65em;">
                              매출: {{ formatPrice(range.salesAmount) }}원
                            </small>
                          </div>
                        </div>
                        <!-- 비교상품이 TOP3 가격대에 없으면 별도 표시 -->
                        <div v-if="comparisonProduct && !isComparisonInAnyTopPriceRange" class="mt-2 pt-2 border-top">
                          <div class="d-flex align-items-center justify-content-between">
                            <small class="text-muted"><i class="fas fa-balance-scale mr-1"></i>비교상품:</small>
                            <span class="badge bg-secondary">{{ formatPrice(comparisonProduct.price) }}원</span>
                          </div>
                        </div>
                      </div>
                      <div v-else class="text-center text-muted py-3">
                        <i class="fas fa-won-sign fa-2x mb-2 opacity-50"></i>
                        <div>가격대 분석 데이터가 없습니다</div>
                        <div v-if="priceEmptyReason" class="small text-warning mt-2">{{ priceEmptyReason }}</div>
                      </div>
                    </div>
                  </div>
                </div>


                <!-- 썸네일 벤치마킹 -->
                <div class="col-md-6">
                  <div class="card h-100">
                    <div class="card-header">
                      <h6 class="card-title mb-0">
                        <i class="fas fa-image mr-2"></i>썸네일 벤치마킹
                      </h6>
                    </div>
                    <div class="card-body p-3">
                      <p class="text-muted small mb-3">
                        <strong>기준:</strong> 베스트CTR, 시선집중도 상품의 썸네일 벤치마킹
                      </p>
                      <div v-if="bestCTRProduct || bestAttentionProduct">
                        <div class="row g-2">
                          <div v-if="bestCTRProduct" class="col-6 text-center">
                            <img v-if="bestCTRProduct.imgUrl"
                                 :src="bestCTRProduct.imgUrl"
                                 alt="베스트CTR 상품"
                                 style="width: 150%; max-width: 150px; height: 150px; object-fit: cover; border-radius: 8px; border: 3px solid #007bff; cursor: pointer; box-shadow: 0 2px 4px rgba(0,123,255,0.3);"
                                 class="mb-3"
                                 @click="openProductPage(bestCTRProduct)"
                                 title="클릭하여 상품페이지 보기">
                            <div v-else style="width: 150%; max-width: 150px; height: 150px; background: #f8f9fa; border-radius: 8px; border: 3px solid #dee2e6; margin: 0 auto 12px; display: flex; align-items: center; justify-content: center;">
                              <i class="fas fa-image text-muted fa-3x"></i>
                            </div>
                            <div class="mt-0">
                              <small class="text-muted d-block fw-bold">베스트CTR</small>
                              <small class="text-primary fw-bold" style="font-size: 1rem;">{{ parseFloat(bestCTRProduct.ctr).toFixed(2) }}%</small>
                            </div>
                            <small class="text-truncate d-block mt-1" :title="bestCTRProduct.productName" style="font-size: 0.8em; line-height: 1.3;">
                              {{ bestCTRProduct.productName.length > 10 ?
                                  bestCTRProduct.productName.substring(0, 10) + '...' :
                                  bestCTRProduct.productName }}
                            </small>
                          </div>

                          <div v-if="bestAttentionProduct && bestAttentionProduct !== bestCTRProduct" class="col-6 text-center px-1">
                            <img v-if="bestAttentionProduct.imgUrl"
                                 :src="bestAttentionProduct.imgUrl"
                                 alt="시선집중도 상품"
                                 style="width: 150%; max-width: 150px; height: 150px; object-fit: cover; border-radius: 8px; border: 3px solid #ffc107; cursor: pointer; box-shadow: 0 2px 4px rgba(255,193,7,0.3);"
                                 class="mb-3"
                                 @click="openProductPage(bestAttentionProduct)"
                                 title="클릭하여 상품페이지 보기">
                            <div v-else style="width: 150%; max-width: 150px; height: 150px; background: #f8f9fa; border-radius: 8px; border: 3px solid #dee2e6; margin: 0 auto 12px; display: flex; align-items: center; justify-content: center;">
                              <i class="fas fa-image text-muted fa-3x"></i>
                            </div>
                            <div class="mt-0">
                              <small class="text-muted d-block fw-bold">시선집중도</small>
                              <small class="text-warning fw-bold" style="font-size: 1rem;">{{ parseFloat(bestAttentionProduct.attentionScore || 0).toFixed(2) }}%</small>
                            </div>
                            <small class="text-truncate d-block mt-1" :title="bestAttentionProduct.productName" style="font-size: 0.8em; line-height: 1.3;">
                              {{ bestAttentionProduct.productName.length > 10 ?
                                  bestAttentionProduct.productName.substring(0, 10) + '...' :
                                  bestAttentionProduct.productName }}
                            </small>
                          </div>

                          <div v-if="bestCTRProduct && bestAttentionProduct === bestCTRProduct" class="col-12 text-center py-3">
                            <div class="alert alert-info mb-0" style="font-size: 0.8em;">
                              <i class="fas fa-info-circle mr-1"></i>
                              베스트CTR과 시선집중도가 동일한 상품입니다.
                            </div>
                          </div>
                        </div>
                      </div>
                      <div v-else>
                        <div class="col-12 text-center py-4">
                          <div class="text-muted">
                            <i class="fas fa-image fa-3x mb-2 opacity-50"></i>
                            <div>썸네일 분석 데이터가 없습니다</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 상세페이지 벤치마킹 -->
                <div class="col-md-6">
                  <div class="card h-100">
                    <div class="card-header">
                      <h6 class="card-title mb-0">
                        <i class="fas fa-file-alt mr-2"></i>상세페이지 벤치마킹
                      </h6>
                    </div>
                    <div class="card-body p-3">
                      <p class="text-muted small mb-3">
                        <strong>기준:</strong> CVR 베스트, 판매가×CVR 베스트 상품의 상세페이지 벤치마킹
                      </p>
                      <div v-if="bestCVRProduct || bestPriceCvrProduct">
                        <div class="row g-2">
                          <div v-if="bestCVRProduct" class="col-6 text-center">
                            <img v-if="bestCVRProduct.imgUrl"
                                 :src="bestCVRProduct.imgUrl"
                                 alt="CVR 베스트 상품"
                                 style="width: 150%; max-width: 150px; height: 150px; object-fit: cover; border-radius: 8px; border: 3px solid #dc3545; cursor: pointer; box-shadow: 0 2px 4px rgba(220,53,69,0.3);"
                                 class="mb-3"
                                 @click="openProductPage(bestCVRProduct)"
                                 title="클릭하여 상품페이지 보기">
                            <div v-else style="width: 150%; max-width: 150px; height: 150px; background: #f8f9fa; border-radius: 8px; border: 3px solid #dee2e6; margin: 0 auto 12px; display: flex; align-items: center; justify-content: center;">
                              <i class="fas fa-file-alt text-muted fa-3x"></i>
                            </div>
                            <div class="mt-2">
                              <div class="text-center mb-1">
                                <small class="text-muted fw-bold d-block">CVR 베스트</small>
                              </div>
                              <div class="text-center mb-1">
                                <small class="text-danger fw-bold d-block">{{ parseFloat(bestCVRProduct.cvr || 0).toFixed(2) }}%</small>
                              </div>
                              <div class="text-center mb-1">
                                <small class="text-primary fw-bold d-block">{{ formatPrice(bestCVRProduct.price) }}</small>
                              </div>
                              <div class="text-center">
                                <small class="text-truncate d-block" :title="bestCVRProduct.productName" style="font-size: 0.8em; line-height: 1.3;">
                                  {{ bestCVRProduct.productName.length > 10 ?
                                      bestCVRProduct.productName.substring(0, 10) + '...' :
                                      bestCVRProduct.productName }}
                                </small>
                              </div>
                            </div>
                          </div>

                          <div v-if="bestPriceCvrProduct && bestPriceCvrProduct !== bestCVRProduct" class="col-6 text-center px-1">
                            <img v-if="bestPriceCvrProduct.imgUrl"
                                 :src="bestPriceCvrProduct.imgUrl"
                                 alt="판매가×CVR 베스트 상품"
                                 style="width: 150%; max-width: 150px; height: 150px; object-fit: cover; border-radius: 8px; border: 3px solid #28a745; cursor: pointer; box-shadow: 0 2px 4px rgba(40,167,69,0.3);"
                                 class="mb-3"
                                 @click="openProductPage(bestPriceCvrProduct)"
                                 title="클릭하여 상품페이지 보기">
                            <div v-else style="width: 150%; max-width: 150px; height: 150px; background: #f8f9fa; border-radius: 8px; border: 3px solid #dee2e6; margin: 0 auto 12px; display: flex; align-items: center; justify-content: center;">
                              <i class="fas fa-file-alt text-muted fa-3x"></i>
                            </div>
                            <div class="mt-2">
                              <div class="text-center mb-1">
                                <small class="text-muted fw-bold d-block">판매가×CVR 베스트</small>
                              </div>
                              <div class="text-center mb-1">
                                <small class="text-danger fw-bold d-block">{{ parseFloat(bestPriceCvrProduct.cvr || 0).toFixed(2) }}%</small>
                              </div>
                              <div class="text-center mb-1">
                                <small class="text-primary fw-bold d-block">{{ formatPrice(bestPriceCvrProduct.price) }}</small>
                              </div>
                              <div class="text-center">
                                <small class="text-truncate d-block" :title="bestPriceCvrProduct.productName" style="font-size: 0.8em; line-height: 1.3;">
                                  {{ bestPriceCvrProduct.productName.length > 10 ?
                                      bestPriceCvrProduct.productName.substring(0, 10) + '...' :
                                      bestPriceCvrProduct.productName }}
                                </small>
                              </div>
                            </div>
                          </div>

                          <div v-if="bestCVRProduct && bestPriceCvrProduct === bestCVRProduct" class="col-12 text-center py-3">
                            <div class="alert alert-info mb-0" style="font-size: 0.8em;">
                              <i class="fas fa-info-circle mr-1"></i>
                              CVR 베스트와 판매가×CVR 베스트가 동일한 상품입니다.
                            </div>
                          </div>
                        </div>
                      </div>
                      <div v-else>
                        <div class="col-12 text-center py-4">
                          <div class="text-muted">
                            <i class="fas fa-file-alt fa-3x mb-2 opacity-50"></i>
                            <div>상세페이지 분석 데이터가 없습니다</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

              <!-- 네 번째 줄: 키워드 SEO 분석 -->

                <div class="col-md-12">
                  <div class="card">
                    <div class="card-header">
                      <h6 class="card-title mb-0">
                        <i class="fas fa-search mr-2"></i>키워드 SEO 분석
                      </h6>
                    </div>
                    <div class="card-body p-3">
                      <div class="row g-3">
                        <!-- 상품명 키워드 분석 -->
                        <div class="col-md-6">
                          <div class="border p-3 rounded">
                            <div class="d-flex justify-content-between align-items-center mb-2">
                              <h6 class="text-primary mb-0">📝 상품명 키워드 (통합분석 1~6위)</h6>
                              <button v-if="keywordSEOAnalysis.productNameKeywords.length > 0"
                                      class="btn btn-sm btn-outline-primary"
                                      @click="copyProductNameKeywords"
                                      title="상품명 복사">
                                <i class="fas fa-copy"></i>
                              </button>
                            </div>

                            <div v-if="keywordSEOAnalysis.productNameKeywords.length > 0">
                              <!-- 추천 상품명 입력창 -->
                              <div class="mb-2">
                                <label class="form-label small mb-1 text-muted">추천상품명</label>
                                <input v-model="generatedProductName"
                                       class="form-control form-control-sm"
                                       placeholder="브랜드명 + 생성된 상품명이 여기에 표시됩니다"
                                       @input="updateGeneratedProductName">
                              </div>

                              <!-- 키워드 배지들 -->
                              <div class="d-flex flex-wrap gap-1">
                                <span v-for="(keyword, index) in keywordSEOAnalysis.productNameKeywords"
                                      :key="index"
                                      class="badge bg-primary small mr-1 mt-1">
                                  {{ keyword.word }} ({{ (keyword.impressions || 0).toLocaleString() }})
                                </span>
                              </div>
                            </div>
                            <div v-else class="text-muted small">분석할 키워드가 없습니다</div>
                          </div>
                        </div>

                        <!-- 태그 키워드 분석 -->
                        <div class="col-md-6">
                          <div class="border p-3 rounded">
                            <div class="d-flex justify-content-between align-items-center mb-2">
                              <div>
                                <h6 class="text-success mb-0">🏷️ 태그 키워드 (최적 카테고리 우선)</h6>
                                <small class="text-muted">
                                  <span class="badge bg-success mr-1">⭐ 최적 카테고리</span>
                                  <span class="badge bg-info">다른 카테고리</span>
                                </small>
                              </div>
                              <button v-if="editableTagKeywords.length > 0"
                                      class="btn btn-sm btn-outline-success"
                                      @click="copyTagKeywords"
                                      title="태그 키워드 복사">
                                <i class="fas fa-copy"></i>
                              </button>
                            </div>

                            <div v-if="editableTagKeywords.length > 0">
                              <!-- 편집 가능한 태그들 -->
                              <div class="d-flex flex-wrap gap-1">
                                <span v-for="(keyword, index) in editableTagKeywords"
                                      :key="index"
                                      :class="getTagKeywordBadgeClass(keyword)"
                                      class="small mr-1 mt-1 d-flex align-items-center">
                                  {{ keyword.word }} ({{ (keyword.impressions || 0).toLocaleString() }})
                                  <span v-if="keyword.isInTable" class="ml-1" title="통합 키워드 테이블">⭐</span>
                                  <button class="btn btn-sm p-0 ml-1"
                                          @click="removeTagKeyword(index)"
                                          style="background: none; border: none; color: white; font-size: 0.7em;">
                                    <i class="fas fa-times"></i>
                                  </button>
                                </span>
                              </div>
                            </div>
                            <div v-else class="text-muted small">분석할 태그가 없습니다</div>
                          </div>
                        </div>

                        <!-- SEO 최적화 제안 -->
                        <div class="col-12">
                          <div class="border p-3 rounded">
                            <h6 class="text-warning mb-2">💡 SEO 최적화 제안</h6>
                            <div v-if="keywordSEOAnalysis.suggestions.length > 0">
                              <ul class="mb-0 small ml-3">
                                <li v-for="(suggestion, index) in keywordSEOAnalysis.suggestions.slice(0, 5)"
                                    :key="index"
                                    class="mb-1">
                                  {{ suggestion }}
                                </li>
                              </ul>
                            </div>
                            <div v-else class="text-muted small">충분한 데이터가 수집되면 SEO 제안을 제공합니다</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 종합 지표 -->
      <div v-if="analysisResults.length > 0 && !isGroupSwitching" class="row mb-4">
        <div class="col-md-6">
          <div class="card">
            <div class="card-header">
              <h4 class="card-title">종합 지표</h4>
            </div>
            <div class="card-body">
              <div class="row text-center">
                <div class="col-4">
                  <h4 class="text-primary mb-1">{{ summaryMetrics.medianCTR }}%</h4>
                  <small class="text-muted">시장CTR</small>
                </div>
                <div class="col-4">
                  <h4 class="text-success mb-1">{{ summaryMetrics.medianCVR }}%</h4>
                  <small class="text-muted">시장CVR</small>
                </div>
                <div class="col-4">
                  <h4 class="text-info mb-1">{{ summaryMetrics.totalProducts }}</h4>
                  <small class="text-muted">총 상품수</small>
                </div>
              </div>
              <hr>
              <div class="row text-center">
                <div class="col-4">
                  <h5 class="text-warning mb-1">{{ formatNumber(summaryMetrics.totalImpressions) }}</h5>
                  <small class="text-muted">총 노출수</small>
                </div>
                <div class="col-4">
                  <h5 class="text-danger mb-1">{{ formatNumber(summaryMetrics.totalClicks) }}</h5>
                  <small class="text-muted">총 클릭수</small>
                </div>
                <div class="col-4">
                  <h5 class="text-secondary mb-1">{{ formatNumber(summaryMetrics.totalSales) }}</h5>
                  <small class="text-muted">총 매출</small>
                  <small v-if="summaryMetrics.salesUnknownCount > 0" class="text-muted d-block">판매수 없음 {{ summaryMetrics.salesUnknownCount }}개 제외</small>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-6" v-if="categoryAnalysis.length > 0">
          <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center">
              <h4 class="card-title mb-0">🎯 카테고리 현황</h4>
              <button v-if="appliedCategoryNames.length > 0" class="btn btn-outline-secondary btn-sm"
                      @click="clearCategorySelection">
                전체 해제
              </button>
            </div>
            <div class="card-body">
              <!-- 현재 적용된 카테고리 칩 표시 -->
              <div class="mb-3">
                <small class="text-muted d-block mb-2">현재 적용 ({{ appliedCategoryNames.length }}개):</small>
                <div class="d-flex flex-wrap gap-1" v-if="appliedCategoryNames.length > 0">
                  <span v-for="catName in appliedCategoryNames" :key="catName"
                        class="badge bg-primary d-inline-flex align-items-center" style="font-size: 0.85em;">
                    {{ catName }}
                    <button type="button" class="btn-close btn-close-white ml-1" style="font-size: 0.6em;"
                            @click="removeCategoryFromApplied(catName)"></button>
                  </span>
                </div>
                <span v-else class="text-muted">{{ optimizedCategoryInfo?.categoryName || '없음' }} (기본)</span>
              </div>
              <!-- 요약 지표 -->
              <div class="row text-center mb-3">
                <div class="col-4">
                  <div class="border-end">
                    <h5 class="text-primary mb-0">{{ appliedCategorySummary?.productCount || optimizedCategoryInfo?.productCount || 0 }}</h5>
                    <small class="text-muted">상품 수</small>
                  </div>
                </div>
                <div class="col-4">
                  <div class="border-end">
                    <h5 class="text-success mb-0">{{ appliedCategorySummary?.keywordCount || optimizedCategoryInfo?.keywordCount || 0 }}</h5>
                    <small class="text-muted">키워드 수</small>
                  </div>
                </div>
                <div class="col-4">
                  <h5 class="text-warning mb-0">{{ formatNumber(appliedCategorySummary?.totalImpressions || optimizedCategoryInfo?.totalImpressions || 0) }}</h5>
                  <small class="text-muted">총 노출수</small>
                </div>
              </div>
              <!-- 전체 카테고리 수 안내 -->
              <div class="text-center">
                <small class="text-muted">
                  전체 {{ categoryAnalysis.length }}개 카테고리 중 선택 (상단 버튼 클릭)
                </small>
              </div>
            </div>
          </div>
        </div>


      </div>

      <!-- 차트 섹션 1 -->
      <div v-if="analysisResults.length > 0 && !isGroupSwitching" class="row mb-4">

        <div class="col-md-6">
          <div class="card">
            <div class="card-header">
              <h4 class="card-title">RPI 경쟁력</h4>
            </div>
            <div class="card-body">
              <ApexChart
                v-if="showRpiCompetitivenessChart"
                ref="rpiCompetitivenessChart"
                element="rpi-competitiveness-chart"
                :chartOption="rpiCompetitivenessChartOptions"
              />
            </div>
          </div>
        </div>
        <div class="col-md-6">
          <div class="card">
            <div class="card-header">
              <h4 class="card-title">가격대별 매출추이</h4>
            </div>
            <div class="card-body">
              <ApexChart
                v-if="showPriceRangeSalesChart"
                ref="priceRangeSalesChart"
                element="price-range-sales-chart"
                :chartOption="priceRangeSalesChartOptions"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- 차트 섹션 2 -->
      <div v-if="analysisResults.length > 0 && !isGroupSwitching" class="row mb-4">
        <div class="col-md-6">
          <div class="card">
            <div class="card-header">
              <h4 class="card-title">광고경쟁력 vs 광고노출비율</h4>
            </div>
            <div class="card-body">
              <!-- popup.js와 동일한 마진율/최소ROAS 입력 -->
              <ApexChart
                v-if="showAdCompetitivenessChart"
                ref="adCompetitivenessChart"
                element="ad-competitiveness-chart"
                :chartOption="adCompetitivenessChartOptions"
              />
              <div v-if="showAdCompetitivenessChart" class="mb-3 p-3 bg-light rounded">
                <div class="row align-items-center">
                  <div class="col-md-4">
                    <label for="marginRateInput" class="form-label small fw-bold">마진율 (%):</label>
                    <input
                      type="number"
                      id="marginRateInput"
                      v-model="marginRate"
                      @input="updateMinROASFromMargin"
                      class="form-control form-control-sm"
                      placeholder="30"
                      min="1"
                      max="100"
                      step="0.1">
                  </div>
                  <div class="col-md-4">
                    <label for="minROASInput" class="form-label small fw-bold">최소 ROAS (%):</label>
                    <input
                      type="number"
                      id="minROASInput"
                      v-model="minROAS"
                      @input="updateMarginFromROAS"
                      class="form-control form-control-sm"
                      placeholder="333"
                      min="100"
                      step="1">
                  </div>
                  <div class="col-md-4">
                    <div class="form-check form-switch mt-4">
                      <input
                        class="form-check-input"
                        type="checkbox"
                        id="sortByImpressionSwitch"
                        v-model="sortByImpression"
                        @change="updateAdCompetitivenessChart">
                      <label class="form-check-label small" for="sortByImpressionSwitch">
                        노출수 기준 정렬
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-6">
          <div class="card">
            <div class="card-header">
              <h4 class="card-title">가격별 CVR 분석</h4>
            </div>
            <div class="card-body">
              <ApexChart
                v-if="showPriceVsCvrChart"
                ref="priceVsCvrChart"
                element="price-vs-cvr-chart"
                :chartOption="priceVsCvrChartOptions"
              />

              <!-- popup.js와 동일한 가격 입력 기능 -->
              <div v-if="showPriceVsCvrChart" class="mt-3 p-3 bg-light rounded">
                <div class="row align-items-center">
                  <div class="col-md-6">
                    <label for="priceInput" class="form-label small fw-bold">판매가 입력:</label>
                    <input
                      type="number"
                      id="priceInput"
                      v-model="priceInput"
                      @input="calculateExpectedMinCVR"
                      class="form-control form-control-sm"
                      placeholder="예: 50000"
                      min="0"
                      step="1000">
                  </div>
                  <div class="col-md-6">
                    <label class="form-label small fw-bold">예상 최소 CVR:</label>
                    <div class="alert alert-info mb-0 py-2">
                      <strong id="expectedMinCVR">{{ expectedMinCVR }}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 일별 트래픽 차트 -->
      <div v-if="analysisResults.length > 0 && !isGroupSwitching" class="row mb-4">
        <div class="col-lg-12">
          <div class="card">
            <div class="card-header">
              <h4 class="card-title">일별 트래픽 분석 (Top20)</h4>
            </div>
            <div class="card-body">
              <ApexChart
                v-if="showDailyTrafficChart"
                ref="dailyTrafficChart"
                element="daily-traffic-chart"
                :chartOption="dailyTrafficChartOptions"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- 베스트 상품 분석 -->
      <div v-if="analysisResults.length > 0 && !isGroupSwitching" class="row mb-4">
        <div class="col-lg-12">
          <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center">
              <h4 class="card-title mb-0">🏆 베스트 상품 분석</h4>
              <button
                v-if="rpiTop20Products.length > 0"
                @click="exportRpiTop20Products"
                class="btn btn-outline-primary btn-sm"
                title="RPI Top20 상품 데이터 다운로드"
              >
                <i class="fas fa-download mr-1"></i>
                Top20 다운로드
              </button>
            </div>
            <div class="card-body">
              <!-- 4가지 베스트 상품 카드 -->
              <div class="row g-3 mb-4">
                <!-- 베스트 CTR -->
                <div class="col-md-3">
                  <div class="card border-primary h-100">
                    <div class="card-header bg-primary text-white text-center py-2">
                      <h6 class="mb-0">🎯 베스트 CTR</h6>
                    </div>
                    <div class="card-body p-3 text-center">
                      <div v-if="bestCTRProduct">
                        <img v-if="bestCTRProduct.imgUrl"
                             :src="bestCTRProduct.imgUrl"
                             alt="베스트 CTR 상품"
                             style="width: 80px; height: 80px; object-fit: cover; border-radius: 8px; cursor: pointer;"
                             class="mb-2"
                             @click="openProductPage(bestCTRProduct)"
                             @error="$event.target.src = '/img/no-image.png'">
                        <div class="fw-bold text-primary mb-1" style="font-size: 1.2rem;">{{ parseFloat(bestCTRProduct.ctr).toFixed(2) }}%</div>
                        <div class="small text-truncate" :title="bestCTRProduct.productName">
                          {{ bestCTRProduct.productName.length > 15 ? bestCTRProduct.productName.substring(0, 15) + '...' : bestCTRProduct.productName }}
                        </div>
                        <div class="small text-muted">{{ formatPrice(bestCTRProduct.price) }}원</div>
                      </div>
                      <div v-else class="text-muted">데이터 없음</div>
                    </div>
                  </div>
                </div>

                <!-- 베스트 CVR -->
                <div class="col-md-3">
                  <div class="card border-success h-100">
                    <div class="card-header bg-success text-white text-center py-2">
                      <h6 class="mb-0">💰 베스트 CVR</h6>
                    </div>
                    <div class="card-body p-3 text-center">
                      <div v-if="bestCVRProduct">
                        <img v-if="bestCVRProduct.imgUrl"
                             :src="bestCVRProduct.imgUrl"
                             alt="베스트 CVR 상품"
                             style="width: 80px; height: 80px; object-fit: cover; border-radius: 8px; cursor: pointer;"
                             class="mb-2"
                             @click="openProductPage(bestCVRProduct)"
                             @error="$event.target.src = '/img/no-image.png'">
                        <div class="fw-bold text-success mb-1" style="font-size: 1.2rem;">{{ parseFloat(bestCVRProduct.cvr).toFixed(2) }}%</div>
                        <div class="small text-truncate" :title="bestCVRProduct.productName">
                          {{ bestCVRProduct.productName.length > 15 ? bestCVRProduct.productName.substring(0, 15) + '...' : bestCVRProduct.productName }}
                        </div>
                        <div class="small text-muted">{{ formatPrice(bestCVRProduct.price) }}원</div>
                      </div>
                      <div v-else class="text-muted">데이터 없음</div>
                    </div>
                  </div>
                </div>

                <!-- 베스트 시선집중도 -->
                <div class="col-md-3">
                  <div class="card border-warning h-100">
                    <div class="card-header bg-warning text-white text-center py-2">
                      <h6 class="mb-0">👁️ 베스트 시선집중도</h6>
                    </div>
                    <div class="card-body p-3 text-center">
                      <div v-if="bestAttentionProduct">
                        <img v-if="bestAttentionProduct.imgUrl"
                             :src="bestAttentionProduct.imgUrl"
                             alt="베스트 시선집중도 상품"
                             style="width: 80px; height: 80px; object-fit: cover; border-radius: 8px; cursor: pointer;"
                             class="mb-2"
                             @click="openProductPage(bestAttentionProduct)"
                             @error="$event.target.src = '/img/no-image.png'">
                        <div class="fw-bold text-warning mb-1" style="font-size: 1.2rem;">
                          {{ parseFloat(bestAttentionProduct.attentionScore || bestAttentionProduct.ctr || 0).toFixed(2) }}%
                          <small v-if="!bestAttentionProduct.attentionScore" class="text-muted d-block" style="font-size: 0.7rem;">
                            (CTR 기준)
                          </small>
                        </div>
                        <div class="small text-truncate" :title="bestAttentionProduct.productName">
                          {{ bestAttentionProduct.productName.length > 15 ? bestAttentionProduct.productName.substring(0, 15) + '...' : bestAttentionProduct.productName }}
                        </div>
                        <div class="small text-muted">{{ formatPrice(bestAttentionProduct.price) }}원</div>
                      </div>
                      <div v-else class="text-muted">데이터 없음</div>
                    </div>
                  </div>
                </div>

                <!-- 베스트 RPI -->
                <div class="col-md-3">
                  <div class="card border-danger h-100">
                    <div class="card-header bg-danger text-white text-center py-2">
                      <h6 class="mb-0">🚀 베스트 RPI</h6>
                    </div>
                    <div class="card-body p-3 text-center">
                      <div v-if="bestRPIProduct">
                        <img v-if="bestRPIProduct.imgUrl"
                             :src="bestRPIProduct.imgUrl"
                             alt="베스트 RPI 상품"
                             style="width: 80px; height: 80px; object-fit: cover; border-radius: 8px; cursor: pointer;"
                             class="mb-2"
                             @click="openProductPage(bestRPIProduct)"
                             @error="$event.target.src = '/img/no-image.png'">
                        <div class="fw-bold text-danger mb-1" style="font-size: 1.2rem;">{{ parseFloat(bestRPIProduct.overall).toFixed(2) }}</div>
                        <div class="small text-truncate" :title="bestRPIProduct.productName">
                          {{ bestRPIProduct.productName.length > 15 ? bestRPIProduct.productName.substring(0, 15) + '...' : bestRPIProduct.productName }}
                        </div>
                        <div class="small text-muted">{{ formatPrice(bestRPIProduct.price) }}원</div>
                      </div>
                      <div v-else class="text-muted">데이터 없음</div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 전체 베스트 상품 테이블 -->
              <CDataTable
                  storage-key="keywordDataCollect.1"
                :headers="bestProductHeaders"
                :items="bestProductsForTable"
                :itemsPerPage="25"
                :search="true"
                :pagination="true"
                :itemKey="'vendorItemId'"
                :fixedHeight="false"
                :rowClass="getComparisonRowClass"
                class="table-responsive"
              >
                <template v-slot:rank="{ item }">
                  <span>{{ item.rank }}</span>
                </template>

                <template v-slot:productName="{ item }">
                  <div class="d-flex align-items-center">
                    <img v-if="item.imgUrl"
                         :src="item.imgUrl"
                         alt="상품 이미지"
                         style="width: 35px; height: 35px; object-fit: cover; border-radius: 3px; cursor: pointer;"
                         class="mr-2"
                         @click="openProductPage(item)"
                         @error="$event.target.src = '/img/no-image.png'"
                         title="클릭하여 상품페이지 보기">
                    <div>
                      <div class="text-truncate" style="max-width: 180px; font-size: 0.85rem;" :title="item.productName">
                        {{ item.productName }}
                      </div>
                      <span v-if="item.isComparisonProduct" class="badge bg-warning text-dark" style="font-size: 0.65rem;">
                        <i class="fas fa-balance-scale mr-1"></i>비교상품
                      </span>
                    </div>
                  </div>
                </template>

                <template v-slot:brandName="{ item }">
                  <span class="badge bg-secondary" v-if="item.brandName">{{ item.brandName }}</span>
                  <span class="text-muted" v-else>-</span>
                </template>

                <template v-slot:currentRank="{ item }">
                  <span class="badge bg-info">{{ item.currentRank || '-' }}위</span>
                </template>

                <template v-slot:impressions="{ item }">
                  <span>{{ formatNumber(item.impressions) }}</span>
                </template>

                <template v-slot:clicks="{ item }">
                  <span>{{ formatNumber(item.clicks) }}</span>
                </template>

                <template v-slot:sales="{ item }">
                  <span>{{ item.sales === null ? '-' : formatNumber(item.sales) }}</span>
                </template>

                <template v-slot:ctr="{ item }">
                  <span>{{ item.ctr }}%</span>
                </template>

                <template v-slot:cvr="{ item }">
                  <span>{{ item.cvr === null ? '-' : item.cvr + '%' }}</span>
                </template>

                <template v-slot:price="{ item }">
                  <span>{{ formatPrice(item.price) }}원</span>
                </template>

                <template v-slot:rpi="{ item }">
                  <span>{{ item.rpi === null ? '-' : item.rpi }}</span>
                </template>

                <template v-slot:revenue="{ item }">
                  <span>{{ item.revenue === null ? '-' : formatPrice(item.revenue) + '원' }}</span>
                </template>

                <template v-slot:attentionScore="{ item }">
                  <span>{{ item.attentionScore }}</span>
                </template>

                <template v-slot:trafficKeyword="{ item }">
                  <span v-for="(keyword, kidx) in item.trafficKeywordDisplay" :key="kidx"
                        class="badge bg-secondary mr-1">
                    {{ keyword }}
                  </span>
                </template>
              </CDataTable>
            </div>
          </div>
        </div>
      </div>

      <!-- 키워드그룹 변경 중 로딩 메시지 -->
      <div v-if="isGroupSwitching" class="row mb-4">
        <div class="col-lg-12">
          <div class="card">
            <div class="card-body text-center py-5">
              <div class="spinner-border text-primary mb-3" role="status">
                <span class="sr-only">Loading...</span>
              </div>
              <h5 class="text-muted">키워드그룹 변경 중...</h5>
              <p class="text-muted small">데이터를 준비하고 있습니다. 잠시만 기다려주세요.</p>
            </div>
          </div>
        </div>
      </div>

      <!-- 데이터가 없을 때 안내 메시지 -->
      <div v-if="analysisResults.length === 0 && !isGroupSwitching && !isAnalyzing" class="row mb-4">
        <div class="col-lg-12">
          <div class="card">
            <div class="card-body text-center py-5">
              <i class="fas fa-search fa-3x text-muted mb-3"></i>
              <h5 class="text-muted">분석 데이터가 없습니다</h5>
              <p class="text-muted">키워드를 추가하고 "전체 수집 시작" 버튼을 클릭하여 데이터를 수집해주세요.</p>
            </div>
          </div>
        </div>
      </div>

      <!-- 통합 키워드 분석 -->
      <div v-if="analysisResults.length > 0 && !isGroupSwitching" class="row mb-4">
        <div class="col-lg-12">
          <div class="card">
            <div class="card-header d-flex justify-content-between">
              <div class="header-title">
                <h4 class="card-title">🔍 통합 키워드 분석</h4>
              </div>
              <div class="d-flex align-items-center">
                <!-- 통합키워드 vs 단일키워드 라디오 버튼 (숨김) -->
                <!-- <div class="btn-group mr-3" role="group">
                  <input type="radio" class="btn-check" name="keywordAnalysisType" id="integrated" value="integrated" v-model="keywordAnalysisType">
                  <label class="btn btn-outline-primary btn-sm" for="integrated">통합키워드</label>

                  <input type="radio" class="btn-check" name="keywordAnalysisType" id="single" value="single" v-model="keywordAnalysisType">
                  <label class="btn btn-outline-success btn-sm" for="single">단일키워드</label>
                </div> -->

                <!-- 최적카테고리 스위치 제거됨 - 상단 카테고리 선택으로 대체 -->

                <div class="custom-control custom-switch custom-switch-color custom-control-inline mr-3">
                  <input type="checkbox" class="custom-control-input bg-info" id="detailViewSwitch" v-model="showDetailView" @change="updateHeaders">
                  <label class="custom-control-label" for="detailViewSwitch">상세보기</label>
                </div>

                <button type="button" class="btn btn-success btn-sm mr-1" @click="downloadSummaryCSV">
                  <i class="las la-download"></i> 통합키워드 CSV
                </button>
                <button type="button" class="btn btn-outline-success btn-sm" @click="downloadCSV">
                  <i class="las la-download"></i> 상품데이터 CSV
                </button>
                <button type="button" class="btn btn-outline-success btn-sm ml-1" @click="downloadManualAdKeywordsCSV">
                  <i class="las la-download"></i> 수동광고키워드 CSV
                </button>

              </div>
            </div>
            <div class="card-body">
              <CDataTable
                  storage-key="keywordDataCollect.2"
                :key="`integrated-keyword-table-${showDetailView ? 'detail' : 'basic'}`"
                :headers="integratedKeywordHeaders"
                :items="integratedKeywordsForTable"
                :itemsPerPage="100"
                :search="true"
                :pagination="true"
                :itemKey="'keyword'"
                :fixedColumns="'키워드'"
                :height="'750px'"
                :headerTooltips="keywordHeaderTooltips"
                class="table-responsive"
              >
                <template v-slot:keyword="{ item }">
                  <span class="badge bg-primary">{{ item.keyword }}</span>
                </template>

                <template v-slot:totalVolume="{ item }">
                  <span>{{ formatNumber(item.totalVolume) }}</span>
                </template>

                <template v-slot:totalClicks="{ item }">
                  <span>{{ formatNumber(item.totalClicks) }}</span>
                </template>

                <template v-slot:totalGmv="{ item }">
                  <span>{{ formatNumber(item.totalGmv) }}원</span>
                </template>

                <template v-slot:avgCtr="{ item }">
                  <span>{{ item.avgCtr }}%</span>
                </template>

                <template v-slot:avgCvr="{ item }">
                  <span>{{ item.avgCvr }}%</span>
                </template>

                <template v-slot:productCount="{ item }">
                  <span>{{ item.productCount }}개</span>
                </template>

                <template v-slot:mainCategory="{ item }">
                  <span class="badge bg-info" v-if="item.mainCategory">{{ item.mainCategory }}</span>
                  <span class="text-muted" v-else>-</span>
                </template>

                <template v-slot:avgRpi="{ item }">
                  <span>{{ item.avgRpi }}</span>
                </template>

                <template v-slot:medianRpi="{ item }">
                  <span>{{ item.medianRpi }}</span>
                </template>

                <template v-slot:avgSalesAmount="{ item }">
                  <span>{{ formatNumber(item.avgSalesAmount) }}원</span>
                </template>

                <template v-slot:medianSalesAmount="{ item }">
                  <span>{{ formatNumber(item.medianSalesAmount) }}원</span>
                </template>

                <template v-slot:revenueShare="{ item }">
                  <span class="badge bg-success">{{ item.revenueShare }}%</span>
                </template>

                <template v-slot:revenueShareBasedRevenueRange="{ item }">
                  <span class="badge bg-success">{{ item.revenueShareBasedRevenueRange }}</span>
                </template>

                <template v-slot:priceRange25to75="{ item }">
                  <span class="badge bg-info text-dark">{{ item.priceRange25to75 }}</span>
                </template>

                <template v-slot:accessibilityScore="{ item }">
                  <span class="badge bg-primary">{{ formatNumber(item.accessibilityScore) }}</span>
                </template>

                <!-- 새로운 단일키워드 매출 컬럼들 -->
                <template v-slot:singleKeywordTotalRevenue="{ item }">
                  <span class="text-primary fw-bold">{{ formatNumber(item.singleKeywordTotalRevenue) }}원</span>
                </template>

                <template v-slot:singleKeywordAvgRevenue="{ item }">
                  <span class="text-primary">{{ formatNumber(item.singleKeywordAvgRevenue) }}원</span>
                </template>

                <template v-slot:singleKeywordMedianRevenue="{ item }">
                  <span class="text-primary">{{ formatNumber(item.singleKeywordMedianRevenue) }}원</span>
                </template>

                <template v-slot:singleKeywordTotalSales="{ item }">
                  <span class="text-info">{{ formatNumber(item.singleKeywordTotalSales) }}개</span>
                </template>

                <template v-slot:singleKeywordMaxRevenue="{ item }">
                  <span class="text-primary">{{ formatNumber(item.singleKeywordMaxRevenue) }}원</span>
                </template>

                <!-- 단일키워드 RPI 지표들 -->
                <template v-slot:singleKeywordMaxRpi="{ item }">
                  <span class="text-danger fw-bold">{{ item.singleKeywordMaxRpi }}</span>
                </template>

                <template v-slot:singleKeywordAvgRpi="{ item }">
                  <span class="text-warning">{{ item.singleKeywordAvgRpi }}</span>
                </template>

                <template v-slot:singleKeywordMedianRpi="{ item }">
                  <span>{{ item.singleKeywordMedianRpi }}</span>
                </template>

                <template v-slot:singleKeywordMinRpi="{ item }">
                  <span class="text-muted">{{ item.singleKeywordMinRpi }}</span>
                </template>

                <!-- 단일키워드 CTR 지표들 -->
                <template v-slot:singleKeywordMaxCtr="{ item }">
                  <span class="text-success fw-bold">{{ item.singleKeywordMaxCtr }}%</span>
                </template>

                <template v-slot:singleKeywordAvgCtr="{ item }">
                  <span class="text-info">{{ item.singleKeywordAvgCtr }}%</span>
                </template>

                <template v-slot:singleKeywordMedianCtr="{ item }">
                  <span>{{ item.singleKeywordMedianCtr }}%</span>
                </template>

                <template v-slot:singleKeywordMinCtr="{ item }">
                  <span class="text-muted">{{ item.singleKeywordMinCtr }}%</span>
                </template>

                <!-- 단일키워드 CVR 지표들 -->
                <template v-slot:singleKeywordMaxCvr="{ item }">
                  <span class="text-success fw-bold">{{ item.singleKeywordMaxCvr }}%</span>
                </template>

                <template v-slot:singleKeywordAvgCvr="{ item }">
                  <span class="text-info">{{ item.singleKeywordAvgCvr }}%</span>
                </template>

                <template v-slot:singleKeywordMedianCvr="{ item }">
                  <span>{{ item.singleKeywordMedianCvr }}%</span>
                </template>

                <template v-slot:singleKeywordMinCvr="{ item }">
                  <span class="text-muted">{{ item.singleKeywordMinCvr }}%</span>
                </template>

                <!-- 새로운 통합키워드 지표들 -->
                <template v-slot:categoryFitness="{ item }">
                  <span class="badge bg-info">{{ item.categoryFitness }}</span>
                </template>

                <template v-slot:totalSalesCount="{ item }">
                  <span>{{ formatNumber(item.totalSalesCount) }}개</span>
                </template>

                <template v-slot:maxSalesAmount="{ item }">
                  <span class="text-success fw-bold">{{ formatNumber(item.maxSalesAmount) }}원</span>
                </template>

                <!-- RPI 지표들 -->
                <template v-slot:maxRpi="{ item }">
                  <span class="text-danger fw-bold">{{ item.maxRpi }}</span>
                </template>

                <template v-slot:minRpi="{ item }">
                  <span class="text-muted">{{ item.minRpi }}</span>
                </template>

                <!-- 상위랭킹 RPI 지표들 -->
                <template v-slot:topRankMaxRpi="{ item }">
                  <span class="text-danger fw-bold">{{ item.topRankMaxRpi }}</span>
                </template>

                <template v-slot:topRankAvgRpi="{ item }">
                  <span class="text-warning fw-bold">{{ item.topRankAvgRpi }}</span>
                </template>

                <template v-slot:topRankMedianRpi="{ item }">
                  <span class="text-info">{{ item.topRankMedianRpi }}</span>
                </template>

                <template v-slot:topRankMinRpi="{ item }">
                  <span class="text-muted">{{ item.topRankMinRpi }}</span>
                </template>

                <!-- CTR 지표들 -->
                <template v-slot:maxCtr="{ item }">
                  <span class="text-success fw-bold">{{ item.maxCtr }}%</span>
                </template>

                <template v-slot:medianCtr="{ item }">
                  <span>{{ item.medianCtr }}%</span>
                </template>

                <template v-slot:minCtr="{ item }">
                  <span class="text-muted">{{ item.minCtr }}%</span>
                </template>

                <!-- CVR 지표들 -->
                <template v-slot:maxCvr="{ item }">
                  <span class="text-success fw-bold">{{ item.maxCvr }}%</span>
                </template>

                <template v-slot:medianCvr="{ item }">
                  <span>{{ item.medianCvr }}%</span>
                </template>

                <template v-slot:minCvr="{ item }">
                  <span class="text-muted">{{ item.minCvr }}%</span>
                </template>

                <template v-slot:marketCvr="{ item }">
                  <span>{{ item.marketCvr }}%</span>
                </template>

                <template v-slot:adImpressions="{ item }">
                  <span>{{ item.adImpressions === null || item.adImpressions === undefined ? '-' : formatNumber(item.adImpressions) }}</span>
                </template>

                <template v-slot:organicImpressions="{ item }">
                  <span>{{ item.organicImpressions === null || item.organicImpressions === undefined ? '-' : formatNumber(item.organicImpressions) }}</span>
                </template>
              </CDataTable>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 대량수집설정 모달 -->
    <div class="modal fade" :class="{ show: showBulkModal }" :style="{ display: showBulkModal ? 'block' : 'none' }" tabindex="-1">
      <div class="modal-dialog modal-xl" style="max-height: 90vh;">
        <div class="modal-content" style="max-height: 90vh; overflow-y: auto;">
          <div class="modal-header">
            <h5 class="modal-title">키워드설정 - {{ activeKeywordGroup }}</h5>
            <button type="button" class="btn-close" @click="showBulkModal = false"></button>
          </div>
          <div class="modal-body">
            <!-- 키워드 추가 통합 영역 -->
            <div class="card mb-4 mt-2">
              <div class="card-body py-2 px-3">
                <table class="w-100" style="border-collapse: separate; border-spacing: 0 6px;">
                  <tbody>
                    <!-- 첫 번째 줄: 페이지 범위 + 키워드 입력 + 업로드 버튼 -->
                    <tr>
                      <td v-if="collectMode === 'pageRange'" style="width: 70px; vertical-align: middle;">
                        <span class="small text-muted">페이지범위</span>
                      </td>
                      <td v-if="collectMode === 'pageRange'" style="width: 160px; vertical-align: middle;">
                        <div class="d-flex align-items-center">
                          <input type="number" class="form-control form-control-sm text-center" style="width: 50px;" v-model="defaultStartPage" :disabled="isAnalyzing" min="1" max="30">
                          <span class="text-muted mx-1">~</span>
                          <input type="number" class="form-control form-control-sm text-center" style="width: 50px;" v-model="defaultEndPage" :disabled="isAnalyzing" min="1" max="30">
                        </div>
                        <small class="text-warning d-block" style="font-size: 0.7em; line-height: 1.1;">로그인해야 여러 페이지 수집</small>
                      </td>
                      <td style="width: 50px; vertical-align: middle; text-align: right; padding-right: 8px;">
                        <span class="small text-muted">키워드</span>
                      </td>
                      <td style="vertical-align: middle;">
                        <input ref="modalKeywordInput" type="text" class="form-control form-control-sm" placeholder="키워드 입력 후 Enter" v-model="targetKeyword" @keyup.enter="addKeywordToList" :disabled="isAnalyzing">
                      </td>
                      <td style="width: 100px; vertical-align: middle; text-align: right;">
                        <button type="button" class="btn btn-sm w-100 ml-2" :class="showExcelUploadSection ? 'btn-primary' : 'btn-outline-secondary'" @click="toggleExcelUploadSection">
                          <i class="fas fa-file-upload mr-2"></i>업로드
                        </button>
                      </td>
                      <td style="width: 130px; vertical-align: middle; text-align: right;">
                        <div class="btn-group btn-group-sm w-100 ml-2" role="group" aria-label="수집 모드">
                          <button type="button" class="btn" :class="collectMode === 'fast' ? 'btn-primary' : 'btn-outline-secondary'" @click="collectMode = 'fast'" :disabled="isAnalyzing">빠른수집</button>
                          <button type="button" class="btn" :class="collectMode === 'pageRange' ? 'btn-primary' : 'btn-outline-secondary'" @click="collectMode = 'pageRange'" :disabled="isAnalyzing">페이지별</button>
                        </div>
                      </td>
                    </tr>
                    <!-- 두 번째 줄: 상품링크 입력 -->
                    <tr>
                      <td style="vertical-align: middle;">
                        <span class="small text-muted">상품링크</span>
                      </td>
                      <td colspan="3" style="vertical-align: middle;">
                        <input ref="modalProductLinkInput" type="text" class="form-control form-control-sm" placeholder="추적할 쿠팡 상품링크 입력 후 Enter (트래픽 키워드 수집 및 상품추적)" v-model="targetProductLink" @keyup.enter="collectProductTrafficKeywords" :disabled="isAnalyzing">
                      </td>
                      <td style="vertical-align: middle; text-align: right;">
                        <button type="button" class="btn btn-outline-primary btn-sm w-100 ml-2" @click="collectProductTrafficKeywords" :disabled="isAnalyzing || !targetProductLink"><i class="fas fa-download mr-1"></i>수집</button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- 네이버데이터랩 섹션 -->
            <div v-if="showDataLabSection" class="row" style="z-index: 0;">
              <div class="col-md-12">
                <div class="card">
                  <div class="card-header">
                    <h6 class="mb-0">네이버 데이터랩 키워드 수집</h6>
                  </div>
                  <div class="card-body">
                    <div class="row">
                      <div class="col-md-12 d-flex align-items-center justify-content-between">
                        <div style="font-size:85%">
                          <b>[수집할 키워드 순위설정]</b><br>
                          새탭에 네이버 데이터랩 쇼핑인사이트 Top500키워드가 나오도록 먼저 세팅해주세요
                        </div>
                        <div class="d-flex align-items-center justify-content-center">
                          <div class="d-flex align-items-center mr-2">
                            <label class="form-label mr-2">시작순위:</label>
                            <input type="number" :min="1" :max="500" class="form-control text-center"
                                   v-model="startRank" style="width: 80px;">
                          </div>
                          <div class="d-flex align-items-center">
                            <label class="form-label mr-2">~ 종료순위:</label>
                            <input type="number" :min="1" :max="500" class="form-control text-center"
                                   v-model="endRank" style="width: 80px;">
                          </div>
                          <button type="button" class="btn btn-success ml-3" @click="collectKeywordRank">
                            수집하기
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 키워드 업로드 섹션 -->
            <div v-if="showExcelUploadSection" class="row" style="z-index: 0;">
              <div class="col-md-12">
                <div class="card">
                  <div class="card-header">
                    <h6 class="mb-0">키워드 업로드</h6>
                  </div>
                  <div class="card-body d-flex justify-content-between">
                    <div style="font-size:85%">
                      <b>[업로드 방법]</b><br>
                        1. 엑셀업로드 포맷 다운로드<br>
                        2. 엑셀 파일에서 키워드 부분(맨윗줄 제외) 업데이트<br>
                        3. 업데이트된 엑셀 파일 업로드 순으로 진행해 주세요.
                    </div>
                    <div class="d-flex justify-content-end align-items-center">
                      <div class="d-flex flex-column mr-4">
                        <label class="form-label mr-4">1. 엑셀업로드 포맷</label>
                        <div>
                          <button type="button" class="btn btn-primary" @click="downloadTemplate">Download</button>
                        </div>
                      </div>
                      <div class="d-flex flex-column mr-4">
                        <label class="form-label">2. 업데이트된 키워드 파일 업로드</label>
                        <div class="custom-file">
                          <input ref="bulkUpload" type="file" class="custom-file-input" @change="uploadBulkFile" accept=".xlsx,.xls" id="inputGroupFile02">
                          <label class="custom-file-label" for="inputGroupFile02">{{ uploadstatus }}</label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 키워드 목록 테이블 -->
            <div class="row mt-2" style="z-index:999">
              <div class="col-md-12">
                  <!-- 일괄 처리 UI (선택된 항목이 있을 때만 표시) -->
                  <div v-if="selectedKeywordItems.length > 0" class="alert alert-info mb-2 py-2 px-3" style="border-left: 4px solid #0d6efd;">
                    <div class="d-flex justify-content-between w-100">
                      <div class="d-flex align-items-center">
                        <i class="fas fa-check-square text-primary me-2"></i>
                        <strong class="text-primary">{{ selectedKeywordItems.length }}개 선택됨</strong>
                      </div>
                      <div class="d-flex align-items-center">
                        <!-- 일괄 페이지범위 변경 -->
                        <div class="d-flex align-items-center" style="gap: 6px;">
                          <span class="small fw-bold">페이지:</span>
                          <input
                            type="number"
                            class="form-control form-control-sm text-center"
                            style="width: 55px; border: 2px solid #0d6efd;"
                            v-model.number="bulkStartPage"
                            min="1"
                            max="30"
                          >
                          <span class="fw-bold">~</span>
                          <input
                            type="number"
                            class="form-control form-control-sm text-center"
                            style="width: 55px; border: 2px solid #0d6efd;"
                            v-model.number="bulkEndPage"
                            min="1"
                            max="30"
                          >
                          <button
                            type="button"
                            class="btn btn-sm btn-primary ml-2"
                            @click="applyBulkPageRange"
                            :disabled="isAnalyzing"
                          >
                            <i class="fas fa-check mr-1"></i>적용
                          </button>
                        </div>
                        <div class="vr" style="height: 24px;"></div>
                        <!-- 일괄 삭제 -->
                        <button
                          type="button"
                          class="btn btn-sm btn-danger ml-3"
                          @click="deleteSelectedKeywords"
                          :disabled="isAnalyzing"
                        >
                          <i class="fas fa-trash-alt mr-1"></i>삭제
                        </button>
                      </div>
                    </div>
                  </div>

                  <div v-if="autoList.length === 0" class="text-center py-4 text-muted">
                    <i class="fas fa-list fa-3x mb-3 opacity-50"></i>
                    <p>등록된 키워드가 없습니다.<br>위에서 키워드를 추가해주세요.</p>
                  </div>
                  <CDataTable
                      storage-key="keywordDataCollect.3"
                    v-else
                    :key="autoList.length + '-' + collectMode"
                    :headers="bulkHeadersFiltered"
                    :items="autoListWithIndex"
                    :itemsPerPage="100"
                    :search="true"
                    :pagination="true"
                    :itemKey="'index'"
                    :show-select="true"
                    v-model="selectedKeywordItems"
                    class="table-responsive"
                  >
                    <template v-slot:index="{ item }">
                      <span>{{ item.index + 1 }}</span>
                    </template>

                    <template v-slot:keyword="{ item }">
                      <span class="fw-bold">{{ item.keyword }}</span>
                    </template>

                    <template v-slot:addRel="{ item }">
                      <button type="button"
                              class="btn btn-outline-primary btn-sm"
                              @click="addRelKeyword(item)"
                              :disabled="isAnalyzing"
                              title="연관 키워드 수집">
                        +
                      </button>
                    </template>

                    <template v-slot:source="{ item }">
                      <span class="badge bg-info">{{ item.source || '직접입력' }}</span>
                    </template>

                    <template v-slot:pageRange="{ item }">
                      <div class="d-flex align-items-center justify-content-center" style="gap: 3px;">
                        <input
                          type="number"
                          class="form-control form-control-sm text-center"
                          style="width: 45px; font-size: 12px;"
                          v-model="item.startPage"
                          min="1"
                          max="30"
                          @input="updatePageRange(item)"
                        >
                        <span class="text-muted small">~</span>
                        <input
                          type="number"
                          class="form-control form-control-sm text-center"
                          style="width: 45px; font-size: 12px;"
                          v-model="item.endPage"
                          min="1"
                          max="30"
                          @input="updatePageRange(item)"
                        >
                      </div>
                    </template>

                    <template v-slot:pageStatus="{ item }">
                      <div class="d-flex flex-wrap gap-1 justify-content-center" style="max-width: 180px;">
                        <span
                          v-for="page in getPageNumbers(item)"
                          :key="page"
                          class="badge"
                          :class="getPageBadgeClass(item, page)"
                          style="font-size: 0.7rem; min-width: 20px;"
                        >
                          {{ page }}
                        </span>
                      </div>
                    </template>

                    <template v-slot:status="{ item }">
                      <span v-if="isAllPagesCompleted(item)" class="badge bg-success">완료</span>
                      <span v-else class="badge bg-secondary">대기</span>
                    </template>

                    <template v-slot:collectedAt="{ item }">
                      <span v-if="item.collectedAt">{{ formatDate(item.collectedAt) }}</span>
                      <span v-else class="text-muted">-</span>
                    </template>

                    <template v-slot:actions="{ item }">
                      <button class="btn btn-outline-secondary btn-sm"
                              @click="deleteKeywordFromList(item.index)"
                              title="키워드 삭제">
                        x
                      </button>
                    </template>
                  </CDataTable>
                </div>
            </div>

            <!-- 수집 설정 -->
            <div class="row mt-3">
              <div class="col-md-12">
                <div class="card">
                  <div class="card-body">
                    <div class="d-flex justify-content-between align-items-center">
                      <div class="d-flex align-items-center">
                        <label class="mr-2 fw-bold">수집 딜레이:</label>
                        <input type="number" class="form-control mr-2" style="width: 100px;" v-model="delayTime" min="100" max="5000">
                        <span class="text-muted small">ms</span>
                      </div>
                      <div class="d-flex align-items-center">
                        <button type="button" class="btn btn-success" @click="collectAllKeywords" :disabled="isAnalyzing || autoList.length === 0 || (analysisResults.length >= maxProducts && analysisResults.length > 0)">
                        <i class="fas fa-play mr-1"></i>전체수집
                        <span v-if="analysisResults.length >= maxProducts && analysisResults.length > 0" class="badge bg-danger ml-1">제한도달</span>
                      </button>
                      <button type="button" class="btn btn-warning ml-2" @click="clearAutoList" v-if="autoList.length > 0">
                      <i class="fas fa-trash mr-1"></i>전체삭제
                    </button>
                      </div>

                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer bg-light">
            <div class="d-flex justify-content-between align-items-center w-100">
              <div class="text-muted small">
                총 {{ autoList.length }}개 키워드 | 미수집 {{ newKeywordsCount }}개
              </div>
              <div>
                <button type="button" class="btn btn-secondary mr-2" @click="showBulkModal = false">
                  <i class="fas fa-times mr-1"></i>닫기
                </button>
                <button type="button" class="btn btn-primary" @click="collectNewKeywords" :disabled="isAnalyzing || newKeywordsCount === 0 || analysisResults.length >= maxProducts">
                  <i class="fas fa-download mr-1"></i>신규 수집 ({{ newKeywordsCount }}개)
                  <span v-if="analysisResults.length >= maxProducts" class="badge bg-danger ml-1">제한도달</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="modal-backdrop fade" :class="{ show: showBulkModal }" v-if="showBulkModal"></div>

    <!-- Task 4: 전체 카테고리 선택 모달 (CDataTable 사용) -->
    <!-- v-show 사용: CDataTable이 unmount되지 않아 선택 상태가 유지됨 -->
    <div class="modal fade" :class="{ show: showCategoryModal, 'd-block': showCategoryModal }"
         tabindex="-1" v-show="showCategoryModal" @click.self="closeCategoryModal">
      <div class="modal-dialog modal-xl modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">🎯 전체 카테고리 선택</h5>
            <div class="d-flex align-items-center gap-2 ml-auto mr-3">
              <small class="text-muted">{{ categoryModalSelectedItems.length }}개 선택됨</small>
            </div>
            <button type="button" class="btn-close" @click="closeCategoryModal"></button>
          </div>
          <div class="modal-body">
            <CDataTable
                storage-key="keywordDataCollect.4"
              ref="categoryModalTable"
              v-model="categoryModalSelectedItems"
              :headers="categoryModalHeaders"
              :items="categoryModalTableDataCache"
              :itemsPerPage="100"
              :itemKey="'category'"
              :showSelect="true"
              :isPerPage="true"
              :isColumn="false"
              :isRadio="false"
              height="500px"
            >
              <template v-slot:category="{ item }">
                <span>{{ item.category }}</span>
              </template>
              <template v-slot:totalImpressions="{ item }">
                {{ formatNumber(item.totalImpressions) }}
              </template>
              <template v-slot:avgCtr="{ item }">
                {{ item.avgCtr.toFixed(2) }}%
              </template>
            </CDataTable>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeCategoryModal">취소</button>
            <button type="button" class="btn btn-primary" @click="applyCategoryModalSelection">
              적용 ({{ categoryModalSelectedItems.length }}개)
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="modal-backdrop fade" :class="{ show: showCategoryModal }" v-show="showCategoryModal"></div>
  </div>
</template>

<script>
import CDataTable from '@/components/common/CDataTable.vue'
import ApexChart from '@/components/charts/ApexChart.vue'
import keywordAnalysisDB from '@/utils/keywordAnalysisDB.js'
import sourcingChecklistService from '@/services/works/sourcingChecklistService'
import workflowService from '@/services/workflowService';
import * as XLSX from 'xlsx';
import axios from 'axios';

// 「기회」 라벨 판정 임계값 (잠정치 — 추후 조건서로 옮긴다)
const OPPORTUNITY_THRESHOLDS = {
  MIN_CLICKS: 11,               // 표본부족 가드: analyzeBestProducts() 의 기존 기준과 동일
  MIN_SALES_28D: 3,             // 표본부족 가드: analyzeBestProducts() 의 기존 기준과 동일
  LOW_RPI_PERCENTILE: 40,       // RPI 백분위가 이 미만이면 '전환약함'
  HIGH_RPI_PERCENTILE: 60,      // RPI 백분위가 이 이상이면 '노출여력' 또는 '이미포화'
  LOW_IMPRESSION_PERCENTILE: 45 // 노출 백분위가 이 이하면 '노출여력'
};

// 윙 계정에 비즈니스인사이트 구독이 없을 때(확장 failReason NO_BUSINESS_INSIGHT) — 조용한 0개 대신 이 안내로 멈춘다
const NO_BUSINESS_INSIGHT_MSG = '이 계정은 비즈니스인사이트 구독이 없어 이 메뉴를 쓸 수 없습니다 — 구독이 있는 윙 계정으로 바꿔 주세요'

export default {
  name: 'KeywordDataCollect',
  components: {
    CDataTable,
    ApexChart
  },
  data() {
    return {
      targetKeyword: '',
      targetProductLink: '', // 상품링크 입력 필드 (트래픽 키워드 수집)
      comparisonProduct: null, // 비교상품 정보 저장
      defaultStartPage: 1, // 새로 추가되는 키워드의 기본 시작 페이지
      defaultEndPage: 1, // 새로 추가되는 키워드의 기본 끝 페이지
      collectMode: 'fast', // 빠른수집(pre-matching+business-insight) 기본 / 페이지별(www 다중페이지) 토글
      isAnalyzing: false,
      progressPrefix: '', // 진행 상황 앞부분 (키워드/페이지 정보)
      progressSuffix: '', // 진행 상황 뒷부분 (Extension 메시지)
      progressPercent: 0,
      analysisResults: [],
      currentKeywordIndex: 0, // 현재 분석 중인 키워드 인덱스

      // 모든 계산이 완료된 데이터 (성능 최적화)
      enrichedAnalysisResults: [],   // 모든 계산값이 포함된 데이터

      // 미리 계산된 필터링된 데이터들 (각 분석에서 재사용)
      validProductsWithOutliers: [],    // 이상치포함 유효 상품들
      validProductsWithoutOutliers: [], // 이상치제외 유효 상품들
      top300ByPriceCvr: [],            // 판매가×CVR Top300 (이상치포함)
      top20ByImpressions: [],          // 총노출수 Top20 (이상치포함)
      top20ByRPI: [],                  // RPI Top20 (이상치제외)

      // 통합 키워드 분석 데이터
      summaryData: [],
      summaryHeaders: [
        { text: '지표', value: 'metric', sortable: false, width: '200px' },
        { text: '값', value: 'value', sortable: false, width: '150px' }
      ],

      // 키워드그룹 관리
      keywordGroups: ['Default'],
      activeKeywordGroup: 'Default',
      showKeywordGroup: true,

      // 키워드설정 (기존 대량수집설정)
      autoList: [],
      selectedKeywordItems: [], // 키워드설정 테이블 다중 선택용
      bulkStartPage: 1, // 일괄 페이지범위 시작
      bulkEndPage: 3, // 일괄 페이지범위 끝
      originalAutoList: null, // 신규 수집 시 원본 백업용
      delayTime: 300,
      uploadstatus: 'Excel 파일 업로드',
      showBulkModal: false,

      // 수집 제한 설정
      maxProducts: 5000, // 최대 수집 상품 수 제한



      // IndexedDB 관련
      currentGroupId: 'default', // 현재 키워드 그룹 ID
      existingVendorItemIds: [], // 중복 방지용 기존 vendorItemId 목록
      isLoadingFromDB: false, // DB에서 데이터 로딩 중 여부

      // 작업 큐 방식
      workQueue: [], // 작업 큐 [{keyword, keywordIndex, pageIndex, totalPages}]
      isProcessingQueue: false, // 큐 처리 중 여부

      // 쿠팡 API 차단 감지 (Preflight + 연속 실패 카운터)
      hasReceivedAnyCollectionSuccess: false, // 시작 이후 단 1건이라도 수집 성공했는지
      consecutivePage1Failures: 0, // 연속 page 1 throw 카운터 (성공 시 리셋)



      // 키워드설정 모달 섹션 표시 상태
      showDataLabSection: false,
      showExcelUploadSection: false, // 기본적으로 숨김

      // 연관+자동완성 키워드 수집 (개별 키워드용)
      relCycle: 50,

      // 네이버 데이터랩 키워드 수집
      startRank: 1,
      endRank: 50,

      // 대량수집 테이블 헤더
      bulkHeaders: [
        { text: '순번', value: 'index', align: 'center', width: 80, isSlot: true },
        { text: '키워드', value: 'keyword', align: 'center', width: 120, isSlot: true },
        { text: '연관\n자동완성', value: 'addRel', align: 'center', width: 80, isSlot: true },
        { text: '수집방법', value: 'source', align: 'center', width: 120, isSlot: true },
        { text: '페이지범위', value: 'pageRange', align: 'center', width: 120, isSlot: true },
        { text: '페이지현황', value: 'pageStatus', align: 'center', width: 200, isSlot: true },
        { text: '상태', value: 'status', align: 'center', width: 80, isSlot: true },
        { text: '수집일시', value: 'collectedAt', align: 'center', width: 100, isSlot: true },
        { text: '삭제', value: 'actions', align: 'center', width: 80, isSlot: true }
      ],
      tableHeaders: [
        { text: '순위', value: 'rank', sortable: true, width: '80px' },
        { text: '상품명', value: 'productName', sortable: true, width: '250px' },
        { text: '판매가', value: 'price', sortable: true, width: '100px' },
        { text: 'CTR', value: 'ctr', sortable: true, width: '80px' },
        { text: 'CVR', value: 'cvr', sortable: true, width: '80px' },
        { text: 'RPI', value: 'overall', sortable: true, width: '80px' },
        { text: '노출수', value: 'impressions', sortable: true, width: '100px' },
        { text: '매출', value: 'salesLast28d', sortable: true, width: '120px' },
        { text: '광고', value: 'ad', sortable: true, width: '60px' },
        { text: '트래픽 키워드', value: 'trafficKeyword', sortable: false, width: '200px' }
      ],
      // popup.js와 동일한 분석 데이터
      summaryMetrics: {
        medianCTR: '0.00',
        medianCVR: '0.00',
        totalProducts: 0,
        totalImpressions: 0,
        totalClicks: 0,
        totalSales: 0,
        totalAdImpressions: 0,
        totalOrganicImpressions: 0
      },
      bestSourcingProduct: null,
      bestPriceRange: '데이터 없음',
      bestPriceRanges: [],
      optimizedCategory: '데이터 없음',
      optimizedCategoryInfo: null,
      categoryAnalysis: [],
      pendingCategoryNames: [], // 체크박스로 선택 중인 카테고리 (적용 전)
      appliedCategoryNames: [], // 실제 적용된 카테고리
      // Task 3/4: 카테고리 선택 모달 상태
      showCategoryModal: false,
      categoryModalSearch: '',
      categoryModalSortKey: 'totalRevenue',
      categoryModalSortDesc: true,
      categoryModalPendingSelection: [], // 모달 내 임시 선택 (deprecated)
      categoryModalSelectedItems: [], // CDataTable v-model용 선택된 아이템 배열
      categoryModalTableDataCache: [], // 모달용 테이블 데이터 캐시 (참조 유지용)
      bestProducts: [],
      integratedKeywords: [],

      // 베스트 상품 테이블 헤더
      bestProductHeaders: [
        { text: '순위', value: 'rank', sortable: true, width: '50px', isSlot: true },
        { text: '상품명', value: 'productName', sortable: true, width: '200px', isSlot: true },
        { text: '브랜드', value: 'brandName', sortable: true, width: '80px', isSlot: true },
        { text: '노출순위', value: 'currentRank', sortable: true, width: '70px', isSlot: true },
        { text: '노출수', value: 'impressions', sortable: true, width: '80px', isSlot: true },
        { text: '클릭수', value: 'clicks', sortable: true, width: '70px', isSlot: true },
        { text: '판매수', value: 'sales', sortable: true, width: '55px', isSlot: true },
        { text: 'CTR', value: 'ctr', sortable: true, width: '50px', isSlot: true },
        { text: 'CVR', value: 'cvr', sortable: true, width: '50px', isSlot: true },
        { text: '판매가', value: 'price', sortable: true, width: '80px', isSlot: true },
        { text: 'RPI', value: 'rpi', sortable: true, width: '50px', isSlot: true },
        { text: '매출', value: 'revenue', sortable: true, width: '120px', isSlot: true },
        { text: '시선집중도', value: 'attentionScore', sortable: true, width: '50px', isSlot: true },
        { text: '트래픽키워드', value: 'trafficKeyword', sortable: false, width: '150px', isSlot: true }
      ],

      // 기본 헤더 (상세보기 OFF)
      basicIntegratedKeywordHeaders: [
        { text: '키워드', value: 'keyword', sortable: true, width: '120px', isSlot: true },
        { text: '총 노출수', value: 'totalVolume', sortable: true, width: '100px', isSlot: true },
        { text: '자연노출수', value: 'organicImpressions', sortable: true, width: '100px', isSlot: true },
        { text: '광고노출수', value: 'adImpressions', sortable: true, width: '100px', isSlot: true },
        { text: '총 클릭수', value: 'totalClicks', sortable: true, width: '100px', isSlot: true },
        { text: '노출 상품수', value: 'productCount', sortable: true, width: '100px', isSlot: true },
        { text: '주요 카테고리', value: 'mainCategory', sortable: true, width: '120px', isSlot: true },
        { text: '카테고리적합도', value: 'categoryFitness', sortable: true, width: '100px', isSlot: true },
        { text: '총 판매량', value: 'totalSalesCount', sortable: true, width: '100px', isSlot: true },
        { text: '평균매출', value: 'avgSalesAmount', sortable: true, width: '100px', isSlot: true },
        { text: '중간매출', value: 'medianSalesAmount', sortable: true, width: '100px', isSlot: true },
        { text: '일매출\n가이드라인', value: 'revenueShareBasedRevenueRange', sortable: true, width: '140px', isSlot: true },
        { text: '가격대\n(25%~75%)', value: 'priceRange25to75', sortable: true, width: '120px', isSlot: true },
        { text: '접근성', value: 'accessibilityScore', sortable: true, width: '100px', isSlot: true },
        { text: '상위랭킹\n평균RPI', value: 'topRankAvgRpi', sortable: true, width: '100px', isSlot: true },
        { text: '평균 RPI', value: 'avgRpi', sortable: true, width: '80px', isSlot: true },
        { text: '중간 RPI', value: 'medianRpi', sortable: true, width: '80px', isSlot: true },
        { text: '평균 CTR', value: 'avgCtr', sortable: true, width: '80px', isSlot: true },
        { text: '중간 CTR', value: 'medianCtr', sortable: true, width: '80px', isSlot: true },
        { text: '평균 CVR', value: 'avgCvr', sortable: true, width: '80px', isSlot: true },
        { text: '중간 CVR', value: 'medianCvr', sortable: true, width: '80px', isSlot: true }
      ],

      // 상세 헤더 (상세보기 ON) - 현재 전체 데이터
      detailIntegratedKeywordHeaders: [
        { text: '키워드', value: 'keyword', sortable: true, width: '120px', isSlot: true },
        { text: '총 노출수', value: 'totalVolume', sortable: true, width: '100px', isSlot: true },
        { text: '자연노출수', value: 'organicImpressions', sortable: true, width: '100px', isSlot: true },
        { text: '광고노출수', value: 'adImpressions', sortable: true, width: '100px', isSlot: true },
        { text: '총 클릭수', value: 'totalClicks', sortable: true, width: '100px', isSlot: true },
        { text: '노출 상품수', value: 'productCount', sortable: true, width: '100px', isSlot: true },
        { text: '주요 카테고리', value: 'mainCategory', sortable: true, width: '120px', isSlot: true },
        { text: '카테고리적합도', value: 'categoryFitness', sortable: true, width: '100px', isSlot: true },
        { text: '총 판매량', value: 'totalSalesCount', sortable: true, width: '100px', isSlot: true },
        { text: '총매출', value: 'totalGmv', sortable: true, width: '120px', isSlot: true },
        { text: '최대매출', value: 'maxSalesAmount', sortable: true, width: '100px', isSlot: true },
        { text: '평균매출', value: 'avgSalesAmount', sortable: true, width: '100px', isSlot: true },
        { text: '중간매출', value: 'medianSalesAmount', sortable: true, width: '100px', isSlot: true },
        { text: '매출점유율', value: 'revenueShare', sortable: true, width: '100px', isSlot: true },
        { text: '일매출\n가이드라인', value: 'revenueShareBasedRevenueRange', sortable: true, width: '140px', isSlot: true },
        { text: '가격대\n(25%~75%)', value: 'priceRange25to75', sortable: true, width: '120px', isSlot: true },
        { text: '접근성', value: 'accessibilityScore', sortable: true, width: '100px', isSlot: true },
        { text: '상위랭킹\n최대RPI', value: 'topRankMaxRpi', sortable: true, width: '100px', isSlot: true },
        { text: '상위랭킹\n평균RPI', value: 'topRankAvgRpi', sortable: true, width: '100px', isSlot: true },
        { text: '상위랭킹\n중간RPI', value: 'topRankMedianRpi', sortable: true, width: '100px', isSlot: true },
        { text: '상위랭킹\n최소RPI', value: 'topRankMinRpi', sortable: true, width: '100px', isSlot: true },
        { text: '최대 RPI', value: 'maxRpi', sortable: true, width: '80px', isSlot: true },
        { text: '평균 RPI', value: 'avgRpi', sortable: true, width: '80px', isSlot: true },
        { text: '중간 RPI', value: 'medianRpi', sortable: true, width: '80px', isSlot: true },
        { text: '최소 RPI', value: 'minRpi', sortable: true, width: '80px', isSlot: true },

        { text: '최대 CTR', value: 'maxCtr', sortable: true, width: '80px', isSlot: true },
        { text: '평균 CTR', value: 'avgCtr', sortable: true, width: '80px', isSlot: true },
        { text: '중간 CTR', value: 'medianCtr', sortable: true, width: '80px', isSlot: true },
        { text: '최소 CTR', value: 'minCtr', sortable: true, width: '80px', isSlot: true },
        { text: '최대 CVR', value: 'maxCvr', sortable: true, width: '80px', isSlot: true },
        { text: '평균 CVR', value: 'avgCvr', sortable: true, width: '80px', isSlot: true },
        { text: '중간 CVR', value: 'medianCvr', sortable: true, width: '80px', isSlot: true },
        { text: '최소 CVR', value: 'minCvr', sortable: true, width: '80px', isSlot: true }
      ],

      // 현재 사용 중인 헤더 (초기값: 빈 배열, mounted에서 설정됨)
      integratedKeywordHeaders: [],

      bestCTRProduct: null,
      bestCVRProduct: null,
      bestPriceCvrProduct: null,
      bestAttentionProduct: null,
      bestRPIProduct: null,
      topSourcingProducts: [], // TOP3 소싱 상품
      // 트래픽 키워드를 하나도 못 받았을 때 화면에 띄울 안내.
      // 이게 비면 노출수/클릭수/RPI 가 전부 0 이라 소싱 후보가 통째로 사라지는데,
      // 예전에는 아무 표시 없이 "수집 완료"만 떠서 사용자가 원인을 알 수 없었다.
      trafficKeywordNotice: '',
      // 트래픽 키워드를 못 받아 건너뛴 상품/키워드 수 (수집 1회 기준, 안내 배너 문구에 쓴다)
      trafficSkippedProducts: 0,
      trafficSkippedKeywords: 0,
      // 건너뛴 상품의 까닭별 수 — 확장이 trafficSkippedBy 를 줄 때만 찬다(옛 확장 = 0 그대로 → 셋 중 하나 안내)
      trafficSkippedBy: { notSubscribed: 0, fetchFail: 0, noDetail: 0 },
      noBusinessInsight: false, // 이 윙 계정은 비즈니스인사이트 구독 없음(수집 멈춤 · 안내 유지)
      trafficNoticeReported: {}, // 안내 순간을 서버에 보낸 까닭(까닭마다 한 번 · 수집 시작마다 비움 · 판 #780)
      // 목록을 못 받아 수집하지 못한 키워드 수 (로그인 풀림 / 그 외 응답 없음)
      collectFailLoginKeywords: 0,
      collectFailOtherKeywords: 0,
      collectFailBlockedKeywords: 0, // 쿠팡이 잠시 막아(403/429) 못 받은 키워드 수
      collectFailWingBlocked: false, // 그 윙 계정의 쿠팡 판매량 조회가 막힘(다른 윙 계정으로 다시)
      collectFailWingBlockedMessage: '', // 확장이 보낸 막힘 글(쓴 판매자코드·까닭·할 일 · cpl-csrc-wingacctmismatch-1) — 옛 확장은 빈 값
      extensionNoResponse: false, // 확장이 60초 동안 응답하지 않아 수집을 멈췄는지
      extensionNoResponseMessage: '확장 프로그램이 응답하지 않습니다. 쿠플러스 확장이 켜져 있고 최신 버전인지 확인해 주세요.',



      // 사용자 임의 제외/복원 상태
      userExcludedVendorItemIds: [],
      userExcludedList: [],


      // 사용자 제외/복원 처리 스피너 상태
      isUserOutlierProcessing: false,
      processingVendorItemId: null,
      processingAllUserExcluded: false,

      // 상품 소싱 체크리스트 관련
      sourcingChecklist: null,
      isGeneratingChecklist: false,
      showFullChecklist: false,
      checkedChecklistItems: [], // 체크된 체크리스트 항목 인덱스

      // 리뷰 기반 체크리스트 생성 관련
      collectedReviews: [],
      isCollectingReviews: false,



      // 다국어 검색어 생성 상태/결과
      isGeneratingMultilingualKeywords: false,
      multilingualKeywords: [], // [{ ko: {keyword, rationale}, zh: {keyword, rationale}, en: {keyword, rationale} }]

      // 키워드 SEO 분석
      keywordSEOAnalysis: {
        productNameKeywords: [],
        tagKeywords: [],
        suggestions: []
      },

      // 키워드 복사 기능
      generatedProductName: '',
      editableTagKeywords: [],

      // 통합키워드 vs 단일키워드 분석 타입 (기본: 단일키워드)
      keywordAnalysisType: 'single', // 'integrated' 또는 'single'

      // 최적 카테고리만 표시 여부 (기본: true)
      showOptimalCategoryOnly: true,

      // 상세보기 여부 (기본: false)
      showDetailView: false,

      // 페이지별 점진적 수집을 위한 변수들
      currentKeywordPageIndex: 1,
      currentKeywordTotalPages: 1,
      currentKeywordResults: [],

      // 키워드 분석 헤더 툴팁 (헤더의 value 값을 키로 사용)
      keywordHeaderTooltips: {
        'keyword': '분석 대상 키워드입니다',
        'totalVolume': '해당 키워드로 노출된 전체 상품들의 총 노출수입니다',
        'organicImpressions': '자연 검색으로 노출된 횟수입니다 (쿠팡 실측 자연노출 합, 실측 없으면 -)',
        'adImpressions': '광고로 노출된 횟수입니다 (쿠팡 실측 광고노출 합, 실측 없으면 -)',
        'totalClicks': '해당 키워드로 발생한 전체 클릭수입니다',
        'productCount': '해당 키워드에 노출된 상품의 개수입니다',
        'mainCategory': '해당 키워드에 노출된 상품들 중 가장 많은 상품이 속한 카테고리입니다 (최적카테고리는 총매출 기준으로 선정)',
        'categoryFitness': '주요 카테고리에 속한 상품의 비율(%)입니다',
        'totalSalesCount': '해당 키워드에 노출된 모든 상품의 실제 총 판매량입니다',
        'totalGmv': '키워드별 클릭수 × CVR × 가격으로 계산된 키워드 기여 매출입니다',
        'maxSalesAmount': '해당 키워드 총 노출수를 상위 20개 상품의 노출수 비율로 분배하여 각 상품 RPI와 곱한 예상 매출의 최대값입니다',
        'avgSalesAmount': '해당 키워드 총 노출수를 상위 20개 상품의 노출수 비율로 분배하여 각 상품 RPI와 곱한 예상 매출의 평균입니다',
        'medianSalesAmount': '해당 키워드 총 노출수를 상위 20개 상품의 노출수 비율로 분배하여 각 상품 RPI와 곱한 예상 매출의 중간값입니다',
        'revenueShare': '해당 키워드에 노출된 상품들의 전체 매출 중 해당 키워드가 실제로 기여한 매출의 비율(%)입니다',
        'revenueShareBasedRevenueRange': '해당 키워드에 노출된 상품 중 매출 기준 상위 20개의 2위, 10위 실제 매출(판매량×가격) 기준 일매출 가이드라인 범위입니다 (실제 월매출 ÷ 30)',
        'priceRange25to75': '해당 키워드 노출수 기준 상위 20개 상품의 가격 중 25% ~ 75% 구간에 해당하는 가격대입니다',
        'accessibilityScore': '매출점유율기준 중간일매출 ÷ 상위랭킹 평균RPI로 계산된 키워드 진입 용이성 지표입니다 (높을수록 진입하기 쉬움)',
        'maxRpi': '해당 키워드에 노출된 상품들 중 최대 RPI입니다',
        'avgRpi': '이 키워드 클릭수로 가중한 평균 RPI입니다 (RPI = 클릭률 × 구매율 × 판매가, 노출 1회당 매출)',
        'medianRpi': '해당 키워드에 노출된 상품들의 RPI 중간값입니다',
        'minRpi': '해당 키워드에 노출된 상품들 중 최소 RPI입니다',
        'topRankMaxRpi': '키워드 노출수 기준 상위 10개 상품 중 최대 RPI입니다',
        'topRankAvgRpi': '키워드 노출수 기준 상위 10개 상품의 이 키워드 클릭수 가중 평균 RPI입니다',
        'topRankMedianRpi': '키워드 노출수 기준 상위 10개 상품의 중간 RPI입니다',
        'topRankMinRpi': '키워드 노출수 기준 상위 10개 상품 중 최소 RPI입니다',
        'maxCtr': '해당 키워드에 노출된 상품들 중 최대 CTR입니다',
        'avgCtr': '총 클릭수 / 총 노출수 × 100으로 계산된 키워드 전체 CTR입니다',
        'medianCtr': '해당 키워드에 노출된 상품들의 CTR 중간값입니다',
        'minCtr': '해당 키워드에 노출된 상품들 중 최소 CTR입니다',
        'maxCvr': '해당 키워드에 노출된 상품들 중 최대 CVR입니다',
        'avgCvr': '해당 키워드에 노출된 상품들의 CVR 평균값입니다',
        'medianCvr': '해당 키워드에 노출된 상품들의 CVR 중간값입니다',
        'minCvr': '해당 키워드에 노출된 상품들 중 최소 CVR입니다'
      },

      // 가격별 CVR 분석
      priceInput: '',
      expectedMinCVR: '-',
      priceVsCvrData: [],

      // 광고경쟁력 분석
      marginRate: 30,
      minROAS: 333,
      sortByImpression: false,
      adCompetitivenessData: [],



      // 차트 표시 상태
      showImpressionChart: false,
      showPriceVsCvrChart: false,
      showRpiCompetitivenessChart: false,
      showAdCompetitivenessChart: false,
      showPriceRangeSalesChart: false,
      showDailyTrafficChart: false,

      // 차트 옵션들
      impressionChartOptions: {},
      priceVsCvrChartOptions: {},
      rpiCompetitivenessChartOptions: {},
      adCompetitivenessChartOptions: {},
      priceRangeSalesChartOptions: {},
      dailyTrafficChartOptions: {},

      // RPI Top20 데이터 (다운로드용)
      rpiTop20Products: [],

      // 메모리 사용량 모니터링
      memoryUsage: {
        totalProducts: 0,
        estimatedSize: 0,
        lastUpdated: null
      },

      // 디버깅용 추적 vendorItemId
      debugTargetVendorId: '92021995662',

      // 키워드그룹 변경 로딩 상태
      isGroupSwitching: false
    }
  },
  async created() {
    // 키워드그룹 초기화 (IndexedDB에서 기존 그룹들 로드)
    await this.initializeKeywordGroups()
  },
  async mounted() {
    // console.log('🚀 [컴포넌트 마운트] 시작')
    // console.log('🚀 [컴포넌트 마운트] 현재 그룹:', this.activeKeywordGroup, '그룹 ID:', this.currentGroupId)

    // Extension 메시지 리스너 등록
    window.addEventListener('message', this.handleExtensionMessage)

    // IndexedDB 초기화 및 기존 데이터 로딩
    // console.log('🚀 [컴포넌트 마운트] IndexedDB 초기화 시작...')
    await this.initializeDB()

    // console.log('🚀 [컴포넌트 마운트] 기존 데이터 로딩 시작...')
    await this.loadExistingData()

    // 초기 헤더 설정
    this.updateHeaders()

    // console.log('✅ [컴포넌트 마운트] 완료 - 키워드:', this.autoList.length, '개, 상품:', this.analysisResults.length, '개')
    // console.log('✅ [컴포넌트 마운트] 헤더 설정 완료:', this.integratedKeywordHeaders.length, '개 헤더')
  },
  beforeDestroy() {
    // 메시지 리스너 제거
    window.removeEventListener('message', this.handleExtensionMessage)
  },
  watch: {
    // 최적카테고리 스위치 제거됨 - 상단 카테고리 선택으로 대체
  },
  computed: {
    // 비교상품이 TOP3에 포함되어 있는지 확인
    isComparisonInTop3() {
      if (!this.comparisonProduct || !this.topSourcingProducts || this.topSourcingProducts.length === 0) {
        return false
      }
      return this.topSourcingProducts.some(p => p.vendorItemId === this.comparisonProduct.vendorItemId)
    },

    // 비교상품의 TOP3 순위 (1, 2, 3 중 하나)
    comparisonTop3Rank() {
      if (!this.comparisonProduct || !this.topSourcingProducts) return null
      const index = this.topSourcingProducts.findIndex(p => p.vendorItemId === this.comparisonProduct.vendorItemId)
      return index >= 0 ? index + 1 : null
    },

    // 비교상품의 RPI 값 계산
    comparisonProductRPI() {
      if (!this.comparisonProduct) return null
      // analysisResults에서 비교상품 찾아서 RPI 계산
      const found = this.enrichedAnalysisResults.find(p => p.vendorItemId === this.comparisonProduct.vendorItemId)
      if (found) {
        return parseFloat(found.calculatedRPI || found.overall || 0).toFixed(2)
      }
      // enrichedAnalysisResults에 없으면 comparisonProduct 자체 데이터 사용
      return this.comparisonProduct.rpi ? parseFloat(this.comparisonProduct.rpi).toFixed(2) : null
    },

    // 비교상품 이미지 URL (여러 필드명 지원 + 쿠팡 CDN 프리픽스 자동 추가)
    comparisonProductImage() {
      if (!this.comparisonProduct) return '/img/no-image.png'

      const COUPANG_CDN_PREFIX = 'https://image10.coupangcdn.com/image/'

      // 이미지 URL 후보들
      const rawUrl = this.comparisonProduct.imgUrl ||
                     this.comparisonProduct.imagePath ||
                     this.comparisonProduct.imageUrl ||
                     this.comparisonProduct.thumbnailUrl ||
                     ''

      if (!rawUrl) return '/img/no-image.png'

      // 이미 완전한 URL이면 그대로 반환
      if (rawUrl.startsWith('http://') || rawUrl.startsWith('https://')) {
        return rawUrl
      }

      // 상대 경로면 쿠팡 CDN 프리픽스 추가
      return COUPANG_CDN_PREFIX + rawUrl
    },

    // 비교상품 카테고리가 선택된 카테고리와 동일한지 확인
    isComparisonCategorySame() {
      if (!this.comparisonProduct || !this.comparisonProduct.categoryName) return false
      if (!this.effectiveOptimizedCategoryInfo) return false
      return this.comparisonProduct.categoryName === this.effectiveOptimizedCategoryInfo.categoryName
    },

    // 비교상품이 TOP3 가격대 중 하나에 속하는지 확인
    isComparisonInAnyTopPriceRange() {
      if (!this.comparisonProduct || !this.bestPriceRanges || this.bestPriceRanges.length === 0) return false
      return this.bestPriceRanges.some(range => this.isComparisonInPriceRange(range))
    },

    // 베스트 상품 테이블 데이터 (CDataTable용)
    bestProductsForTable() {
      if (!this.bestProducts || this.bestProducts.length === 0) {
        return []
      }

      const mapProduct = (product, index, isComparison = false) => {
        const ctr = this.calculateCTR(product)
        const cvr = this.calculateCVR(product)
        const rpi = this.calculateRPI(product)
        const sales = product.salesLast28d === null ? null : (product.salesLast28d || 0)
        const price = product.price || 0
        const impressions = ctr > 0 ? Math.round((product.pvLast28Day || 0) * 100 / ctr) : 0
        const clicks = product.pvLast28Day || 0
        const attentionScore = product.attentionScore ? parseFloat(product.attentionScore).toFixed(2) : this.calculateAttentionScore(product)
        const trafficKeywordDisplay = (product.trafficKeyword || []).slice(0, 2).map(k => k.keywordDetail?.keyword || k.keyword)

        return {
          ...product,
          rank: index + 1,
          impressions,
          clicks,
          sales,
          ctr,
          cvr,
          rpi,
          revenue: sales === null ? null : sales * price,
          attentionScore,
          trafficKeywordDisplay,
          isComparisonProduct: isComparison
        }
      }

      // 기본 테이블 데이터 생성
      const tableData = this.bestProducts.map((product, index) => {
        const isComparison = this.comparisonProduct && product.vendorItemId === this.comparisonProduct.vendorItemId
        return mapProduct(product, index, isComparison)
      })

      // 비교상품이 TOP20에 없으면 21번째에 추가 (enrichedAnalysisResults에 없어도 comparisonProduct 사용)
      if (this.comparisonProduct) {
        const isComparisonInTop20 = tableData.some(p => p.isComparisonProduct)
        if (!isComparisonInTop20) {
          // enrichedAnalysisResults에서 먼저 찾고, 없으면 comparisonProduct 자체 데이터 사용
          const comparisonFromResults = this.enrichedAnalysisResults.find(
            p => p.vendorItemId === this.comparisonProduct.vendorItemId
          )

          if (comparisonFromResults) {
            tableData.push(mapProduct(comparisonFromResults, tableData.length, true))
          } else {
            // comparisonProduct 자체 데이터로 테이블 행 생성
            const product = this.comparisonProduct
            const price = product.price || 0
            const sales = product.salesLast28d || 0
            const ctr = parseFloat(product.ctr || 0).toFixed(2)
            const cvr = parseFloat(product.cvr || 0).toFixed(2)
            const rpi = parseFloat(product.rpi || 0).toFixed(2)

            // trafficKeyword에서 노출수/클릭수 계산
            const trafficKeywords = product.trafficKeyword || []
            const totalImpressions = trafficKeywords.reduce((sum, kw) =>
              sum + (kw.keywordMetric?.impression || 0), 0)
            const totalClicks = trafficKeywords.reduce((sum, kw) =>
              sum + (kw.keywordMetric?.srpClick || 0), 0)

            // 시선집중도 계산 (조회수 기반)
            const pvLast28Day = product.pvLast28Day || 0
            const attentionScore = pvLast28Day > 0 ? (sales / pvLast28Day * 100).toFixed(2) : 0

            // 트래픽 키워드 표시 (상위 2개)
            const trafficKeywordDisplay = trafficKeywords.slice(0, 2).map(k =>
              k.keywordDetail?.keyword || k.keyword
            )

            tableData.push({
              ...product,
              rank: tableData.length + 1,
              imagePath: product.imgUrl || product.imagePath || '',
              productName: product.productName || '',
              price: price,
              sales: sales,
              impressions: product._debug?.totalImpressions || totalImpressions,
              clicks: product._debug?.totalClicks || totalClicks,
              ctr: ctr,
              cvr: cvr,
              rpi: rpi,
              revenue: sales * price,
              attentionScore: attentionScore,
              trafficKeywordDisplay: trafficKeywordDisplay,
              isComparisonProduct: true,
              productId: product.productId,
              vendorItemId: product.vendorItemId
            })
            console.log('📊 [베스트 상품 테이블] 비교상품 추가:', product.productName, {
              impressions: totalImpressions,
              clicks: totalClicks,
              attentionScore,
              trafficKeywords: trafficKeywordDisplay
            })
          }
        }
      }

      return tableData
    },

    // 적용된 카테고리들의 요약 정보
    appliedCategorySummary() {
      if (!this.categoryAnalysis || this.categoryAnalysis.length === 0) {
        return null
      }
      // 적용된 카테고리가 없으면 첫 번째(최고 매출) 카테고리 사용
      const appliedCats = this.appliedCategoryNames.length > 0
        ? this.categoryAnalysis.filter(cat => this.appliedCategoryNames.includes(cat.category))
        : [this.categoryAnalysis[0]]

      if (appliedCats.length === 0) return null

      return {
        totalImpressions: appliedCats.reduce((sum, cat) => sum + (cat.totalImpressions || 0), 0),
        productCount: appliedCats.reduce((sum, cat) => sum + (cat.productCount || 0), 0),
        keywordCount: appliedCats.reduce((sum, cat) => sum + (cat.keywordCount || 0), 0),
        totalRevenue: appliedCats.reduce((sum, cat) => sum + (cat.totalRevenue || 0), 0),
        categories: appliedCats.map(cat => cat.category)
      }
    },

    // 카테고리 선택 변경 여부 (적용 버튼 표시용)
    hasCategorySelectionChanged() {
      // 둘 다 비어있으면 변경 없음
      if (this.pendingCategoryNames.length === 0 && this.appliedCategoryNames.length === 0) {
        return false
      }
      // 길이가 다르면 변경됨
      if (this.pendingCategoryNames.length !== this.appliedCategoryNames.length) {
        return true
      }
      // 내용 비교
      const sortedPending = [...this.pendingCategoryNames].sort()
      const sortedApplied = [...this.appliedCategoryNames].sort()
      return !sortedPending.every((cat, idx) => cat === sortedApplied[idx])
    },

    // Task 4: 모달에서 사용할 필터링+정렬된 카테고리 목록
    filteredCategoryAnalysis() {
      let result = [...this.categoryAnalysis]
      // 검색 필터
      if (this.categoryModalSearch) {
        const search = this.categoryModalSearch.toLowerCase()
        result = result.filter(c => c.category.toLowerCase().includes(search))
      }
      // 정렬
      const key = this.categoryModalSortKey
      const desc = this.categoryModalSortDesc
      result.sort((a, b) => {
        const aVal = a[key] ?? 0
        const bVal = b[key] ?? 0
        if (typeof aVal === 'string') {
          return desc ? bVal.localeCompare(aVal) : aVal.localeCompare(bVal)
        }
        return desc ? bVal - aVal : aVal - bVal
      })
      return result
    },

    // CDataTable용 카테고리 모달 헤더 (총검색량 제거, select는 showSelect로 자동 처리)
    categoryModalHeaders() {
      return [
        { text: '카테고리', value: 'category', sortable: true, width: '200px', isSlot: true },
        { text: '상품수', value: 'productCount', sortable: true, width: '80px' },
        { text: '키워드수', value: 'keywordCount', sortable: true, width: '80px' },
        { text: '총노출수', value: 'totalImpressions', sortable: true, width: '100px', isSlot: true },
        { text: '평균CTR', value: 'avgCtr', sortable: true, width: '80px', isSlot: true }
      ]
    },

    // CDataTable용 카테고리 모달 테이블 데이터
    categoryModalTableData() {
      return this.categoryAnalysis.map(cat => ({
        category: cat.category,
        productCount: cat.productCount,
        keywordCount: cat.keywordCount,
        totalImpressions: cat.totalImpressions,
        avgCtr: cat.avgCtr
      }))
    },

    // 실효 최적화 카테고리 정보 (적용된 카테고리 기반)
    effectiveOptimizedCategoryInfo() {
      if (!this.categoryAnalysis || this.categoryAnalysis.length === 0) {
        return null
      }
      // 적용된 카테고리가 없으면 첫 번째(최고 매출) 카테고리 사용
      if (this.appliedCategoryNames.length === 0) {
        return this.optimizedCategoryInfo
      }
      // 적용된 카테고리 중 첫 번째를 대표 카테고리로 사용
      const firstAppliedCat = this.categoryAnalysis.find(cat => this.appliedCategoryNames.includes(cat.category))
      if (!firstAppliedCat) return this.optimizedCategoryInfo

      return {
        categoryName: firstAppliedCat.category,
        productCount: firstAppliedCat.productCount,
        keywordCount: firstAppliedCat.keywordCount,
        totalImpressions: firstAppliedCat.totalImpressions,
        totalClicks: firstAppliedCat.totalClicks,
        totalRevenue: firstAppliedCat.totalRevenue
      }
    },

    // CDataTable용 통합 키워드 데이터 (라디오 버튼에 따라 값 변환)
    integratedKeywordsForTable() {
      if (!this.integratedKeywords || this.integratedKeywords.length === 0) {
        return []
      }

      // 적용된 카테고리 기반 필터링
      let filteredKeywords = this.integratedKeywords

      // 적용된 카테고리가 있으면 해당 카테고리로 필터링 (없으면 전체 표시)
      if (this.appliedCategoryNames.length > 0) {
        filteredKeywords = this.integratedKeywords.filter(item => {
          return this.appliedCategoryNames.includes(item.mainCategory)
        })
      }

      return filteredKeywords.map(item => {
        if (this.keywordAnalysisType === 'integrated') {
          // 통합키워드: 기존 값 그대로 사용
          return {
            ...item,
            // 기본 지표는 그대로
            totalSalesCount: item.totalSalesCount,
            totalGmv: item.totalGmv,
            maxSalesAmount: item.maxSalesAmount,
            avgSalesAmount: item.avgSalesAmount,
            medianSalesAmount: item.medianSalesAmount,
            revenueShare: item.revenueShare,
            revenueShareBasedMedianRevenue: item.revenueShareBasedMedianRevenue,
            revenueShareBasedRevenueRange: item.revenueShareBasedRevenueRange,
            priceRange25to75: item.priceRange25to75,
            accessibilityScore: item.accessibilityScore,
            maxRpi: item.maxRpi,
            avgRpi: item.avgRpi,
            medianRpi: item.medianRpi,
            minRpi: item.minRpi,
            topRankMaxRpi: item.topRankMaxRpi,
            topRankAvgRpi: item.topRankAvgRpi,
            topRankMedianRpi: item.topRankMedianRpi,
            topRankMinRpi: item.topRankMinRpi,
            maxCtr: item.maxCtr,
            avgCtr: item.avgCtr,
            medianCtr: item.medianCtr,
            minCtr: item.minCtr,
            maxCvr: item.maxCvr,
            avgCvr: item.avgCvr,
            medianCvr: item.medianCvr,
            minCvr: item.minCvr
          }
        } else {
          // 단일키워드: 단일키워드 값을 기본 필드에 매핑
          return {
            ...item,
            // 단일키워드 값을 동일한 필드명에 할당
            totalSalesCount: item.singleKeywordTotalSales || 0,
            totalGmv: item.singleKeywordTotalRevenue || 0,
            maxSalesAmount: item.singleKeywordMaxRevenue || 0,
            avgSalesAmount: item.singleKeywordAvgRevenue || 0,
            medianSalesAmount: item.singleKeywordMedianRevenue || 0,
            // 단일키워드에서도 동일한 매출점유율 사용 (전체 상품 매출 대비)
            revenueShare: item.revenueShare,
            // 단일키워드에서도 동일한 매출점유율기준 중간매출 사용
            revenueShareBasedMedianRevenue: item.revenueShareBasedMedianRevenue,
            // 단일키워드에서도 동일한 일매출 가이드라인 사용
            revenueShareBasedRevenueRange: item.revenueShareBasedRevenueRange,
            // 단일키워드에서도 동일한 가격대 사용
            priceRange25to75: item.priceRange25to75,
            // 단일키워드에서도 매출점유율기준 중간일매출과 상위랭킹 평균RPI로 접근성점수 계산
            accessibilityScore: (() => {
              const avgRpi = parseFloat(item.topRankAvgRpi);
              const medianRevenue = item.revenueShareBasedMedianRevenue || 0;
              if (!avgRpi || avgRpi <= 0 || isNaN(avgRpi)) return 0;
              if (!medianRevenue || isNaN(medianRevenue)) return 0;
              const result = Math.round(medianRevenue / avgRpi);
              return isNaN(result) ? 0 : result;
            })(),
            maxRpi: item.singleKeywordMaxRpi || '0.00',
            avgRpi: item.singleKeywordAvgRpi || '0.00',
            medianRpi: item.singleKeywordMedianRpi || '0.00',
            minRpi: item.singleKeywordMinRpi || '0.00',
            // 단일키워드에서는 상위랭킹 RPI를 통합키워드 값으로 사용
            topRankMaxRpi: item.topRankMaxRpi || '0.00',
            topRankAvgRpi: item.topRankAvgRpi || '0.00',
            topRankMedianRpi: item.topRankMedianRpi || '0.00',
            topRankMinRpi: item.topRankMinRpi || '0.00',
            maxCtr: item.singleKeywordMaxCtr || '0.00',
            avgCtr: item.singleKeywordAvgCtr || '0.00',
            medianCtr: item.singleKeywordMedianCtr || '0.00',
            minCtr: item.singleKeywordMinCtr || '0.00',
            maxCvr: item.singleKeywordMaxCvr || '0.00',
            avgCvr: item.singleKeywordAvgCvr || '0.00',
            medianCvr: item.singleKeywordMedianCvr || '0.00',
            minCvr: item.singleKeywordMinCvr || '0.00'
          }
        }
      })
    },
    // 현재 분석 데이터가 있는지 확인
    hasAnalysisData() {
      return this.analysisResults.length > 0
    },



    // 키워드설정 관련 computed
    autoListWithIndex() {
      return this.autoList.map((item, index) => {
        // 기존 데이터에 startPage, endPage가 없는 경우 처리
        let startPage = item.startPage || 1
        let endPage = item.endPage || item.pageRange || 1

        // pageRange가 문자열인 경우 파싱
        if (typeof item.pageRange === 'string' && item.pageRange.includes('-')) {
          const [start, end] = item.pageRange.split('-').map(Number)
          startPage = start || 1
          endPage = end || start || 1
        } else if (typeof item.pageRange === 'number') {
          startPage = endPage = item.pageRange
        }

        return {
          ...item,
          index,
          startPage,
          endPage
        }
      })
    },

    // 키워드 수집 실패 안내 문구 (실패 키워드는 미완료로 남아 「신규 수집」으로 다시 수집된다)
    collectFailNotices() {
      const notices = []
      if (this.extensionNoResponse) {
        notices.push(this.extensionNoResponseMessage)
      }
      if (this.collectFailLoginKeywords > 0) {
        notices.push(`쿠팡 윙 로그인이 풀려 ${this.collectFailLoginKeywords}개 키워드를 수집하지 못했습니다. 쿠팡 윙에 다시 로그인한 뒤 해당 키워드를 다시 수집해주세요.`)
      }
      if (this.collectFailWingBlocked) {
        // 확장 글(쓴 판매자코드·까닭·할 일)을 그대로 — 글을 안 싣는 옛 확장일 때만 옛 글자
        notices.push(this.collectFailWingBlockedMessage || '이 윙 계정은 쿠팡 판매량 조회가 제한돼 있어요 — 다른 윙 계정으로 로그인해 다시 조회해 주세요')
      }
      if (this.collectFailBlockedKeywords > 0) {
        notices.push(`쿠팡이 잠시 막아 ${this.collectFailBlockedKeywords}개 키워드를 수집하지 못했습니다. 10분쯤 뒤 남은 키워드를 나눠서 다시 수집해주세요.`)
      }
      if (this.collectFailOtherKeywords > 0) {
        notices.push(`쿠팡 응답을 받지 못해 ${this.collectFailOtherKeywords}개 키워드를 수집하지 못했습니다. 잠시 후 다시 시도해주세요.`)
      }
      return notices
    },
    // 트래픽 키워드가 비었을 때 안내 — 쿠팡 차단으로 실패한 키워드가 있으면 구독 탓으로 안내하지 않는다
    // 확장이 준 까닭이 윙 조회 실패뿐이면(구독 밖 0) 윙 로그인 안내(딩톡 #24)
    trafficNoticeText() {
      if (this.noBusinessInsight) return NO_BUSINESS_INSIGHT_MSG
      if (this.collectFailBlockedKeywords > 0) {
        return '쿠팡이 잠시 막아 일부 키워드 수집이 실패했습니다. 잠시 뒤 나눠서 다시 수집해주세요.'
      }
      if (this.trafficSkippedBy.fetchFail > 0 && this.trafficSkippedBy.notSubscribed === 0) {
        return '트래픽 키워드를 받지 못해 노출수·클릭수가 비어 있습니다. 윙 조회가 실패했습니다 — 윙 로그인 상태를 확인해 주세요.'
      }
      return '트래픽 키워드를 받지 못해 노출수·클릭수가 비어 있습니다. 비즈니스인사이트 구독 상태를 확인해 주세요.'
    },
    // TOP3 가 빈 까닭 — 결과에서 센다(kwsrcfix-1). RPI = 트래픽(노출) × 판매수라 둘 다 가진 상품이 있어야 한다.
    // 건너뛴 상품이 있다고 «트래픽 못 받음» 으로 단정하지 않는다(가위: 트래픽 40/40 · 판매수 0)
    sourcingEmptyReason() {
      const list = this.analysisResults || []
      if (list.length === 0) return ''
      const hasTraffic = p => Array.isArray(p.trafficKeyword) && p.trafficKeyword.length > 0
      const hasSales = p => p.salesLast28d > 0
      const t = list.filter(hasTraffic).length
      const s = list.filter(hasSales).length
      if (t === 0) return this.trafficNoticeText
      if (s === 0) return `노출 상품 ${t}개는 있지만 판매수를 받은 상품이 0개라 RPI를 셀 수 없습니다(이 윙 계정으로 받은 판매수가 0 또는 모름).`
      if (!list.some(p => hasTraffic(p) && hasSales(p))) return `판매수 있는 상품 ${s}개와 트래픽 있는 상품 ${t}개가 서로 달라, 둘 다 가진 상품이 0개라 RPI를 셀 수 없습니다.`
      return ''
    },
    // 가격대가 빈 까닭 — 가격대는 판매수 > 0 상품으로 센다(analyzePriceRanges)
    priceEmptyReason() {
      const list = this.analysisResults || []
      if (list.length === 0 || list.some(p => p.price > 0 && p.salesLast28d > 0)) return ''
      return '판매수를 받은 상품이 0개라 가격대를 셀 수 없습니다(이 윙 계정으로 받은 판매수가 0 또는 모름).'
    },
    newKeywordsCount() {
      return this.autoList.filter(item => !item.analysisData || !item.collectedAt).length
    },

    // 빠른수집이면 페이지범위/페이지현황 컬럼 숨김 (단발 수집이라 무의미)
    bulkHeadersFiltered() {
      if (this.collectMode === 'fast') {
        return this.bulkHeaders.filter(h => h.value !== 'pageRange' && h.value !== 'pageStatus')
      }
      return this.bulkHeaders
    },

    // 진행 상황 메시지 (앞부분 + 뒷부분 조합)
    progressMessage() {
      return this.progressPrefix + this.progressSuffix
    },

  },
  methods: {

    // 비교상품이 특정 가격대에 속하는지 확인
    isComparisonInPriceRange(range) {
      if (!this.comparisonProduct || !range) return false
      const price = parseInt(this.comparisonProduct.price) || 0
      if (price <= 0) return false
      // bestPriceRanges 구조: { startPrice: 18000, priceRangeText: "18,000원~18,999원" }
      // 가격대는 1000원 단위 (startPrice ~ startPrice + 999)
      const minPrice = parseInt(range.startPrice) || 0
      const maxPrice = minPrice + 999
      return price >= minPrice && price <= maxPrice
    },

    // 비교상품 행 클래스 반환 (CDataTable rowClass용)
    getComparisonRowClass(item) {
      return item.isComparisonProduct ? 'comparison-product-row' : ''
    },

    // 비교상품 데이터 업데이트 (analysisResults에서 찾아서 풍부한 데이터로 업데이트)
    updateComparisonProductFromResults() {
      if (!this.comparisonProduct || !this.comparisonProduct.vendorItemId) {
        console.log('ℹ️ [비교상품 업데이트] 비교상품이 설정되지 않음')
        return
      }

      // analysisResults에서 비교상품 찾기
      const foundProduct = this.analysisResults.find(
        p => p.vendorItemId === this.comparisonProduct.vendorItemId
      )

      if (foundProduct) {
        console.log('✅ [비교상품 업데이트] analysisResults에서 비교상품 발견:', foundProduct.productName)

        // 기존 comparisonProduct를 분석 결과 데이터로 업데이트
        this.comparisonProduct = {
          ...this.comparisonProduct,
          ...foundProduct,
          // 핵심 필드 명시적 설정
          price: foundProduct.price || foundProduct.salePrice || this.comparisonProduct.price || 0,
          imgUrl: foundProduct.imgUrl || foundProduct.imagePath || this.comparisonProduct.imgUrl || '',
          salesLast28d: foundProduct.salesLast28d || this.comparisonProduct.salesLast28d || 0,
          pvLast28Day: foundProduct.pvLast28Day || this.comparisonProduct.pvLast28Day || 0,
          ctr: foundProduct.ctr || this.comparisonProduct.ctr || 0,
          cvr: foundProduct.cvr || this.comparisonProduct.cvr || 0,
          rpi: foundProduct.overall || foundProduct.rpi || this.comparisonProduct.rpi || 0
        }
        console.log('📌 [비교상품 업데이트 완료]:', this.comparisonProduct)
      } else {
        console.log('ℹ️ [비교상품 업데이트] analysisResults에서 비교상품을 찾지 못함, vendorItemId:', this.comparisonProduct.vendorItemId)
      }
    },

    // 비교상품 제거
    async clearComparisonProduct() {
      this.comparisonProduct = null
      this.targetProductLink = ''
      console.log('🗑️ [비교상품 제거] 비교상품 초기화 완료')

      // IndexedDB에서도 비교상품 정보 제거
      await this.saveComparisonProductToIndexedDB()

      // 차트 재생성 (비교상품 마커 제거)
      this.$nextTick(() => {
        this.createCharts()
      })
    },

    // 비교상품 정보를 IndexedDB에 저장
    async saveComparisonProductToIndexedDB() {
      try {
        if (!this.currentGroupId) {
          console.warn('⚠️ [비교상품 저장] currentGroupId가 없음')
          return
        }

        const existingData = await keywordAnalysisDB.getKeywordGroup(this.currentGroupId)

        if (existingData) {
          const updatedData = {
            ...existingData,
            comparisonProduct: this.comparisonProduct,
            targetProductLink: this.targetProductLink,
            lastUpdated: new Date().toISOString()
          }
          await keywordAnalysisDB.saveKeywordGroup(this.currentGroupId, updatedData)
          console.log('💾 [비교상품 저장] IndexedDB 저장 완료, groupId:', this.currentGroupId)
        } else {
          // 기존 데이터가 없으면 새로 생성
          const newData = {
            groupId: this.currentGroupId,
            groupName: this.activeKeywordGroup,
            comparisonProduct: this.comparisonProduct,
            targetProductLink: this.targetProductLink,
            autoList: this.autoList || [],
            analysisResults: this.analysisResults || [],
            lastUpdated: new Date().toISOString()
          }
          await keywordAnalysisDB.saveKeywordGroup(this.currentGroupId, newData)
          console.log('💾 [비교상품 저장] IndexedDB 새로 생성 완료, groupId:', this.currentGroupId)
        }
      } catch (error) {
        console.error('❌ [비교상품 저장] IndexedDB 저장 실패:', error)
      }
    },

    // ========== Task 2: 카테고리 경로 파싱 유틸 ==========
    // 카테고리 문자열을 '>'로 split해 levels 배열 반환
    parseCategoryPath(categoryString) {
      if (!categoryString || typeof categoryString !== 'string') return []
      return categoryString.split('>').map(s => s.trim()).filter(s => s.length > 0)
    },

    // 특정 깊이(level)까지의 카테고리 경로 반환 (1차=0, 2차=1, ...)
    getCategoryAtLevel(categoryString, level) {
      const levels = this.parseCategoryPath(categoryString)
      if (level >= levels.length) return categoryString // 깊이 초과 시 전체 반환
      return levels.slice(0, level + 1).join('>')
    },

    // 두 카테고리가 지정 깊이까지 일치하는지 확인
    matchCategoryAtLevel(cat1, cat2, level) {
      return this.getCategoryAtLevel(cat1, level) === this.getCategoryAtLevel(cat2, level)
    },

    // 상위 카테고리 prefix 매칭 (cat1이 cat2의 상위이거나 같은지)
    isCategoryPrefix(parentCat, childCat) {
      if (!parentCat || !childCat) return false
      return childCat === parentCat || childCat.startsWith(parentCat + '>')
    },
    // ========== End Task 2 ==========

    // 카테고리 체크박스 토글
    toggleCategorySelection(categoryName) {
      const index = this.pendingCategoryNames.indexOf(categoryName)
      if (index > -1) {
        // 이미 선택되어 있으면 제거
        this.pendingCategoryNames.splice(index, 1)
      } else {
        // 선택되어 있지 않으면 추가
        this.pendingCategoryNames.push(categoryName)
      }
    },

    // 카테고리 선택 적용 (적용 버튼 클릭 시)
    applyCategorySelection() {
      // pending을 applied로 복사
      this.appliedCategoryNames = [...this.pendingCategoryNames]
      // 모든 분석 재계산 (카테고리 기반)
      this.recalculateAllAnalysis()
      // 토스트 메시지
      const categoryText = this.appliedCategoryNames.length > 0
        ? `${this.appliedCategoryNames.length}개 카테고리`
        : '전체 카테고리'
      if (this.$toast) {
        this.$toast.success(`${categoryText}가 적용되었습니다.`)
      }
    },

    // Task 3: 카테고리 모달 열기
    openCategoryModal() {
      // 테이블 데이터를 캐시하여 객체 참조 유지 (CDataTable 선택 상태 유지를 위함)
      this.categoryModalTableDataCache = this.categoryAnalysis.map(cat => ({
        category: cat.category,
        productCount: cat.productCount,
        keywordCount: cat.keywordCount,
        totalImpressions: cat.totalImpressions,
        avgCtr: cat.avgCtr
      }))
      // 현재 적용된 카테고리를 캐시된 데이터에서 찾아 선택 상태로 설정
      this.categoryModalSelectedItems = this.categoryModalTableDataCache.filter(item =>
        this.appliedCategoryNames.includes(item.category)
      )
      this.showCategoryModal = true

      // CDataTable 내부 selected 상태를 직접 동기화 (v-show로 인해 컴포넌트가 유지됨)
      this.$nextTick(() => {
        if (this.$refs.categoryModalTable) {
          this.$refs.categoryModalTable.selected = [...this.categoryModalSelectedItems]
        }
      })
    },

    // Task 3: 카테고리 모달 닫기
    closeCategoryModal() {
      this.showCategoryModal = false
    },

    // Task 3: 카테고리 모달에서 적용
    applyCategoryModalSelection() {
      // 선택된 아이템에서 카테고리 이름 추출
      const selectedCategoryNames = this.categoryModalSelectedItems.map(item => item.category)
      this.appliedCategoryNames = [...selectedCategoryNames]
      this.pendingCategoryNames = [...selectedCategoryNames]
      this.showCategoryModal = false
      // 모든 분석 재계산 (카테고리 기반)
      this.recalculateAllAnalysis()
      if (this.$toast) {
        this.$toast.success(`${this.appliedCategoryNames.length}개 카테고리가 적용되었습니다.`)
      }
    },

    // Task 3: 전체 카테고리 선택 해제
    clearCategorySelection() {
      this.appliedCategoryNames = []
      this.pendingCategoryNames = []
      // 모든 분석 재계산 (전체 데이터 기준)
      this.recalculateAllAnalysis()
      if (this.$toast) {
        this.$toast.info('카테고리 선택이 초기화되었습니다.')
      }
    },

    // Task 3: 적용된 카테고리 중 하나 제거
    removeCategoryFromApplied(categoryName) {
      this.appliedCategoryNames = this.appliedCategoryNames.filter(c => c !== categoryName)
      this.pendingCategoryNames = this.pendingCategoryNames.filter(c => c !== categoryName)
      // 모든 분석 재계산
      this.recalculateAllAnalysis()
    },

    // 카테고리 변경 시 모든 분석 재계산
    recalculateAllAnalysis() {
      console.log('🔄 [카테고리 변경] 모든 분석 재계산 시작 - 선택된 카테고리:', this.appliedCategoryNames)

      // 1. 카테고리 필터링된 데이터 생성
      this.createEnrichedAnalysisData()

      // 2. 종합 지표 재계산
      this.calculateSummaryMetrics()

      // 3. 베스트 상품 분석 (CTR, CVR, RPI, 시선집중도)
      this.analyzeBestProducts()

      // 4. 소싱 전략 분석
      this.analyzeSourcingStrategy()

      // 5. 통합 키워드 분석
      this.analyzeIntegratedKeywords()

      // 6. 키워드 SEO 분석
      this.analyzeKeywordSEO()

      // 7. 차트 재생성 (일별 트래픽, 광고경쟁력, 가격별 CVR, RPI 경쟁력, 가격대별 매출)
      this.$nextTick(() => {
        this.createCharts()
      })

      // 8. 카테고리 선택 상태를 IndexedDB에 저장
      this.saveCategorySelectionToIndexedDB()

      console.log('✅ [카테고리 변경] 모든 분석 재계산 완료')
    },

    // 카테고리 선택 상태만 IndexedDB에 저장
    async saveCategorySelectionToIndexedDB() {
      // 로딩 중일 때는 저장하지 않음 (불필요한 쓰기 방지)
      if (this.isLoadingFromDB) {
        console.log('ℹ️ [카테고리 선택 저장] 로딩 중이므로 저장 건너뜀')
        return
      }
      try {
        const existingData = await keywordAnalysisDB.getKeywordGroup(this.currentGroupId)
        if (existingData) {
          const updatedData = {
            ...existingData,
            appliedCategoryNames: this.appliedCategoryNames,
            pendingCategoryNames: this.pendingCategoryNames,
            lastUpdated: new Date().toISOString()
          }
          await keywordAnalysisDB.saveKeywordGroup(this.currentGroupId, updatedData)
          console.log('💾 [카테고리 선택 저장] IndexedDB 저장 완료:', this.appliedCategoryNames.length, '개 카테고리')
        }
      } catch (error) {
        console.error('❌ [카테고리 선택 저장] IndexedDB 저장 실패:', error)
      }
    },

    // Task 4: 모달 내 카테고리 토글
    toggleCategoryInModal(categoryName) {
      const idx = this.categoryModalPendingSelection.indexOf(categoryName)
      if (idx > -1) {
        this.categoryModalPendingSelection.splice(idx, 1)
      } else {
        this.categoryModalPendingSelection.push(categoryName)
      }
    },

    // Task 4: CDataTable 행 클릭 핸들러
    handleCategoryRowClick(item) {
      this.toggleCategoryInModal(item.category)
    },

    // Task 4: 모달 내 전체 선택/해제
    toggleAllCategoriesInModal(selectAll) {
      if (selectAll) {
        this.categoryModalPendingSelection = this.filteredCategoryAnalysis.map(c => c.category)
      } else {
        this.categoryModalPendingSelection = []
      }
    },

    // IndexedDB 초기화
    async initializeDB() {
      try {
        await keywordAnalysisDB.init()
        // console.log('IndexedDB 초기화 완료')
      } catch (error) {
        console.error('IndexedDB 초기화 실패:', error)
      }
    },

    // 기존 데이터 로딩
    async loadExistingData() {
      try {
        this.isLoadingFromDB = true

        // 현재 그룹의 기존 데이터 로딩
        const groupData = await keywordAnalysisDB.getKeywordGroup(this.currentGroupId)

        console.log('🔍 [IndexedDB 로딩] 그룹 ID:', this.currentGroupId)
        console.log('🔍 [IndexedDB 로딩] 전체 그룹 데이터:', groupData)

        // 데이터 구조 상세 로깅
        // if (groupData) {
        //   console.log('📊 [IndexedDB 데이터 구조 분석]')
        //   console.log('  - groupId:', groupData.groupId)
        //   console.log('  - groupName:', groupData.groupName)
        //   console.log('  - autoList 길이:', groupData.autoList?.length || 0)
        //   console.log('  - analysisResults 길이:', groupData.analysisResults?.length || 0)
        //   console.log('  - lastUpdated:', groupData.lastUpdated)

        //   // autoList 샘플 데이터
        //   if (groupData.autoList && groupData.autoList.length > 0) {
        //     console.log('📝 [autoList 샘플 데이터] 첫 번째 키워드:')
        //     console.log(JSON.stringify(groupData.autoList[0], null, 2))
        //   }

        //   // analysisResults 샘플 데이터
        //   if (groupData.analysisResults && groupData.analysisResults.length > 0) {
        //     console.log('📈 [analysisResults 샘플 데이터] 첫 번째 상품:')
        //     console.log(JSON.stringify(groupData.analysisResults[0], null, 2))
        //   }
        // }

        if (groupData) {
          // 기존 키워드 설정이 있으면 복원
          if (groupData.autoList && groupData.autoList.length > 0) {
            this.autoList = groupData.autoList
            console.log('✅ [키워드 설정 복원]', this.autoList.length, '개 키워드 로딩')

            // 각 키워드 상태 상세 로깅
            this.autoList.forEach((keyword, index) => {
              console.log(`🔍 [로딩된 키워드 ${index + 1}] ${keyword.keyword}:`, {
                pageRange: keyword.pageRange,
                startPage: keyword.startPage,
                endPage: keyword.endPage,
                startPageType: typeof keyword.startPage,
                endPageType: typeof keyword.endPage,
                completedPages: keyword.completedPages
              })
            })
          }

          // 기존 분석 결과가 있으면 복원
          if (groupData.analysisResults && groupData.analysisResults.length > 0) {
            console.log('🔍 [분석 결과 복원 시작] 원본 데이터:', groupData.analysisResults.length, '개')

            // 현재 그룹의 키워드 목록 생성
            const currentGroupKeywords = new Set(this.autoList.map(item => item.keyword))
            console.log('🔍 [그룹 키워드 목록]', [...currentGroupKeywords])

            // 기존 데이터에 키 정보가 없으면 추가 (하위 호환성)
            let allResults = groupData.analysisResults.map(result => {
              if (!result.collectionKey && result.sourceKeyword && (result.sourcePage || result.sourcePageRange)) {
                const page = result.sourcePage || parseInt(result.sourcePageRange) || 1
                return {
                  ...result,
                  sourcePage: page,
                  collectionKey: `${result.sourceKeyword}:${page}`
                }
              }
              return result
            })

            // 현재 그룹의 키워드에 해당하는 데이터만 필터링
            this.analysisResults = allResults.filter(result => {
              const isCurrentGroup = currentGroupKeywords.has(result.sourceKeyword)
              if (!isCurrentGroup) {
                console.log('🚫 [데이터 필터링] 다른 그룹 데이터 제외:', result.sourceKeyword, result.productName?.substring(0, 30))
              }
              return isCurrentGroup
            })

            console.log('🔍 [데이터 필터링 결과] 전체:', allResults.length, '개 → 현재 그룹:', this.analysisResults.length, '개')

            // 특정 vendorItemId 로딩 시 추적
            const hasTargetVendor = this.analysisResults.some(r => r.vendorItemId === this.debugTargetVendorId)
            if (hasTargetVendor) {
              console.log(`🔍 [추적] ${this.debugTargetVendorId} 로딩됨 - analysisResults에서 발견`)
            }

            console.log('✅ [분석 결과 복원]', this.analysisResults.length, '개 상품 데이터 로딩')
            console.log('📊 [분석 결과 샘플] 첫 3개 상품의 키워드:', this.analysisResults.slice(0, 3).map(r => r.sourceKeyword))
            console.log('📊 [분석 결과 키워드 분포]', [...new Set(this.analysisResults.map(r => r.sourceKeyword))])
            // console.log('🔍 [분석 결과 타입 확인]', {
            //   isArray: Array.isArray(this.analysisResults),
            //   length: this.analysisResults.length,
            //   hasVendorItemId: this.analysisResults[0]?.vendorItemId || 'N/A',
            //   hasProductName: this.analysisResults[0]?.productName || 'N/A'
            // })

            // 키 정보가 추가된 경우 IndexedDB 업데이트
            const hasNewKeys = this.analysisResults.some(result =>
              result.collectionKey && !groupData.analysisResults.find(original =>
                original.vendorItemId === result.vendorItemId && original.collectionKey
              )
            )

            if (hasNewKeys) {
              // console.log('🔑 [키 정보 추가] 기존 데이터에 collectionKey 추가됨, IndexedDB 업데이트')
              await this.saveAllAnalysisResults()
            }

            // 메모리 사용량 업데이트
            this.updateMemoryUsage()

            // 기존 체크리스트가 있으면 복원 (선택적)
            if (groupData.sourcingChecklist) {
              this.sourcingChecklist = groupData.sourcingChecklist
              // console.log('✅ [체크리스트 복원] AI 소싱 분석 결과 로딩 완료')
            }

            // 최대 수집 상품 수 설정 복원
            if (groupData.maxProducts && groupData.maxProducts > 0) {
              this.maxProducts = groupData.maxProducts
              // console.log('✅ [최대 수집 설정 복원]', this.maxProducts, '개')
            }

            // ★ 중요: 카테고리 선택 상태를 분석 수행 이전에 복원해야 함
            // (performComprehensiveAnalysis → analyzeCategoryDistribution에서 기본값 설정 방지)
            if (groupData.appliedCategoryNames && groupData.appliedCategoryNames.length > 0) {
              this.appliedCategoryNames = groupData.appliedCategoryNames
              this.pendingCategoryNames = groupData.pendingCategoryNames || [...groupData.appliedCategoryNames]
              console.log('✅ [카테고리 선택 복원]', this.appliedCategoryNames.length, '개 카테고리')
            }

            // 비교상품 정보 복원
            console.log('🔍 [비교상품 체크] groupData.comparisonProduct:', groupData.comparisonProduct ? '있음' : '없음')
            if (groupData.comparisonProduct) {
              this.comparisonProduct = groupData.comparisonProduct
              this.targetProductLink = groupData.targetProductLink || ''
              console.log('✅ [비교상품 복원]', this.comparisonProduct.productName, {
                vendorItemId: this.comparisonProduct.vendorItemId,
                imgUrl: this.comparisonProduct.imgUrl,
                price: this.comparisonProduct.price
              })
            } else {
              console.log('ℹ️ [비교상품 복원] 저장된 비교상품 없음')
            }

            // 기존 데이터가 있을 때 스피너와 함께 자연스럽게 로딩
            // console.log('🔄 [기존 데이터 로딩] 분석 스피너 시작')
            this.isAnalyzing = true
            this.progressPrefix = '기존 분석 데이터 로딩 중'
            this.progressSuffix = '데이터를 불러오는 중...'
            this.progressPercent = 50

            // UI 업데이트 보장 후 통합 분석 수행 (약간의 지연으로 스피너 표시)
            await this.$nextTick()
            await new Promise(resolve => setTimeout(resolve, 800)) // 0.8초 지연

            this.progressSuffix = '데이터 최적화 중...'
            this.progressPercent = 60
            // 모든 계산이 완료된 데이터 생성
            this.createEnrichedAnalysisData()

            this.progressSuffix = '통합 분석 중...'
            this.progressPercent = 80
            // 미리 계산된 데이터로 분석 수행
            this.performComprehensiveAnalysis()

            // 비교상품 데이터 업데이트 (기존 데이터 로드 시에도)
            this.updateComparisonProductFromResults()

            this.progressPercent = 100
            this.progressSuffix = '완료'
            await new Promise(resolve => setTimeout(resolve, 300)) // 완료 메시지 잠시 표시

            this.isAnalyzing = false
            // console.log('✅ [기존 데이터 로딩] 분석 완료')
          } else {
            console.log('ℹ️ [분석 결과] 기존 분석 결과가 없습니다. analysisResults:', groupData.analysisResults)
          }

          // 계산 데이터들은 analysisResults에서 생성 (저장하지 않음)
          // console.log('🔄 [계산 데이터 생성] summaryMetrics, top20Products 등 생성 시작')
        } else {
          console.log('ℹ️ [IndexedDB 로딩] 해당 그룹의 기존 데이터가 없습니다.')
        }

        // 중복 방지용 vendorItemId 목록 업데이트
        await this.updateExistingVendorItemIds()

        // 분석 결과 기반으로 키워드별 페이지 현황 동적 업데이트
        this.updatePageStatusFromAnalysisResults()

        // 분석 결과가 있으면 모든 분석 재계산 (카테고리 선택 상태 적용 포함)
        if (this.analysisResults && this.analysisResults.length > 0) {
          console.log('🔄 [데이터 로딩 완료] 분석 재계산 시작 - 카테고리:', this.appliedCategoryNames.length, '개 선택됨')
          this.recalculateAllAnalysis()
        }

      } catch (error) {
        console.error('기존 데이터 로딩 실패:', error)
      } finally {
        this.isLoadingFromDB = false
      }
    },

    // 중복 방지용 vendorItemId 목록 업데이트
    async updateExistingVendorItemIds() {
      try {
        this.existingVendorItemIds = await keywordAnalysisDB.getExistingVendorItemIds(this.currentGroupId)

        // 특정 vendorItemId existingVendorItemIds 로딩 추적
        if (this.existingVendorItemIds.includes(this.debugTargetVendorId)) {
          console.log(`🔍 [추적] ${this.debugTargetVendorId} existingVendorItemIds에서 발견됨, 총 수:`, this.existingVendorItemIds.length)
        }

        // console.log('기존 vendorItemId 목록:', this.existingVendorItemIds.length, '개')
      } catch (error) {
        console.error('vendorItemId 목록 업데이트 실패:', error)
        this.existingVendorItemIds = []
      }
    },

    // 분석 결과 기반으로 키워드별 페이지 현황 동적 업데이트 (수집 시도 기록 보존)
    updatePageStatusFromAnalysisResults() {
      // console.log('🔄 [페이지 현황 업데이트] 시작 - 분석 결과:', this.analysisResults.length, '개, 키워드:', this.autoList.length, '개')

      // 분석 결과 샘플 로깅
      // if (this.analysisResults && this.analysisResults.length > 0) {
        // console.log('📈 [분석 결과 샘플] 첫 번째 상품의 키워드 정보:')
        // const sample = this.analysisResults[0]
        // console.log('  - sourceKeyword:', sample.sourceKeyword)
        // console.log('  - sourcePage:', sample.sourcePage)
        // console.log('  - sourcePageRange:', sample.sourcePageRange)
        // console.log('  - collectionKey:', sample.collectionKey)
        // console.log('  - productName:', sample.productName)
      // }

      if (!this.analysisResults || this.analysisResults.length === 0) {
        // console.log('ℹ️ [페이지 현황 업데이트] 분석 결과가 없음 - 기존 completedPages 유지')
        // 분석 결과가 없어도 기존 수집 시도 기록은 유지
        return
      }

      // 각 키워드별로 수집된 페이지 확인 (기존 completedPages와 병합)
      this.autoList.forEach((keyword) => {
        const keywordName = keyword.keyword || keyword.targetKeyword
        if (!keywordName) return

        // console.log(`🔍 [페이지 현황 확인] 키워드: ${keywordName}`)

        // 기존 수집 시도 기록 보존
        const existingCompletedPages = new Set(keyword.completedPages || [])

        // 해당 키워드로 수집된 분석 결과 찾기
        const keywordResults = this.analysisResults.filter(result =>
          result.sourceKeyword === keywordName
        )

        // console.log(`📊 [키워드 결과] ${keywordName}: ${keywordResults.length}개 상품`)

        // 실제 데이터가 있는 페이지들 추출
        const dataCollectedPages = new Set()
        keywordResults.forEach(result => {
          if (result.sourcePage) {
            dataCollectedPages.add(parseInt(result.sourcePage))
          } else if (result.sourcePageRange) {
            // sourcePageRange에서 페이지 번호 추출 (예: "1-3" → [1,2,3])
            const pageRange = result.sourcePageRange.toString()
            if (pageRange.includes('-')) {
              const [start, end] = pageRange.split('-').map(p => parseInt(p.trim()))
              for (let p = start; p <= end; p++) {
                dataCollectedPages.add(p)
              }
            } else {
              dataCollectedPages.add(parseInt(pageRange))
            }
          }
        })

        // 기존 수집 시도 기록 + 실제 데이터가 있는 페이지 병합
        const allCompletedPages = new Set([...existingCompletedPages, ...dataCollectedPages])

        // 페이지 범위 내에서만 유효한 페이지들로 필터링
        const pageRange = this.parsePageRange(keyword.pageRange)
        const validCompletedPages = []

        for (let page = pageRange.startPage; page <= pageRange.endPage; page++) {
          if (allCompletedPages.has(page)) {
            validCompletedPages.push(page)
          }
        }

        // Vue 반응성 보장을 위해 $set 사용 (기존 기록 보존)
        this.$set(keyword, 'completedPages', validCompletedPages.sort((a, b) => a - b))

        // console.log(`✅ [페이지 현황 업데이트] ${keywordName}: 완료된 페이지 [${validCompletedPages.join(', ')}] (기존 기록 보존)`)
      })

      // console.log('✅ [페이지 현황 업데이트] 완료 - 수집 시도 기록 보존됨')
    },

    // autoList 필드 정리 (불필요한 필드 제거)
    cleanAutoListFields() {
      // console.log('🧹 [autoList 정리] 불필요한 필드 제거 시작')

      this.autoList.forEach((keyword, index) => {
        // 필요한 필드만 유지 (페이지 상태는 모두 보존: completed/failed/skipped)
        const cleanedKeyword = {
          keyword: keyword.keyword || keyword.targetKeyword,
          pageRange: keyword.pageRange,
          startPage: keyword.startPage,
          endPage: keyword.endPage,
          completedPages: keyword.completedPages || [],
          failedPages: keyword.failedPages || [],
          skippedPages: keyword.skippedPages || [],
          source: keyword.source || '직접입력'
        }

        // Vue 반응성 보장을 위해 $set 사용
        this.$set(this.autoList, index, cleanedKeyword)
      })

      // console.log('✅ [autoList 정리] 완료 - 핵심 필드만 유지')
    },

    // 분석 결과 저장 (개별 상품 저장은 제거, 메모리에만 유지)
    async saveAnalysisResult(keyword, productData) {
      try {
        // vendorItemId가 없는 상품 데이터는 저장하지 않음
        if (!productData || !productData.vendorItemId) {
          console.warn('⚠️ vendorItemId가 없는 상품 데이터 저장 스킵:', productData)
          return
        }

        // console.log('💾 [즉시 저장] 키워드:', keyword, '상품:', productData.productName?.substring(0, 30))

        // vendorItemId 목록 업데이트
        if (!this.existingVendorItemIds.includes(productData.vendorItemId)) {
          this.existingVendorItemIds.push(productData.vendorItemId)
        }

        // 신규 데이터만 즉시 증분 저장 (성능 최적화)
        await this.saveNewDataToIndexedDB([productData])
        // console.log('💾 [증분 저장] 완료 - 전체 상품 수:', this.analysisResults.length)

      } catch (error) {
        console.error('분석 결과 저장 실패:', error)
      }
    },

    // 페이지별 데이터를 즉시 IndexedDB에 저장
    async savePageDataToIndexedDB() {
      try {
        // console.log('🚨 [savePageDataToIndexedDB 호출] 호출 스택:', new Error().stack)
        // console.log('💾 [페이지별 저장] 시작 - 그룹:', this.currentGroupId, '상품 수:', this.analysisResults.length)

        // 기존 그룹 데이터 가져오기
        const existingData = await keywordAnalysisDB.getKeywordGroup(this.currentGroupId) || {}

        // 현재 그룹의 키워드 목록 생성
        const currentGroupKeywords = new Set(this.autoList.map(item => item.keyword))

        // 현재 그룹에 속한 데이터만 저장 (데이터 오염 방지)
        const filteredAnalysisResults = this.analysisResults.filter(result => {
          const isCurrentGroup = currentGroupKeywords.has(result.sourceKeyword)
          if (!isCurrentGroup) {
            console.log('🚫 [페이지별 저장 필터링] 다른 그룹 데이터 제외:', result.sourceKeyword, result.productName?.substring(0, 30))
          }
          return isCurrentGroup
        })

        console.log('🔍 [페이지별 저장 검증] 전체:', this.analysisResults.length, '개 → 현재 그룹:', filteredAnalysisResults.length, '개')

        // 현재 analysisResults로 업데이트
        const updatedData = {
          ...existingData,
          groupId: this.currentGroupId,
          groupName: this.activeKeywordGroup,
          autoList: this.autoList,
          analysisResults: filteredAnalysisResults, // 필터링된 데이터만 저장
          summaryMetrics: this.summaryMetrics,
          integratedKeywords: this.integratedKeywords,
          lastUpdated: new Date().toISOString()
        }

        await keywordAnalysisDB.saveKeywordGroup(this.currentGroupId, updatedData)
        // console.log('✅ [페이지별 저장] IndexedDB 저장 완료:', this.analysisResults)

      } catch (error) {
        console.error('❌ [페이지별 저장] 실패:', error)
      }
    },

    // 신규 데이터만 IndexedDB에 증분 추가 (성능 최적화)
    async saveNewDataToIndexedDB(newResults) {
      if (!newResults || newResults.length === 0) {
        // console.log('💾 [증분 저장] 신규 데이터 없음 - 저장 생략')
        return
      }

      try {
        // console.log('💾 [증분 저장] 시작 - 신규 데이터:', newResults.length, '개')

        // 기존 그룹 데이터 가져오기
        const existingData = await keywordAnalysisDB.getKeywordGroup(this.currentGroupId) || {}
        const existingResults = existingData.analysisResults || []

        // 신규 데이터만 기존 데이터에 추가
        const updatedResults = [...existingResults, ...newResults]

        // 업데이트된 데이터 저장 (신규 데이터만 추가)
        // 현재 그룹의 키워드 목록 생성
        const currentGroupKeywords = new Set(this.autoList.map(item => item.keyword))

        // 현재 그룹에 속한 데이터만 저장 (데이터 오염 방지)
        const filteredUpdatedResults = updatedResults.filter(result => {
          const isCurrentGroup = currentGroupKeywords.has(result.sourceKeyword)
          if (!isCurrentGroup) {
            console.log('🚫 [증분 저장 필터링] 다른 그룹 데이터 제외:', result.sourceKeyword, result.productName?.substring(0, 30))
          }
          return isCurrentGroup
        })

        console.log('🔍 [증분 저장 검증] 전체:', updatedResults.length, '개 → 현재 그룹:', filteredUpdatedResults.length, '개')

        const updatedData = {
          ...existingData,
          groupId: this.currentGroupId,
          groupName: this.activeKeywordGroup,
          autoList: this.autoList,
          analysisResults: filteredUpdatedResults, // 필터링된 데이터만 저장
          lastUpdated: new Date().toISOString()
        }

        await keywordAnalysisDB.saveKeywordGroup(this.currentGroupId, updatedData)
        // console.log('✅ [증분 저장] 완료 - 전체:', updatedResults.length, '개 (신규 +', newResults.length, '개)')

      } catch (error) {
        console.error('❌ [증분 저장] 실패:', error)
        // 실패 시 기존 방식으로 폴백
        // console.log('🔄 [증분 저장] 폴백 - 전체 저장 방식으로 재시도')
        // await this.savePageDataToIndexedDB()
      }
    },

    // 전체 분석 결과를 IndexedDB에 저장 (키워드 분석 완료 후 호출)
    async saveAllAnalysisResults() {
      try {
        // console.log('💾 [전체 저장] 시작 - 그룹:', this.currentGroupId, '상품 수:', this.analysisResults.length)

        // 저장 전 autoList 정리
        this.cleanAutoListFields()

        // 기존 그룹 데이터 가져오기 (비교상품 보존용)
        const existingData = await keywordAnalysisDB.getKeywordGroup(this.currentGroupId) || {}
        // console.log('💾 [전체 저장] 기존 데이터:', existingData.analysisResults?.length || 0, '개 상품')

        // 현재 그룹의 키워드 목록 생성
        const currentGroupKeywords = new Set(this.autoList.map(item => item.keyword))

        // 현재 그룹에 속한 데이터만 저장 (데이터 오염 방지)
        const filteredAnalysisResults = this.analysisResults.filter(result => {
          const isCurrentGroup = currentGroupKeywords.has(result.sourceKeyword)
          if (!isCurrentGroup) {
            console.log('🚫 [전체 저장 필터링] 다른 그룹 데이터 제외:', result.sourceKeyword, result.productName?.substring(0, 30))
          }
          return isCurrentGroup
        })

        console.log('🔍 [전체 저장 검증] 전체:', this.analysisResults.length, '개 → 현재 그룹:', filteredAnalysisResults.length, '개')

        // 비교상품: 현재 메모리에 있으면 사용, 없으면 기존 IndexedDB 데이터 보존
        const comparisonProductToSave = this.comparisonProduct || existingData.comparisonProduct || null
        const targetProductLinkToSave = this.targetProductLink || existingData.targetProductLink || ''

        // 핵심 데이터만 저장 (계산 데이터는 제외)
        const updatedData = {
          groupId: this.currentGroupId,
          groupName: this.activeKeywordGroup,
          autoList: this.autoList,
          analysisResults: filteredAnalysisResults, // 필터링된 데이터만 저장
          // Task 9: 카테고리 선택 상태 저장
          appliedCategoryNames: this.appliedCategoryNames,
          pendingCategoryNames: this.pendingCategoryNames,
          // 비교상품 정보 저장 (기존 데이터 보존)
          comparisonProduct: comparisonProductToSave,
          targetProductLink: targetProductLinkToSave,
          lastUpdated: new Date().toISOString()
          // summaryMetrics, integratedKeywords, top20DailyTrafficData 등은 로딩 시 생성
        }

        // console.log('💾 [전체 저장] 저장할 데이터:', updatedData.analysisResults?.length || 0, '개 상품')

        // 저장할 데이터 구조 상세 로깅
        // console.log('📊 [저장 데이터 구조 분석]')
        // console.log('  - groupId:', updatedData.groupId)
        // console.log('  - groupName:', updatedData.groupName)
        // console.log('  - autoList 길이:', updatedData.autoList?.length || 0)
        // console.log('  - analysisResults 길이:', updatedData.analysisResults?.length || 0)

        // autoList 샘플 데이터
        // if (updatedData.autoList && updatedData.autoList.length > 0) {
        //   console.log('📝 [저장할 autoList 샘플] 첫 번째 키워드:')
        //   console.log(JSON.stringify(updatedData.autoList[0], null, 2))
        // }

        // // analysisResults 샘플 데이터
        // if (updatedData.analysisResults && updatedData.analysisResults.length > 0) {
        //   console.log('📈 [저장할 analysisResults 샘플] 첫 번째 상품:')
        //   console.log(JSON.stringify(updatedData.analysisResults[0], null, 2))
        // }

        await keywordAnalysisDB.saveKeywordGroup(this.currentGroupId, updatedData)
        // console.log('✅ [전체 저장] IndexedDB 저장 완료:', this.analysisResults.length, '개 상품')

        // 저장 후 검증
        // const verifyData = await keywordAnalysisDB.getKeywordGroup(this.currentGroupId)
        // console.log('🔍 [전체 저장] 저장 검증:', verifyData?.analysisResults?.length || 0, '개 상품')

      } catch (error) {
        console.error('❌ [전체 저장] 실패:', error)
      }
    },

    // IndexedDB에서 현재 그룹의 모든 데이터 삭제
    async clearIndexedDBData() {
      try {
        // 현재 그룹의 모든 분석 결과 삭제 (더 효율적)
        await keywordAnalysisDB.clearGroupAnalysisData(this.currentGroupId)

        // 그룹 전체 데이터도 초기화
        await keywordAnalysisDB.saveKeywordGroup(this.currentGroupId, {
          groupId: this.currentGroupId,
          groupName: this.activeKeywordGroup,
          autoList: this.autoList.map(item => ({
            ...item,
            analysisData: null,
            newAnalysisData: null,
            collectedAt: null
          })),
          analysisResults: [],
          summaryMetrics: {},
          integratedKeywords: []
        })

        //console.log('✅ IndexedDB 데이터 삭제 완료:', this.currentGroupId)
      } catch (error) {
        console.error('❌ IndexedDB 데이터 삭제 실패:', error)
        throw error
      }
    },

    // 분석 데이터만 초기화 (키워드 설정은 유지)
    resetAnalysisData() {
      // 호출 스택 정보 로깅 (디버깅용)
      // console.log('🚨 [resetAnalysisData 호출] 호출 스택:', new Error().stack)

      // 메모리 정리 먼저 수행
      this.cleanupMemory()

      // 분석 결과 데이터 초기화 (가장 중요!)
      this.analysisResults = []
      // console.log('🔄 [데이터 초기화] analysisResults 초기화 완료')

      // 분석 데이터 초기화
      this.summaryMetrics = {
        medianCTR: '0.00',
        medianCVR: '0.00',
        totalProducts: 0,
        totalImpressions: 0,
        totalClicks: 0,
        totalSales: 0,
        totalSalesAmount: 0,
        avgSalePrice: 0,
        avgRPI: '0.00'
      }

      this.bestSourcingProduct = null
      this.topSourcingProducts = []
      this.trafficKeywordNotice = ''
      this.trafficSkippedProducts = 0
      this.trafficSkippedKeywords = 0
      this.trafficSkippedBy = { notSubscribed: 0, fetchFail: 0, noDetail: 0 }
      this.noBusinessInsight = false
      this.trafficNoticeReported = {}
      this.collectFailLoginKeywords = 0
      this.collectFailOtherKeywords = 0
      this.collectFailBlockedKeywords = 0
      this.collectFailWingBlocked = false
      this.collectFailWingBlockedMessage = ''
      this.bestPriceRange = '데이터 없음'
      this.bestPriceRanges = []
      this.optimizedCategory = '데이터 없음'
      this.optimizedCategoryInfo = null
      this.categoryAnalysis = []
      this.pendingCategoryNames = [] // 선택 중인 카테고리 초기화
      this.appliedCategoryNames = [] // 적용된 카테고리 초기화
      // 비교상품 초기화 (그룹별 분리)
      this.comparisonProduct = null
      this.targetProductLink = ''
      this.bestProducts = []
      this.integratedKeywords = []
      this.bestCTRProduct = null
      this.bestCVRProduct = null
      this.bestPriceCvrProduct = null
      this.bestAttentionProduct = null
      this.bestRPIProduct = null

      // 키워드 SEO 분석 초기화
      this.keywordSEOAnalysis = {
        productNameKeywords: [],
        tagKeywords: [],
        suggestions: []
      }

      // 키워드 복사 기능 초기화
      this.generatedProductName = ''
      this.editableTagKeywords = []

      // 가격별 CVR 분석 초기화
      this.priceInput = ''
      this.expectedMinCVR = '-'
      this.priceVsCvrData = []

      // 광고경쟁력 분석 초기화
      this.marginRate = 30
      this.minROAS = 333
      this.sortByImpression = false
      this.adCompetitivenessData = []

      // 중복 방지용 vendorItemId 목록 초기화
      this.existingVendorItemIds = []

      // autoList의 페이지 상태도 초기화 (Vue 반응성 보장)
      this.autoList.forEach(item => {
        this.$set(item, 'completedPages', [])
        this.$set(item, 'failedPages', [])
        this.$set(item, 'skippedPages', [])
      })

      // 차트 표시 상태 초기화
      this.showImpressionChart = false
      this.showPriceVsCvrChart = false
      this.showRpiCompetitivenessChart = false
      this.showAdCompetitivenessChart = false
      this.showPriceRangeSalesChart = false
      this.showDailyTrafficChart = false

      // 차트 옵션 초기화
      this.impressionChartOptions = {}
      this.priceVsCvrChartOptions = {}
      this.rpiCompetitivenessChartOptions = {}
      this.adCompetitivenessChartOptions = {}
      this.priceRangeSalesChartOptions = {}
      this.dailyTrafficChartOptions = {}

      // 메모리 사용량 초기화
      this.memoryUsage = {
        totalProducts: 0,
        estimatedSize: 0,
        lastUpdated: null
      }
    },

    // 키워드그룹 초기화 (IndexedDB에서 기존 그룹들 로드)
    async initializeKeywordGroups() {
      try {
        // IndexedDB에서 모든 키워드 그룹 불러오기
        const allGroups = await keywordAnalysisDB.getAllKeywordGroups()

        if (allGroups && allGroups.length > 0) {
          // 기존 그룹들의 이름 추출 (groupName 우선, 없으면 groupId 사용)
          const groupNames = allGroups.map(group => {
            // groupName이 있으면 사용, 없으면 groupId를 첫글자 대문자로 변환
            if (group.groupName && group.groupName !== group.groupId) {
              return group.groupName
            }
            // groupId를 적절한 이름으로 변환 (default -> Default)
            return group.groupId.charAt(0).toUpperCase() + group.groupId.slice(1)
          })

          // Default 그룹이 없으면 추가
          if (!groupNames.includes('Default')) {
            groupNames.unshift('Default')
          }

          this.keywordGroups = [...new Set(groupNames)] // 중복 제거
          //console.log('✅ IndexedDB에서 키워드그룹 로드 완료:', this.keywordGroups)
        } else {
          // 기존 그룹이 없으면 Default만 설정
          this.keywordGroups = ['Default']
          //console.log('✅ 기본 키워드그룹 초기화:', this.keywordGroups)
        }
      } catch (error) {
        console.error('❌ 키워드그룹 초기화 실패:', error)
        // 오류 시 기본값으로 설정
        this.keywordGroups = ['Default']
      }
    },

    // 키워드를 autoList에 추가
    addKeywordToList() {
      if (!this.targetKeyword.trim()) {
        alert('키워드를 입력해주세요.')
        return
      }

      // 페이지 범위 유효성 검사
      if (this.defaultStartPage > this.defaultEndPage) {
        alert('시작페이지가 끝페이지보다 클 수 없습니다.')
        return
      }

      const keyword = this.targetKeyword.trim()

      // 중복 체크
      if (this.autoList.some(item => item.keyword === keyword)) {
        alert('이미 추가된 키워드입니다.')
        return
      }

      // 페이지 범위 계산
      const pageRange = this.defaultStartPage === this.defaultEndPage ?
        this.defaultStartPage :
        `${this.defaultStartPage}-${this.defaultEndPage}`

      // autoList에 추가
      this.autoList.push({
        keyword: keyword,
        pageRange: pageRange,
        startPage: this.defaultStartPage,
        endPage: this.defaultEndPage,
        completedPages: [], // 수집 완료된 페이지들
        failedPages: [], // 수집 실패한 페이지들
        skippedPages: [], // 스킵된 페이지들
        source: '직접입력',
        analysisData: null,
        collectedAt: null
      })

      // 입력란 초기화
      this.targetKeyword = ''

      // 키워드 추가 즉시 IndexedDB에 저장
      this.saveCurrentGroupKeywords()
      //console.log('현재 autoList:', this.autoList)
      //console.log('autoListWithIndex:', this.autoListWithIndex)

      // Vue 반응성 강제 업데이트
      this.$forceUpdate()

      // 입력창에 다시 포커스
      this.$nextTick(() => {
        if (this.$refs.modalKeywordInput) {
          this.$refs.modalKeywordInput.focus()
        }
      })
    },

    // 페이지 범위 업데이트
    async updatePageRange(item) {
      // autoList에서 해당 키워드의 실제 인덱스 찾기
      const keywordIndex = this.autoList.findIndex(kw =>
        (kw.keyword || kw.targetKeyword) === (item.keyword || item.targetKeyword)
      )

      if (keywordIndex === -1) {
        console.error('❌ [페이지 범위 업데이트] 키워드를 찾을 수 없음:', item.keyword || item.targetKeyword)
        return
      }

      // autoList의 실제 항목 참조
      const actualItem = this.autoList[keywordIndex]

      // 숫자형으로 변환 (문자열 입력 방지)
      const newStartPage = parseInt(item.startPage) || 1
      const newEndPage = parseInt(item.endPage) || newStartPage

      if (newStartPage > newEndPage) {
        item.endPage = newStartPage
        return // 잘못된 범위이므로 다시 입력하도록 함
      }

      const newPageRange = newStartPage === newEndPage ?
        newStartPage.toString() :
        `${newStartPage}-${newEndPage}`

      // console.log(`🔧 [페이지 범위 업데이트] ${actualItem.keyword}: ${actualItem.pageRange} → ${newPageRange}`)
      // console.log(`🔧 [페이지 범위 상세] startPage: ${newStartPage} (${typeof newStartPage}), endPage: ${newEndPage} (${typeof newEndPage})`)

      // 페이지 범위가 변경되었는지 확인
      const pageRangeChanged = actualItem.pageRange !== newPageRange

      if (pageRangeChanged) {
        // 새로운 페이지 범위 계산
        const newPages = []
        for (let i = newStartPage; i <= newEndPage; i++) {
          newPages.push(i)
        }

        // 기존 완료된 페이지 중 새 범위에 없는 페이지들 확인
        const completedPages = actualItem.completedPages || []
        const pagesToRemove = completedPages.filter(page => !newPages.includes(page))

        // 범위 축소로 인해 완료된 페이지가 제거되는 경우 확인
        if (pagesToRemove.length > 0) {
          const keywordName = actualItem.keyword || actualItem.targetKeyword
          if (!confirm(`"${keywordName}" 키워드의 페이지 범위를 축소하면 완료된 페이지 [${pagesToRemove.join(', ')}]의 데이터가 삭제됩니다.\n\n변경하시겠습니까?\n\n기존: ${actualItem.pageRange} → 변경: ${newPageRange}`)) {
            // 취소 시 원래 값으로 되돌리기
            const originalRange = this.parsePageRange(actualItem.pageRange)
            item.startPage = originalRange.startPage
            item.endPage = originalRange.endPage
            return
          }

          // 확인 시 해당 페이지의 데이터 제거
          this.removePageDataFromAnalysisResults(actualItem.keyword, pagesToRemove)
        }

        // autoList의 실제 항목을 직접 업데이트 (Vue 반응성 보장)
        this.$set(this.autoList, keywordIndex, {
          ...actualItem,
          pageRange: newPageRange,
          startPage: newStartPage,
          endPage: newEndPage,
          completedPages: completedPages.filter(page => newPages.includes(page))
        })

        // console.log(`✅ [페이지 범위 업데이트 완료] ${actualItem.keyword}:`, {
        //   pageRange: newPageRange,
        //   startPage: newStartPage,
        //   endPage: newEndPage,
        //   startPageType: typeof newStartPage,
        //   endPageType: typeof newEndPage,
        //   completedPages: this.autoList[keywordIndex].completedPages
        // })

        // Vue의 반응성 시스템이 완전히 처리된 후 저장
        await this.$nextTick()
        this.saveCurrentGroupKeywords()
      }
    },



    startAnalysis() {
      if (this.autoList.length === 0) {
        alert('키워드설정에서 분석할 키워드를 추가해주세요.')
        return
      }

      // 최대 수집 제한 체크
      if (this.analysisResults.length >= this.maxProducts) {
        alert(`현재 ${this.analysisResults.length}개 상품으로 최대 제한(${this.maxProducts}개)에 도달했습니다.\n초기화 후 다시 수집해주세요.`)
        return
      }

      this.startKeywordAnalysis()
    },

    // 쿠팡 API 차단 에러 분류 (Preflight + 연속 감지)
    // returns: 'hard' | 'soft-keyword' | 'soft-page' | 'other'
    classifyCollectionError(errorData, pageNum) {
      // 확장이 기다렸다 다시 불러도 계속 막힌 경우 → 남은 키워드 수집을 멈춘다
      if (errorData && errorData.blockedStop) {
        return { action: 'hard', message: errorData.message || '쿠팡이 잠시 막았습니다 — 10분 뒤 다시 수집해주세요.' }
      }
      if (!errorData || !errorData.blocked) {
        return { action: 'other' }
      }
      // Preflight: 시작 후 첫 응답이 차단 에러
      if (!this.hasReceivedAnyCollectionSuccess) {
        return {
          action: 'hard',
          message: '쿠팡 API 접근이 차단되었습니다.\n\nCLI가 정상 실행 중인지, 쿠팡 계정이 로그인되어 있는지 확인해주세요.'
        }
      }
      if (pageNum === 1) {
        this.consecutivePage1Failures++
        if (this.consecutivePage1Failures >= 3) {
          return {
            action: 'hard',
            message: '수집 중 쿠팡 API 차단이 감지되었습니다 (3개 키워드 연속 실패).\n\n지금까지 수집된 데이터는 유지됩니다. 잠시 후 다시 시도해주세요.'
          }
        }
        return { action: 'soft-keyword' }
      }
      // page 2+ → 페이지네이션 한계, 정상 상황
      this.consecutivePage1Failures = 0
      return { action: 'soft-page' }
    },

    // 트래픽 키워드를 못 받아 건너뛴 경우의 에러 코드인지 (전체 중단하지 않고 키워드만 건너뛴다)
    isTrafficSkipError(error) {
      return error === 'CATEGORY_NOT_UNDER_SELLER' || error === 'TRAFFIC_FETCH_FAILED' || error === 'TRAFFIC_KEYWORD_SKIPPED'
    },

    // 건너뛴 상품/키워드 수를 모으고 안내 배너를 갱신 · by = 확장이 준 까닭별 수(옛 확장은 없음)
    addTrafficSkipped(productCount, keywordSkipped, by) {
      const n = parseInt(productCount) || 0
      if (n <= 0 && !keywordSkipped) return
      this.trafficSkippedProducts += n
      if (keywordSkipped) this.trafficSkippedKeywords++
      if (by) for (const k of ['notSubscribed', 'fetchFail', 'noDetail']) this.trafficSkippedBy[k] += parseInt(by[k]) || 0
      this.updateTrafficSkipNotice()
    },

    // 까닭을 알면 까닭별로(조회 실패 = 윙 로그인 확인) · 모르면(옛 확장) 셋 중 하나 — 구독 탓으로 단정하지 않는다(딩톡 #24)
    updateTrafficSkipNotice() {
      const parts = []
      if (this.trafficSkippedProducts > 0) parts.push(`상품 ${this.trafficSkippedProducts}개`)
      if (this.trafficSkippedKeywords > 0) parts.push(`키워드 ${this.trafficSkippedKeywords}개`)
      if (parts.length === 0) return
      const by = this.trafficSkippedBy
      const why = []
      if (by.notSubscribed > 0) why.push(`비즈니스인사이트 구독 카테고리 밖 ${by.notSubscribed}개`)
      if (by.fetchFail > 0) why.push(`윙 조회 실패 ${by.fetchFail}개(윙 로그인 상태를 확인해 주세요)`)
      if (by.noDetail > 0) why.push(`상세 정보 없음 ${by.noDetail}개`)
      this.trafficKeywordNotice = why.length
        ? `${parts.join('/')}는 정보를 받지 못해 건너뛰었습니다 — ${why.join(' · ')}.`
        : `${parts.join('/')}는 정보를 받지 못해 건너뛰었습니다(구독 카테고리 밖·윙 조회 실패·상세 정보 없음 중 하나 — 확장을 최신으로 하면 까닭이 보입니다).`
    },

    // 트래픽 키워드를 하나도 못 받았을 때 — 쿠팡 차단 먼저 · 까닭이 윙 조회 실패뿐이면 구독 탓이 아니라 윙 로그인
    trafficNoneNotice() {
      const tail = ' (노출수·클릭수가 비어 소싱 후보가 만들어지지 않습니다)'
      if (this.collectFailBlockedKeywords > 0) return '쿠팡이 잠시 막아 일부 키워드 수집이 실패했습니다. 잠시 뒤 나눠서 다시 수집해주세요.' + tail
      const by = this.trafficSkippedBy
      if (by.fetchFail > 0 && by.notSubscribed === 0) return '트래픽 키워드를 하나도 받지 못했습니다. 윙 조회가 실패했습니다 — 윙 로그인 상태를 확인해 주세요.' + tail
      return '트래픽 키워드를 하나도 받지 못했습니다. 비즈니스인사이트 구독 상태를 확인해 주세요.' + tail
    },

    // 목록을 못 받아 수집 실패한 키워드를 사유별로 센다 (구버전 확장은 failReason 을 안 보내 여기 안 온다)
    // 안내가 화면에 뜬 순간을 서버 관측으로(판 #780 · cpl-openapi-decisionreport-1) — 회사는 로그인 세션이 붙인다 · 까닭마다 수집 한 번에 한 번
    // 확장 판은 같은 순간 확장이 보낸 요약(extDecision)에 실린다 · 문구·계정 값은 싣지 않는다
    reportTrafficNotice(reason) {
      const key = String(reason || 'other').slice(0, 40)
      if (this.trafficNoticeReported[key]) return
      this.trafficNoticeReported[key] = true
      axios.post('/api/diag/login-event', { flow: 'screen', step: 'trafficNotice', verdict: key, client: 'screen', detail: 'page=keywordDataCollect' })
        .catch(e => console.error('[키워드소싱분석] 안내 관측 전송 실패:', e && e.message))
    },

    addCollectFail(reason, message) {
      this.reportTrafficNotice(reason)
      if (reason === 'NO_BUSINESS_INSIGHT') {
        this.noBusinessInsight = true
        this.trafficKeywordNotice = NO_BUSINESS_INSIGHT_MSG
        return
      }
      if (reason === 'WING_LOGIN') this.collectFailLoginKeywords++
      else if (reason === 'BLOCKED') this.collectFailBlockedKeywords++
      else if (reason === 'WING_BLOCKED') {
        this.collectFailWingBlocked = true
        if (typeof message === 'string' && message) this.collectFailWingBlockedMessage = message
      }
      else this.collectFailOtherKeywords++
    },

    // 쿠팡 API 차단 감지 카운터 리셋 (수집 성공 시)
    markCollectionSuccess() {
      this.hasReceivedAnyCollectionSuccess = true
      this.consecutivePage1Failures = 0
    },

    // 키워드 분석 시작 (공통 메서드)
    async startKeywordAnalysis() {
      this.isAnalyzing = true
      this.progressPercent = 0
      this.progressPrefix = ''
      this.progressSuffix = '키워드 및 페이지 범위 검증 중...'

      // 차단 감지 카운터 초기화
      this.hasReceivedAnyCollectionSuccess = false
      this.consecutivePage1Failures = 0
      // 트래픽 키워드 건너뜀 집계 초기화
      this.trafficSkippedProducts = 0
      this.trafficSkippedKeywords = 0
      this.trafficSkippedBy = { notSubscribed: 0, fetchFail: 0, noDetail: 0 }
      this.noBusinessInsight = false
      this.trafficNoticeReported = {}
      this.collectFailLoginKeywords = 0
      this.collectFailOtherKeywords = 0
      this.collectFailBlockedKeywords = 0
      this.collectFailWingBlocked = false
      this.collectFailWingBlockedMessage = ''
      this.extensionNoResponse = false

      // console.log('🔍 [수집 시작] 키워드와 페이지 범위 재검증 시작...')

      // 검증 전 현재 autoList 상태 로깅
      // console.log('🔍 [검증 전] 현재 autoList 길이:', this.autoList.length)
      // this.autoList.forEach((keyword, index) => {
      //   console.log(`🔍 [검증 전 키워드 ${index + 1}] ${keyword.keyword}:`, {
      //     pageRange: keyword.pageRange,
      //     startPage: keyword.startPage,
      //     endPage: keyword.endPage,
      //     startPageType: typeof keyword.startPage,
      //     endPageType: typeof keyword.endPage,
      //     completedPages: keyword.completedPages
      //   })
      // })

      // 1단계: 현재 키워드 상태 재검증 및 정리
      await this.validateAndCleanupKeywords()

      // 검증 후 autoList 상태 로깅
      // console.log('🔍 [검증 후] 현재 autoList 길이:', this.autoList.length)
      // this.autoList.forEach((keyword, index) => {
      //   console.log(`🔍 [검증 후 키워드 ${index + 1}] ${keyword.keyword}:`, {
      //     pageRange: keyword.pageRange,
      //     startPage: keyword.startPage,
      //     endPage: keyword.endPage,
      //     startPageType: typeof keyword.startPage,
      //     endPageType: typeof keyword.endPage,
      //     completedPages: keyword.completedPages
      //   })
      // })

      // 2단계: 기존 결과 초기화 (신규수집이 아닌 경우에만)
      if (!this.originalAutoList) {
        // 전체수집인 경우에만 기존 데이터 초기화
        this.analysisResults = []
        // console.log('🔄 [전체수집] 기존 analysisResults 초기화')
      } else {
        // 신규수집인 경우 기존 데이터 보존
        console.log('🔄 [신규수집] 기존 analysisResults 보존:', this.analysisResults.length, '개 상품')
      }

      // 3단계: 작업 큐 생성
      this.createWorkQueue()

      // 4단계: 큐 처리 시작
      this.processWorkQueue()
    },

    // 키워드와 페이지 범위 재검증 및 정리
    async validateAndCleanupKeywords() {
      // console.log('🔍 [키워드 검증] 시작 - 총', this.autoList.length, '개 키워드')

      for (let i = 0; i < this.autoList.length; i++) {
        const keyword = this.autoList[i]
        // console.log(`🔍 [키워드 검증] ${i + 1}/${this.autoList.length}: ${keyword.keyword}`)
        // console.log(`🔍 [키워드 상태] ${keyword.keyword}:`, {
        //   pageRange: keyword.pageRange,
        //   startPage: keyword.startPage,
        //   endPage: keyword.endPage,
        //   startPageType: typeof keyword.startPage,
        //   endPageType: typeof keyword.endPage
        // })

        // 1. 기존 pageRange에서 startPage, endPage 파싱 (IndexedDB 로딩 시 누락될 수 있음)
        if (!keyword.startPage || !keyword.endPage) {
          const parsedRange = this.parsePageRange(keyword.pageRange)
          keyword.startPage = parsedRange.startPage
          keyword.endPage = parsedRange.endPage
          // console.log(`🔧 [페이지 범위 파싱] ${keyword.keyword}: pageRange "${keyword.pageRange}" → startPage: ${keyword.startPage}, endPage: ${keyword.endPage}`)
        } else {
          // console.log(`✅ [페이지 범위 확인] ${keyword.keyword}: startPage: ${keyword.startPage}, endPage: ${keyword.endPage} (파싱 불필요)`)
        }

        // 2. 페이지 범위 정규화
        const currentStartPage = parseInt(keyword.startPage) || 1
        const currentEndPage = parseInt(keyword.endPage) || 1
        const normalizedPageRange = currentStartPage === currentEndPage ?
          currentStartPage.toString() :
          `${currentStartPage}-${currentEndPage}`

        // console.log(`🔍 [페이지 범위 검증] ${keyword.keyword}: 기존 "${keyword.pageRange}" → 현재 "${normalizedPageRange}"`)

        // 3. 페이지 범위가 변경된 경우 처리
        if (keyword.pageRange !== normalizedPageRange) {
          // console.log(`🔧 [페이지 범위 변경] ${keyword.keyword}: ${keyword.pageRange} → ${normalizedPageRange}`)

          // 현재 페이지 범위 생성
          const newPages = []
          for (let page = currentStartPage; page <= currentEndPage; page++) {
            newPages.push(page)
          }

          // 기존 완료된 페이지 확인
          const completedPages = keyword.completedPages || []
          const pagesToRemove = completedPages.filter(page => !newPages.includes(page))

          // 범위 축소로 인한 데이터 삭제
          if (pagesToRemove.length > 0) {
            // console.log(`🗑️ [데이터 정리] ${keyword.keyword}: 페이지 [${pagesToRemove.join(', ')}] 데이터 삭제`)
            this.removePageDataFromAnalysisResults(keyword.keyword, pagesToRemove)
          }

          // 키워드 정보 업데이트
          keyword.pageRange = normalizedPageRange
          keyword.startPage = currentStartPage
          keyword.endPage = currentEndPage
          keyword.completedPages = completedPages.filter(page => newPages.includes(page))

          // console.log(`✅ [키워드 업데이트] ${keyword.keyword}: 페이지 범위 ${normalizedPageRange}, 완료된 페이지 [${keyword.completedPages.join(', ')}]`)
        }
      }

      // 4. 모든 변경사항을 IndexedDB에 저장
      // console.log('💾 [키워드 검증] IndexedDB 저장 시작...')
      await this.saveCurrentGroupKeywords()
      // console.log('✅ [키워드 검증] 완료 및 저장됨')
    },

    // 작업 큐 생성
    createWorkQueue() {
      this.workQueue = []

      this.autoList.forEach((keyword, keywordIndex) => {
        // console.log(`📋 [작업 큐 생성] 키워드 ${keywordIndex + 1}: ${keyword.keyword}`)
        // console.log(`📋 [작업 큐 생성] 키워드 상태:`, {
        //   pageRange: keyword.pageRange,
        //   startPage: keyword.startPage,
        //   endPage: keyword.endPage,
        //   completedPages: keyword.completedPages
        // })

        const pageRange = this.parsePageRange(keyword.pageRange)
        const completedPages = keyword.completedPages || []

        // console.log(`📋 [작업 큐] ${keyword.keyword}: 페이지 범위 ${pageRange.startPage}-${pageRange.endPage}, 완료된 페이지 [${completedPages.join(',')}]`)

        // 페이지 범위 내에서 완료되지 않은 페이지만 큐에 추가
        for (let pageIndex = pageRange.startPage; pageIndex <= pageRange.endPage; pageIndex++) {
          if (!completedPages.includes(pageIndex)) {
            // console.log(`📋 [작업 큐 추가] ${keyword.keyword} 페이지 ${pageIndex} 추가`)
            this.workQueue.push({
              keyword: keyword.keyword || keyword.targetKeyword,
              keywordIndex: keywordIndex,
              pageIndex: pageIndex,
              totalPages: pageRange.endPage
            })
          } else {
            console.log(`📋 [작업 큐 건너뛰기] ${keyword.keyword} 페이지 ${pageIndex} (이미 완료됨)`)
          }
        }
      })

      // console.log(`📋 [작업 큐] 생성 완료: ${this.workQueue.length}개 작업 (미완료 페이지만)`)
    },

    // 작업 큐 처리
    async processWorkQueue() {
      if (this.isProcessingQueue || this.workQueue.length === 0) {
        return
      }

      this.isProcessingQueue = true

      while (this.workQueue.length > 0 && this.isAnalyzing) {
        const work = this.workQueue.shift()

        // 진행률 업데이트
        const totalWork = this.autoList.reduce((sum, kw) => {
          const range = this.parsePageRange(kw.pageRange)
          return sum + (range.endPage - range.startPage + 1)
        }, 0)
        const completedWork = totalWork - this.workQueue.length
        this.progressPercent = (completedWork / totalWork) * 100

        // 진행 메시지 분리 설정 (상세 정보 포함)
        this.progressPrefix = `키워드 ${work.keywordIndex + 1}/${this.autoList.length}: ${work.keyword} [${work.pageIndex}p/${work.totalPages}p] `

        // 첫 번째 작업이거나 키워드가 바뀔 때만 progressSuffix 초기화
        if (!this.progressSuffix || this.progressSuffix === '키워드 분석을 시작합니다...') {
          this.progressSuffix = '상품 수집 중...'
        }
        // console.log('🔧 작업 큐에서 progressMessage 설정:', this.progressMessage)

        // console.log(`🚀 작업 처리: ${work.keyword} ${work.pageIndex}페이지`)

        // 작업 실행
        await this.executeWork(work)

        // 딜레이
        await new Promise(resolve => setTimeout(resolve, 500))
      }

      this.isProcessingQueue = false

      // 모든 작업 완료
      if (this.workQueue.length === 0) {
        this.completeAnalysis()
      }
    },

    // 개별 작업 실행
    executeWork(work) {
      return new Promise((resolve) => {
        // 현재 작업 정보 설정
        this.currentKeywordIndex = work.keywordIndex
        this.currentKeywordPageIndex = work.pageIndex
        this.currentKeywordTotalPages = work.totalPages

        // 확장이 60초 동안 아무 응답(진행·완료·에러)도 없으면 멈추고 안내한다
        // (확장에 처리 코드가 없으면 '검증 중'에서 끝없이 기다리던 문제). 이미 모은 결과는 그대로 둔다.
        let noResponseTimer = null
        const armNoResponseTimer = () => {
          clearTimeout(noResponseTimer)
          noResponseTimer = setTimeout(() => {
            window.removeEventListener('message', handleMessage)
            // 사용자가 이미 멈춘 경우엔 조용히 끝낸다
            if (!this.isAnalyzing) { resolve(); return }
            console.error(`⏱️ [executeWork] 키워드 "${work.keyword}" ${work.pageIndex}p — 확장 응답 60초 없음, 수집 중단`)
            this.isProcessingQueue = false
            this.isAnalyzing = false
            this.workQueue = []
            this.extensionNoResponse = true
            if (this.$toast) this.$toast.error(this.extensionNoResponseMessage)
            resolve()
          }, 60000)
        }

        // 완료 및 에러 핸들러 등록
        const handleMessage = (event) => {
          const res = event.data && event.data.res
          if (typeof res !== 'string' || !res.startsWith('keywordSeoAnalysis')) return
          armNoResponseTimer()

          // 완료 처리
          if (event.data.res === 'keywordSeoAnalysisComplete') {
            // 구버전 확장은 트래픽 키워드 에러를 보낸 뒤 data 없는 완료 메시지를 한 번 더 보낸다 — 무시
            if (!Array.isArray(event.data.data)) return
            clearTimeout(noResponseTimer)
            window.removeEventListener('message', handleMessage)

            this.addTrafficSkipped(event.data.trafficSkipped, false, event.data.trafficSkippedBy)

            // 결과 처리
            this.processWorkResult(work, event.data.data || [])

            resolve()
          }

          // 에러 처리
          if (event.data.res === 'keywordSeoAnalysisError') {
            clearTimeout(noResponseTimer)
            window.removeEventListener('message', handleMessage)

            console.error(`❌ [executeWork] 키워드 "${work.keyword}" ${work.pageIndex}p 수집 실패:`, event.data.error)

            // 트래픽 키워드 에러(카테고리 미구독·수집 실패·연속 실패) → 전체 중단 대신 이 키워드만
            // 아래 일반 에러 경로로 건너뛰고, 건너뛴 수는 안내 배너에 모은다
            if (this.isTrafficSkipError(event.data.error)) {
              this.addTrafficSkipped(event.data.trafficSkipped, true, event.data.trafficSkippedBy)
            }
            // 목록 수집 실패(로그인 풀림·응답 없음) → 0건 완료가 아니라 실패로 남기고(아래 일반 에러 경로) 안내에 센다
            if (event.data.failReason) {
              this.addCollectFail(event.data.failReason, event.data.message)
            }

            // 쿠팡 API 차단 감지 (Preflight / 연속 3회 / 페이지네이션 한계)
            const decision = this.classifyCollectionError(event.data, work.pageIndex)
            if (decision.action === 'hard') {
              console.error('🛑 [차단 감지] Hard Failure:', decision.message)
              this.isProcessingQueue = false
              this.isAnalyzing = false
              this.workQueue = []
              alert(decision.message)
              resolve()
              return
            }
            // soft-page(페이지 2+ 한계) 또는 정상 빈 결과(emptyResult) → 에러 아님: 트림
            if (decision.action === 'soft-page' || event.data.emptyResult) {
              const kw = this.autoList[work.keywordIndex]
              if (kw) {
                const ns = parseInt(kw.startPage) || 1
                const lastValid = work.pageIndex - 1
                const ne = lastValid >= ns ? lastValid : ns
                kw.endPage = ne
                kw.pageRange = ns === ne ? `${ns}` : `${ns}-${ne}`
                if (kw.failedPages) kw.failedPages = kw.failedPages.filter(p => p < work.pageIndex)
                if (kw.skippedPages) kw.skippedPages = kw.skippedPages.filter(p => p < work.pageIndex)
                kw.analysisData = this.analysisResults.filter(r => r.sourceKeyword === (kw.keyword || kw.targetKeyword))
                kw.collectedAt = new Date()
              }
              this.workQueue = this.workQueue.filter(w => w.keywordIndex !== work.keywordIndex)
              console.log(`✂️ [auto-trim] "${work.keyword}" ${work.pageIndex}p 이후 정상 트림 (페이지한계/빈결과)`)
              resolve()
              return
            }
            // soft-keyword(페이지 1 일시 차단) 등 그 외 → 아래 기존 로직(failedPages/skippedPages)

            // 일반 에러: 해당 키워드를 에러 상태로 표시
            const keyword = this.autoList[work.keywordIndex]
            if (keyword) {
              keyword.analysisData = 'error'
              keyword.collectedAt = new Date()

              // failedPages 초기화
              if (!keyword.failedPages) {
                this.$set(keyword, 'failedPages', [])
              }
              // skippedPages 초기화
              if (!keyword.skippedPages) {
                this.$set(keyword, 'skippedPages', [])
              }

              // 현재 페이지를 실패로 표시
              if (!keyword.failedPages.includes(work.pageIndex)) {
                keyword.failedPages.push(work.pageIndex)
                keyword.failedPages.sort((a, b) => a - b)
              }

              // 큐에서 같은 키워드의 남은 페이지들을 찾아서 스킵 처리
              const skippedPages = this.workQueue
                .filter(w => w.keywordIndex === work.keywordIndex)
                .map(w => w.pageIndex)

              // 스킵된 페이지들을 skippedPages에 추가
              skippedPages.forEach(page => {
                if (!keyword.skippedPages.includes(page)) {
                  keyword.skippedPages.push(page)
                }
              })
              keyword.skippedPages.sort((a, b) => a - b)

              if (skippedPages.length > 0) {
                console.log(`⏭️ [executeWork] 키워드 "${work.keyword}"의 페이지 [${skippedPages.join(', ')}] 스킵됨`)
              }
            }

            // 같은 키워드의 남은 페이지 작업들을 큐에서 제거
            const removedCount = this.workQueue.length
            this.workQueue = this.workQueue.filter(w => w.keywordIndex !== work.keywordIndex)
            const actualRemoved = removedCount - this.workQueue.length

            if (actualRemoved > 0) {
              console.log(`🗑️ [executeWork] 키워드 "${work.keyword}"의 남은 ${actualRemoved}개 페이지 작업 제거됨`)
            }

            // 다음 키워드로 진행
            resolve()
          }
        }

        window.addEventListener('message', handleMessage)
        armNoResponseTimer()

        // Extension에 요청 전송
        window.postMessage({
          greeting: 'startKeywordSeoAnalysis',
          keyword: work.keyword,
          pageRange: work.pageIndex,
          existingVendorItemIds: this.existingVendorItemIds,
          isProgressiveCollection: true,
          fastCollect: this.collectMode === 'fast',
          wingHadSuccess: this.hasReceivedAnyCollectionSuccess // 쿠팡 윙 막힘 판정 — 이번 수집 성공 0 에 첫 제한이면 바로 멈춘다
        }, "*")
      })
    },

    // 작업 결과 처리
    async processWorkResult(work, rawResults) {
      // 쿠팡 API 차단 감지 카운터 리셋
      this.markCollectionSuccess()

      const processedResults = this.processCollectedData(rawResults)

      // 키워드 정보 추가 (더 체계적으로)
      processedResults.forEach(result => {
        result.sourceKeyword = work.keyword
        result.sourcePageRange = `${work.pageIndex}`
        result.sourcePage = work.pageIndex // 숫자형 페이지 번호
        result.collectionKey = `${work.keyword}:${work.pageIndex}` // 고유 수집 키
        result.keywordIndex = work.keywordIndex // 키워드 인덱스

        // 특정 vendorItemId 추적
        if (result.vendorItemId === this.debugTargetVendorId) {
          console.log(`🔍 [추적] ${this.debugTargetVendorId} 수집됨:`, {
            keyword: work.keyword,
            page: work.pageIndex,
            productName: result.productName,
            price: result.price
          })
        }
      })

      // 중복 제거 후 전체 결과에 추가
      const existingVendorIds = new Set(this.analysisResults.map(item => item.vendorItemId))
      const newResults = processedResults.filter(result => !existingVendorIds.has(result.vendorItemId))

      // 특정 vendorItemId 중복 체크 추적
      if (processedResults.some(r => r.vendorItemId === this.debugTargetVendorId)) {
        const isExisting = existingVendorIds.has(this.debugTargetVendorId)
        const isFiltered = !newResults.some(r => r.vendorItemId === this.debugTargetVendorId)
        console.log(`🔍 [추적] ${this.debugTargetVendorId} 중복체크:`, {
          기존존재: isExisting,
          필터됨: isFiltered,
          기존analysisResults수: this.analysisResults.length,
          existingVendorItemIds포함: this.existingVendorItemIds.includes(this.debugTargetVendorId)
        })
      }

      // 모든 신규 상품을 추가 (최대 제한을 약간 초과해도 마지막 페이지 데이터는 모두 포함)
      this.analysisResults = this.analysisResults.concat(newResults)

      // 특정 vendorItemId analysisResults 추가 추적
      if (newResults.some(r => r.vendorItemId === this.debugTargetVendorId)) {
        console.log(`🔍 [추적] ${this.debugTargetVendorId} analysisResults에 추가됨, 전체 수:`, this.analysisResults.length)
      }

      // 최대 수집 제한 체크 (추가 후)
      const finalProductCount = this.analysisResults.length

      // 최대 제한을 초과했으면 수집 중단
      if (finalProductCount > this.maxProducts) {
        console.warn(`⚠️ [점진적 수집 완료] ${finalProductCount}개 상품 수집 완료 (${this.maxProducts}개 제한 초과로 수집 중단)`)

        // 수집 중단
        this.isAnalyzing = false
        this.progressMessage = `상품 수집 완료 (${finalProductCount}개, ${this.maxProducts}개 제한 초과)`
        this.progressPercent = 100

        // 사용자에게 알림
        if (this.$toast) {
          this.$toast.success(`상품 수집이 완료되었습니다 (${finalProductCount}개)`)
        }

        // 현재까지의 결과로 분석 완료 처리
        this.completeAnalysis()
        return
      }

      // existingVendorItemIds 업데이트 (실제 추가된 상품만)
      newResults.forEach(result => {
        if (result.vendorItemId && !this.existingVendorItemIds.includes(result.vendorItemId)) {
          this.existingVendorItemIds.push(result.vendorItemId)

          // 특정 vendorItemId existingVendorItemIds 추가 추적
          if (result.vendorItemId === this.debugTargetVendorId) {
            console.log(`🔍 [추적] ${this.debugTargetVendorId} existingVendorItemIds에 추가됨, 총 수:`, this.existingVendorItemIds.length)
          }
        }
      })

      // console.log(`✅ [작업 완료] ${work.keyword} ${work.pageIndex}p: ${processedResults.length}개 중 ${newResults.length}개 신규 (전체: ${this.analysisResults.length}개)`)

      // 페이지 완료 처리 (페이지 뱃지 시스템)
      this.markPageAsCompleted(work.keywordIndex, work.pageIndex)

      // 실시간 분석 업데이트
      this.performComprehensiveAnalysis()

      // 실시간 메모리 사용량 업데이트 (수집 중 UI 반영)
      this.updateMemoryUsage()

      // 페이지별 즉시 저장 (신규 데이터만 증분 저장)
      await this.saveNewDataToIndexedDB(newResults)
    },

    // 다음 키워드 분석
    analyzeNextKeyword() {
      if (this.currentKeywordIndex >= this.autoList.length) {
        // 모든 키워드 분석 완료
        this.completeAnalysis()
        return
      }

      const currentKeyword = this.autoList[this.currentKeywordIndex]
      if (!currentKeyword) {
        console.error('❌ 유효하지 않은 키워드:', this.currentKeywordIndex)
        this.completeAnalysis()
        return
      }

      this.progressPercent = (this.currentKeywordIndex / this.autoList.length) * 100

      // 키워드 정보 표시 개선
      const keywordName = currentKeyword.keyword || currentKeyword.targetKeyword
      const keywordInfo = this.autoList.length > 1 ? `키워드 (${this.currentKeywordIndex + 1}/${this.autoList.length}) ` : '키워드 '
      this.progressPrefix = `${keywordInfo}: ${keywordName} `
      this.progressSuffix = '분석 시작...'

      // 현재 키워드의 페이지별 수집 초기화
      this.currentKeywordPageIndex = 1
      this.currentKeywordTotalPages = this.parsePageRange(currentKeyword.pageRange).endPage
      this.currentKeywordResults = []

      // 첫 번째 페이지부터 점진적 수집 시작
      this.analyzeKeywordPage(currentKeyword)
    },

    // 페이지 범위 파싱 유틸리티
    parsePageRange(pageRange) {
      // console.log(`🔧 [parsePageRange] 입력:`, pageRange, `(타입: ${typeof pageRange})`)

      let startPage = 1, endPage = 1
      if (typeof pageRange === 'string' && pageRange.includes('-')) {
        const [start, end] = pageRange.split('-').map(Number)
        startPage = start || 1
        endPage = end || start || 1
        // console.log(`🔧 [parsePageRange] 범위 파싱: "${pageRange}" → startPage: ${startPage}, endPage: ${endPage}`)
      } else {
        startPage = endPage = parseInt(pageRange) || 1
        // console.log(`🔧 [parsePageRange] 단일 페이지: "${pageRange}" → ${startPage}`)
      }

      const result = { startPage, endPage }
      // console.log(`🔧 [parsePageRange] 결과:`, result)
      return result
    },

    // 키워드의 특정 페이지 분석
    analyzeKeywordPage(currentKeyword) {
      // 분석 중이 아니면 중단
      if (!this.isAnalyzing) {
        // console.log('⚠️ 분석이 중단되어 페이지 분석을 건너뜁니다.')
        return
      }

      // 중복 실행 방지
      const requestId = `${currentKeyword.keyword}-${this.currentKeywordPageIndex}`
      if (this.currentRequestId === requestId) {
        // console.log('⚠️ 중복 요청 방지:', requestId)
        return
      }
      this.currentRequestId = requestId

      const keywordInfo = this.autoList.length > 1 ? `키워드 (${this.currentKeywordIndex + 1}/${this.autoList.length}) ` : '키워드 '
      const pageInfo = this.currentKeywordTotalPages > 1 ? ` [${this.currentKeywordPageIndex}/${this.currentKeywordTotalPages}페이지]` : ''

      this.progressPrefix = `${keywordInfo}: ${currentKeyword.keyword}${pageInfo} `
      this.progressSuffix = '분석 중...'

      // console.log(`🚀 [페이지 분석 시작] ${currentKeyword.keyword} ${this.currentKeywordPageIndex}페이지`)

      // Extension에 단일 페이지 분석 요청 전송
      window.postMessage({
        greeting: 'startKeywordSeoAnalysis',
        keyword: currentKeyword.keyword,
        pageRange: this.currentKeywordPageIndex, // 현재 페이지만
        existingVendorItemIds: this.existingVendorItemIds,
        isProgressiveCollection: true, // 점진적 수집 플래그
        fastCollect: this.collectMode === 'fast',
        wingHadSuccess: this.hasReceivedAnyCollectionSuccess // 쿠팡 윙 막힘 판정 — 이번 수집 성공 0 에 첫 제한이면 바로 멈춘다
      }, "*")
    },

    // 현재 키워드 완료 처리
    completeCurrentKeyword(currentKeyword) {
      // 인덱스 유효성 검사
      if (this.currentKeywordIndex >= this.autoList.length || !this.autoList[this.currentKeywordIndex]) {
        console.error('❌ 유효하지 않은 키워드 인덱스:', this.currentKeywordIndex, '전체 키워드 수:', this.autoList.length)
        return
      }

      // autoList의 해당 키워드에 분석 완료 표시
      this.autoList[this.currentKeywordIndex].analysisData = this.currentKeywordResults
      this.autoList[this.currentKeywordIndex].newAnalysisData = this.currentKeywordResults.filter(result => {
        const existingVendorIds = new Set(this.analysisResults.map(item => item.vendorItemId))
        return !existingVendorIds.has(result.vendorItemId)
      })
      this.autoList[this.currentKeywordIndex].collectedAt = new Date()

      console.log(`✅ [키워드 완료] ${currentKeyword.keyword}: 총 ${this.currentKeywordResults.length}개 상품 수집 완료`)

      // 다음 키워드로 진행 또는 전체 완료
      this.currentKeywordIndex++
      if (this.currentKeywordIndex < this.autoList.length) {
        // 다음 키워드 분석 시작
        setTimeout(() => {
          // 분석이 중단되었으면 실행하지 않음
          if (this.isAnalyzing) {
            this.analyzeNextKeyword()
          }
        }, 1000)
      } else {
        // 모든 키워드 분석 완료
        this.completeAnalysis()
      }
    },

    // 전체 분석 완료 처리
    async completeAnalysis() {
      this.isAnalyzing = false
      this.isProcessingQueue = false
      this.progressPercent = 100

      // 완료 메시지
      const keywordCount = this.autoList.length
      const productCount = this.analysisResults.length
      const keywordInfo = keywordCount > 1 ? `${keywordCount}개 키워드` : '키워드'
      this.progressPrefix = ''
      this.progressSuffix = this.extensionNoResponse
        ? `확장 프로그램 응답이 없어 수집을 멈췄습니다 (지금까지 ${productCount}개 상품 수집)`
        : `${keywordInfo} 분석 완료! (총 ${productCount}개 상품 수집)`

      // 상품은 쌓였는데 트래픽 키워드가 하나도 안 붙은 경우 = 노출수/클릭수/RPI 가 전부 0.
      // 소싱 후보가 통째로 비게 되므로 "완료"만 띄우고 넘어가지 않는다.
      const withTraffic = this.analysisResults.filter(
        p => Array.isArray(p.trafficKeyword) && p.trafficKeyword.length > 0
      ).length
      if (this.noBusinessInsight) {
        // 구독 없는 계정 — 0개 완료로 보이지 않게 안내를 남긴다(건너뜀 띠로 덮지 않는다)
        this.trafficKeywordNotice = NO_BUSINESS_INSIGHT_MSG
        this.progressSuffix += ' — 비즈니스인사이트 구독 없음'
      } else if (productCount > 0 && withTraffic === 0) {
        this.trafficKeywordNotice = this.trafficNoneNotice()
        this.reportTrafficNotice('TRAFFIC_NONE')
        this.progressSuffix += ' — 트래픽 키워드 없음'
        console.warn('⚠️ [트래픽 키워드] 수집된 상품 전부에 트래픽 키워드가 없습니다:', productCount, '개')
      } else if (this.trafficSkippedProducts > 0 || this.trafficSkippedKeywords > 0) {
        this.updateTrafficSkipNotice()
      } else {
        this.trafficKeywordNotice = ''
      }

      // console.log('🎉 [전체 분석 완료] 총', this.analysisResults.length, '개 상품 수집 완료')

      // 신규 키워드 수집인 경우 원래 autoList 복원
      if (this.originalAutoList) {
        // 분석 결과를 원래 autoList에 반영
        this.autoList.forEach(analyzedItem => {
          if (analyzedItem.originalIndex !== undefined) {
            this.originalAutoList[analyzedItem.originalIndex] = {
              ...analyzedItem,
              originalIndex: undefined // 임시 속성 제거
            }
          }
        })
        this.autoList = this.originalAutoList
        this.originalAutoList = null
      }

      // 분석 결과 기반으로 키워드별 페이지 현황 동적 업데이트
      this.updatePageStatusFromAnalysisResults()

      // 비교상품 데이터 업데이트 (analysisResults에서 찾아서 풍부하게)
      this.updateComparisonProductFromResults()

      // 통합 분석 결과 생성
      this.generateIntegratedAnalysis()

      // 전체 분석 결과를 IndexedDB에 저장 (새로고침 전 저장 보장)
      await this.saveAllAnalysisResults()
    },

    // 통합 분석 결과 생성
    generateIntegratedAnalysis() {
      // console.log('📊 [통합 분석] 시작 - 상품 수:', this.analysisResults.length)

      if (this.analysisResults.length === 0) {
        // console.log('ℹ️ [통합 분석] 분석 결과가 없어 통합 분석을 건너뜁니다.')
        return
      }

      // console.log('📊 [통합 분석] 상품 데이터 샘플:', this.analysisResults.slice(0, 2))

      // 1단계: 모든 계산이 완료된 데이터 생성 (한 번만 계산)
      this.createEnrichedAnalysisData()

      // 2단계: popup.js와 동일한 종합 분석 수행 (미리 계산된 데이터 사용)
      this.performComprehensiveAnalysis()

      // console.log('✅ [통합 분석] 완료')
    },

    // 모든 계산이 완료된 데이터 생성 (성능 최적화)
    createEnrichedAnalysisData() {
      console.log('🔄 [데이터 최적화] 모든 계산값 생성 시작 - 상품 수:', this.analysisResults.length)

      // 1단계: 이상치 분석 적용 (downloadCSV 로직 활용)
      const dataWithOutlierAnalysis = this.addOutlierAnalysisToAllData()

      // 2단계: 모든 계산값 미리 계산해서 저장
      this.enrichedAnalysisResults = dataWithOutlierAnalysis.map(item => ({
        ...item,
        // 성과 지표 계산값들
        calculatedCTR: parseFloat(this.calculateCTR(item)),
        // 판매수 모르는 상품은 null — 유효 상품(RPI > 0) 필터에서 빠져 정렬·TOP·점유율 계산에 안 들어간다
        calculatedCVR: this.numOrNull(this.calculateCVR(item)),
        calculatedRPI: this.numOrNull(this.calculateRPI(item)),
        // overall(표·매출 추정·평균이 읽는 값)도 정본 RPI 로 맞춘다 — 예전엔 수집 직후 원래 클릭률로 계산한 값이 남아 TOP3 와 달랐다
        overall: this.rpiOf(item),
        calculatedAttentionScore: parseFloat(this.calculateAttentionScore(item)),
        calculatedImpressions: this.calculateImpressions(item),
        calculatedClicks: this.calculateClicks(item),
        calculatedAdRatio: parseFloat(this.calculateAdRatio(item)),

        // 분석용 조합값들
        priceCvrValue: this.calculateCVR(item) === null ? null : (item.price || 0) * parseFloat(this.calculateCVR(item)),
        ctrPriceValue: parseFloat(this.calculateCTR(item)) * (item.price || 0),
        cvrPriceValue: this.calculateCVR(item) === null ? null : parseFloat(this.calculateCVR(item)) * (item.price || 0),
        rpiPriceValue: this.calculateRPI(item) === null ? null : parseFloat(this.calculateRPI(item)) * (item.price || 0),

        // 이상치 여부 (Y/N) - 가격×CTR 이상치, 전키워드이상치 추가
        isAnyOutlier: (item.isPriceXCvrOutlier || item.isPriceXCtrOutlier || item.isTrafficVariationOutlier || item.isCTROutlier || item.isRPIOutlier || item.allKeywordsOutlier || item.isUserOutlierApplied)
      }))

      // 3단계: 필터링된 데이터셋들 미리 생성
      this.generateFilteredDatasets()

      console.log('✅ [데이터 최적화] 완료:', {
        enrichedProducts: this.enrichedAnalysisResults.length,
        withOutliers: this.validProductsWithOutliers.length,
        withoutOutliers: this.validProductsWithoutOutliers.length,
        top300ByPriceCvr: this.top300ByPriceCvr.length,
        top20ByImpressions: this.top20ByImpressions.length,
        top20ByRPI: this.top20ByRPI.length
      })
    },

    // 필터링된 데이터셋들 미리 생성 (카테고리 필터링 포함)
    generateFilteredDatasets() {
      // 0. 카테고리 필터링 (appliedCategoryNames가 있으면 해당 카테고리만)
      let baseData = this.enrichedAnalysisResults
      if (this.appliedCategoryNames && this.appliedCategoryNames.length > 0) {
        baseData = this.enrichedAnalysisResults.filter(p => {
          // 상품의 카테고리가 선택된 카테고리 중 하나와 일치하는지 확인
          const productCategory = p.category || p.categoryName || ''
          return this.appliedCategoryNames.some(appliedCat => productCategory.includes(appliedCat))
        })
        console.log(`📊 [카테고리 필터링] ${this.enrichedAnalysisResults.length}개 → ${baseData.length}개 (${this.appliedCategoryNames.join(', ')})`)
      }

      // 1. 이상치포함 유효 상품들 (기본 유효성 검사만)
      this.validProductsWithOutliers = baseData.filter(p =>
        p.calculatedRPI > 0 && (p.price || 0) > 0
      )

      // 2. 이상치제외 유효 상품들
      this.validProductsWithoutOutliers = this.validProductsWithOutliers.filter(p =>
        !p.isAnyOutlier
      )

      // 3. 판매가×CVR Top300 (이상치포함)
      this.top300ByPriceCvr = [...this.validProductsWithOutliers]
        .filter(p => p.priceCvrValue > 0)
        .sort((a, b) => b.priceCvrValue - a.priceCvrValue)
        .slice(0, 300)

      // 4. 총노출수 Top20 (이상치포함)
      this.top20ByImpressions = [...this.validProductsWithOutliers]
        .filter(p => p.calculatedImpressions > 0)
        .sort((a, b) => b.calculatedImpressions - a.calculatedImpressions)
        .slice(0, 20)

      // 5. RPI Top20 (이상치제외)
      this.top20ByRPI = [...this.validProductsWithoutOutliers]
        .sort((a, b) => b.calculatedRPI - a.calculatedRPI)
        .slice(0, 20)
    },

    async resetData() {
      // 확인 창 표시
      if (!confirm('현재 키워드그룹의 모든 분석 데이터를 삭제하시겠습니까?\n\n- 수집된 모든 상품 데이터가 삭제됩니다\n- 키워드 설정은 유지되지만 모두 대기 상태로 변경됩니다')) {
        return
      }

      try {
        // IndexedDB에서 현재 그룹의 모든 데이터 삭제
        await this.clearIndexedDBData()

        this.analysisResults = []
        this.summaryData = []
        this.progressPercent = 0
        this.progressPrefix = ''
        this.progressSuffix = ''
        this.currentKeywordIndex = 0

        // autoList의 분석 데이터 초기화 (키워드는 유지, 상태만 대기로 변경)
        this.autoList.forEach(item => {
          item.analysisData = null
          item.newAnalysisData = null
          item.collectedAt = null
          // Vue 반응성 보장을 위해 $set 사용
          this.$set(item, 'completedPages', [])
          this.$set(item, 'failedPages', [])
          this.$set(item, 'skippedPages', [])
          // 키워드와 페이지 범위는 유지
        })

        // 분석 데이터 초기화
        this.resetAnalysisData()

        // 체크리스트 초기화
        this.sourcingChecklist = null

        // 키워드 설정 저장 (completedPages 초기화 반영)
        await this.saveCurrentGroupKeywords()

        // console.log('✅ 데이터 초기화 완료 - 키워드 설정은 유지됨')
        alert('데이터가 성공적으로 초기화되었습니다.')
      } catch (error) {
        console.error('❌ 데이터 초기화 실패:', error)
        alert('데이터 초기화 중 오류가 발생했습니다.')
      }
      this.rpiCompetitivenessChartOptions = {}
      this.adCompetitivenessChartOptions = {}
      this.priceRangeSalesChartOptions = {}
      this.dailyTrafficChartOptions = {}
    },

    // 키워드설정 모달 열기
    openKeywordModal() {
      // 모달 열기 전에 페이지 현황 업데이트
      this.updatePageStatusFromAnalysisResults()

      this.showBulkModal = true
      this.focusModalInput()
    },

    // 상품링크에서 트래픽 키워드 수집 (Micro-Task 2에서 실제 로직 연결)
    collectProductTrafficKeywords() {
      // 상품링크 유효성 검사
      if (!this.targetProductLink || !this.targetProductLink.trim()) {
        alert('상품링크를 입력해주세요.')
        return
      }

      const productLink = this.targetProductLink.trim()

      // 쿠팡 상품링크 형식 검사
      if (!productLink.includes('coupang.com') || !productLink.includes('vendorItemId')) {
        alert('유효한 쿠팡 상품링크를 입력해주세요.\n(vendorItemId가 포함된 상품 상세 페이지 링크)')
        return
      }

      // 분석 상태 시작
      this.isAnalyzing = true
      this.progressSuffix = '트래픽 키워드 수집 중...'
      this.progressPercent = 10

      // Extension에 트래픽 키워드 수집 요청
      window.postMessage({
        greeting: 'getProductTrafficKeywords',
        productLink: productLink,
        days: [30,7],
        limit: 10
      }, '*')

      // 진행률 표시 업데이트
      this.progressPercent = 30
      this.progressSuffix = '쿠팡윙에서 트래픽 키워드 조회 중...'
    },

    // 네이버데이터랩 섹션 토글
    toggleDataLabSection() {
      if (this.showDataLabSection) {
        // 이미 열려있으면 닫기
        this.showDataLabSection = false
      } else {
        // 닫혀있으면 열고 다른 섹션은 닫기
        this.showDataLabSection = true
        this.showExcelUploadSection = false
      }
    },

    // 키워드 업로드 섹션 토글
    toggleExcelUploadSection() {
      if (this.showExcelUploadSection) {
        // 이미 열려있으면 닫기
        this.showExcelUploadSection = false
      } else {
        // 닫혀있으면 열고 다른 섹션은 닫기
        this.showExcelUploadSection = true
        this.showDataLabSection = false
      }
    },

    // 키워드설정 모달이 열릴 때 입력 필드에 포커스
    focusModalInput() {
      this.$nextTick(() => {
        if (this.$refs.modalKeywordInput) {
          this.$refs.modalKeywordInput.focus()
        }
      })
    },



    isPageCompleted(item, page) {
      // 특정 키워드 아이템의 특정 페이지가 수집 완료되었는지 확인
      return item.completedPages && item.completedPages.includes(page)
    },

    // 페이지 뱃지 클래스 결정
    getPageBadgeClass(item, page) {
      // 실패한 페이지 (빨간색)
      if (item.failedPages && item.failedPages.includes(page)) {
        return 'bg-danger'
      }
      // 스킵된 페이지 (주황색)
      if (item.skippedPages && item.skippedPages.includes(page)) {
        return 'bg-warning'
      }
      // 완료된 페이지 (초록색)
      if (item.completedPages && item.completedPages.includes(page)) {
        return 'bg-success'
      }
      // 대기 중인 페이지 (회색)
      return 'bg-secondary'
    },

    // 키워드 아이템의 페이지 번호 배열 생성
    getPageNumbers(item) {
      if (!item.startPage || !item.endPage) return []

      const pages = []
      for (let i = item.startPage; i <= item.endPage; i++) {
        pages.push(i)
      }
      return pages
    },

    // 키워드 아이템의 모든 페이지가 완료되었는지 확인
    isAllPagesCompleted(item) {
      const allPages = this.getPageNumbers(item)
      if (allPages.length === 0) return false

      const completedPages = item.completedPages || []
      return allPages.every(page => completedPages.includes(page))
    },

    // 페이지를 완료로 표시
    markPageAsCompleted(keywordIndex, pageIndex) {
      if (keywordIndex >= 0 && keywordIndex < this.autoList.length) {
        const keyword = this.autoList[keywordIndex]
        if (!keyword.completedPages) {
          keyword.completedPages = []
        }
        if (!keyword.completedPages.includes(pageIndex)) {
          keyword.completedPages.push(pageIndex)
          keyword.completedPages.sort((a, b) => a - b)
          // console.log(`✅ [페이지 완료] ${keyword.keyword} ${pageIndex}페이지 완료 표시`)
        }
        // 재수집 성공 시 이전 skipped/failed 흔적 제거 (색상 중복 방지)
        if (keyword.skippedPages && keyword.skippedPages.length) {
          keyword.skippedPages = keyword.skippedPages.filter(p => p !== pageIndex)
        }
        if (keyword.failedPages && keyword.failedPages.length) {
          keyword.failedPages = keyword.failedPages.filter(p => p !== pageIndex)
        }
      }
    },

    // 특정 키워드의 특정 페이지 데이터를 analysisResults에서 제거 (개선된 버전)
    removePageDataFromAnalysisResults(keyword, pagesToRemove) {
      // const initialCount = this.analysisResults.length

      // 제거할 collectionKey 목록 생성
      const keysToRemove = pagesToRemove.map(page => `${keyword}:${page}`)

      this.analysisResults = this.analysisResults.filter(result => {
        // collectionKey를 우선 사용하고, 없으면 기존 방식 사용 (하위 호환성)
        if (result.collectionKey) {
          return !keysToRemove.includes(result.collectionKey)
        } else {
          // 기존 방식 (하위 호환성)
          if (result.sourceKeyword === keyword && pagesToRemove.includes(parseInt(result.sourcePageRange))) {
            return false // 제거
          }
          return true // 유지
        }
      })

      // const removedCount = initialCount - this.analysisResults.length
      // console.log(`🗑️ [페이지 데이터 제거] ${keyword} 키워드의 페이지 [${pagesToRemove.join(', ')}] 데이터 ${removedCount}개 제거`)
      // console.log(`🔑 [제거된 키] ${keysToRemove.join(', ')}`)

      // IndexedDB에서도 해당 데이터 제거
      this.removeDataFromIndexedDB(keysToRemove)

      // 메모리 사용량 업데이트
      this.updateMemoryUsage()

      // 분석 데이터 재계산
      this.performComprehensiveAnalysis()
    },

    // IndexedDB에서 특정 collectionKey 데이터 제거
    async removeDataFromIndexedDB(keysToRemove) {
      try {
        // 현재 그룹의 데이터 로드
        const groupData = await keywordAnalysisDB.getKeywordGroup(this.currentGroupId)
        if (!groupData || !groupData.analysisResults) return

        // collectionKey 기반으로 데이터 필터링
        const filteredResults = groupData.analysisResults.filter(result => {
          if (result.collectionKey) {
            return !keysToRemove.includes(result.collectionKey)
          } else {
            // 기존 데이터 호환성을 위해 sourceKeyword:sourcePage 형태로 키 생성
            const legacyKey = `${result.sourceKeyword}:${result.sourcePage || result.sourcePageRange}`
            return !keysToRemove.includes(legacyKey)
          }
        })

        // IndexedDB 업데이트
        await keywordAnalysisDB.saveKeywordGroup(this.currentGroupId, {
          ...groupData,
          analysisResults: filteredResults
        })

        // console.log(`💾 [IndexedDB 업데이트] ${keysToRemove.length}개 키의 데이터 제거 완료`)
      } catch (error) {
        console.error('IndexedDB 데이터 제거 실패:', error)
      }
    },

    // IndexedDB에서 특정 키워드의 모든 데이터 제거
    async removeKeywordDataFromIndexedDB(keywordName) {
      try {
        // 현재 그룹의 데이터 로드
        const groupData = await keywordAnalysisDB.getKeywordGroup(this.currentGroupId)
        if (!groupData || !groupData.analysisResults) return

        // const initialCount = groupData.analysisResults.length

        // 해당 키워드의 모든 데이터 필터링
        const filteredResults = groupData.analysisResults.filter(result => {
          // collectionKey 기반 필터링 (우선)
          if (result.collectionKey) {
            return !result.collectionKey.startsWith(`${keywordName}:`)
          }
          // 기존 방식 필터링 (하위 호환성)
          return result.sourceKeyword !== keywordName
        })

        // IndexedDB 업데이트
        await keywordAnalysisDB.saveKeywordGroup(this.currentGroupId, {
          ...groupData,
          analysisResults: filteredResults
        })

        // const removedCount = initialCount - filteredResults.length
        // console.log(`💾 [IndexedDB 키워드 삭제] ${keywordName}: ${removedCount}개 데이터 제거 완료`)
      } catch (error) {
        console.error('IndexedDB 키워드 데이터 제거 실패:', error)
      }
    },

    // 상품 페이지 열기 (popup.js와 동일)
    openProductPage(product) {
      if (product.productId && product.itemId && product.vendorItemId && !product.vendorItemIdMissing) {
        const productUrl = `https://www.coupang.com/vp/products/${product.productId}?itemId=${product.itemId}&vendorItemId=${product.vendorItemId}`
        window.open(productUrl, '_blank')
      } else if (product.productId) {
        // 빠른수집 상품은 vendorItemId 가 없다(대체키) → vendorItemId 없이 연다
        const itemQuery = product.itemId ? `?itemId=${product.itemId}` : ''
        window.open(`https://www.coupang.com/vp/products/${product.productId}${itemQuery}`, '_blank')
      } else {
        console.warn('상품 링크 정보가 부족합니다:', product)
      }
    },

    // TOP3 임의 제외/복원
    async excludeTopProduct(product) {
      try {
        const id = product && product.vendorItemId
        if (!id) return
        if (!Array.isArray(this.userExcludedVendorItemIds)) this.userExcludedVendorItemIds = []
        if (!Array.isArray(this.userExcludedList)) this.userExcludedList = []
        if (!this.userExcludedVendorItemIds.includes(id)) {
          this.userExcludedVendorItemIds.push(id)
        }
        if (!this.userExcludedList.find(x => x.vendorItemId === id)) {
          this.userExcludedList.push({
            vendorItemId: id,
            productName: product.productName || '-',
            price: product.price || product.salePrice || 0,
            imgUrl: product.imgUrl || product.imagePath || '',
            rpi: Number(product.overall || 0)
          })
        }
        console.log('[UserOutlier] exclude', id, product.productName)
        // 스피너 on 후 렌더링 기회를 준 뒤 재계산
        this.isUserOutlierProcessing = true
        this.processingVendorItemId = id
        this.$nextTick(() => { setTimeout(() => {
        try {
          this.performComprehensiveAnalysis()
        } finally {
          this.isUserOutlierProcessing = false
          this.processingVendorItemId = null
          this.processingAllUserExcluded = false
        }
        }, 0) })
      } catch (e) {
        console.error('[UserOutlier] exclude error', e)
        this.isUserOutlierProcessing = false
        this.processingVendorItemId = null
        this.processingAllUserExcluded = false
      }
    },

    async restoreExcludedProduct(vendorItemId) {
      try {
        if (!Array.isArray(this.userExcludedVendorItemIds)) this.userExcludedVendorItemIds = []
        if (!Array.isArray(this.userExcludedList)) this.userExcludedList = []
        this.userExcludedVendorItemIds = this.userExcludedVendorItemIds.filter(id => id !== vendorItemId)
        this.userExcludedList = this.userExcludedList.filter(x => x.vendorItemId !== vendorItemId)
        console.log('[UserOutlier] restore', vendorItemId)
        this.isUserOutlierProcessing = true
        this.processingVendorItemId = vendorItemId
        this.$nextTick(() => { setTimeout(() => {
        try {
          this.performComprehensiveAnalysis()
        } finally {
          this.isUserOutlierProcessing = false
          this.processingVendorItemId = null
          this.processingAllUserExcluded = false
        }
        }, 0) })
      } catch (e) {
        console.error('[UserOutlier] restore error', e)
        this.isUserOutlierProcessing = false
        this.processingVendorItemId = null
        this.processingAllUserExcluded = false
      }
    },

    async restoreAllExcluded() {
      try {
        this.userExcludedVendorItemIds = []
        this.userExcludedList = []
        console.log('[UserOutlier] restore all')
        this.isUserOutlierProcessing = true
        this.processingAllUserExcluded = true
        this.processingVendorItemId = null
        this.$nextTick(() => { setTimeout(() => {
        try {
          this.performComprehensiveAnalysis()
        } finally {
          this.isUserOutlierProcessing = false
          this.processingAllUserExcluded = false
          this.processingVendorItemId = null
        }
        }, 0) })
      } catch (e) {
        console.error('[UserOutlier] restoreAll error', e)
        this.isUserOutlierProcessing = false
        this.processingAllUserExcluded = false
        this.processingVendorItemId = null
      }
    },


    // 수집된 원본 데이터를 popup 로직처럼 처리 (필드명 그대로 사용)


    processCollectedData(rawData) {


      if (!rawData || !Array.isArray(rawData)) {
        return []
      }

      // 식별자가 하나도 없는 상품만 필터링
      // 빠른수집 목록은 vendorItemId 없이 itemId/productId 만 온다 → 그걸 대체키로 쓴다
      const validData = rawData.filter(item => {
        if (!item || !(item.vendorItemId || item.itemId || item.productId)) {
          console.warn('⚠️ 식별자(vendorItemId/itemId/productId)가 없는 상품 데이터 필터링:', item)
          return false
        }
        return true
      })

      return validData.map(item => {
        // vendorItemId 가 없으면 대체키를 넣어 중복제거·저장·이상치 키가 null 끼리 묶이지 않게 한다
        const vendorItemIdMissing = !item.vendorItemId
        const fallbackKey = item.itemId ? `item-${item.itemId}` : `product-${item.productId}`
        // 원본 데이터 구조 그대로 사용하되, popup.js와 동일한 계산 로직 적용
        const processedItem = {
          ...item, // 원본 데이터 모든 필드 유지
          vendorItemId: vendorItemIdMissing ? fallbackKey : item.vendorItemId,
          vendorItemIdMissing,

          // popup.js와 동일한 계산 필드들 추가
          ctr: this.calculateCTRFromRawData(item),
          cvr: this.calculateCVRFromRawData(item),
          overall: this.calculateRPIFromRawData(item),

          // 노출수/클릭수 계산 (trafficKeyword에서)
          impressions: this.calculateImpressionsFromRawData(item),
          clicks: this.calculateClicksFromRawData(item),

          // 광고 비율 계산
          adRatio: this.calculateAdRatioFromRawData(item),

          // 경쟁도/어필도/주목도 계산
          competitiveness: this.calculateCompetitivenessFromRawData(item),
          appeal: this.calculateAppealFromRawData(item),
          attentionScore: this.calculateAttentionScoreFromRawData(item)
        }

        return processedItem
      })
    },

    // 원본 데이터에서 노출수 계산
    calculateImpressionsFromRawData(item) {
      let totalImpressions = 0
      if (item.trafficKeyword && item.trafficKeyword.length > 0) {
        item.trafficKeyword.forEach(tk => {
          totalImpressions += tk.keywordMetric?.impression || 0
        })
      }
      return totalImpressions
    },

    // 원본 데이터에서 클릭수 계산
    calculateClicksFromRawData(item) {
      let totalClicks = 0
      if (item.trafficKeyword && item.trafficKeyword.length > 0) {
        item.trafficKeyword.forEach(tk => {
          totalClicks += tk.keywordMetric?.srpClick || 0
        })
      }
      return totalClicks
    },

    // 원본 데이터에서 CTR 계산 (popup.js와 정확히 동일한 로직)
    calculateCTRFromRawData(item) {
      const totalImpressions = this.calculateImpressionsFromRawData(item)
      const totalClicks = this.calculateClicksFromRawData(item)

      // popup.js: productCTR = productImpressions > 0 ? (productClicks / productImpressions * 100) : 0
      const ctr = totalImpressions > 0 ? (totalClicks / totalImpressions * 100) : 0
      return parseFloat(ctr.toFixed(2))
    },

    // 원본 데이터에서 CVR 계산 (popup.js와 정확히 동일한 로직)
    calculateCVRFromRawData(item) {
      // 판매수를 모르는 상품(빠른수집 category-insight 상품, salesLast28d: null)은 전환율도 모른다 → null
      if (item.salesLast28d === null) return null
      // popup.js: productCVR = pvLast28Day > 0 ? (salesLast28d / pvLast28Day * 100) : 0
      const salesLast28d = item.salesLast28d || 0
      const pvLast28Day = item.pvLast28Day || 0

      const cvr = pvLast28Day > 0 ? (salesLast28d / pvLast28Day * 100) : 0
      return parseFloat(cvr.toFixed(2))
    },

    // ── RPI(노출 1회당 매출) 정본 — 이 화면의 모든 RPI(표·TOP3·매출 추정·툴팁·평균)는 아래 세 함수만 쓴다 ──
    // RPI = 클릭률(%) × 구매율(%) × 판매가 / 10000. 구매율을 모르면(판매수 null) RPI 도 null.
    rpiFromParts(ctr, cvr, price) {
      if (cvr === null || cvr === undefined) return null
      return parseFloat(((Number(ctr) || 0) * Number(cvr) * (Number(price) || 0) / 10000).toFixed(2))
    },

    // 상품 RPI — 클릭률은 이상 키워드를 걷어낸 값(cleanCTR)이 있으면 그것, 없으면 원래 값
    rpiOf(item) {
      const ctr = item.cleanCTR !== undefined ? item.cleanCTR : this.calculateCTRFromRawData(item)
      return this.rpiFromParts(ctr, this.calculateCVRFromRawData(item), item.price || 0)
    },

    // RPI 평균 = 클릭수 가중 평균 (Σ RPI×클릭 / Σ클릭). entries: [{ rpi, clicks }] — 클릭 0 은 빠진다
    // 클릭 합이 0 이면(클릭 실측 없음) RPI>0 항목의 단순 평균으로 폴백 — 0 으로 떨어뜨리지 않는다
    weightedAvgRpi(entries) {
      const valid = (entries || []).filter(e => e.rpi > 0 && e.clicks > 0)
      const totalClicks = valid.reduce((sum, e) => sum + e.clicks, 0)
      if (totalClicks === 0) {
        const withRpi = (entries || []).filter(e => e.rpi > 0)
        return withRpi.length ? withRpi.reduce((sum, e) => sum + e.rpi, 0) / withRpi.length : 0
      }
      return valid.reduce((sum, e) => sum + e.rpi * e.clicks, 0) / totalClicks
    },

    // 원본 데이터에서 RPI 계산 (수집 직후 — 아직 cleanCTR 없음)
    calculateRPIFromRawData(item) {
      return this.rpiFromParts(this.calculateCTRFromRawData(item), this.calculateCVRFromRawData(item), item.price || 0)
    },

    // 원본 데이터에서 광고 비율 계산
    calculateAdRatioFromRawData(item) {
      let totalAdImpression = 0
      let totalKeywordImpression = 0

      if (item.trafficKeyword && item.trafficKeyword.length > 0) {
        item.trafficKeyword.forEach(tk => {
          totalAdImpression += tk.keywordMetric?.adImpression || 0
          totalKeywordImpression += tk.keywordMetric?.impression || 0
        })
      }

      return totalKeywordImpression > 0 ?
        parseFloat(((totalAdImpression / totalKeywordImpression) * 100).toFixed(2)) : 0
    },

    // 원본 데이터에서 경쟁도 계산
    calculateCompetitivenessFromRawData(item) {
      const impressions = this.calculateImpressionsFromRawData(item)
      const adRatio = this.calculateAdRatioFromRawData(item)

      // popup.js 로직: 노출수가 높고 광고 비율이 높을수록 경쟁도 높음
      let competitiveness = 0
      if (impressions > 10000) competitiveness += 30
      else if (impressions > 5000) competitiveness += 20
      else if (impressions > 1000) competitiveness += 10

      if (adRatio > 50) competitiveness += 30
      else if (adRatio > 30) competitiveness += 20
      else if (adRatio > 10) competitiveness += 10

      return Math.min(competitiveness, 100)
    },

    // 원본 데이터에서 어필도 계산
    calculateAppealFromRawData(item) {
      const ctr = this.calculateCTRFromRawData(item)
      const rating = parseFloat(item.rating) || 0
      const ratingCount = parseInt(item.ratingcount) || 0

      // popup.js 로직: CTR, 평점, 리뷰수 기반
      let appeal = 0
      if (ctr > 5) appeal += 30
      else if (ctr > 3) appeal += 20
      else if (ctr > 1) appeal += 10

      if (rating >= 4.5) appeal += 25
      else if (rating >= 4.0) appeal += 15
      else if (rating >= 3.5) appeal += 10

      if (ratingCount > 1000) appeal += 25
      else if (ratingCount > 500) appeal += 15
      else if (ratingCount > 100) appeal += 10

      return Math.min(appeal, 100)
    },

    // 원본 데이터에서 시선집중도 계산 (popup.js와 정확히 동일)
    calculateAttentionScoreFromRawData(item) {
      // popup.js: 시선집중도 계산 (dailyTraffic에서 glanceView/impression 비율)
      let attentionScore = 0

      if (item.dailyTraffic && typeof item.dailyTraffic === 'object') {
        let totalGlanceView = 0
        let totalDailyImpression = 0

        Object.entries(item.dailyTraffic).forEach(([, dailyDataArray]) => {
          if (Array.isArray(dailyDataArray) && dailyDataArray.length > 0) {
            const dailyData = dailyDataArray[0] // 첫 번째 요소 사용
            const impression = dailyData.impression || 0
            const glanceView = dailyData.glanceView || 0

            totalGlanceView += glanceView
            totalDailyImpression += impression
          }
        })

        if (totalDailyImpression > 0) {
          attentionScore = (totalGlanceView / totalDailyImpression) * 100
        }
      }

      return attentionScore ? attentionScore.toFixed(2) : 0
    },

    // CTR 계산 (cleanCTR 우선, 없으면 원본 데이터 사용)
    calculateCTR(item) {
      if (item.cleanCTR !== undefined) return item.cleanCTR.toFixed(2)
      return this.calculateCTRFromRawData(item).toFixed(2)
    },

    // null 은 null 로 둔다(parseFloat(null) = NaN 방지)
    numOrNull(v) {
      return v === null || v === undefined ? null : parseFloat(v)
    },

    // CVR 계산 (원본 데이터 함수 사용)
    calculateCVR(item) {
      const cvr = this.calculateCVRFromRawData(item)
      return cvr === null ? null : cvr.toFixed(2)
    },

    // RPI 계산 (정본 rpiOf 를 문자열로)
    calculateRPI(item) {
      const rpi = this.rpiOf(item)
      return rpi === null ? null : rpi.toFixed(2)
    },

    // 경쟁력 계산 (원본 데이터 함수 사용)
    calculateCompetitiveness(item) {
      return this.calculateCompetitivenessFromRawData(item)
    },

    // 매력도 계산 (원본 데이터 함수 사용)
    calculateAppeal(item) {
      return this.calculateAppealFromRawData(item)
    },

    // 주목도 계산 (원본 데이터 함수 사용)
    calculateAttentionScore(item) {
      return this.calculateAttentionScoreFromRawData(item)
    },

    // 클릭수 계산 (원본 데이터 함수 사용)
    calculateClicks(item) {
      return this.calculateClicksFromRawData(item)
    },

    // 노출수 계산 (원본 데이터 함수 사용)
    calculateImpressions(item) {
      return this.calculateImpressionsFromRawData(item)
    },

    // 광고 비율 계산 (원본 데이터 함수 사용)
    calculateAdRatio(item) {
      return this.calculateAdRatioFromRawData(item)
    },

    async handleExtensionMessage(event) {
      const message = event.data
      // console.log('📨 [handleExtensionMessage] 메시지 수신:', message)

      // 큐 시스템 사용 중이면 Progress 메시지와 에러 메시지만 허용
      if (this.isProcessingQueue && message.res !== 'keywordSeoAnalysisProgress' && message.res !== 'keywordSeoAnalysisError') {
        // console.log('⚠️ [handleExtensionMessage] 큐 처리 중이므로 메시지를 무시합니다:', message.res)
        return
      }

      // 연관키워드, 네이버데이터랩, 트래픽 키워드 수집은 분석 상태와 관계없이 허용
      // 에러 메시지도 항상 허용해야 함
      const allowedMessages = ['adkeywords', 'keywordRankComplete', 'keywordSeoAnalysisError', 'productTrafficKeywordsComplete', 'productTrafficKeywordsError']

      // 분석이 진행 중이 아니면 허용된 메시지 외에는 무시
      if (!this.isAnalyzing && !allowedMessages.includes(message.res)) {
        // console.log('⚠️ [handleExtensionMessage] 분석이 진행 중이 아니므로 메시지를 무시합니다:', message.res)
        return
      }

      // Extension에서 오는 모든 메시지 로그
      // console.log('🔔 Extension 메시지:', message)

      // console.log('📨 Extension 메시지:', message.res, message.message)

      if (message.res === 'keywordSeoAnalysisProgress') {
        // 개별 키워드 분석 진행률은 전체 진행률에 반영
        const keywordProgress = message.percent || 0
        const baseProgress = (this.currentKeywordIndex / this.autoList.length) * 100
        const currentKeywordWeight = 100 / this.autoList.length
        this.progressPercent = baseProgress + (keywordProgress * currentKeywordWeight / 100)

        // Extension 메시지로 progressSuffix만 교체
        this.progressSuffix = message.message || '상품 분석 중...'
      }

      if (message.res === 'keywordSeoAnalysisError') {
        console.error('키워드 분석 오류:', message.error)
        // console.log('🔍 [디버그] 받은 메시지:', message)

        // 트래픽 키워드 에러(카테고리 미구독·수집 실패·연속 실패) → 전체 중단 대신 이 키워드만
        // 아래 일반 에러 경로로 건너뛰고, 건너뛴 수는 안내 배너에 모은다
        if (this.isTrafficSkipError(message.error)) {
          // 큐 처리 중이면 executeWork 가 같은 메시지를 받아 처리한다 — 여기서 또 다음 키워드로 넘기지 않는다
          if (this.isProcessingQueue) return
          this.addTrafficSkipped(message.trafficSkipped, true, message.trafficSkippedBy)
        }
        // 목록 수집 실패(로그인 풀림·응답 없음) — 큐 처리 중이면 executeWork 가 처리한다
        if (message.failReason) {
          if (this.isProcessingQueue) return
          this.addCollectFail(message.failReason, message.message)
        }

        // 쿠팡 API 차단 감지 (Preflight / 연속 3회 / 페이지네이션 한계)
        const decision = this.classifyCollectionError(message, this.currentKeywordPageIndex)
        if (decision.action === 'hard') {
          console.error('🛑 [차단 감지] Hard Failure:', decision.message)
          this.isAnalyzing = false
          this.isProcessingQueue = false
          this.workQueue = []
          alert(decision.message)
          return
        }

        // 일반 에러의 경우 현재 키워드를 실패로 표시하고 다음으로 진행
        if (this.currentKeywordIndex < this.autoList.length) {
          this.autoList[this.currentKeywordIndex].analysisData = 'error'
          this.autoList[this.currentKeywordIndex].collectedAt = new Date()
        }

        this.currentKeywordIndex++

        // 다음 키워드로 진행 (오류가 있어도 계속)
        setTimeout(() => {
          // 분석이 중단되었으면 실행하지 않음
          if (this.isAnalyzing) {
            this.analyzeNextKeyword()
          }
        }, 1000)
        return
      }

      if (message.res === 'keywordSeoAnalysisComplete') {
        // 구버전 확장은 트래픽 키워드 에러를 보낸 뒤 data 없는 완료 메시지를 한 번 더 보낸다.
        // 그대로 받으면 다음 키워드의 결과로 잘못 처리되므로 무시한다.
        if (!Array.isArray(message.data)) {
          console.warn('⚠️ [keywordSeoAnalysisComplete] 결과 없는 완료 메시지 무시')
          return
        }
        this.addTrafficSkipped(message.trafficSkipped, false, message.trafficSkippedBy)

        // 쿠팡 API 차단 감지 카운터 리셋
        this.markCollectionSuccess()

        // 중복 메시지 방지 (키워드 인덱스 + 페이지 인덱스로 고유 ID 생성)
        const messageId = `${this.currentKeywordIndex}-${this.currentKeywordPageIndex}`
        if (this.processedMessageIds.has(messageId)) {
          // console.log('⚠️ 중복 메시지 무시:', messageId)
          return
        }
        this.processedMessageIds.add(messageId)

        // 현재 요청 ID 초기화 (다음 요청 허용)
        this.currentRequestId = null

        // 인덱스 유효성 검사
        if (this.currentKeywordIndex >= this.autoList.length) {
          console.error('❌ 유효하지 않은 키워드 인덱스:', this.currentKeywordIndex, '전체 키워드 수:', this.autoList.length)
          return
        }

        // 현재 키워드의 분석 결과를 저장
        const currentKeyword = this.autoList[this.currentKeywordIndex]
        if (!currentKeyword) {
          console.error('❌ 현재 키워드가 존재하지 않습니다:', this.currentKeywordIndex)
          return
        }

        const rawKeywordResults = message.data || [] // 원본 데이터

        // 원본 데이터를 popup 로직으로 처리
        const processedKeywordResults = this.processCollectedData(rawKeywordResults)

        // 현재 키워드 정보를 결과에 추가 (더 체계적으로)
        const keywordName = currentKeyword.keyword || currentKeyword.targetKeyword || ''
        processedKeywordResults.forEach(result => {
          result.sourceKeyword = keywordName
          result.sourcePageRange = `${this.currentKeywordPageIndex}` // 현재 페이지 정보
          result.sourcePage = this.currentKeywordPageIndex // 숫자형 페이지 번호
          result.collectionKey = `${keywordName}:${this.currentKeywordPageIndex}` // 고유 수집 키
          result.keywordIndex = this.currentKeywordIndex // 키워드 인덱스
        })

        // 현재 키워드의 페이지별 결과를 누적
        this.currentKeywordResults = this.currentKeywordResults.concat(processedKeywordResults)

        // 강화된 중복 제거 로직 (vendorItemId 기준)
        const existingVendorIds = new Set(this.analysisResults.map(item => item.vendorItemId).filter(id => id))
        const newResults = processedKeywordResults.filter(result => {
          // vendorItemId가 없는 상품은 제외
          if (!result.vendorItemId) {
            console.warn('⚠️ vendorItemId가 없는 상품 제외:', result.productName)
            return false
          }
          // 이미 존재하는 vendorItemId는 제외
          return !existingVendorIds.has(result.vendorItemId)
        })

        // console.log(`🔍 [페이지별 수집] ${this.currentKeywordPageIndex}/${this.currentKeywordTotalPages}페이지: ${rawKeywordResults.length}개 중 ${newResults.length}개 신규 상품 추가`)

        // 모든 신규 상품을 추가 (최대 제한을 약간 초과해도 마지막 페이지 데이터는 모두 포함)
        this.analysisResults = this.analysisResults.concat(newResults)

        // 최대 수집 제한 체크 (추가 후)
        const finalProductCount = this.analysisResults.length

        // 최대 제한을 초과했으면 수집 중단
        if (finalProductCount > this.maxProducts) {
          console.warn(`⚠️ [상품 수집 완료] ${finalProductCount}개 상품 수집 완료 (${this.maxProducts}개 제한 초과로 수집 중단)`)

          // 수집 중단
          this.isAnalyzing = false
          this.progressMessage = `상품 수집 완료 (${finalProductCount}개, ${this.maxProducts}개 제한 초과)`
          this.progressPercent = 100

          // 사용자에게 알림
          if (this.$toast) {
            this.$toast.success(`상품 수집이 완료되었습니다 (${finalProductCount}개)`)
          }

          // 현재까지의 결과로 분석 완료 처리
          this.completeAnalysis()
          return
        }

        // 메모리 사용량 업데이트
        this.updateMemoryUsage()

        // 다음 페이지가 있으면 계속 수집, 없으면 키워드 완료 처리
        if (this.currentKeywordPageIndex < this.currentKeywordTotalPages) {
          this.currentKeywordPageIndex++
          // 500ms 딜레이 후 다음 페이지 수집
          setTimeout(() => {
            // 분석이 중단되었으면 실행하지 않음
            if (this.isAnalyzing) {
              this.analyzeKeywordPage(currentKeyword)
            }
          }, 500)
        } else {
          // 현재 키워드의 모든 페이지 수집 완료
          this.completeCurrentKeyword(currentKeyword)
        }

        // console.log(`키워드 "${currentKeyword.keyword}" 분석 완료. 수집: ${processedKeywordResults.length}개, 신규: ${newResults.length}개`)

        // 실시간으로 IndexedDB에 저장 (중복 제거된 신규 데이터만)
        for (const result of newResults) {
          await this.saveAnalysisResult(currentKeyword.keyword, result)
        }

        // 실시간으로 통합 분석 업데이트 (페이지별로 업데이트)
        this.generateIntegratedAnalysis()
      }

      if (message.res === 'keywordSeoAnalysisError') {
        console.error('키워드 분석 오류:', message.error)
        // console.log('🔍 [디버그] 받은 메시지:', message)

        // 트래픽 키워드 에러(카테고리 미구독·수집 실패·연속 실패) → 전체 중단 대신 이 키워드만
        // 아래 일반 에러 경로로 건너뛰고, 건너뛴 수는 안내 배너에 모은다
        if (this.isTrafficSkipError(message.error)) {
          // 큐 처리 중이면 executeWork 가 같은 메시지를 받아 처리한다 — 여기서 또 다음 키워드로 넘기지 않는다
          if (this.isProcessingQueue) return
          this.addTrafficSkipped(message.trafficSkipped, true, message.trafficSkippedBy)
        }

        // 일반 에러의 경우 현재 키워드를 실패로 표시하고 다음으로 진행
        if (this.currentKeywordIndex < this.autoList.length) {
          this.autoList[this.currentKeywordIndex].analysisData = 'error'
          this.autoList[this.currentKeywordIndex].collectedAt = new Date()
        }

        this.currentKeywordIndex++

        // 다음 키워드로 진행 (오류가 있어도 계속)
        setTimeout(() => {
          // 분석이 중단되었으면 실행하지 않음
          if (this.isAnalyzing) {
            this.analyzeNextKeyword()
          }
        }, this.delayTime)
      }

      // 연관+자동완성 키워드 수집 결과 처리
      if (message.res === 'adkeywords') {
        // console.log('🔍 연관키워드 수집 결과:', message.relatedkeywords)
        const keywords = message.relatedkeywords || []
        this.addKeywordsToList(keywords, '연관+자동완성')
      }

      // 네이버 데이터랩 키워드 수집 결과 처리
      // 확장은 'collectKeyword'(keywords) 로 보낸다 — 이 화면이 'keywordRankComplete'(data) 만 기다려 결과가 늘 버려졌다. 둘 다 받는다
      if (message.res === 'keywordRankComplete' || message.res === 'collectKeyword') {
        if (message.error) {
          alert(message.error)
          return
        }
        const keywords = message.data || message.keywords || []
        this.addKeywordsToList(keywords, '네이버데이터랩')
      }

      // 상품 트래픽 키워드 수집 결과 처리
      if (message.res === 'productTrafficKeywordsComplete') {
        console.log('✅ [트래픽 키워드] 수집 완료:', message.productInfo)
        this.isAnalyzing = false
        this.progressPercent = 100
        this.progressSuffix = '트래픽 키워드 수집 완료'

        // 비교상품 정보 저장 - CTR/RPI를 trafficKeyword 원본 데이터에서 직접 계산
        if (message.productInfo) {
          const info = message.productInfo
          const trafficKeywords = info.trafficKeyword || []

          // CTR 계산: trafficKeyword에서 (총클릭수 / 총노출수) * 100
          const totalImpressions = trafficKeywords.reduce((sum, tk) =>
            sum + (tk.keywordMetric?.impression || 0), 0)
          const totalClicks = trafficKeywords.reduce((sum, tk) =>
            sum + (tk.keywordMetric?.srpClick || 0), 0)
          const calculatedCtr = totalImpressions > 0
            ? parseFloat((totalClicks / totalImpressions * 100).toFixed(2))
            : 0

          // CVR 계산: (판매량 / 조회수) * 100
          // 확장이 윙 판매·조회를 못 봤으면 null(모름) — 0 으로 바꾸지 않는다(화면은 «모름»)
          const salesLast28d = info.salesLast28d == null ? null : info.salesLast28d
          const pvLast28Day = info.pvLast28Day == null ? null : info.pvLast28Day
          const calculatedCvr = (salesLast28d == null || pvLast28Day == null) ? null
            : pvLast28Day > 0 ? parseFloat((salesLast28d / pvLast28Day * 100).toFixed(2)) : 0

          // RPI 계산: 정본 rpiFromParts (클릭률 × 구매율 × 판매가 / 10000)
          const price = info.price || info.salePrice || 0
          const calculatedRpi = this.rpiFromParts(calculatedCtr, calculatedCvr, price)

          this.comparisonProduct = {
            ...info,
            productName: info.productName || '',
            price: price,
            categoryName: info.categoryName || '',
            vendorItemId: info.vendorItemId || '',
            imgUrl: info.imgUrl || info.imagePath || '',
            salesLast28d: salesLast28d,
            pvLast28Day: pvLast28Day,
            ctr: calculatedCtr,
            cvr: calculatedCvr,
            rpi: calculatedRpi,
            // 디버깅용 원본 데이터
            _debug: {
              totalImpressions,
              totalClicks,
              trafficKeywordCount: trafficKeywords.length
            }
          }
          console.log('📌 [비교상품 저장]:', this.comparisonProduct)
          console.log(`   CTR: ${calculatedCtr}% (${totalClicks}클릭/${totalImpressions}노출)`)
          console.log(`   CVR: ${calculatedCvr}% (${salesLast28d}판매/${pvLast28Day}조회)`)
          console.log(`   RPI: ${calculatedRpi} (${calculatedCtr}×${calculatedCvr}×${price}/10000)`)

          // IndexedDB에 비교상품 정보 저장 (await 필수 - 다른 저장이 먼저 실행되면 덮어씌워짐)
          await this.saveComparisonProductToIndexedDB()

          // 차트 재생성 (비교상품 마커 표시)
          this.$nextTick(() => {
            if (this.analysisResults.length > 0) {
              this.createCharts()
            }
          })
        }

        // trafficKeyword 배열에서 키워드 문자열만 추출
        const trafficKeywords = message.productInfo?.trafficKeyword || []
        const keywords = trafficKeywords
          .map(item => item?.keywordDetail?.keyword)
          .filter(kw => kw) // null/undefined 제거

        if (keywords.length > 0) {
          this.addKeywordsToList(keywords, '트래픽키워드')
          // 입력 필드 유지 (삭제하지 않음)
        } else {
          alert('수집된 트래픽 키워드가 없습니다.')
        }
      }

      // 상품 트래픽 키워드 수집 에러 처리
      if (message.res === 'productTrafficKeywordsError') {
        console.error('❌ [트래픽 키워드] 수집 실패:', message.message)
        this.isAnalyzing = false
        this.progressPercent = 0
        this.progressSuffix = ''
        alert('트래픽 키워드 수집 실패: ' + (message.message || '알 수 없는 오류'))
      }

    },

    formatPrice(price) {
      if (!price) return '0'
      return parseInt(price).toLocaleString()
    },

    formatNumber(number) {
      if (!number || isNaN(number)) return '0'
      return parseInt(number).toLocaleString()
    },

    // popup.js와 동일한 종합 분석 수행
    performComprehensiveAnalysis() {
      if (!this.analysisResults || this.analysisResults.length === 0) return

      // console.log('종합 분석 시작:', this.analysisResults.length, '개 상품')

      // 1단계: 모든 계산이 완료된 데이터 생성 (최신 데이터 반영)
      this.createEnrichedAnalysisData()

      // 2단계: 종합 지표 계산
      this.calculateSummaryMetrics()

      // 베스트 상품 분석
      this.analyzeBestProducts()

      // 소싱 전략 분석
      this.analyzeSourcingStrategy()

      // 통합 키워드 분석 (먼저 실행)
      this.analyzeIntegratedKeywords()

      // 키워드 SEO 분석 (통합 키워드 분석 후 실행)
      this.analyzeKeywordSEO()

      // 차트 생성 (일별 트래픽은 Top20으로 최적화됨)
      this.$nextTick(() => {
        this.createCharts()
      })
    },

    // 종합 지표 계산 (popup.js의 updateSummaryMetrics와 유사)
    calculateSummaryMetrics() {
      const products = this.analysisResults

      // CTR, CVR 배열 생성 (실시간 계산)
      const ctrs = products.map(p => parseFloat(this.calculateCTR(p))).filter(ctr => ctr > 0)
      const cvrs = products.map(p => parseFloat(this.calculateCVR(p))).filter(cvr => cvr > 0)

      // 중앙값 계산
      this.summaryMetrics.medianCTR = this.calculateMedian(ctrs).toFixed(2)
      this.summaryMetrics.medianCVR = this.calculateMedian(cvrs).toFixed(2)

      // 총합 계산 (실시간 계산)
      this.summaryMetrics.totalProducts = products.length
      this.summaryMetrics.totalImpressions = products.reduce((sum, p) => sum + this.calculateImpressions(p), 0)
      this.summaryMetrics.totalClicks = products.reduce((sum, p) => sum + this.calculateClicks(p), 0)
      // 판매수 모르는 상품(salesLast28d: null)은 판매 합계·매출액에서 빼고 개수만 따로 보여준다
      const salesKnown = products.filter(p => p.salesLast28d !== null)
      this.$set(this.summaryMetrics, 'salesUnknownCount', products.length - salesKnown.length)
      this.summaryMetrics.totalSales = salesKnown.reduce((sum, p) => sum + (parseInt(p.salesLast28d) || 0), 0)

      // 매출액 계산 (판매개수 × 가격)
      this.summaryMetrics.totalSalesAmount = salesKnown.reduce((sum, p) => {
        const salesCount = parseInt(p.salesLast28d) || 0
        const price = parseInt(p.price) || 0
        return sum + (salesCount * price)
      }, 0)

      // 평균 판매가격 계산
      const validPriceProducts = products.filter(p => p.price > 0)
      this.summaryMetrics.avgSalePrice = validPriceProducts.length > 0
        ? validPriceProducts.reduce((sum, p) => sum + (parseInt(p.price) || 0), 0) / validPriceProducts.length
        : 0

      // 평균 RPI 계산 (클릭수 가중)
      // (cleanCTR 이 붙은 enrichedAnalysisResults 기준 — 직전에 createEnrichedAnalysisData 가 돈다)
      const rpiSource = (this.enrichedAnalysisResults && this.enrichedAnalysisResults.length) ? this.enrichedAnalysisResults : products
      this.summaryMetrics.avgRPI = this.weightedAvgRpi(
        rpiSource.map(p => ({ rpi: this.rpiOf(p) || 0, clicks: this.calculateClicksFromRawData(p) }))
      ).toFixed(2)

      // 광고/자연 노출수 = 실측(keywordMetric.adImpression / organicImpression) 합만. 키워드 표와 같은 기준 —
      // 한 항목이라도 실측이 없으면 추정으로 채우지 않고 비운다(null → 차트 숨김)
      let totalAdImpressions = 0
      let totalOrganicImpressions = 0
      let impressionSplitMissing = false

      products.forEach(product => {
        (product.trafficKeyword || []).forEach(tk => {
          const adImp = tk.keywordMetric?.adImpression
          const orgImp = tk.keywordMetric?.organicImpression
          if (typeof adImp === 'number' && typeof orgImp === 'number') {
            totalAdImpressions += adImp
            totalOrganicImpressions += orgImp
          } else {
            impressionSplitMissing = true
          }
        })
      })

      this.summaryMetrics.totalAdImpressions = impressionSplitMissing ? null : totalAdImpressions
      this.summaryMetrics.totalOrganicImpressions = impressionSplitMissing ? null : totalOrganicImpressions

      // console.log('노출수 계산 완료:', {
      //   totalAdImpressions: this.summaryMetrics.totalAdImpressions,
      //   totalOrganicImpressions: this.summaryMetrics.totalOrganicImpressions,
      //   total: this.summaryMetrics.totalAdImpressions + this.summaryMetrics.totalOrganicImpressions
      // })

      // console.log('종합 지표 계산 완료:', this.summaryMetrics)
      // console.log('- 중앙값 CTR:', this.summaryMetrics.medianCTR)
      // console.log('- 중앙값 CVR:', this.summaryMetrics.medianCVR)
      // console.log('- 총 노출수:', this.summaryMetrics.totalImpressions)
      // console.log('- 광고 노출수:', this.summaryMetrics.totalAdImpressions)
      // console.log('- 자연 노출수:', this.summaryMetrics.totalOrganicImpressions)
    },

    // min/median/max 세 점만으로 구하는 선형보간 백분위 (0~100).
    // 사분위 데이터가 없어서 min~median 을 0~50, median~max 를 50~100 구간으로 나눠 선형보간한다.
    // 분포를 정확히 복원하는 값이 아니라 「아래쪽이냐 위쪽이냐」를 가르는 용도다.
    // 값이 비었거나(null) 숫자가 아니면 0 으로 뭉개지 않고 null 을 돌려준다.
    percentileFromMinMedianMax(value, min, median, max) {
      const v = parseFloat(value)
      const lo = parseFloat(min)
      const mid = parseFloat(median)
      const hi = parseFloat(max)
      if (![v, lo, mid, hi].every(n => Number.isFinite(n))) return null
      if (v <= lo) return 0
      if (v >= hi) return 100
      if (v <= mid) {
        return mid > lo ? ((v - lo) / (mid - lo)) * 50 : 50
      }
      return hi > mid ? 50 + ((v - mid) / (hi - mid)) * 50 : 50
    },

    // 중앙값 계산 함수 (popup.js와 동일)
    calculateMedian(arr) {
      if (arr.length === 0) return 0
      const sorted = [...arr].sort((a, b) => a - b)
      const mid = Math.floor(sorted.length / 2)
      return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2
    },

    // 베스트 상품 분석 (미리 계산된 데이터 사용)
    analyzeBestProducts() {
      // 적용된 카테고리 기반 필터링 (통합키워드/SEO와 동일한 기준 사용)
      const effectiveCategories = this.appliedCategoryNames.length > 0
        ? this.appliedCategoryNames
        : (this.optimizedCategoryInfo?.categoryName ? [this.optimizedCategoryInfo.categoryName] : [])

      // 미리 계산된 이상치제외 유효 상품들 사용
      let validProducts = this.validProductsWithoutOutliers.filter(p => {
        // popup.js와 동일한 추가 필터링 조건
        const hasMinimumClicks = p.calculatedClicks >= 11
        const hasMinimumSales = (parseInt(p.salesLast28d) || 0) >= 3
        const hasImpressions = p.calculatedImpressions > 0
        const hasValidRPI = p.calculatedRPI > 0

        return hasMinimumClicks && hasMinimumSales && hasImpressions && hasValidRPI
      })

      // 선택된 카테고리가 있으면 해당 카테고리로 필터링
      if (effectiveCategories.length > 0) {
        validProducts = validProducts.filter(p =>
          effectiveCategories.includes(p.categoryName)
        )
      }

      // console.log('🔍 베스트 상품 분석 필터링 결과:', {
      //   totalValidProducts: validProducts.length
      // })

      // 베스트 상품들은 analyzeSourcingStrategy()에서 설정됨

      // 전체 상품을 노출수 기준으로 정렬하여 현재노출순위 계산 (미리 계산된 값 사용)
      const allProductsByImpressions = validProducts
        .map(product => ({
          ...product,
          totalImpressions: product.calculatedImpressions
        }))
        .sort((a, b) => b.totalImpressions - a.totalImpressions)

      // 각 상품에 현재노출순위 부여
      allProductsByImpressions.forEach((product, index) => {
        product.currentRank = index + 1
      })

      // 전체 베스트 상품 목록 (RPI 기준 상위 20개, 현재노출순위 + 시선집중도 포함)
      this.bestProducts = allProductsByImpressions
        .sort((a, b) => b.calculatedRPI - a.calculatedRPI)
        .slice(0, 20)
        .map(product => ({
          ...product,
          // 시선집중도 컬럼 추가
          attentionScore: product.calculatedAttentionScore
        }))

      // console.log('베스트 상품 분석 완료:', {
      //   bestCTRProduct: this.bestCTRProduct?.productName,
      //   bestCVRProduct: this.bestCVRProduct?.productName,
      //   bestAttentionProduct: this.bestAttentionProduct?.productName,
      //   bestRPIProduct: this.bestRPIProduct?.productName,
      //   totalBestProducts: this.bestProducts.length
      // })
    },

    // 키워드 SEO 분석 (선택된 카테고리 키워드만 사용)
    analyzeKeywordSEO() {
      if (!this.integratedKeywords || this.integratedKeywords.length === 0) {
        this.keywordSEOAnalysis.productNameKeywords = []
        this.keywordSEOAnalysis.tagKeywords = []
        return
      }

      // 적용된 카테고리가 있으면 해당 카테고리로 필터링, 없으면 기본 최적 카테고리 사용
      const effectiveCategories = this.appliedCategoryNames.length > 0
        ? this.appliedCategoryNames
        : (this.optimizedCategoryInfo?.categoryName ? [this.optimizedCategoryInfo.categoryName] : [])

      // 통합 키워드 테이블에 있는 키워드 (필터링된 고품질 키워드)
      let tableKeywords = this.integratedKeywords
      if (effectiveCategories.length > 0) {
        tableKeywords = this.integratedKeywords.filter(keyword =>
          effectiveCategories.includes(keyword.mainCategory)
        )
      }
      const tableKeywordSet = new Set(tableKeywords.map(k => k.keyword))

      // 카테고리 분석에서 선택된 카테고리의 전체 키워드 가져오기
      const categoryKeywordsAll = []
      if (effectiveCategories.length > 0 && this.categoryAnalysis.length > 0) {
        this.categoryAnalysis
          .filter(cat => effectiveCategories.includes(cat.category))
          .forEach(cat => {
            if (cat.keywords) {
              cat.keywords.forEach(kw => {
                // 중복 방지
                if (!categoryKeywordsAll.find(k => k.keyword === kw.keyword)) {
                  categoryKeywordsAll.push(kw)
                }
              })
            }
          })
      }

      // 상품명 키워드: 통합 키워드 테이블 상위 1~6번
      this.keywordSEOAnalysis.productNameKeywords = tableKeywords
        .slice(0, 6)
        .map(keyword => ({
          word: keyword.keyword,
          count: keyword.totalVolume,
          impressions: keyword.totalVolume
        }))

      // 태그 키워드 구성:
      // 1. 통합 키워드 테이블에 있는 키워드 (초록색) - 노출수 순
      const inTableKeywords = tableKeywords
        .slice(0, 20)
        .map(keyword => ({
          word: keyword.keyword,
          count: keyword.totalVolume,
          impressions: keyword.totalVolume,
          category: keyword.mainCategory,
          isInTable: true
        }))

      // 2. 카테고리의 나머지 키워드 (파란색) - 노출수 순, 통합 테이블에 없는 것만
      const otherKeywords = categoryKeywordsAll
        .filter(kw => !tableKeywordSet.has(kw.keyword))
        .sort((a, b) => (b.impressions || 0) - (a.impressions || 0))
        .slice(0, 20 - inTableKeywords.length)
        .map(kw => ({
          word: kw.keyword,
          count: kw.impressions || 0,
          impressions: kw.impressions || 0,
          category: effectiveCategories[0] || '',
          isInTable: false
        }))

      // 태그 키워드 설정: 통합 테이블 키워드 + 나머지 키워드
      this.keywordSEOAnalysis.tagKeywords = [...inTableKeywords, ...otherKeywords]

      // 편집 가능한 태그 키워드 초기화 (원본 복사)
      this.editableTagKeywords = [...this.keywordSEOAnalysis.tagKeywords]

      // 상품명 자동 생성
      this.generateProductName()

      // console.log('✅ 키워드 SEO 분석 완료:', {
      //   optimalCategory: optimalCategoryName,
      //   filteredKeywordsCount: filteredKeywords.length,
      //   productNameKeywords: this.keywordSEOAnalysis.productNameKeywords.length,
      //   tagKeywords: this.keywordSEOAnalysis.tagKeywords.length,
      //   optimalCategoryTagKeywords: optimalCategoryTagKeywords.length,
      //   otherCategoryKeywords: otherCategoryKeywords.length,
      //   productNameSample: this.keywordSEOAnalysis.productNameKeywords.slice(0, 2),
      //   tagKeywordsSample: this.keywordSEOAnalysis.tagKeywords.slice(0, 5).map(k => `${k.word} (${k.category}${k.isOptimalCategory ? ' ⭐' : ''})`)
      // })

      // SEO 최적화 제안 생성
      this.keywordSEOAnalysis.suggestions = this.generateSEOSuggestions()
    },

    // SEO 최적화 제안 생성
    generateSEOSuggestions() {
      const suggestions = []
      const topProductKeywords = this.keywordSEOAnalysis.productNameKeywords.slice(0, 5)

      // 적용된 카테고리가 있으면 해당 카테고리 사용, 없으면 기본 최적 카테고리 사용
      const effectiveCategories = this.appliedCategoryNames.length > 0
        ? this.appliedCategoryNames
        : (this.optimizedCategoryInfo?.categoryName ? [this.optimizedCategoryInfo.categoryName] : [])

      if (topProductKeywords.length > 0) {
        suggestions.push(`상품명에 "${topProductKeywords[0].word}" 키워드를 포함하면 검색 노출이 향상될 수 있습니다.`)
      }

      if (effectiveCategories.length > 0) {
        const categoryText = effectiveCategories.length === 1
          ? `"${effectiveCategories[0]}"`
          : `"${effectiveCategories.join('", "')}" (${effectiveCategories.length}개)`
        suggestions.push(`${categoryText} 카테고리에 최적화된 키워드를 우선적으로 사용하세요.`)
      }

      // if (topTagKeywords.length > 0) {
      //   suggestions.push(`"${topTagKeywords[0].word}" 키워드로 광고를 집행하면 효과적일 수 있습니다.`)
      // }

      if (this.targetKeyword) {
        const targetInProductNames = topProductKeywords.some(k => k.word.includes(this.targetKeyword.toLowerCase()))
        if (!targetInProductNames) {
          suggestions.push(`타겟 키워드 "${this.targetKeyword}"를 상품명에 포함하는 것을 고려해보세요.`)
        }
      }

      if (suggestions.length === 0) {
        suggestions.push('충분한 데이터가 수집되면 더 구체적인 SEO 제안을 제공할 수 있습니다.')
      }

      return suggestions
    },

    // 상품명 자동 생성 로직 (오른쪽부터 왼쪽으로 채우기)
    generateProductName() {
      if (this.keywordSEOAnalysis.productNameKeywords.length === 0) {
        this.generatedProductName = ''
        return
      }

      // 노출수 큰 순으로 키워드 정렬
      const sortedKeywords = this.keywordSEOAnalysis.productNameKeywords
        .sort((a, b) => b.impressions - a.impressions)

      // 결과 배열 (오른쪽부터 왼쪽으로 채워나감)
      let result = []

      // 노출수 높은 순으로 하나씩 처리
      sortedKeywords.forEach((keywordObj) => {
        const keyword = keywordObj.word
        // console.log(`${index + 1}. "${keyword}" 처리 시작`)

        // 키워드를 단어로 분해 (복합 키워드 처리)
        const words = keyword.split(/\s+/).filter(word => word.length > 0)

        if (words.length === 1) {
          // 단일 키워드 처리
          const singleWord = words[0]
          if (!result.includes(singleWord)) {
            if (result.length === 0) {
              // 첫 번째 키워드는 맨 오른쪽
              result.push(singleWord)
              // console.log(`   첫 키워드 "${singleWord}" 맨 오른쪽 추가:`, result.join(' '))
            } else {
              // 이후 키워드는 맨 왼쪽에 추가 (오른쪽부터 왼쪽으로 채우기)
              result.unshift(singleWord)
              // console.log(`   키워드 "${singleWord}" 맨 왼쪽 추가:`, result.join(' '))
            }
          }
        } else {
          // 복합 키워드 처리
          const mainWord = words[words.length - 1] // 마지막 단어가 메인

          // 메인 키워드가 이미 result에 있는지 확인
          const mainWordIndex = result.indexOf(mainWord)

          if (mainWordIndex !== -1) {
            // 메인 키워드가 이미 있는 경우
            // 수식어들을 메인 키워드 앞에 추가하고, 후속어들을 메인 키워드 뒤에 추가
            words.forEach((word, wordIndex) => {
              if (!result.includes(word)) {
                if (wordIndex < words.length - 1) {
                  // 메인 키워드가 아닌 경우
                  if (wordIndex < words.indexOf(mainWord)) {
                    // 수식어: 메인 키워드 앞에 추가
                    result.splice(mainWordIndex, 0, word)
                    // console.log(`   수식어 "${word}"를 "${mainWord}" 앞에 추가:`, result.join(' '))
                  } else {
                    // 후속어: 메인 키워드 뒤에 추가
                    const currentMainIndex = result.indexOf(mainWord)
                    result.splice(currentMainIndex + 1, 0, word)
                    // console.log(`   후속어 "${word}"를 "${mainWord}" 뒤에 추가:`, result.join(' '))
                  }
                }
              }
            })
          } else {
            // 메인 키워드가 없으면 전체 키워드를 하나로 처리
            if (result.length === 0) {
              // 첫 번째면 맨 오른쪽
              result.push(keyword)
              // console.log(`   첫 복합키워드 "${keyword}" 맨 오른쪽 추가:`, result.join(' '))
            } else {
              // 이후면 맨 왼쪽
              result.unshift(keyword)
              // console.log(`   복합키워드 "${keyword}" 맨 왼쪽 추가:`, result.join(' '))
            }
          }
        }

        // console.log(`   ${index + 1}번 처리 완료:`, result.join(' '))
      })

      const productNamePart = result.join(' ')
      this.generatedProductName = `브랜드명 ${productNamePart}`

      // console.log('🔍 상품명 생성 로직 (오른쪽부터 왼쪽으로 채우기):', {
      //   originalKeywords: sortedKeywords.map(k => `${k.word} (${k.impressions.toLocaleString()})`),
      //   stepByStep: '위의 단계별 로그 참조',
      //   finalResult: result,
      //   finalProductName: this.generatedProductName
      // })
    },

    // 상품명 입력창 업데이트 시 호출
    updateGeneratedProductName() {
      // 사용자가 직접 수정한 경우 별도 처리 없음 (수정 허용)
    },

    // 상품명 키워드 복사
    async copyProductNameKeywords() {
      try {
        await navigator.clipboard.writeText(this.generatedProductName)
        this.$toast.success('상품명이 클립보드에 복사되었습니다!')
      } catch (err) {
        console.error('복사 실패:', err)
        // 폴백: 텍스트 선택 방식
        const textArea = document.createElement('textarea')
        textArea.value = this.generatedProductName
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        this.$toast.success('상품명이 클립보드에 복사되었습니다!')
      }
    },

    // 상세보기 스위치 토글
    toggleDetailView() {
      this.showDetailView = !this.showDetailView
      this.updateHeaders()
      // console.log('🔄 상세보기 토글:', {
      //   showDetailView: this.showDetailView,
      //   headerType: this.showDetailView ? '상세보기' : '기본보기',
      //   headerCount: this.integratedKeywordHeaders.length
      // })
    },

    // 헤더 업데이트
    updateHeaders() {
      // console.log('🔄 헤더 업데이트 시작:', {
      //   showDetailView: this.showDetailView,
      //   기본헤더수: this.basicIntegratedKeywordHeaders.length,
      //   상세헤더수: this.detailIntegratedKeywordHeaders.length
      // })

      const newHeaders = this.showDetailView ?
        [...this.detailIntegratedKeywordHeaders] :
        [...this.basicIntegratedKeywordHeaders]

      // Vue의 반응성을 보장하기 위해 명시적으로 할당
      this.$set(this, 'integratedKeywordHeaders', newHeaders)

      // console.log('✅ 헤더 업데이트 완료:', {
      //   새헤더수: this.integratedKeywordHeaders.length,
      //   헤더타입: this.showDetailView ? '상세보기' : '기본보기'
      // })

      // 다음 틱에서 강제 업데이트
      this.$nextTick(() => {
        this.$forceUpdate()
      })
    },

    // 태그 키워드 뱃지 클래스 결정
    getTagKeywordBadgeClass(keyword) {
      if (keyword.isInTable) {
        return 'badge bg-success' // 통합 키워드 테이블에 있는 키워드: 초록색
      } else {
        return 'badge bg-info' // 테이블에 없는 키워드: 파란색
      }
    },

    // 태그 키워드 삭제
    removeTagKeyword(index) {
      this.editableTagKeywords.splice(index, 1)
    },

    // 태그 키워드 복사 (앞에 20개만)
    async copyTagKeywords() {
      if (this.editableTagKeywords.length === 0) return

      const first20Keywords = this.editableTagKeywords.slice(0, 20)
      const tagString = first20Keywords.map(k => k.word).join(', ')

      try {
        await navigator.clipboard.writeText(tagString)
        this.$toast.success(`태그 키워드 ${first20Keywords.length}개가 클립보드에 복사되었습니다!`)
      } catch (err) {
        console.error('복사 실패:', err)
        // 폴백: 텍스트 선택 방식
        const textArea = document.createElement('textarea')
        textArea.value = tagString
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        this.$toast.success(`태그 키워드 ${first20Keywords.length}개가 클립보드에 복사되었습니다!`)
      }
    },



    // 소구점 요약 텍스트 생성 - 주석처리
    /*
    buildSellingPointsSummary() {
      const sc = this.sourcingChecklist || {}
      const parts = []
      // 장점(USP)
      if (sc.reviewAnalysis && sc.reviewAnalysis.advantages && Array.isArray(sc.reviewAnalysis.advantages.items)) {
        const pts = sc.reviewAnalysis.advantages.items.slice(0, 6).map(x => x.point).filter(Boolean)
        if (pts.length) parts.push(`USPs: ${pts.join(', ')}`)
      }
      // 단점 → 문제해결형 키워드 힌트로만 사용
      if (sc.reviewAnalysis && sc.reviewAnalysis.disadvantages && Array.isArray(sc.reviewAnalysis.disadvantages.items)) {
        const probs = sc.reviewAnalysis.disadvantages.items.slice(0, 6).map(x => x.point).filter(Boolean)
        if (probs.length) parts.push(`Problems: ${probs.join(', ')}`)
      }
      // 개선 방향
      if (sc.improvementDirections && Array.isArray(sc.improvementDirections.items)) {
        const imps = sc.improvementDirections.items.slice(0, 6).map(x => x.description).filter(Boolean)
        if (imps.length) parts.push(`Improvements: ${imps.join('; ')}`)
      }
      // 보완된 USP
      if (sc.enhancedUSP && sc.enhancedUSP.oneLiner) {
        parts.push(`One-liner USP: ${sc.enhancedUSP.oneLiner}`)
      }
      return parts.join('\n')
    },
    */

    // 다국어 검색어 생성 - 주석처리
    /*
    async generateMultilingualKeywords() {
      if (!this.sourcingChecklist) {
        this.$toast.info('AI 소싱 분석을 먼저 실행해주세요')
        return
      }
      try {
        this.isGeneratingMultilingualKeywords = true
        const sellingPoints = this.buildSellingPointsSummary()
        const prompt = `You are an e-commerce keyword expert. Generate 5 sets of multilingual search queries where each set contains the SAME MEANING keywords in Korean, Chinese, and English.

Context (Korean analysis summary):
${sellingPoints}

Rules:
- Return VALID JSON only. No prose.
- Generate exactly 5 keyword sets
- Each set must have the SAME MEANING across all 3 languages
- JSON structure: {"keywordSets": [{"ko": {"keyword": string, "rationale": string}, "zh": {"keyword": string, "rationale": string}, "en": {"keyword": string, "rationale": string}}, ...]}
- Korean: Natural search phrases that strongly reflect selling points
- Chinese: Simplified Chinese (简体中文) typical 1688 search style, same meaning as Korean
- English: Amazon/Alibaba style queries, same meaning as Korean
- Prioritize queries that best express the USPs and improvements
- Keep 2-5 words if possible; avoid punctuation/special characters

Example JSON:
{
  "keywordSets": [
    {
      "ko": {"keyword": "미끄럼방지 옷걸이", "rationale": "논슬립 USP 반영"},
      "zh": {"keyword": "防滑衣架", "rationale": "防滑卖点"},
      "en": {"keyword": "non slip hanger", "rationale": "non-slip USP"}
    }
  ]
}`

        const result = await workflowService.requestExtensionWithPromise({
          workType: 'SOURCING_CHECKLIST',
          task: 'generate_multilingual_keywords',
          requestArr: [{ prompt }]
        }, { timeout: 60000, maxRetries: 2 })

        this.multilingualKeywords = this.parseMultilingualKeywords(result)
        this.$toast.success('다국어 검색어 생성 완료')
      } catch (err) {
        console.error('다국어 검색어 생성 실패:', err)
        this.$toast.error('다국어 검색어 생성 중 오류가 발생했습니다')
      } finally {
        this.isGeneratingMultilingualKeywords = false
      }
    },
    */

    // 응답 파싱 및 정규화 - 주석처리
    /*
    parseMultilingualKeywords(result) {
      try {
        let parsed = result
        if (parsed && typeof parsed === 'object' && parsed.batchResults && Array.isArray(parsed.batchResults)) {
          parsed = parsed.batchResults[0]?.result || parsed
        }
        if (typeof parsed === 'string') {
          parsed = JSON.parse(parsed)
        }

        const keywordSets = Array.isArray(parsed?.keywordSets) ? parsed.keywordSets : []
        const normalizedSets = []

        for (let i = 0; i < Math.min(5, keywordSets.length); i++) {
          const set = keywordSets[i]
          if (set && typeof set === 'object') {
            const normalizedSet = {}

            // 한국어
            if (set.ko && set.ko.keyword) {
              normalizedSet.ko = {
                keyword: set.ko.keyword.trim(),
                rationale: (set.ko.rationale || '').trim()
              }
            }

            // 중국어
            if (set.zh && set.zh.keyword) {
              normalizedSet.zh = {
                keyword: set.zh.keyword.trim(),
                rationale: (set.zh.rationale || '').trim()
              }
            }

            // 영어
            if (set.en && set.en.keyword) {
              normalizedSet.en = {
                keyword: set.en.keyword.trim(),
                rationale: (set.en.rationale || '').trim()
              }
            }

            // 최소 하나의 언어라도 있으면 추가
            if (normalizedSet.ko || normalizedSet.zh || normalizedSet.en) {
              normalizedSets.push(normalizedSet)
            }
          }
        }

        return normalizedSets
      } catch (e) {
        console.error('다국어 키워드 파싱 실패:', e, result)
        return []
      }
    },

    // 단일 키워드 복사
    async copyKeyword(keyword) {
      try {
        await navigator.clipboard.writeText(keyword)
        this.$toast.success('키워드를 복사했습니다')
      } catch (err) {
        console.error('복사 실패:', err)
        this.$toast.error('복사 중 오류가 발생했습니다')
      }
    },
    */

    // 소싱 전략 분석 (미리 계산된 데이터 사용)
    analyzeSourcingStrategy() {
      // 미리 계산된 이상치제외 유효 상품들 사용
      const validProducts = this.validProductsWithoutOutliers

      // console.log('🔍 베스트 상품 분석 결과:', {
      //   validProducts: validProducts.length
      // })

      // 베스트 CTR 상품 (이상치제외 전체데이터 중 CTR 최고)
      this.bestCTRProduct = validProducts
        .filter(p => p.calculatedCTR > 0 && (p.price || 0) > 0)
        .sort((a, b) => b.calculatedCTR - a.calculatedCTR)[0] || null

      // 베스트 시선집중도 상품 (이상치제외 전체데이터 중 시선집중도 최고)
      this.bestAttentionProduct = validProducts
        .filter(p => p.calculatedAttentionScore > 0)
        .sort((a, b) => b.calculatedAttentionScore - a.calculatedAttentionScore)[0] || null

      // 베스트 CVR 상품 (이상치제외 전체데이터 중 CVR 최고)
      this.bestCVRProduct = validProducts
        .filter(p => p.calculatedCVR > 0 && (p.price || 0) > 0)
        .sort((a, b) => b.calculatedCVR - a.calculatedCVR)[0] || null

      // 베스트 판매가×CVR 상품 (이상치제외 전체데이터 중 판매가×CVR 최고)
      this.bestPriceCvrProduct = validProducts
        .filter(p => p.calculatedCVR > 0 && (p.price || 0) > 0)
        .sort((a, b) => (b.price * b.calculatedCVR) - (a.price * a.calculatedCVR))[0] || null

      // 베스트 RPI 상품 (이상치제외 전체데이터 중 RPI 최고)
      this.bestRPIProduct = validProducts
        .filter(p => p.calculatedRPI > 0 && (p.price || 0) > 0)
        .sort((a, b) => b.calculatedRPI - a.calculatedRPI)[0] || null

      // 베스트 소싱 상품 (순수 RPI 기준)
      this.bestSourcingProduct = validProducts
        .sort((a, b) => b.calculatedRPI - a.calculatedRPI)[0] || null

      // TOP3 소싱 상품 = 베스트 RPI 20의 상위 3개 (동일 기준, 정렬/표시 일관성)
      this.topSourcingProducts = this.bestProducts.slice(0, 3)

      // 가격대별 분석
      this.analyzePriceRanges()

      // 카테고리 분석
      this.analyzeCategoryOptimization()

      // console.log('소싱 전략 분석 완료:', {
      //   bestCTRProduct: this.bestCTRProduct?.productName,
      //   bestAttentionProduct: this.bestAttentionProduct?.productName,
      //   bestSourcingProduct: this.bestSourcingProduct?.productName
      // })
    },

    // 소싱관리 보드 담기 (TOP 소싱상품 1개당 1행, stage 1 벤치마킹)
    async addToSourcingBoard() {
      const products = this.topSourcingProducts || []

      // 대표 키워드 (검색량 1위) = 키워드 레벨 지표 출처
      const kw = (this.integratedKeywords && this.integratedKeywords.length > 0) ? this.integratedKeywords[0] : {}
      const keyword = kw.keyword || kw.targetKeyword || this.targetKeyword || ''

      if (!keyword && products.length === 0) {
        alert('담을 데이터가 없습니다.')
        return
      }

      // SEO 키워드 배열을 ', ' 문자열로 조인 (객체는 word, 문자열은 그대로)
      const seoJoin = (arr) => Array.isArray(arr)
        ? arr.map(k => (typeof k === 'string' ? k : (k && (k.word || k.keyword)) || '')).filter(Boolean).join(', ')
        : (arr || '')

      // 키워드 레벨 raw (같은 키워드의 모든 상품 행에 동일하게 복사)
      const keywordRaw = {
        maxRpi: kw.maxRpi,
        avgRpi: kw.avgRpi,
        medianRpi: kw.medianRpi,
        minRpi: kw.minRpi,
        topRankMaxRpi: kw.topRankMaxRpi,
        topRankAvgRpi: kw.topRankAvgRpi,
        topRankMedianRpi: kw.topRankMedianRpi,
        topRankMinRpi: kw.topRankMinRpi,
        maxImpressions: kw.maxImpressions,
        avgImpressions: kw.avgImpressions,
        medianImpressions: kw.medianImpressions,
        minImpressions: kw.minImpressions,
        maxCtr: kw.maxCtr,
        avgCtr: kw.avgCtr,
        medianCtr: kw.medianCtr,
        minCtr: kw.minCtr,
        maxCvr: kw.maxCvr,
        avgCvr: kw.avgCvr,
        medianCvr: kw.medianCvr,
        minCvr: kw.minCvr,
        accessibilityScore: kw.accessibilityScore,
        revenueShare: kw.revenueShare,
        revenueShareBasedRevenueRange: kw.revenueShareBasedRevenueRange,
        priceRange25to75: kw.priceRange25to75
      }

      const seoRaw = {
        seo_productNameKeywords: seoJoin(this.keywordSEOAnalysis && this.keywordSEOAnalysis.productNameKeywords),
        seo_tagKeywords: seoJoin(this.keywordSEOAnalysis && this.keywordSEOAnalysis.tagKeywords),
        seo_suggestions: seoJoin(this.keywordSEOAnalysis && this.keywordSEOAnalysis.suggestions)
      }

      const monthlyRevenue = kw.avgSalesAmount !== undefined ? kw.avgSalesAmount : kw.maxSalesAmount

      // 담기 3경로 중 이 화면만 '카테고리'를 안 보내서 보드의 카테고리 컬럼이 늘 빈칸이었다.
      // 상품별 categoryName 이 우선, 없으면 적용/최적 카테고리 → 키워드 주요 카테고리 순으로 대체한다.
      const fallbackCategory = (this.effectiveOptimizedCategoryInfo && this.effectiveOptimizedCategoryInfo.categoryName) || kw.mainCategory || ''

      // 상품 단위 노출수도 '이 키워드에 해당하는' 값이어야 한다.
      // ⚠ product.calculatedImpressions(= 전 키워드 impression 합계)를 넣으면 안 된다 — 키워드
      //    1페이지 안에서의 위치를 재는 값이 아니라 그 상품이 몇 개 키워드로 뜨는지를 재는 값이 된다.
      //    이 키워드로 안 뜬 상품이면 값이 '없는' 것이므로 0 이 아니라 null 을 준다.
      const keywordImpressionOf = (product) => {
        const tk = product && Array.isArray(product.trafficKeyword)
          ? product.trafficKeyword.find(t => (t.keywordDetail?.keyword || t.keyword) === keyword)
          : null
        const v = tk && tk.keywordMetric ? tk.keywordMetric.impression : null
        return (typeof v === 'number' && v > 0) ? v : null
      }

      // 「기회」 라벨 — 보드가 아니라 여기서 판정한다.
      // 1페이지 전체 데이터(integratedKeywords 의 분포)가 이 화면에만 있고, 보드에는 상위 3행만
      // 가므로 보드는 위치를 다시 계산할 수 없다.
      // 백분위는 위 (1) 에서 만든 min/median/max 를 percentileFromMinMedianMax() 로 선형보간해 구한다.
      // RPI 분포(maxRpi/medianRpi/minRpi)가 p.overall 기준이라 상품값도 overall 을 먼저 쓴다.
      const opportunityOf = (product) => {
        if (!product) return null

        const clicks = parseInt(product.calculatedClicks) || 0
        const sales = parseInt(product.salesLast28d) || 0
        if (clicks < OPPORTUNITY_THRESHOLDS.MIN_CLICKS || sales < OPPORTUNITY_THRESHOLDS.MIN_SALES_28D) {
          return '표본부족'
        }

        const productRpi = parseFloat(product.overall) || parseFloat(product.calculatedRPI) || 0
        const rpiPct = this.percentileFromMinMedianMax(productRpi, kw.minRpi, kw.medianRpi, kw.maxRpi)
        if (rpiPct === null) return null

        // '전환약함' 은 RPI 만으로 성립하므로 노출 유무를 볼 필요가 없다.
        if (rpiPct < OPPORTUNITY_THRESHOLDS.LOW_RPI_PERCENTILE) return '전환약함'

        // '노출여력'/'이미포화' 는 노출 백분위가 있어야 성립하는 판정이다.
        // ⚠ 노출을 모르는데 '이미포화'(= RPI 도 노출도 상위, 이미 잘 돌고 있다)로 단정하면
        //    「모른다」가 「포화다」로 둔갑한다. 게다가 이미포화는 기회계수가 노출여력보다 낮아
        //    가장 값진 후보가 정렬에서 밀려난다 — 「세어보지 않은 것」을 값으로 쓴 사고다.
        //    그래서 노출 백분위 null 은 별도 라벨('노출미상')로 끝까지 살린다.
        const impPct = this.percentileFromMinMedianMax(
          keywordImpressionOf(product), kw.minImpressions, kw.medianImpressions, kw.maxImpressions
        )
        if (impPct === null) return '노출미상'

        if (rpiPct >= OPPORTUNITY_THRESHOLDS.HIGH_RPI_PERCENTILE) {
          return impPct <= OPPORTUNITY_THRESHOLDS.LOW_IMPRESSION_PERCENTILE ? '노출여력' : '이미포화'
        }

        return null
      }

      const buildRow = (product) => {
        const row = {
          키워드: keyword,
          카테고리: fallbackCategory,
          상품명: '',
          월매출: monthlyRevenue,
          ...keywordRaw,
          ...seoRaw,
          진도출처: '키워드소싱분석'
        }
        if (product) {
          if (product.categoryName) row.카테고리 = product.categoryName
          row.벤치마킹상품 = product.productName
          row.벤치마킹판매가 = product.price
          row.썸네일 = product.imgUrl
          row.벤치마킹썸네일 = product.imgUrl
          row.상품_calculatedRPI = product.calculatedRPI
          row.상품_ctr = product.ctr
          row.상품_cvr = product.cvr
          row.상품_impressions = keywordImpressionOf(product)
          row.상품_기회 = opportunityOf(product)
          row.상품_vendorItemId = product.vendorItemIdMissing ? '' : product.vendorItemId
        }
        return row
      }

      const rows = products.length > 0 ? products.map(p => buildRow(p)) : [buildRow(null)]

      if (!confirm(rows.length + '개 행을 소싱관리에 담으시겠습니까?')) return

      try {
        const { data } = await axios.post('/api/sourcing/board/save', rows)
        if (data && data.res === 'OK') {
          alert(rows.length + '개를 소싱관리에 담았습니다.')
        } else {
          alert((data && data.alert) || '소싱관리 담기에 실패했습니다.')
        }
      } catch (e) {
        console.error('소싱관리 담기 실패', e)
        alert('소싱관리 담기에 실패했습니다.')
      }
    },

    // 가격대별 분석 (popup.js의 updateBestPriceRange와 동일)
    analyzePriceRanges() {
      const validPriceProducts = this.analysisResults.filter(product =>
        product.price > 0 && product.salesLast28d > 0
      )

      if (validPriceProducts.length > 0) {
        const priceRangeData = new Map()

        validPriceProducts.forEach(product => {
          // popup.js와 동일: 1000원 단위로 그룹화
          const priceRange = Math.floor(product.price / 1000) * 1000
          const sales = product.salesLast28d * product.price
          priceRangeData.set(priceRange, (priceRangeData.get(priceRange) || 0) + sales)
        })

        const sortedRanges = Array.from(priceRangeData.entries())
          .sort((a, b) => b[1] - a[1])
          .slice(0, 3) // 상위 3개만 가져오기

        if (sortedRanges.length > 0) {
          this.bestPriceRanges = sortedRanges.map(range => ({
            priceRangeText: `${range[0].toLocaleString()}원~${(range[0] + 999).toLocaleString()}원`,
            salesAmount: range[1],
            startPrice: range[0] // 1688 검색용 시작 가격 저장
          }))

          // 기존 bestPriceRange도 유지 (1등만)
          this.bestPriceRange = this.bestPriceRanges[0].priceRangeText
        } else {
          this.bestPriceRanges = []
          this.bestPriceRange = '데이터 없음'
        }
      } else {
        this.bestPriceRanges = []
        this.bestPriceRange = '데이터 없음'
      }

      // console.log('가격대별 분석 완료:', this.bestPriceRanges)
    },

    // 카테고리 분석 (popup.js와 동일한 로직)
    analyzeCategoryOptimization() {
      const products = this.analysisResults
      // console.log('카테고리 분석 시작:', products.length, '개 상품')

      // 카테고리 정보 디버깅
      // const categoryDebug = products.map(p => ({
      //   productName: p.productName?.substring(0, 20) + '...',
      //   categoryName: p.categoryName
      // }))
      // console.log('상품별 카테고리:', categoryDebug)

      const categoryCount = new Map()
      const categoryKeywords = new Map()

      // 카테고리별 상품 수와 키워드 데이터 수집
      products.forEach(product => {
        const category = product.categoryName || '기타'

        // 카테고리별 상품 수 계산
        categoryCount.set(category, (categoryCount.get(category) || 0) + 1)

        // 카테고리별 키워드 데이터 수집
        if (product.trafficKeyword && product.trafficKeyword.length > 0) {
          if (!categoryKeywords.has(category)) {
            categoryKeywords.set(category, new Map())
          }

          const keywordMap = categoryKeywords.get(category)
          product.trafficKeyword.forEach(tk => {
            const keyword = tk.keywordDetail?.keyword || tk.keyword
            if (keyword) {
              if (!keywordMap.has(keyword)) {
                keywordMap.set(keyword, {
                  keyword: keyword,
                  impressions: 0,
                  clicks: 0,
                  products: new Set()
                })
              }

              const keywordData = keywordMap.get(keyword)
              keywordData.impressions += tk.keywordMetric?.impression || 0
              keywordData.clicks += tk.keywordMetric?.srpClick || 0
              keywordData.products.add(product.productId)
            }
          })
        }
      })

      // 카테고리별 총매출 계산을 위해 통합 키워드 분석 결과 활용
      const categoryRevenue = new Map()

      // 통합 키워드 분석에서 카테고리별 총매출 집계
      this.integratedKeywords.forEach(keyword => {
        if (keyword.mainCategory && keyword.totalGmv) {
          const currentRevenue = categoryRevenue.get(keyword.mainCategory) || 0
          categoryRevenue.set(keyword.mainCategory, currentRevenue + (keyword.totalGmv || 0))
        }
      })

      // 카테고리별 분석 데이터 생성 (총매출 기준)
      this.categoryAnalysis = Array.from(categoryKeywords.entries()).map(([category, keywordMap]) => {
        const keywords = Array.from(keywordMap.values())
        const totalImpressions = keywords.reduce((sum, kw) => sum + kw.impressions, 0)
        const totalClicks = keywords.reduce((sum, kw) => sum + kw.clicks, 0)
        const totalRevenue = categoryRevenue.get(category) || 0
        // Task 1: 총검색량(키워드별 노출수 합) 및 평균CTR 추가
        const totalSearchVolume = totalImpressions // 현재 노출수를 검색량으로 대체 사용
        const avgCtr = totalImpressions > 0 ? ((totalClicks / totalImpressions) * 100) : 0

        return {
          category,
          productCount: categoryCount.get(category) || 0,
          keywordCount: keywords.length,
          totalImpressions,
          totalClicks,
          totalRevenue, // 총매출 추가
          totalSearchVolume, // 총검색량 추가
          avgCtr, // 평균CTR 추가 (%)
          keywords: keywords.sort((a, b) => b.impressions - a.impressions)
        }
      }).sort((a, b) => b.totalRevenue - a.totalRevenue) // 총매출 기준으로 정렬

      // 최적화 카테고리 찾기 (가장 많은 총매출을 가진 카테고리)
      if (this.categoryAnalysis.length > 0) {
        const topCategory = this.categoryAnalysis[0]
        this.optimizedCategory = [topCategory.category, topCategory.productCount]
        this.optimizedCategoryInfo = {
          categoryName: topCategory.category,
          productCount: topCategory.productCount,
          keywordCount: topCategory.keywordCount,
          totalImpressions: topCategory.totalImpressions,
          totalClicks: topCategory.totalClicks,
          totalRevenue: topCategory.totalRevenue
        }
        // 기본값으로 최고 매출 카테고리 선택 (적용된 카테고리가 없을 때만)
        if (this.pendingCategoryNames.length === 0 && this.appliedCategoryNames.length === 0) {
          this.pendingCategoryNames = [topCategory.category]
          this.appliedCategoryNames = [topCategory.category]
        }
      } else {
        this.optimizedCategory = null
        this.optimizedCategoryInfo = null
      }

      // console.log('카테고리 분석 완료:', {
      //   optimizedCategory: this.optimizedCategory,
      //   optimizedCategoryInfo: this.optimizedCategoryInfo,
      //   categoryAnalysis: this.categoryAnalysis
      // })
    },

    // 통합 키워드 분석 (이상치포함 전체데이터 기준)
    analyzeIntegratedKeywords() {
      // console.log(this.analysisResults)
      const keywordMap = {}

      // 이상치포함 유효 상품들 사용 (미리 계산된 데이터)
      const validProducts = this.validProductsWithOutliers.filter(p =>
        p.impressions && p.clicks && p.salesLast28d && p.price &&
        p.calculatedCTR > 0 && p.calculatedCVR > 0 && parseFloat(p.overall) > 0 &&
        p.trafficKeyword && p.trafficKeyword.length > 0
      )

      // console.log('🔍 통합키워드 분석 대상 상품:', {
      //   totalProducts: this.analysisResults.length,
      //   validProducts: validProducts.length,
      //   sampleValidProduct: validProducts[0] ? {
      //     productName: validProducts[0].productName,
      //     salesLast28d: validProducts[0].salesLast28d,
      //     price: validProducts[0].price,
      //     trafficKeywordCount: validProducts[0].trafficKeyword?.length || 0
      //   } : null
      // })

      validProducts.forEach(product => {
          product.trafficKeyword.forEach(keyword => {
            const key = keyword.keywordDetail?.keyword || keyword.keyword
            if (!keywordMap[key]) {
              keywordMap[key] = {
                keyword: key,
                totalVolume: 0,
                totalClicks: 0,
                totalGmv: 0,
                productCount: 0,
                products: [],
                // 자연/광고 노출 실측 합 (keywordMetric.organicImpression / adImpression)
                measuredAdImpressions: 0,
                measuredOrganicImpressions: 0,
                impressionSplitMissing: false,
                impressionSplitMissingProducts: 0
              }
            }

            keywordMap[key].totalVolume += keyword.keywordMetric?.impression || 0  // impression 사용

            // 자연/광고 노출 = 실측만. 한 항목이라도 실측이 없으면 이 키워드는 비운다(추정으로 채우지 않는다)
            const adImp = keyword.keywordMetric?.adImpression
            const orgImp = keyword.keywordMetric?.organicImpression
            if (typeof adImp === 'number' && typeof orgImp === 'number') {
              keywordMap[key].measuredAdImpressions += adImp
              keywordMap[key].measuredOrganicImpressions += orgImp
            } else {
              keywordMap[key].impressionSplitMissing = true
              keywordMap[key].impressionSplitMissingProducts += 1
            }
            keywordMap[key].totalClicks += keyword.keywordMetric?.srpClick || 0

            // 키워드별 매출 계산 (간단한 방법)
            const keywordClicks = keyword.keywordMetric?.srpClick || 0
            const productCVR = parseFloat(product.cvr) || 0  // 상품 CVR 고정
            const productPrice = parseInt(product.price) || 0

            // 키워드별 판매량 = 키워드 클릭수 × 상품 CVR / 100
            const keywordSales = keywordClicks * (productCVR / 100)

            // 키워드별 매출 = 키워드별 판매량 × 상품 가격
            const keywordRevenue = keywordSales * productPrice

            // 기존 총 매출 (전체 상품 매출을 키워드에 할당)
            const productSales = parseInt(product.salesLast28d) || 0
            const productRevenue = productSales * productPrice
            keywordMap[key].totalGmv += productRevenue

            // 새로운 키워드별 매출 데이터 추가
            if (!keywordMap[key].keywordSpecificData) {
              keywordMap[key].keywordSpecificData = {
                totalKeywordRevenue: 0,
                keywordRevenues: [],
                keywordSales: []
              }
            }

            keywordMap[key].keywordSpecificData.totalKeywordRevenue += keywordRevenue
            keywordMap[key].keywordSpecificData.keywordRevenues.push(keywordRevenue)
            keywordMap[key].keywordSpecificData.keywordSales.push(keywordSales)

            keywordMap[key].productCount++
            keywordMap[key].products.push(product.productName)
          })
      })

      // 키워드별 상세 지표 계산 및 정렬
      this.integratedKeywords = Object.values(keywordMap)
        .map(keyword => {
          // 해당 키워드와 관련된 유효한 상품들의 데이터 수집
          const relatedProducts = validProducts.filter(product =>
            product.trafficKeyword?.some(tk =>
              (tk.keywordDetail?.keyword || tk.keyword) === keyword.keyword
            )
          )

          // 평균 CVR 계산
          const avgCvr = relatedProducts.length > 0 ?
            (relatedProducts.reduce((sum, p) => sum + (parseFloat(p.cvr) || 0), 0) / relatedProducts.length).toFixed(2) : '0.00'

          // CTR/CVR 데이터 수집
          const ctrs = relatedProducts.map(p => parseFloat(p.ctr) || 0).filter(c => c > 0)
          const cvrs = relatedProducts.map(p => parseFloat(p.cvr) || 0).filter(c => c > 0)

          // 주요 카테고리 계산 (가장 많은 상품이 속한 카테고리)
          const categoryCount = {}
          relatedProducts.forEach(product => {
            const category = product.categoryName || '기타'
            categoryCount[category] = (categoryCount[category] || 0) + 1
          })
          const mainCategory = Object.keys(categoryCount).length > 0 ?
            Object.keys(categoryCount).reduce((a, b) => categoryCount[a] > categoryCount[b] ? a : b) : null

          // 카테고리 적합도 계산 (주요 카테고리 비중 %)
          const categoryFitness = mainCategory && relatedProducts.length > 0 ?
            ((categoryCount[mainCategory] / relatedProducts.length) * 100).toFixed(1) + '%' : '0%'

          // RPI 지표 계산 (최대, 평균, 중간, 최소)
          const rpis = relatedProducts.map(p => parseFloat(p.overall) || 0).filter(r => r > 0)
          const maxRpi = rpis.length > 0 ? Math.max(...rpis).toFixed(2) : '0.00'
          // 평균 RPI = 이 키워드 클릭수 가중 평균 (도움말 정의와 같게 — 예전엔 단순 평균이었다)
          const keywordClicksOf = (p) => p.trafficKeyword?.find(tk =>
            (tk.keywordDetail?.keyword || tk.keyword) === keyword.keyword
          )?.keywordMetric?.srpClick || 0
          const avgRpi = this.weightedAvgRpi(
            relatedProducts.map(p => ({ rpi: parseFloat(p.overall) || 0, clicks: keywordClicksOf(p) }))
          ).toFixed(2)
          const medianRpi = rpis.length > 0 ? this.calculateMedian(rpis).toFixed(2) : '0.00'
          const minRpi = rpis.length > 0 ? Math.min(...rpis).toFixed(2) : '0.00'

          // 노출수 지표 계산 (최대, 평균, 중간, 최소)
          // RPI = 노출 1회당 기대매출이므로 총매출 ≈ RPI × 노출수 다.
          // RPI 는 높은데 노출수가 낮은 상품 = 노출만 더 주면 팔릴 자리 → 그 판정에 쓰는 분포다.
          // ⚠ product.calculatedImpressions 를 쓰면 안 된다. 그건 calculateImpressionsFromRawData()
          //    가 그 상품이 노출된 '모든 키워드'의 impression 을 합친 값이라, 여러 키워드로 뜨는
          //    상품이 무조건 상위로 잡힌다. 「이 키워드 1페이지 안에서의 위치」를 재는 데 쓰면 틀린다.
          //    반드시 이 키워드에 해당하는 trafficKeyword 항목의 keywordMetric.impression 을 쓴다.
          const keywordImpressions = relatedProducts
            .map(p => {
              const tk = p.trafficKeyword?.find(t =>
                (t.keywordDetail?.keyword || t.keyword) === keyword.keyword
              )
              return tk?.keywordMetric?.impression || 0
            })
            .filter(v => v > 0)
          // 표본이 없으면 0 이 아니라 null — 「세어보지 않은 것」과 「0 이었다」는 다른 값이고,
          // 뒷단이 0 을 진짜 0 으로 읽으면 잘못 판정한다.
          const maxImpressions = keywordImpressions.length > 0 ? Math.max(...keywordImpressions) : null
          const avgImpressions = keywordImpressions.length > 0 ?
            Math.round(keywordImpressions.reduce((sum, v) => sum + v, 0) / keywordImpressions.length) : null
          const medianImpressions = keywordImpressions.length > 0 ?
            Math.round(this.calculateMedian(keywordImpressions)) : null
          const minImpressions = keywordImpressions.length > 0 ? Math.min(...keywordImpressions) : null

          // 상위랭킹 RPI 계산 (키워드 노출수 기준 상위 10개 상품)
          const topRankProducts = relatedProducts
            .map(product => {
              const keywordTraffic = product.trafficKeyword?.find(tk =>
                (tk.keywordDetail?.keyword || tk.keyword) === keyword.keyword
              )
              const impressions = keywordTraffic?.keywordMetric?.impression || 0
              const rpi = parseFloat(product.overall) || 0
              return { product, impressions, rpi }
            })
            .filter(item => item.impressions > 0 && item.rpi > 0)
            .sort((a, b) => b.impressions - a.impressions) // 노출수 기준 내림차순 정렬
            .slice(0, 10) // 상위 10개만 선택

          const topRankRpis = topRankProducts.map(item => item.rpi)
          const topRankMaxRpi = topRankRpis.length > 0 ? Math.max(...topRankRpis).toFixed(2) : '0.00'
          const topRankAvgRpi = this.weightedAvgRpi(
            topRankProducts.map(item => ({ rpi: item.rpi, clicks: keywordClicksOf(item.product) }))
          ).toFixed(2)
          const topRankMedianRpi = topRankRpis.length > 0 ? this.calculateMedian(topRankRpis).toFixed(2) : '0.00'
          const topRankMinRpi = topRankRpis.length > 0 ? Math.min(...topRankRpis).toFixed(2) : '0.00'

          // CTR 지표 계산 (최대, 평균, 중간, 최소) - 기존 ctrs 변수 재사용
          const maxCtr = ctrs.length > 0 ? Math.max(...ctrs).toFixed(2) : '0.00'
          const avgCtr = keyword.totalVolume > 0 ? ((keyword.totalClicks / keyword.totalVolume) * 100).toFixed(2) : '0.00'
          const medianCtr = ctrs.length > 0 ? this.calculateMedian(ctrs).toFixed(2) : '0.00'
          const minCtr = ctrs.length > 0 ? Math.min(...ctrs).toFixed(2) : '0.00'

          // CVR 지표 계산 (최대, 평균, 중간, 최소) - 기존 cvrs 변수 재사용
          const maxCvr = cvrs.length > 0 ? Math.max(...cvrs).toFixed(2) : '0.00'
          const medianCvr = cvrs.length > 0 ? this.calculateMedian(cvrs).toFixed(2) : '0.00'
          const minCvr = cvrs.length > 0 ? Math.min(...cvrs).toFixed(2) : '0.00'

          // 해당 키워드 노출수 기준 상위 20개 상품 선정
          const top20ProductsByImpressions = relatedProducts
            .map(product => {
              const keywordTraffic = product.trafficKeyword?.find(tk =>
                (tk.keywordDetail?.keyword || tk.keyword) === keyword.keyword
              )
              const impressions = keywordTraffic?.keywordMetric?.impression || 0
              const rpi = parseFloat(product.overall) || 0

              return { product, impressions, rpi }
            })
            .filter(item => item.impressions > 0 && item.rpi > 0)
            .sort((a, b) => b.impressions - a.impressions) // 노출수 기준 내림차순 정렬
            .slice(0, 20) // 상위 20개만 선택

          // 해당 키워드의 총 노출수와 상위 20개 상품의 키워드 노출수 합계
          const keywordTotalImpressions = keyword.totalVolume || 0
          const top20TotalImpressions = top20ProductsByImpressions.reduce((sum, item) => sum + item.impressions, 0)

          // 디버깅: 노출수 데이터 확인
          if (keyword.keyword === '무선이어폰' || keywordTotalImpressions > 100000) {
            console.log(`🔍 [매출계산 디버깅] 키워드: ${keyword.keyword}`)
            console.log(`📊 키워드 총 노출수: ${keywordTotalImpressions.toLocaleString()}`)
            console.log(`📊 상위 20개 노출수 합계: ${top20TotalImpressions.toLocaleString()}`)
            console.log(`📊 비율: ${top20TotalImpressions > 0 ? ((top20TotalImpressions / keywordTotalImpressions) * 100).toFixed(1) : 0}%`)
            console.log(`📊 상위 3개 상품:`, top20ProductsByImpressions.slice(0, 3).map(item => ({
              productName: item.product.productName?.substring(0, 20),
              impressions: item.impressions,
              rpi: item.rpi
            })))
          }

          // 상위 20개 상품의 예상 매출 계산 (키워드 총 노출수 비율 분배 × RPI)
          const top20ProjectedSalesAmounts = top20ProductsByImpressions.map(item => {
            if (top20TotalImpressions === 0) return 0

            // 해당 상품의 키워드 노출수 비율
            const impressionRatio = item.impressions / top20TotalImpressions

            // 키워드 총 노출수를 비율별로 분배한 예상 노출수
            const projectedImpressions = keywordTotalImpressions * impressionRatio

            // 예상 매출 = 예상 노출수 × RPI
            const projectedSalesAmount = projectedImpressions * item.rpi

            // 디버깅: 첫 번째 상품의 계산 과정
            if (item === top20ProductsByImpressions[0] && (keyword.keyword === '무선이어폰' || keywordTotalImpressions > 100000)) {
              console.log(`💰 [1위 상품 계산] ${item.product.productName?.substring(0, 20)}`)
              console.log(`   노출수: ${item.impressions.toLocaleString()} (비율: ${(impressionRatio * 100).toFixed(1)}%)`)
              console.log(`   예상 노출수: ${projectedImpressions.toLocaleString()}`)
              console.log(`   RPI: ${item.rpi}`)
              console.log(`   예상 매출: ${projectedSalesAmount.toLocaleString()}원`)
            }

            return projectedSalesAmount
          }).filter(amount => amount > 0)

          // 기존 매출 계산 (총 판매량용)
          const salesCounts = relatedProducts.map(p => parseInt(p.salesLast28d) || 0).filter(s => s > 0)

          // 최대매출: 키워드 총 노출수 비율 분배 × RPI 기준 상위 20개 상품 중 최대값
          const maxSalesAmount = top20ProjectedSalesAmounts.length > 0 ? Math.max(...top20ProjectedSalesAmounts) : 0
          // 평균매출: 키워드 총 노출수 비율 분배 × RPI 기준 상위 20개 상품
          const avgSalesAmount = top20ProjectedSalesAmounts.length > 0 ?
            Math.round(top20ProjectedSalesAmounts.reduce((sum, s) => sum + s, 0) / top20ProjectedSalesAmounts.length) : 0
          // 중간매출: 키워드 총 노출수 비율 분배 × RPI 기준 상위 20개 상품
          const medianSalesAmount = top20ProjectedSalesAmounts.length > 0 ?
            Math.round(this.calculateMedian(top20ProjectedSalesAmounts)) : 0
          const totalSalesCount = salesCounts.length > 0 ? salesCounts.reduce((sum, s) => sum + s, 0) : 0

          // 디버깅: 최종 매출 결과
          if (keyword.keyword === '무선이어폰' || keywordTotalImpressions > 100000) {
            console.log(`💰 [최종 매출 결과] 키워드: ${keyword.keyword}`)
            console.log(`   최대매출: ${Math.round(maxSalesAmount).toLocaleString()}원`)
            console.log(`   평균매출: ${avgSalesAmount.toLocaleString()}원`)
            console.log(`   중간매출: ${medianSalesAmount.toLocaleString()}원`)
            console.log(`   예상매출 배열 길이: ${top20ProjectedSalesAmounts.length}`)
            console.log(`   예상매출 상위 3개: ${top20ProjectedSalesAmounts.slice(0, 3).map(s => Math.round(s).toLocaleString()).join(', ')}`)
            console.log(`---`)
          }

          // 매출점유율 계산 (해당 키워드 매출 / 해당 키워드에 노출된 전체 상품 매출 * 100)
          // 해당 키워드에 노출된 상품들의 전체 매출 계산
          const keywordExposedProductsRevenue = relatedProducts.reduce((sum, product) => {
            const productSales = parseInt(product.salesLast28d) || 0
            const productPrice = parseInt(product.price) || 0
            return sum + (productSales * productPrice)
          }, 0)

          // 매출점유율은 항상 단일키워드 기준으로 계산 (실제 키워드 기여 매출)
          // keywordSpecificData가 없는 경우 직접 계산
          let keywordRevenue = keyword.keywordSpecificData?.totalKeywordRevenue || 0

          if (keywordRevenue === 0) {
            // 키워드별 실제 기여 매출 직접 계산
            keywordRevenue = relatedProducts.reduce((sum, product) => {
              const keywordTraffic = product.trafficKeyword?.find(tk =>
                (tk.keywordDetail?.keyword || tk.keyword) === keyword.keyword
              )
              if (keywordTraffic && keywordTraffic.keywordMetric) {
                const clicks = keywordTraffic.keywordMetric.click || 0
                const cvr = parseFloat(product.cvr) || 0
                const price = parseInt(product.price) || 0
                const keywordSales = Math.round(clicks * (cvr / 100))
                return sum + (keywordSales * price)
              }
              return sum
            }, 0)
          }

          // 디버깅: 매출점유율 계산 값들 확인
          // console.log(`[매출점유율 디버깅] 키워드: ${keyword.keyword}`)
          // console.log(`[매출점유율 디버깅] keywordRevenue:`, keywordRevenue)
          // console.log(`[매출점유율 디버깅] keywordExposedProductsRevenue:`, keywordExposedProductsRevenue)

          const revenueShare = keywordExposedProductsRevenue > 0 ?
            ((keywordRevenue / keywordExposedProductsRevenue) * 100).toFixed(2) : '0.00'

          // console.log(`[매출점유율 디버깅] 계산식: (${keywordRevenue} / ${keywordExposedProductsRevenue}) * 100 = ${revenueShare}%`)
          // console.log(`[매출점유율 디버깅] 최종 revenueShare:`, revenueShare)

          // 일매출 가이드라인 계산: 해당 키워드에 노출된 전체 상품 중 매출 기준 상위 20개 사용
          const allProductsWithSales = relatedProducts.map(product => {
            const productSales = parseInt(product.salesLast28d) || 0
            const productPrice = parseInt(product.price) || 0
            const salesAmount = productSales * productPrice
            return { product, salesAmount }
          }).filter(item => item.salesAmount > 0)

          // 매출 기준으로 정렬하여 상위 20개 선정
          const top20BySales = allProductsWithSales
            .sort((a, b) => b.salesAmount - a.salesAmount)
            .slice(0, 20)

          const top20RealSalesAmounts = top20BySales.map(item => item.salesAmount)

          // 매출점유율기준 중간매출 계산 (접근성 점수용 - 기존 로직 유지)
          const topRankMedianSalesAmount = top20ProjectedSalesAmounts.length > 0 ?
            this.calculateMedian(top20ProjectedSalesAmounts) : 0

          // 상위 2위와 10위 기준 일매출 가이드라인 계산 (실제 매출 기준)
          const sortedTop20RealSalesAmounts = [...top20RealSalesAmounts].sort((a, b) => b - a) // 실제 매출액 기준 내림차순 정렬
          // console.log(sortedTop20RealSalesAmounts)
          // 상품이 1개만 있는 경우 또는 값이 하나만 있는 경우 처리
          let topRank2ndSalesAmount, topRank10thSalesAmount

          if (sortedTop20RealSalesAmounts.length === 1) {
            // 1개 상품만 있는 경우: 동일한 값 사용
            topRank2ndSalesAmount = sortedTop20RealSalesAmounts[0]
            topRank10thSalesAmount = sortedTop20RealSalesAmounts[0]
          } else if (sortedTop20RealSalesAmounts.length >= 2 && sortedTop20RealSalesAmounts.length < 10) {
            // 2-9개 상품이 있는 경우: 2위값과 마지막값 사용
            topRank2ndSalesAmount = sortedTop20RealSalesAmounts[1]
            topRank10thSalesAmount = sortedTop20RealSalesAmounts[sortedTop20RealSalesAmounts.length - 1]
          } else if (sortedTop20RealSalesAmounts.length >= 10) {
            // 10개 이상 상품이 있는 경우: 2위값과 10위값 사용
            topRank2ndSalesAmount = sortedTop20RealSalesAmounts[1]
            topRank10thSalesAmount = sortedTop20RealSalesAmounts[9]
          } else {
            // 상품이 없는 경우
            topRank2ndSalesAmount = 0
            topRank10thSalesAmount = 0
          }

          // 단순화: 실제 매출을 30으로 나누어 일매출 계산
          const revenueShareBased2ndRevenue = Math.round(topRank2ndSalesAmount / 30)
          const revenueShareBased10thRevenue = Math.round(topRank10thSalesAmount / 30)

          // 범위 형태로 표시 (큰 값 ~ 작은 값)
          // 값이 같거나 하나만 있는 경우 "값~값" 형태로 표시
          const revenueShareBasedRevenueRange = revenueShareBased10thRevenue === revenueShareBased2ndRevenue ?
            `${this.formatNumber(revenueShareBased2ndRevenue)}원~${this.formatNumber(revenueShareBased2ndRevenue)}원` :
            `${this.formatNumber(revenueShareBased2ndRevenue)}원~${this.formatNumber(revenueShareBased10thRevenue)}원`

          // 기존 호환성을 위해 중간값도 유지 (접근성 점수 계산용)
          const revenueShareBasedMedianRevenue = (() => {
            const topRankMedian = topRankMedianSalesAmount || 0;
            const revenueShareNum = parseFloat(revenueShare) || 0;
            if (revenueShareNum === 0) return 0;
            const result = Math.round((topRankMedian / 30) / (revenueShareNum / 100));
            return isNaN(result) ? 0 : result;
          })()

          // 디버깅: 일매출 가이드라인 계산 과정
          if (keyword.keyword === '무선이어폰' || keywordTotalImpressions > 100000) {
            console.log(`📊 [일매출 가이드라인] 키워드: ${keyword.keyword}`)
            console.log(`   전체 상품 수: ${relatedProducts.length}`)
            console.log(`   매출 있는 상품 수: ${allProductsWithSales.length}`)
            console.log(`   매출 기준 상위 20개 매출: ${sortedTop20RealSalesAmounts.slice(0, 10).map(s => Math.round(s).toLocaleString()).join(', ')}`)
            console.log(`   매출 2위: ${Math.round(topRank2ndSalesAmount).toLocaleString()}원 (월매출)`)
            console.log(`   매출 10위: ${Math.round(topRank10thSalesAmount).toLocaleString()}원 (월매출)`)
            console.log(`   2위 기준 일매출: ${revenueShareBased2ndRevenue.toLocaleString()}원`)
            console.log(`   10위 기준 일매출: ${revenueShareBased10thRevenue.toLocaleString()}원`)
            console.log(`   일매출 가이드라인: ${revenueShareBasedRevenueRange}`)
          }

          // 가격대 25%~75% 계산 (키워드 노출수 기준 상위 20개 상품 기준)
          const prices = top20ProductsByImpressions.map(item => parseInt(item.product.price) || 0).filter(price => price > 0).sort((a, b) => a - b)
          let priceRange25to75 = '0원 ~ 0원'

          if (prices.length > 0) {
            const q1Index = Math.floor(prices.length * 0.25)
            const q3Index = Math.floor(prices.length * 0.75)
            const price25 = prices[q1Index] || prices[0]
            const price75 = prices[q3Index] || prices[prices.length - 1]
            priceRange25to75 = `${price25.toLocaleString()}원 ~ ${price75.toLocaleString()}원`
          }

          // 접근성점수 계산 (매출점유율기준 중간일매출 / 상위랭킹 평균RPI)
          const accessibilityScore = (() => {
            const avgRpi = parseFloat(topRankAvgRpi);
            if (!avgRpi || avgRpi <= 0 || isNaN(avgRpi)) return 0;
            if (!revenueShareBasedMedianRevenue || isNaN(revenueShareBasedMedianRevenue)) return 0;
            const result = Math.round(revenueShareBasedMedianRevenue / avgRpi);
            return isNaN(result) ? 0 : result;
          })()

          // console.log(`[접근성점수 디버깅] 키워드: ${keyword.keyword}`)
          // console.log(`[접근성점수 디버깅] revenueShareBasedMedianRevenue:`, revenueShareBasedMedianRevenue)
          // console.log(`[접근성점수 디버깅] topRankAvgRpi:`, topRankAvgRpi)
          // console.log(`[접근성점수 디버깅] 계산식: ${revenueShareBasedMedianRevenue} / ${topRankAvgRpi} = ${accessibilityScore}`)

          // 키워드별 매출 지표 계산
          let singleKeywordTotalRevenue = 0
          let singleKeywordAvgRevenue = 0
          let singleKeywordMedianRevenue = 0
          let singleKeywordMaxRevenue = 0
          let singleKeywordTotalSales = 0

          // 단일키워드 RPI, CTR, CVR 지표 계산
          let singleKeywordMaxRpi = 0, singleKeywordAvgRpi = 0, singleKeywordMedianRpi = 0, singleKeywordMinRpi = 0
          let singleKeywordMaxCtr = 0, singleKeywordAvgCtr = 0, singleKeywordMedianCtr = 0, singleKeywordMinCtr = 0
          let singleKeywordMaxCvr = 0, singleKeywordAvgCvr = 0, singleKeywordMedianCvr = 0, singleKeywordMinCvr = 0

          if (keyword.keywordSpecificData) {
            singleKeywordTotalRevenue = Math.round(keyword.keywordSpecificData.totalKeywordRevenue)
            singleKeywordTotalSales = Math.round(keyword.keywordSpecificData.keywordSales.reduce((sum, s) => sum + s, 0))

            // 단일키워드도 동일한 로직 적용: 키워드 총 노출수 비율 분배 × RPI
            singleKeywordMaxRevenue = top20ProjectedSalesAmounts.length > 0 ? Math.max(...top20ProjectedSalesAmounts) : 0
            singleKeywordAvgRevenue = top20ProjectedSalesAmounts.length > 0 ?
              Math.round(top20ProjectedSalesAmounts.reduce((sum, s) => sum + s, 0) / top20ProjectedSalesAmounts.length) : 0
            singleKeywordMedianRevenue = top20ProjectedSalesAmounts.length > 0 ?
              Math.round(this.calculateMedian(top20ProjectedSalesAmounts)) : 0

            // 단일키워드 기반 RPI, CTR, CVR 계산 (키워드별 기여도 기반)
            // 실제로는 키워드별 클릭수와 기여 매출을 기반으로 계산해야 하지만
            // 현재는 상품별 지표의 가중평균으로 근사치 계산
            const keywordBasedRpis = relatedProducts.map(p => {
              return { rpi: parseFloat(p.overall) || 0, clicks: keywordClicksOf(p) }
            }).filter(item => item.rpi > 0 && item.clicks > 0)

            if (keywordBasedRpis.length > 0) {
              const rpiValues = keywordBasedRpis.map(item => item.rpi)
              singleKeywordMaxRpi = Math.max(...rpiValues).toFixed(2)
              singleKeywordMinRpi = Math.min(...rpiValues).toFixed(2)

              // 클릭수 가중평균 (정본 weightedAvgRpi)
              singleKeywordAvgRpi = this.weightedAvgRpi(keywordBasedRpis).toFixed(2)
              singleKeywordMedianRpi = this.calculateMedian(rpiValues).toFixed(2)
            }

            // CTR, CVR도 동일한 방식으로 계산 (간소화)
            singleKeywordMaxCtr = maxCtr
            singleKeywordAvgCtr = avgCtr
            singleKeywordMedianCtr = medianCtr
            singleKeywordMinCtr = minCtr

            singleKeywordMaxCvr = maxCvr
            singleKeywordAvgCvr = avgCvr
            singleKeywordMedianCvr = medianCvr
            singleKeywordMinCvr = minCvr
          }

          // 광고/자연 노출수 = keywordMetric 실측 합. 실측이 빠진 키워드는 null(화면 «-») — 예전 «총노출×0.3» 고정값 추정은 없앴다
          const adImpressions = keyword.impressionSplitMissing ? null : keyword.measuredAdImpressions
          const organicImpressions = keyword.impressionSplitMissing ? null : keyword.measuredOrganicImpressions
          if (keyword.impressionSplitMissing) {
            // 같은 키워드로 반복 출력하지 않는다 (비반응 인스턴스 필드)
            if (!this._impressionSplitWarned) this._impressionSplitWarned = new Set()
            if (!this._impressionSplitWarned.has(keyword.keyword)) {
              this._impressionSplitWarned.add(keyword.keyword)
              console.warn(`[키워드표] 자연/광고 노출 실측 없음 → «-» : ${keyword.keyword} (실측 빠진 상품 ${keyword.impressionSplitMissingProducts}개)`)
            }
          }

          return {
            ...keyword,
            // 기본 지표
            avgCtr,
            avgCvr,
            mainCategory,
            categoryFitness,
            // 통합키워드 매출 지표
            totalSalesCount,
            totalGmv: keyword.totalGmv || 0,
            maxSalesAmount,
            avgSalesAmount,
            medianSalesAmount,
            revenueShare,
            revenueShareBasedMedianRevenue,
            revenueShareBasedRevenueRange,
            priceRange25to75,
            accessibilityScore,
            // 단일키워드 매출 지표
            singleKeywordTotalSales,
            singleKeywordTotalRevenue,
            singleKeywordMaxRevenue,
            singleKeywordAvgRevenue,
            singleKeywordMedianRevenue,
            // 단일키워드 RPI 지표
            singleKeywordMaxRpi,
            singleKeywordAvgRpi,
            singleKeywordMedianRpi,
            singleKeywordMinRpi,
            // 단일키워드 CTR 지표
            singleKeywordMaxCtr,
            singleKeywordAvgCtr,
            singleKeywordMedianCtr,
            singleKeywordMinCtr,
            // 단일키워드 CVR 지표
            singleKeywordMaxCvr,
            singleKeywordAvgCvr,
            singleKeywordMedianCvr,
            singleKeywordMinCvr,
            // RPI 지표
            maxRpi,
            avgRpi,
            medianRpi,
            minRpi,
            // 노출수 지표 (이 키워드에 해당하는 노출수 기준. calculatedImpressions 아님 — 위 주석 참고)
            maxImpressions,
            avgImpressions,
            medianImpressions,
            minImpressions,
            // 상위랭킹 RPI 지표
            topRankMaxRpi,
            topRankAvgRpi,
            topRankMedianRpi,
            topRankMinRpi,
            // CTR 지표
            maxCtr,
            medianCtr,
            minCtr,
            // CVR 지표
            maxCvr,
            medianCvr,
            minCvr,
            // 노출 지표
            adImpressions,
            organicImpressions
          }
        })
        .sort((a, b) => b.totalVolume - a.totalVolume)
        .slice(0, 40) // 상위 40개까지 표시

      // console.log('통합 키워드 분석 완료:', this.integratedKeywords.length, '개')
      // console.log('🔍 integratedKeywords 데이터:', this.integratedKeywords.slice(0, 3))

      // 통합 분석 데이터 계산
      this.calculateSummaryData()
    },

    // 통합 키워드 분석 데이터 계산
    calculateSummaryData() {
      if (this.analysisResults.length === 0) {
        this.summaryData = []
        return
      }

      // 기본 데이터 수집 (overall = 정본 RPI 가 붙은 enrichedAnalysisResults 기준)
      const summarySource = (this.enrichedAnalysisResults && this.enrichedAnalysisResults.length) ? this.enrichedAnalysisResults : this.analysisResults
      const validProducts = summarySource.filter(p =>
        // 빠른수집 행은 판매가가 price 칸에만 있다(salePrice 없음) — salePrice 만 보면 빠른수집 요약이 늘 0
        p.impressions && p.clicks && p.salesLast28d && (p.salePrice || p.price) &&
        parseFloat(p.ctr) > 0 && parseFloat(p.cvr) > 0 && parseFloat(p.overall) > 0
      )

      // 총합 계산
      const totalImpressions = validProducts.reduce((sum, p) => sum + (parseInt(p.impressions) || 0), 0)
      const totalClicks = validProducts.reduce((sum, p) => sum + (parseInt(p.clicks) || 0), 0)

      // 실제 매출액 계산 (판매개수 × 판매가격)
      const totalSalesAmount = validProducts.reduce((sum, p) => {
        const salesCount = parseInt(p.salesLast28d) || 0
        const salePrice = parseInt(p.price || p.salePrice) || 0  // 원본 필드명 사용
        return sum + (salesCount * salePrice)
      }, 0)

      // 총 판매개수
      const totalSalesCount = validProducts.reduce((sum, p) => sum + (parseInt(p.salesLast28d) || 0), 0)

      // 디버깅: 계산 과정 로그
      // console.log('🔍 매출 계산 디버깅:', {
      //   validProductsCount: validProducts.length,
      //   totalSalesAmount: totalSalesAmount,
      //   totalSalesCount: totalSalesCount,
      //   sampleProducts: validProducts.slice(0, 3).map(p => ({
      //     productName: p.productName,
      //     salesLast28d: p.salesLast28d,
      //     price: p.price,  // 원본 필드명 사용
      //     calculatedRevenue: (parseInt(p.salesLast28d) || 0) * (parseInt(p.price) || 0)
      //   }))
      // })

      // 평균 계산
      const avgSalesAmount = validProducts.length > 0 ? totalSalesAmount / validProducts.length : 0
      const avgSalesCount = validProducts.length > 0 ? totalSalesCount / validProducts.length : 0

      // console.log('📊 평균 계산 결과:', {
      //   avgSalesAmount: avgSalesAmount,
      //   avgSalesCount: avgSalesCount
      // })
      const avgCTR = validProducts.length > 0 ?
        validProducts.reduce((sum, p) => sum + parseFloat(p.ctr), 0) / validProducts.length : 0
      const avgCVR = validProducts.length > 0 ?
        validProducts.reduce((sum, p) => sum + parseFloat(p.cvr), 0) / validProducts.length : 0
      // 평균 RPI = 클릭수 가중
      const avgRPI = this.weightedAvgRpi(
        validProducts.map(p => ({ rpi: parseFloat(p.overall) || 0, clicks: parseInt(p.clicks) || 0 }))
      )

      // 중간값 계산
      // 개별 상품의 매출액 배열 생성 (판매개수 × 판매가격)
      const salesAmountArray = validProducts.map(p => {
        const salesCount = parseInt(p.salesLast28d) || 0
        const salePrice = parseInt(p.salePrice || p.price) || 0
        return salesCount * salePrice
      }).sort((a, b) => a - b)

      const salesCountArray = validProducts.map(p => parseInt(p.salesLast28d) || 0).sort((a, b) => a - b)
      const sortedCTR = validProducts.map(p => parseFloat(p.ctr) || 0).sort((a, b) => a - b)
      const sortedCVR = validProducts.map(p => parseFloat(p.cvr) || 0).sort((a, b) => a - b)
      const sortedRPI = validProducts.map(p => parseFloat(p.overall) || 0).sort((a, b) => a - b)

      const getMedian = (arr) => {
        const mid = Math.floor(arr.length / 2)
        return arr.length % 2 !== 0 ? arr[mid] : (arr[mid - 1] + arr[mid]) / 2
      }

      const medianSalesAmount = salesAmountArray.length > 0 ? getMedian(salesAmountArray) : 0
      const medianSalesCount = salesCountArray.length > 0 ? getMedian(salesCountArray) : 0

      // console.log('📈 중간값 계산 디버깅:', {
      //   salesAmountArrayLength: salesAmountArray.length,
      //   salesAmountArraySample: salesAmountArray.slice(0, 10),
      //   medianSalesAmount: medianSalesAmount,
      //   medianSalesCount: medianSalesCount,
      //   salesAmountArraySum: salesAmountArray.reduce((sum, val) => sum + val, 0)
      // })
      const medianCTR = sortedCTR.length > 0 ? getMedian(sortedCTR) : 0
      const medianCVR = sortedCVR.length > 0 ? getMedian(sortedCVR) : 0
      const medianRPI = sortedRPI.length > 0 ? getMedian(sortedRPI) : 0

      // 통합 분석 데이터 구성
      this.summaryData = [
        { metric: '총 노출수', value: totalImpressions.toLocaleString() },
        { metric: '총 클릭수', value: totalClicks.toLocaleString() },
        { metric: '총 판매개수', value: `${totalSalesCount.toLocaleString()}개` },
        { metric: '총 매출액', value: `${totalSalesAmount.toLocaleString()}원` },
        { metric: '평균 매출액', value: `${Math.round(avgSalesAmount).toLocaleString()}원` },
        { metric: '중간 매출액', value: `${Math.round(medianSalesAmount).toLocaleString()}원` },
        { metric: '평균 판매개수', value: `${Math.round(avgSalesCount).toLocaleString()}개` },
        { metric: '중간 판매개수', value: `${Math.round(medianSalesCount).toLocaleString()}개` },
        { metric: '평균 CTR', value: `${avgCTR.toFixed(2)}%` },
        { metric: '평균 CVR', value: `${avgCVR.toFixed(2)}%` },
        { metric: '평균 RPI', value: avgRPI.toFixed(2) },
        { metric: '중간 CTR', value: `${medianCTR.toFixed(2)}%` },
        { metric: '중간 CVR', value: `${medianCVR.toFixed(2)}%` },
        { metric: '중간 RPI', value: medianRPI.toFixed(2) }
      ]

      // console.log('통합 분석 데이터 계산 완료:', this.summaryData)
      // console.log('🔍 summaryData 상세:', this.summaryData.map(item => ({
      //   metric: item.metric,
      //   value: item.value,
      //   rawValue: item.metric.includes('매출액') ?
      //     (item.metric === '총 매출액' ? totalSalesAmount :
      //      item.metric === '평균 매출액' ? avgSalesAmount :
      //      item.metric === '중간 매출액' ? medianSalesAmount : 'N/A') : 'N/A'
      // })))
    },

    // 차트 생성 (growthSalesLedger.vue 방식으로 ApexChart 컴포넌트 사용)
    createCharts() {
      this.createImpressionChart()
      this.createPriceVsCvrChart()
      this.createRpiCompetitivenessChart()
      this.createAdCompetitivenessChart()
      this.createPriceRangeSalesChart()
      this.createDailyTrafficChart()
    },

    // 노출 비율 원형 차트 (popup.js의 createImpressionChart와 동일)
    createImpressionChart() {
      this.showImpressionChart = false

      // console.log('노출비율 차트 생성 시도:', {
      //   totalAdImpressions: this.summaryMetrics.totalAdImpressions,
      //   totalOrganicImpressions: this.summaryMetrics.totalOrganicImpressions
      // })

      const totalAdImpressions = this.summaryMetrics.totalAdImpressions || 0
      const totalOrganicImpressions = this.summaryMetrics.totalOrganicImpressions || 0
      const totalImpressions = totalAdImpressions + totalOrganicImpressions

      if (totalImpressions === 0) {
        // console.log('노출 데이터가 없어 차트를 생성하지 않습니다.')
        this.showImpressionChart = false
        return
      }

      // console.log('차트 데이터:', { totalAdImpressions, totalOrganicImpressions, totalImpressions })



      this.impressionChartOptions = {
        series: [totalAdImpressions, totalOrganicImpressions],
        chart: {
          type: 'donut',
          height: 300
        },
        labels: ['광고 노출', '자연 노출'],
        colors: ['#ff6b6b', '#4ecdc4'],
        plotOptions: {
          pie: {
            donut: {
              size: '60%',
              labels: {
                show: true,
                total: {
                  show: true,
                  label: '총 노출수',
                  formatter: function () {
                    return totalImpressions.toLocaleString()
                  }
                }
              }
            }
          }
        },
        dataLabels: {
          enabled: true,
          formatter: function(val, opts) {
            const value = opts.w.config.series[opts.seriesIndex]
            return value.toLocaleString() + '\n(' + val.toFixed(1) + '%)'
          }
        },
        legend: {
          position: 'bottom'
        },
        responsive: [{
          breakpoint: 480,
          options: {
            chart: {
              height: 200
            }
          }
        }]
      }

      setTimeout(() => {
        this.showImpressionChart = true
      }, 100)
    },

    // 가격별 CVR 차트 (이상치포함 판매가×CVR Top300)
    createPriceVsCvrChart() {
      this.showPriceVsCvrChart = false

      // 미리 계산된 판매가×CVR Top300 상품 사용
      const products = this.top300ByPriceCvr
      if (products.length === 0) return

      // 차트 데이터 생성 (미리 계산된 값 사용)
      this.priceVsCvrData = products.map(product => ({
        x: parseInt(product.price),
        y: product.calculatedCVR,
        productName: product.productName,
        brandName: product.brandName || '브랜드 없음',
        sales: product.salesLast28d || 0,
        views: product.calculatedImpressions,
        rpi: product.calculatedRPI,
        vendorItemId: product.vendorItemId,
        isComparisonProduct: this.comparisonProduct && product.vendorItemId === this.comparisonProduct.vendorItemId
      })).sort((a, b) => a.x - b.x)

      // 비교상품이 TOP300에 없으면 추가 (enrichedAnalysisResults에 없어도 comparisonProduct 사용)
      if (this.comparisonProduct && !this.priceVsCvrData.some(d => d.isComparisonProduct)) {
        const comparisonFromResults = this.enrichedAnalysisResults.find(
          p => p.vendorItemId === this.comparisonProduct.vendorItemId
        )
        const comparisonData = comparisonFromResults || this.comparisonProduct

        this.priceVsCvrData.push({
          x: parseInt(comparisonData.price || this.comparisonProduct.price) || 0,
          y: parseFloat(comparisonData.cvr || this.comparisonProduct.cvr) || 0,
          productName: comparisonData.productName || this.comparisonProduct.productName,
          brandName: comparisonData.brandName || '브랜드 없음',
          sales: comparisonData.salesLast28d || this.comparisonProduct.salesLast28d || 0,
          views: this.calculateImpressions(comparisonData) || this.comparisonProduct._debug?.totalImpressions || 0,
          rpi: comparisonFromResults ? comparisonFromResults.calculatedRPI : this.comparisonProduct.rpi,
          vendorItemId: comparisonData.vendorItemId || this.comparisonProduct.vendorItemId,
          isComparisonProduct: true
        })
        // 다시 정렬
        this.priceVsCvrData.sort((a, b) => a.x - b.x)
        console.log('📊 [가격별 CVR 차트] 비교상품 추가:', comparisonData.productName || this.comparisonProduct.productName)
      }

      // 이상치 감지 및 색상 계산 (popup.js와 동일)
      const outlierInfo = this.calculateOutlierScores(this.priceVsCvrData)

      // 데이터에 색상 정보 추가 (비교상품은 노란색으로 표시)
      const enhancedData = this.priceVsCvrData.map((item, index) => ({
        ...item,
        fillColor: item.isComparisonProduct ? '#ffc107' : outlierInfo[index].color,
        isOutlier: outlierInfo[index].isOutlier,
        outlierScore: outlierInfo[index].score,
        zScore: outlierInfo[index].zScore
      }))

      const chartData = enhancedData

      this.priceVsCvrChartOptions = {
        series: [{
          name: '상품',
          data: chartData
        }],
        chart: {
          type: 'scatter',
          height: 300,
          zoom: {
            enabled: true,
            type: 'xy'
          },
          toolbar: {
            show: true
          }
        },
        colors: enhancedData.map(d => d.fillColor), // 각 점마다 개별 색상 (비교상품: 노란색)
        markers: {
          size: enhancedData.map(d => d.isComparisonProduct ? 12 : 6), // 비교상품은 크게 표시
          strokeWidth: enhancedData.map(d => d.isComparisonProduct ? 3 : 1),
          strokeColors: enhancedData.map(d => d.isComparisonProduct ? '#000' : undefined),
          hover: {
            size: 10
          }
        },
        xaxis: {
          type: 'numeric',
          title: {
            text: '판매가 (원)'
          },
          labels: {
            formatter: function(val) {
              return parseInt(val).toLocaleString() + '원'
            }
          },
          min: function(min) {
            return Math.max(0, min * 0.95)
          },
          max: function(max) {
            return max * 1.05
          }
        },
        yaxis: {
          title: {
            text: 'CVR (%)'
          },
          labels: {
            formatter: function(value) {
              return Math.round(value) + '%'
            }
          }
        },
        tooltip: {
          custom: function({dataPointIndex}) {
            if (enhancedData[dataPointIndex]) {
              const data = enhancedData[dataPointIndex]
              let outlierText = ''
              if (data.isOutlier) {
                const intensity = data.outlierScore > 0.7 ? '매우 높음' :
                                data.outlierScore > 0.4 ? '높음' : '보통'
                outlierText = `<div style="color: ${data.fillColor}; font-weight: bold;">🔥 고효율 이상치 (${intensity})</div>`
              }
              // RPI = 표·TOP3 와 같은 정본 값(calculatedRPI). 예전엔 클릭률이 빠진 CVR×가격/100 이었다
              const rpi = (data.rpi === null || data.rpi === undefined) ? '-' : Number(data.rpi).toFixed(2)
              return `<div class="p-2" style="background: white; border: 1px solid #ccc; border-radius: 4px;">
                <div><strong>${data.productName}</strong></div>
                ${outlierText}
                <div>브랜드: ${data.brandName}</div>
                <div>가격: ${data.x.toLocaleString()}원</div>
                <div>CVR: ${data.y.toFixed(2)}%</div>
                <div>RPI: ${rpi}</div>
                <div>판매수: ${data.sales}개</div>
                <div>조회수: ${data.views.toLocaleString()}</div>
              </div>`
            }
            return ''
          }
        },
        legend: {
          show: false
        },
        grid: {
          show: true,
          borderColor: '#e0e0e0'
        }
      }

      setTimeout(() => {
        this.showPriceVsCvrChart = true
      }, 100)
    },

    // 이상치 감지 및 점수 계산 (IQR + Z-score 조합으로 극단값에 강건하게)
    calculateOutlierScores(data) {
      if (data.length < 3) return data.map(() => ({ isOutlier: false, score: 0, color: '#008FFB' }))

      // 가격×CVR 값 계산
      const priceXCvrValues = data.map(d => d.x * d.y) // 가격 × CVR

      // 1단계: IQR 방법으로 극단적 이상치 감지
      const sortedPriceXCvr = [...priceXCvrValues].sort((a, b) => a - b)
      const q1Index = Math.floor(sortedPriceXCvr.length * 0.25)
      const q3Index = Math.floor(sortedPriceXCvr.length * 0.75)
      const q1 = sortedPriceXCvr[q1Index]
      const q3 = sortedPriceXCvr[q3Index]
      const iqr = q3 - q1
      const iqrUpperBound = q3 + 1.5 * iqr
      // const iqrLowerBound = q1 - 1.5 * iqr

      // 2단계: 상단 극단값만 제거 후 Z-score 계산 (하단은 정상 범위로 처리)
      const nonExtremeValues = priceXCvrValues.filter(v => v <= iqrUpperBound)
      let mean, stdDev, zThreshold = 2.0 // IQR과 조합 시 Z-score는 더 보수적으로

      if (nonExtremeValues.length >= 3) {
        mean = nonExtremeValues.reduce((sum, val) => sum + val, 0) / nonExtremeValues.length
        const variance = nonExtremeValues.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / nonExtremeValues.length
        stdDev = Math.sqrt(variance)
      } else {
        // 데이터가 너무 적으면 전체 데이터로 계산
        mean = priceXCvrValues.reduce((sum, val) => sum + val, 0) / priceXCvrValues.length
        const variance = priceXCvrValues.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / priceXCvrValues.length
        stdDev = Math.sqrt(variance)
      }

      // console.log('🔍 가격×CVR 이상치 감지 정보 (IQR + Z-score 조합):', {
      //   dataCount: data.length,
      //   allPriceXCvrValues: priceXCvrValues.map(v => v.toFixed(2)),
      //   iqrInfo: {
      //     q1: q1.toFixed(2),
      //     q3: q3.toFixed(2),
      //     iqr: iqr.toFixed(2),
      //     iqrUpperBound: iqrUpperBound.toFixed(2),
      //     iqrLowerBound: iqrLowerBound.toFixed(2)
      //   },
      //   zScoreInfo: {
      //     mean: mean.toFixed(2),
      //     stdDev: stdDev.toFixed(2),
      //     nonExtremeCount: nonExtremeValues.length,
      //     zThreshold: zThreshold
      //   }
      // })

      return data.map((d) => {
        const priceXCvr = d.x * d.y // 해당 상품의 가격×CVR

        // 이상치 판정: IQR 상한만 체크 (하한은 정상 범위로 처리)
        const isIQROutlier = priceXCvr > iqrUpperBound
        const zScore = stdDev > 0 ? Math.abs((priceXCvr - mean) / stdDev) : 0
        const isZScoreOutlier = zScore > zThreshold

        // 두 방법 중 하나라도 이상치로 판정하면 이상치로 처리
        const isOutlier = isIQROutlier || isZScoreOutlier

        // 상위 5개 또는 높은 가격×CVR 값은 로그 출력
        // if (index < 5 || priceXCvr > 1000) {
          // // console.log(`📊 가격×CVR 분석 [${index + 1}]:`, {
          //   product: d.productName?.substring(0, 20) + '...',
          //   price: d.x?.toLocaleString(),
          //   cvr: d.y?.toFixed(2),
          //   priceXCvr: priceXCvr.toFixed(2),
          //   zScore: zScore.toFixed(2),
          //   isIQROutlier: isIQROutlier,
          //   isZScoreOutlier: isZScoreOutlier,
          //   isOutlier: isOutlier,
          //   iqrUpperBound: iqrUpperBound.toFixed(2)
          // })
        // }

        // 이상치 점수 계산 (0-1 범위)
        let outlierScore = 0
        if (isOutlier) {
          if (isIQROutlier) {
            // IQR 이상치는 더 강한 점수 부여
            outlierScore = Math.min(Math.abs(priceXCvr - iqrUpperBound) / iqrUpperBound, 1)
          } else {
            // Z-score 이상치는 기존 방식
            outlierScore = Math.min((zScore - zThreshold) / 2, 1)
          }
        }

        // 색상 계산 (파란색 → 주황색 → 빨간색)
        let color = '#008FFB' // 기본 파란색
        if (isOutlier) {
          if (outlierScore < 0.5) {
            // 파란색 → 주황색
            const ratio = outlierScore * 2
            const r = Math.round(0 + (255 - 0) * ratio)
            const g = Math.round(143 + (165 - 143) * ratio)
            const b = Math.round(251 + (0 - 251) * ratio)
            color = `rgb(${r}, ${g}, ${b})`
          } else {
            // 주황색 → 빨간색
            const ratio = (outlierScore - 0.5) * 2
            const r = 255
            const g = Math.round(165 + (0 - 165) * ratio)
            const b = 0
            color = `rgb(${r}, ${g}, ${b})`
          }
        }

        // if (isOutlier) {
        //   console.log('🔥 가격×CVR 이상치 발견:', {
        //     product: d.productName,
        //     price: d.x.toLocaleString(),
        //     cvr: d.y.toFixed(2),
        //     priceXCvr: priceXCvr.toFixed(2),
        //     reason: isIQROutlier ? 'IQR 방법' : 'Z-score 방법',
        //     iqrOutlier: isIQROutlier,
        //     zScoreOutlier: isZScoreOutlier,
        //     zScore: zScore.toFixed(2),
        //     outlierScore: outlierScore.toFixed(2),
        //     color: color
        //   })
        // }

        return { isOutlier, score: outlierScore, color, zScore }
      })
    },

    // 가격×CVR 기준 이상치 분석 (popup.js와 동일)
    analyzeOutliersByPriceXCvr(products) {
      if (products.length < 3) return products.map(p => ({ ...p, isPriceXCvrOutlier: false }))

      // 데이터 신뢰성 필터: 최근 28일 판매량 > 5인 상품만 이상치 분석
      const eligible = products.filter(p => Number(p.salesLast28d || 0) > 5)
      if (eligible.length < 3) {
        return products.map(p => ({
          ...p,
          isPriceXCvrOutlier: false,
          priceXCvr: p.price * parseFloat(this.calculateCVR(p)),
          priceXCvrZScore: 0,
          priceXCvrOutlierReason: ''
        }))
      }

      // 가격×CVR 값 계산 및 평균과 표준편차 계산 (eligible 대상)
      const priceXCvrValues = eligible.map(p => p.price * parseFloat(this.calculateCVR(p)))
      const mean = priceXCvrValues.reduce((sum, val) => sum + val, 0) / priceXCvrValues.length
      const variance = priceXCvrValues.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / priceXCvrValues.length
      const stdDev = Math.sqrt(variance)

      // 상위 25% 가격×CVR 값 계산 (eligible 대상)
      const sortedPriceXCvr = [...priceXCvrValues].sort((a, b) => b - a)
      const top25PercentIndex = Math.floor(sortedPriceXCvr.length * 0.25)
      const top25PercentThreshold = sortedPriceXCvr[top25PercentIndex] || mean

      const zThreshold = 2.0 // 기존 기준 유지

      return products.map(p => {
        const priceXCvr = p.price * parseFloat(this.calculateCVR(p))

        // 신뢰성 미달 데이터는 이상치 아님 처리
        if (Number(p.salesLast28d || 0) <= 5) {
          return {
            ...p,
            isPriceXCvrOutlier: false,
            priceXCvr,
            priceXCvrZScore: 0,
            priceXCvrOutlierReason: ''
          }
        }

        const zScore = stdDev > 0 ? Math.abs((priceXCvr - mean) / stdDev) : 0
        const isHighPriceXCvr = priceXCvr >= top25PercentThreshold
        const isPriceXCvrOutlier = zScore > zThreshold && isHighPriceXCvr

        let priceXCvrOutlierReason = ''
        if (isPriceXCvrOutlier) {
          priceXCvrOutlierReason = `가격×CVR(${priceXCvr.toFixed(0)}) Z-Score(${zScore.toFixed(2)}) > ${zThreshold}, 상위25%(${top25PercentThreshold.toFixed(0)})`
        }

        return {
          ...p,
          isPriceXCvrOutlier,
          priceXCvr,
          priceXCvrZScore: zScore,
          priceXCvrOutlierReason
        }
      })
    },

    // 가격×CTR 기준 이상치 분석 (가격×CVR과 동일한 로직)
    analyzeOutliersByPriceXCtr(products) {
      if (products.length < 3) return products.map(p => ({ ...p, isPriceXCtrOutlier: false }))

      // 데이터 신뢰성 필터: 최근 28일 판매량 > 5인 상품만 이상치 분석
      const eligible = products.filter(p => Number(p.salesLast28d || 0) > 5)
      if (eligible.length < 3) {
        return products.map(p => ({
          ...p,
          isPriceXCtrOutlier: false,
          priceXCtr: p.price * parseFloat(this.calculateCTR(p)),
          priceXCtrZScore: 0,
          priceXCtrOutlierReason: ''
        }))
      }

      // 가격×CTR 값 계산 및 평균과 표준편차 계산 (eligible 대상)
      const priceXCtrValues = eligible.map(p => p.price * parseFloat(this.calculateCTR(p)))
      const mean = priceXCtrValues.reduce((sum, val) => sum + val, 0) / priceXCtrValues.length
      const variance = priceXCtrValues.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / priceXCtrValues.length
      const stdDev = Math.sqrt(variance)

      // 상위 25% 가격×CTR 값 계산 (eligible 대상)
      const sortedPriceXCtr = [...priceXCtrValues].sort((a, b) => b - a)
      const top25PercentIndex = Math.floor(sortedPriceXCtr.length * 0.25)
      const top25PercentThreshold = sortedPriceXCtr[top25PercentIndex] || mean

      const zThreshold = 2.0 // 기존 기준 유지

      return products.map(p => {
        const priceXCtr = p.price * parseFloat(this.calculateCTR(p))

        // 신뢰성 미달 데이터는 이상치 아님 처리
        if (Number(p.salesLast28d || 0) <= 5) {
          return {
            ...p,
            isPriceXCtrOutlier: false,
            priceXCtr,
            priceXCtrZScore: 0,
            priceXCtrOutlierReason: ''
          }
        }

        const zScore = stdDev > 0 ? Math.abs((priceXCtr - mean) / stdDev) : 0
        const isHighPriceXCtr = priceXCtr >= top25PercentThreshold
        const isPriceXCtrOutlier = zScore > zThreshold && isHighPriceXCtr

        let priceXCtrOutlierReason = ''
        if (isPriceXCtrOutlier) {
          priceXCtrOutlierReason = `가격×CTR(${priceXCtr.toFixed(0)}) Z-Score(${zScore.toFixed(2)}) > ${zThreshold}, 상위25%(${top25PercentThreshold.toFixed(0)})`
        }

        return {
          ...p,
          isPriceXCtrOutlier,
          priceXCtr,
          priceXCtrZScore: zScore,
          priceXCtrOutlierReason
        }
      })
    },

    // 노출수 변화량 기준 이상치 분석 (popup.js와 동일)
    analyzeOutliersByTrafficVariation(products) {
      // dailyTraffic 데이터가 없으면 스킵
      if (products.length < 3) {
        return products.map(p => ({ ...p, isTrafficVariationOutlier: false }))
      }

      // 각 상품의 노출수 변화량 계산
      const productsWithVariation = products.map(p => {
        if (!p.dailyTraffic || typeof p.dailyTraffic !== 'object') {
          return {
            ...p,
            trafficVariation: 0,
            trafficRangeRatio: 0,
            trafficStdDev: 0,
            hasTrafficData: false
          }
        }

        // 일별 노출수 데이터 추출
        const dailyImpressions = []
        Object.entries(p.dailyTraffic).forEach(([, dailyDataArray]) => {
          if (Array.isArray(dailyDataArray) && dailyDataArray.length > 0) {
            const impression = dailyDataArray[0].impression || 0
            dailyImpressions.push(impression)
          }
        })

        if (dailyImpressions.length < 2) {
          return {
            ...p,
            trafficVariation: 0,
            trafficRangeRatio: 0,
            trafficStdDev: 0,
            hasTrafficData: false
          }
        }

        // 변동계수 계산 (표준편차 / 평균)
        const mean = dailyImpressions.reduce((sum, val) => sum + val, 0) / dailyImpressions.length
        const variance = dailyImpressions.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / dailyImpressions.length
        const stdDev = Math.sqrt(variance)
        const trafficVariation = mean > 0 ? stdDev / mean : 0

        return {
          ...p,
          trafficVariation,
          trafficRangeRatio: Math.max(...dailyImpressions) / Math.max(Math.min(...dailyImpressions), 1),
          trafficStdDev: stdDev,
          hasTrafficData: true
        }
      })

      // 변동계수 기준 이상치 분석
      const variationValues = productsWithVariation.filter(p => p.hasTrafficData).map(p => p.trafficVariation)
      if (variationValues.length < 3) {
        return productsWithVariation.map(p => ({ ...p, isTrafficVariationOutlier: false }))
      }

      const mean = variationValues.reduce((sum, val) => sum + val, 0) / variationValues.length
      const variance = variationValues.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / variationValues.length
      const stdDev = Math.sqrt(variance)

      // 상위 25% 변동계수 값 계산
      const sortedVariation = [...variationValues].sort((a, b) => b - a)
      const top25PercentIndex = Math.floor(sortedVariation.length * 0.25)
      const top25PercentThreshold = sortedVariation[top25PercentIndex] || mean

      const zThreshold = 2.0 // IQR과 조합하지 않는 단순 Z-score는 2.0 유지

      return productsWithVariation.map(p => {
        if (!p.hasTrafficData) {
          return { ...p, isTrafficVariationOutlier: false }
        }

        const zScore = stdDev > 0 ? Math.abs((p.trafficVariation - mean) / stdDev) : 0
        const isHighVariation = p.trafficVariation >= top25PercentThreshold
        const isTrafficVariationOutlier = zScore > zThreshold && isHighVariation

        // 이상치 사유 생성
        let trafficVariationOutlierReason = ''
        if (isTrafficVariationOutlier) {
          trafficVariationOutlierReason = `트래픽변동계수(${p.trafficVariation.toFixed(2)}) Z-Score(${zScore.toFixed(2)}) > ${zThreshold}, 상위25%(${top25PercentThreshold.toFixed(2)})`
        }

        return {
          ...p,
          isTrafficVariationOutlier,
          trafficVariationZScore: zScore,
          trafficVariationOutlierReason
        }
      })
    },

    // 키워드 수준 CTR 이상치 분석 (노출수 가중 중앙값 + MAD Modified Z-score)
    // 전체 상품의 모든 키워드를 풀(pool)로 묶어 분석
    // 핵심: 노출수를 가중치로 사용하여 기준선 산출 → 임의 기준값 없이 데이터가 스스로 결정
    analyzeKeywordCTROutliers(products) {
      // 1. 전체 키워드 풀 수집 (클릭 > 0인 키워드만)
      const keywordPool = []
      products.forEach((product, productIdx) => {
        if (product.trafficKeyword && product.trafficKeyword.length > 0) {
          product.trafficKeyword.forEach((tk, kwIdx) => {
            const impression = tk.keywordMetric?.impression || 0
            const clicks = tk.keywordMetric?.srpClick || 0
            if (impression > 0 && clicks > 0) {
              keywordPool.push({
                productIdx,
                kwIdx,
                vendorItemId: product.vendorItemId,
                impression,
                clicks,
                rawCTR: clicks / impression  // 비율 (0~1)
              })
            }
          })
        }
      })

      // 클릭이 있는 키워드가 너무 적으면 분석 불가
      if (keywordPool.length < 3) {
        return products.map(p => ({
          ...p,
          outlierKeywordIndices: [],
          cleanCTR: this.calculateCTRFromRawData(p),
          keywordCTROutlierCount: 0,
          keywordCTROutlierReason: ''
        }))
      }

      // 2. 노출수 가중 중앙값/MAD 산출
      //    각 키워드의 노출수가 가중치 → imp=10000이 imp=1보다 10000배 더 기준선에 기여
      //    임의 임계값 없이 데이터의 트래픽 분포가 자연스럽게 기준선을 결정
      const weightedMedian = (values, weights) => {
        const pairs = values.map((v, i) => ({ v, w: weights[i] }))
          .sort((a, b) => a.v - b.v)
        const totalWeight = pairs.reduce((sum, p) => sum + p.w, 0)
        const halfWeight = totalWeight / 2
        let cumWeight = 0
        for (const p of pairs) {
          cumWeight += p.w
          if (cumWeight >= halfWeight) return p.v
        }
        return pairs[pairs.length - 1].v
      }

      const logCTRs = keywordPool.map(kw => Math.log1p(kw.rawCTR))
      const weights = keywordPool.map(kw => kw.impression)

      const med = weightedMedian(logCTRs, weights)
      const absDevs = logCTRs.map(v => Math.abs(v - med))
      const mad = weightedMedian(absDevs, weights)
      const threshold = 3.5  // Iglewicz & Hoaglin(1993) 권장 임계값

      // MAD=0 보조: 가중 IQR 상한 산출
      let iqrUpperBound = Infinity
      if (!mad || mad === 0) {
        const pairs = logCTRs.map((v, i) => ({ v, w: weights[i] }))
          .sort((a, b) => a.v - b.v)
        const totalWeight = pairs.reduce((sum, p) => sum + p.w, 0)
        let cumW = 0
        let q1 = pairs[0].v, q3 = pairs[pairs.length - 1].v
        for (const p of pairs) {
          cumW += p.w
          if (q1 === pairs[0].v && cumW >= totalWeight * 0.25) q1 = p.v
          if (cumW >= totalWeight * 0.75) { q3 = p.v; break }
        }
        iqrUpperBound = q3 + 1.5 * (q3 - q1)
      }

      // 3. 모든 키워드를 기준선으로 평가
      keywordPool.forEach((kw, idx) => {
        const logCTR = logCTRs[idx]

        // Tier 1: clicks > impression은 데이터 오류 → 무조건 이상치
        if (kw.clicks > kw.impression) {
          kw.modifiedZ = 99
          kw.isOutlier = true
          kw.outlierTier = 'data_error'
          return
        }

        // Tier 2: 가중 기준선 대비 Modified Z-score
        if (mad && mad > 0) {
          kw.modifiedZ = 0.6745 * (logCTR - med) / mad
          kw.isOutlier = (logCTR > med) && (kw.modifiedZ > threshold)
        } else {
          kw.modifiedZ = 0
          kw.isOutlier = logCTR > iqrUpperBound
        }
        kw.outlierTier = kw.isOutlier ? 'statistical' : 'normal'
      })

      console.log('🔍 [키워드 CTR 이상치] 분석 결과:', {
        totalKeywordsWithClicks: keywordPool.length,
        weightedMedianCTR: (Math.expm1(med) * 100).toFixed(2) + '%',
        weightedMAD: mad.toFixed(6),
        threshold,
        dataErrors: keywordPool.filter(kw => kw.outlierTier === 'data_error').length,
        statisticalOutliers: keywordPool.filter(kw => kw.outlierTier === 'statistical').length,
        totalOutliers: keywordPool.filter(kw => kw.isOutlier).length,
        sampleOutliers: keywordPool.filter(kw => kw.isOutlier).slice(0, 5).map(kw => ({
          ctr: (kw.rawCTR * 100).toFixed(1) + '%',
          imp: kw.impression,
          clicks: kw.clicks,
          tier: kw.outlierTier,
          modZ: kw.modifiedZ.toFixed(2)
        }))
      })

      // 4. 상품별 이상치 키워드 그룹핑
      const outlierMap = new Map()
      keywordPool.forEach(kw => {
        if (kw.isOutlier) {
          if (!outlierMap.has(kw.vendorItemId)) {
            outlierMap.set(kw.vendorItemId, { indices: new Set(), reasons: [] })
          }
          const info = outlierMap.get(kw.vendorItemId)
          info.indices.add(kw.kwIdx)
          const keywordName = products[kw.productIdx].trafficKeyword[kw.kwIdx]?.keywordDetail?.keyword || `키워드${kw.kwIdx + 1}`
          const tierLabel = kw.outlierTier === 'data_error' ? '데이터오류' : `ModZ=${kw.modifiedZ.toFixed(2)}`
          info.reasons.push(`${keywordName}(CTR=${(kw.rawCTR * 100).toFixed(1)}%,imp=${kw.impression},${tierLabel})`)
        }
      })

      // 5. 이상치 키워드 제외 후 상품별 CTR 재계산
      return products.map(p => {
        const outlierInfo = outlierMap.get(p.vendorItemId)

        if (!outlierInfo || outlierInfo.indices.size === 0) {
          return {
            ...p,
            outlierKeywordIndices: [],
            cleanCTR: this.calculateCTRFromRawData(p),
            keywordCTROutlierCount: 0,
            keywordCTROutlierReason: ''
          }
        }

        // 이상치 키워드 제외하고 CTR 재계산
        let cleanImpressions = 0
        let cleanClicks = 0
        const totalKeywords = p.trafficKeyword?.length || 0

        if (p.trafficKeyword) {
          p.trafficKeyword.forEach((tk, idx) => {
            if (!outlierInfo.indices.has(idx)) {
              cleanImpressions += tk.keywordMetric?.impression || 0
              cleanClicks += tk.keywordMetric?.srpClick || 0
            }
          })
        }

        // 모든 키워드가 이상치 → 신뢰할 데이터 없음 → CTR=0
        const allKeywordsOutlier = cleanImpressions === 0
        const cleanCTR = cleanImpressions > 0
          ? parseFloat((cleanClicks / cleanImpressions * 100).toFixed(2))
          : 0

        return {
          ...p,
          outlierKeywordIndices: [...outlierInfo.indices],
          cleanCTR,
          allKeywordsOutlier,
          keywordCTROutlierCount: outlierInfo.indices.size,
          keywordCTROutlierReason: `이상키워드 ${outlierInfo.indices.size}/${totalKeywords}개 제외: ${outlierInfo.reasons.join(', ')}`
        }
      })
    },

    // CTR 기준 이상치 분석 (로그변환 + Modified Z-score(MAD) 기반, 상단만 판정)
    analyzeOutliersByCTR(products) {
      if (products.length < 3) return products.map(p => ({ ...p, isCTROutlier: false }))

      // 데이터 신뢰성 필터: 조회수(pvLast28Day) > 15인 상품만 이상치 분석
      const idxEligible = products.map((p,i)=>({i, pv: Number(p.pvLast28Day || 0)})).filter(o=>o.pv>15).map(o=>o.i)
      const ctrValuesAll = products.map(p => p.cleanCTR !== undefined ? p.cleanCTR : this.calculateCTRFromRawData(p))

      if (idxEligible.length < 3) {
        return products.map((p, idx) => ({
          ...p,
          isCTROutlier: false,
          ctrValue: ctrValuesAll[idx],
          ctrZScore: 0,
          ctrOutlierReason: ''
        }))
      }

      // 1) CTR 및 로그변환 값 계산 (eligible 대상)
      const yEligible = idxEligible.map(i => Math.log1p(ctrValuesAll[i]))

      // 2) 중앙값/ MAD 계산 함수
      const median = (arr) => {
        if (!arr || arr.length === 0) return 0
        const s = [...arr].sort((a,b)=>a-b)
        const m = Math.floor(s.length/2)
        return (s.length % 2 === 1) ? s[m] : (s[m-1] + s[m]) / 2
      }
      const med = median(yEligible)
      const mad = median(yEligible.map(v => Math.abs(v - med)))

      const threshold = 3.5

      // MAD=0 보조: eligible y에 대해 IQR 상한 산출
      let iqrUpperBound = Infinity
      if (!mad || mad === 0) {
        const sortedY = [...yEligible].sort((a,b)=>a-b)
        const q1 = sortedY[Math.floor(sortedY.length * 0.25)]
        const q3 = sortedY[Math.floor(sortedY.length * 0.75)]
        const iqr = (q3 - q1)
        iqrUpperBound = q3 + 1.5 * iqr
      }

      const eligibleSet = new Set(idxEligible)

      return products.map((p, idx) => {
        const ctr = ctrValuesAll[idx]
        // 신뢰성 미달 데이터는 이상치 평가 제외
        if (!eligibleSet.has(idx)) {
          return { ...p, isCTROutlier: false, ctrValue: ctr, ctrZScore: 0, ctrOutlierReason: '' }
        }

        const y = Math.log1p(ctr)
        let isCTROutlier = false
        let ctrModZ = 0
        let ctrOutlierReason = ''

        if (mad && mad > 0) {
          ctrModZ = 0.6745 * (y - med) / mad
          isCTROutlier = (y > med) && (ctrModZ > threshold)
          if (isCTROutlier) ctrOutlierReason = `log-CTR ModifiedZ(${ctrModZ.toFixed(2)}) > ${threshold}`
        } else {
          isCTROutlier = y > iqrUpperBound
          if (isCTROutlier) ctrOutlierReason = `log-CTR IQR상한(${iqrUpperBound.toFixed(4)}) 초과`
        }

        return { ...p, isCTROutlier, ctrValue: ctr, ctrZScore: ctrModZ, ctrOutlierReason }
      })
    },


    // RPI 기준 이상치 분석 (로그변환 + Modified Z-score(MAD) 기반, 상단만 판정)
    analyzeOutliersByRPI(products) {
      if (products.length < 3) return products.map(p => ({ ...p, isRPIOutlier: false }))

      // 1) RPI 및 로그변환 값 계산 (cleanCTR 반영)
      const rpiValues = products.map(p => this.rpiOf(p) || 0)
      const yValues = rpiValues.map(v => Math.log1p(v)) // y = log(1 + RPI)

      // 2) 중앙값/ MAD 계산 함수
      const median = (arr) => {
        if (!arr || arr.length === 0) return 0
        const s = [...arr].sort((a,b)=>a-b)
        const m = Math.floor(s.length/2)
        return (s.length % 2 === 1) ? s[m] : (s[m-1] + s[m]) / 2
      }
      const med = median(yValues)
      const devs = yValues.map(v => Math.abs(v - med))
      const mad = median(devs)

      // 3) MAD 기반 Modified Z-score로 상단 이상치 판정 (기본 임계 3.5)
      const threshold = 3.5

      // MAD=0 등 특이 케이스 대비: y에 대해 IQR 상한 보조 적용
      let iqrUpperBound = Infinity
      if (!mad || mad === 0) {
        const sortedY = [...yValues].sort((a,b)=>a-b)
        const q1 = sortedY[Math.floor(sortedY.length * 0.25)]
        const q3 = sortedY[Math.floor(sortedY.length * 0.75)]
        const iqr = (q3 - q1)
        iqrUpperBound = q3 + 1.5 * iqr
      }

      return products.map((p, idx) => {
        const rpi = rpiValues[idx]
        const y = yValues[idx]

        let isRPIOutlier = false
        let modZ = 0
        let rpiOutlierReason = ''

        if (mad && mad > 0) {
          modZ = 0.6745 * (y - med) / mad
          // 상단만 판정: 중앙값보다 큰 값 중 Modified Z > 3.5
          isRPIOutlier = (y > med) && (modZ > threshold)
          if (isRPIOutlier) {
            rpiOutlierReason = `log-RPI ModifiedZ(${modZ.toFixed(2)}) > ${threshold}`
          }
        } else {
          // MAD가 0인 경우: 로그값에 대한 IQR 상한 초과만 이상치로 처리
          isRPIOutlier = y > iqrUpperBound
          if (isRPIOutlier) {
            rpiOutlierReason = `log-RPI IQR상한(${iqrUpperBound.toFixed(4)}) 초과`
          }
        }

        return {
          ...p,
          isRPIOutlier,
          rpiValue: rpi,
          rpiZScore: modZ, // 기존 필드명 유지(여기서는 Modified Z)
          rpiOutlierReason
        }
      })
    },

    // 상품 소싱 체크리스트 생성
    async generateSourcingChecklist() {
      if (!this.topSourcingProducts || this.topSourcingProducts.length === 0) {
        alert('TOP3 소싱 상품 데이터가 없습니다.')
        return
      }

      this.isGeneratingChecklist = true
      this.sourcingChecklist = null

      try {
        // console.log('🔍 상품 소싱 체크리스트 생성 시작')

        // 분석 데이터 준비
        const analysisData = {
          totalProducts: this.summaryMetrics.totalProducts,
          avgSalePrice: this.summaryMetrics.avgSalePrice,
          medianCTR: this.summaryMetrics.medianCTR,
          medianCVR: this.summaryMetrics.medianCVR,
          optimizedCategory: this.optimizedCategory
        }

        // 체크리스트 생성 요청
        const result = await sourcingChecklistService.generateSourcingChecklist(
          this.topSourcingProducts,
          analysisData
        )

        this.sourcingChecklist = result
        // console.log('✅ 상품 소싱 체크리스트 생성 완료:', result)

      } catch (error) {
        console.error('❌ 체크리스트 생성 실패:', error)
        alert('체크리스트 생성 중 오류가 발생했습니다.')
      } finally {
        this.isGeneratingChecklist = false
      }
    },

    // 체크리스트 헬퍼 메서드들
    getCategoryTitle(categoryKey) {
      const titles = {
        productAnalysis: '상품 분석',
        marketAnalysis: '시장 분석',
        competitorAnalysis: '경쟁사 분석',
        riskAssessment: '리스크 평가'
      }
      return titles[categoryKey] || categoryKey
    },

    getTotalItemsInCategory(category) {
      return category.reduce((total, section) => total + section.items.length, 0)
    },

    getPriorityBorderClass(priority) {
      const classes = {
        high: 'border-danger',
        medium: 'border-warning',
        low: 'border-success'
      }
      return classes[priority] || 'border-secondary'
    },

    getPriorityBadgeClass(priority) {
      const classes = {
        high: 'bg-danger',
        medium: 'bg-warning text-dark',
        low: 'bg-success'
      }
      return classes[priority] || 'bg-secondary'
    },

    getPriorityText(priority) {
      const texts = {
        high: '높음',
        medium: '보통',
        low: '낮음'
      }
      return texts[priority] || priority
    },

    // 새로운 점수+등급 체계용 헬퍼 메서드들
    getChecklistRowClass(priority) {
      const classes = {
        critical: '',
        recommended: '',
        optional: 'table-secondary'
      }
      return classes[priority] || ''
    },

    getCategoryBadgeClass(category) {
      const classes = {
        '장점 유지': 'bg-success',
        '단점 개선': 'bg-warning text-dark',
        '차별화 요소': 'bg-info'
      }
      return classes[category] || 'bg-secondary'
    },

    getCategoryShortName(category) {
      const shortNames = {
        '장점 유지': '장점',
        '단점 개선': '단점',
        '차별화 요소': '차별화'
      }
      return shortNames[category] || category
    },

    getScoreTextClass(score) {
      if (score >= 80) return 'text-danger'
      if (score >= 50) return 'text-warning'
      return 'text-success'
    },

    getNewPriorityBadgeClass(priority) {
      const classes = {
        critical: 'bg-danger',
        recommended: 'bg-warning text-dark',
        optional: 'bg-success'
      }
      return classes[priority] || 'bg-secondary'
    },

    getNewPriorityText(priority) {
      const texts = {
        critical: '🔴 필수',
        recommended: '🟡 권장',
        optional: '🟢 참고'
      }
      return texts[priority] || priority
    },

    // 우선순위별 체크리스트 필터링
    getChecklistByPriority(priority) {
      if (!this.sourcingChecklist?.checklistItems) return []
      return this.sourcingChecklist.checklistItems.filter(item => item.priority === priority)
    },

    // 1688 검색 페이지 열기 (최적 판매가 기반 priceEnd 포함)
    open1688Search(keyword) {
      const encodedKeyword = encodeURIComponent(keyword)

      // 최적 판매가격대의 시작 가격을 1/1000로 변환하여 priceEnd로 사용
      let priceEndParam = ''
      if (this.bestPriceRanges && this.bestPriceRanges.length > 0 && this.bestPriceRanges[0].startPrice) {
        const priceEnd = Math.floor(this.bestPriceRanges[0].startPrice / 1000)
        if (priceEnd > 0) {
          priceEndParam = `&priceEnd=${priceEnd}`
        }
      }

      const url = `https://s.1688.com/selloffer/offer_search.htm?keywords=${encodedKeyword}&spm=a260k.home2025.searchbox.0&charset=utf8&sortType=va_sales360&descendOrder=true&beginPage=1${priceEndParam}`
      window.open(url, '_blank')
    },

    isChecklistItemChecked(index) {
      if (!this.checkedChecklistItems) {
        this.checkedChecklistItems = []
      }
      return this.checkedChecklistItems.includes(index)
    },

    toggleChecklistItem(index) {
      if (!this.checkedChecklistItems) {
        this.checkedChecklistItems = []
      }
      const idx = this.checkedChecklistItems.indexOf(index)
      if (idx > -1) {
        this.checkedChecklistItems.splice(idx, 1)
      } else {
        this.checkedChecklistItems.push(index)
      }
    },

    // 리뷰 기반 체크리스트 생성
    async generateReviewBasedChecklist() {
      if (!this.topSourcingProducts || this.topSourcingProducts.length === 0) {
        alert('TOP3 소싱 상품 데이터가 없습니다.')
        return
      }

      try {
        // console.log('🔍 AI 소싱 분석 시작')

        // 1단계: 리뷰 수집
        this.isCollectingReviews = true
        this.collectedReviews = []

        const reviewData = await this.collectTop3Reviews()

        if (!reviewData || reviewData.length === 0) {
          alert('리뷰 데이터를 수집할 수 없습니다.')
          return
        }

        // 2단계: workflowService로 체크리스트 생성
        this.isCollectingReviews = false
        this.isGeneratingChecklist = true

        const result = await this.generateChecklistWithReviews(reviewData)
        this.sourcingChecklist = result

        // IndexedDB에 체크리스트 저장
        await this.saveChecklistToIndexedDB(result)

        // console.log('✅ AI 소싱 분석 완료:', result)

        // AI 소싱 분석 완료 후 자동으로 다국어 검색어 생성 - 주석처리
        // await this.generateMultilingualKeywords()

      } catch (error) {
        console.error('❌ 리뷰 기반 체크리스트 생성 실패:', error)
        alert('AI 소싱 분석 중 오류가 발생했습니다.')
      } finally {
        this.isCollectingReviews = false
        this.isGeneratingChecklist = false
      }
    },

    // TOP3 상품 리뷰 수집 (background.js 활용)
    async collectTop3Reviews() {
      return new Promise((resolve, reject) => {
        // console.log('📝 TOP3 상품 리뷰 수집 시작')

        // Extension에 리뷰 수집 요청
        window.postMessage({
          greeting: 'collectTop3ProductReviews',
          topProducts: this.topSourcingProducts
        }, "*")

        // 응답 핸들러
        const handleReviewCollection = (event) => {
          if (event.data.res === 'top3ReviewsCollected') {
            window.removeEventListener('message', handleReviewCollection)

            this.collectedReviews = event.data.reviews || []
            // console.log('✅ 리뷰 수집 완료:', this.collectedReviews.length, '개')

            resolve(event.data.reviews)
          }

          if (event.data.res === 'reviewCollectionProgress') {
            // console.log('📊 리뷰 수집 진행:', event.data.message)
            // UI 업데이트는 collectedReviews 배열 길이로 표시
          }

          if (event.data.res === 'reviewCollectionError') {
            window.removeEventListener('message', handleReviewCollection)
            reject(new Error(event.data.error))
          }
        }

        window.addEventListener('message', handleReviewCollection)

        // 타임아웃 설정 (180초) — 상품 3개 × 별점 5개를 쇼핑 로그인 길 서버 도구로 하나씩 부른다
        setTimeout(() => {
          window.removeEventListener('message', handleReviewCollection)
          reject(new Error('리뷰 수집 타임아웃'))
        }, 180000)
      })
    },

    // 리뷰 데이터와 함께 체크리스트 생성
    async generateChecklistWithReviews(reviewData) {
      try {
        // 리뷰 텍스트 준비
        const reviewTexts = reviewData.map(review =>
          `${review.리뷰제목 || ''} ${review.리뷰내용 || ''}`
        ).filter(text => text.trim()).join('\n')

        // 상품 정보 준비
        const productInfo = this.topSourcingProducts.map((product, index) => `
TOP${index + 1} - ${product.productName}
- 가격: ${product.salePrice?.toLocaleString()}원
- RPI: ${parseFloat(product.overall || 0).toFixed(2)}
- CTR: ${parseFloat(product.ctr || 0).toFixed(2)}%
- CVR: ${parseFloat(product.cvr || 0).toFixed(2)}%
- 브랜드: ${product.brandName || '정보없음'}
        `).join('\n')

        // 상품 카테고리 추출 (키워드 또는 상품명에서)
        const productCategory = this.targetKeyword || this.topSourcingProducts[0]?.productName?.split(' ')[0] || '상품'

        // 리뷰 분석 기반 소싱 체크리스트 프롬프트 (1688 소싱 셀러 관점)
        const checklistPrompt = `당신은 1688(알리바바) 소싱 전문 컨설턴트입니다.
사용자는 1688에서 상품을 소싱하여 쿠팡에서 판매하려는 셀러입니다.
TOP3 고성과 상품의 실제 고객 리뷰를 분석하여, "어떤 상품을 소싱해야 하는지" 관점에서 체크리스트를 생성해주세요.

중요: 제조공장 입장이 아닌, "1688에서 이런 조건의 상품을 찾아 소싱해야 한다"는 셀러 관점으로 작성하세요.

=== 현재 분석 중인 상품 카테고리 ===
${productCategory}

=== TOP3 상품 정보 ===
${productInfo}

=== 실제 고객 리뷰 데이터 (총 ${reviewData.length}개) ===
${reviewTexts}

=== 점수 산정 기준 ===
각 항목의 점수(0-100)는 다음 기준으로 산정:
- 언급 빈도 (40%): 전체 리뷰 중 해당 내용이 언급된 비율
- 감정 강도 (30%): 강하게 만족/불만을 표현한 정도
- 구매 영향도 (30%): 구매 결정에 영향을 미치는 정도

등급 기준:
- critical (필수): 80-100점, 1688 소싱 시 반드시 확인해야 할 핵심 조건
- recommended (권장): 50-79점, 소싱 시 우선 고려하면 좋은 조건
- optional (참고): 0-49점, 경쟁력 강화를 위한 추가 고려사항

=== title 작성 규칙 (매우 중요) ===
title은 추상적인 표현이 아닌, 리뷰에서 언급된 구체적인 키워드/수치를 그대로 사용해야 합니다.

❌ 잘못된 예시:
- "디자인 감성 및 독창성 확보"
- "적절한 사이즈와 활용도 높은 상품 선택"

✅ 올바른 예시:
- "보헤미안 스타일, 유럽풍 그림, 기하학 패턴 등 독창적 디자인"
- "14.3cm 또는 20.5cm 사이즈로 나물, 김치, 작은 찬거리 담기 적합한 사이즈"

=== searchKeywords 작성 규칙 (매우 중요) ===
searchKeywords는 1688에서 실제로 해당 상품을 검색할 때 사용하는 중국어 검색 키워드 배열입니다.

⚠️ 핵심 규칙: 단순 번역이 아닌, "상품 카테고리 + 특성"이 결합된 실제 검색 가능한 키워드여야 합니다.

❌ 잘못된 예시 (단순 번역, 상품명 없음):
- "波西米亚风" (보헤미안 스타일) → 의류, 가방 등 다른 상품이 검색됨
- "欧式图案" (유럽풍 그림) → 벽지, 커튼 등 다른 상품이 검색됨

✅ 올바른 예시 (상품 카테고리 포함, 실제 검색용):
현재 상품이 "접시"인 경우:
- "波西米亚风餐盘" (보헤미안 스타일 접시)
- "欧式图案盘子" (유럽풍 그림 접시)
- "几何图案陶瓷盘" (기하학 패턴 도자기 접시)

현재 상품이 "옷걸이"인 경우:
- "防滑衣架" (논슬립 옷걸이)
- "加厚塑料衣架" (두꺼운 플라스틱 옷걸이)

중국 구매자가 1688에서 실제로 검색할 만한 키워드로 작성하세요.

=== actionRequired 작성 규칙 ===
searchKeywords와 별개로, 소싱 시 확인해야 할 사항을 간단히 작성합니다.
- 예: "상세페이지에서 다양한 디자인 옵션 확인"
- 예: "샘플 주문 시 실제 사이즈 측정 필수"

다음 JSON 형식으로 응답해주세요:

{
  "checklistItems": [
    {
      "category": "장점 유지",
      "title": "보헤미안, 유럽풍, 기하학 패턴 등 트렌디한 디자인",
      "description": "식탁 위를 예쁘게 연출하는 보헤미안 스타일, 유럽풍 그림, 기하학 패턴 등 독창적 디자인이 고객 만족도를 높임",
      "score": 92,
      "priority": "critical",
      "mentionCount": 28,
      "source": "리뷰 28건에서 디자인 만족 언급",
      "searchKeywords": ["波西米亚风餐盘", "欧式图案盘子", "几何图案陶瓷盘", "复古餐盘"],
      "actionRequired": "상세페이지에서 다양한 디자인 옵션 확인"
    },
    {
      "category": "장점 유지",
      "title": "14.3cm/20.5cm 사이즈로 나물, 김치, 소스 담기 적합",
      "description": "너무 크지도 작지도 않아 반찬, 소스, 간식 등 다양한 용도로 활용 가능한 실속 사이즈",
      "score": 85,
      "priority": "critical",
      "mentionCount": 25,
      "source": "리뷰 25건에서 사이즈 만족 언급",
      "searchKeywords": ["14cm小碟子", "20cm餐盘", "小号调料碟", "迷你陶瓷盘"],
      "actionRequired": "상세페이지에서 정확한 사이즈(cm) 확인"
    }
  ],
  "enhancedUSP": {
    "currentUSP": "현재 TOP3 제품들의 공통 강점 (구체적 키워드로)",
    "improvedUSP": "고객 불만을 해결한 차별화 포인트",
    "searchKeywords": ["핵심중국어키워드1", "핵심중국어키워드2", "핵심중국어키워드3"]
  },
  "summary": {
    "totalItems": 0,
    "criticalCount": 0,
    "recommendedCount": 0,
    "optionalCount": 0,
    "topPriority": "가장 중요한 소싱 포인트 요약"
  }
}

주의사항:
- checklistItems는 점수가 높은 순서대로 정렬
- 각 category는 "장점 유지", "단점 개선", "차별화 요소" 중 하나
- title은 반드시 리뷰에 언급된 구체적 키워드/수치 사용 (추상적 표현 금지)
- searchKeywords는 반드시 상품 카테고리가 포함된 실제 검색 가능한 중국어 키워드 배열
- 최대 10개 항목으로 제한
- 반드시 유효한 JSON 형식으로만 응답`

        // 이미지 URL 수집
        const imageUrls = this.topSourcingProducts
          .filter(product => product.imagePath)
          .map(product => product.imagePath)
          .slice(0, 3)

        // workflowService를 통한 체크리스트 생성
        const requestData = {
          workType: 'SOURCING_CHECKLIST',
          task: 'generate_review_based_checklist',
          requestArr: [{
            prompt: checklistPrompt,
            images: imageUrls
          }]
        }

        const result = await workflowService.requestExtensionWithPromise(
          requestData,
          {
            timeout: 60000,
            maxRetries: 2
          }
        )

        // console.log('🤖 workflowService 응답 결과:', {
        //   type: typeof result,
        //   isObject: typeof result === 'object',
        //   keys: typeof result === 'object' ? Object.keys(result || {}) : 'N/A',
        //   result: result
        // })

        return this.parseChecklistResult(result)

      } catch (error) {
        console.error('❌ 리뷰 기반 체크리스트 생성 실패:', error)
        throw error
      }
    },

    // AI 응답 결과 파싱 (새로운 리뷰 분석 구조)
    parseChecklistResult(result) {
      try {
        // console.log('🔍 parseChecklistResult 입력 데이터:', {
        //   type: typeof result,
        //   isObject: typeof result === 'object',
        //   hasReviewAnalysis: result?.reviewAnalysis ? true : false,
        //   hasSourcingChecklist: result?.sourcingChecklist ? true : false,
        //   hasChecklist: result?.checklist ? true : false,
        //   keys: typeof result === 'object' ? Object.keys(result || {}) : 'N/A'
        // })

        let parsed = null

        // result가 이미 객체인 경우
        if (typeof result === 'object' && result !== null) {
          parsed = result
          // console.log('✅ 객체 형태의 결과 감지')

          // workflowService의 batchResults 구조 처리
          if (result.batchResults && Array.isArray(result.batchResults) && result.batchResults.length > 0) {
            // console.log('🔍 batchResults 구조 감지, 첫 번째 결과 추출')
            parsed = result.batchResults[0].result
            // console.log('✅ batchResults에서 실제 데이터 추출:', parsed)
          }
        }
        // result가 문자열인 경우 JSON 파싱
        else if (typeof result === 'string') {
          // console.log('📝 문자열 형태의 결과 감지, JSON 파싱 시도')
          parsed = JSON.parse(result)
          // console.log('✅ JSON 파싱 성공')
        }

        if (parsed) {
          console.log('🔍 파싱된 데이터 구조:', {
            hasChecklistItems: !!parsed.checklistItems,
            hasReviewAnalysis: !!parsed.reviewAnalysis,
            hasSourcingChecklist: !!parsed.sourcingChecklist,
            keys: Object.keys(parsed || {})
          })
        }

        // 새로운 점수+등급 체계 구조 확인 (checklistItems 배열)
        if (parsed && parsed.checklistItems && Array.isArray(parsed.checklistItems)) {
          console.log('✅ 새로운 점수+등급 체크리스트 구조 감지')
          return parsed
        }

        // 기존 구조 확인 (하위 호환성 - reviewAnalysis, sourcingChecklist)
        if (parsed && (parsed.reviewAnalysis || parsed.sourcingChecklist)) {
          console.log('✅ 기존 리뷰 분석 구조 감지, 새 구조로 변환')
          // 기존 구조를 새 구조로 변환
          return this.convertLegacyChecklistToNew(parsed)
        }

        // 더 오래된 구조 확인 (하위 호환성 - checklist)
        if (parsed && parsed.checklist) {
          console.log('✅ 레거시 체크리스트 구조 감지')
          return this.convertLegacyChecklistToNew(parsed)
        }

        console.log('⚠️ 인식 가능한 구조가 없음, 기본값 반환')
        return this.getDefaultReviewBasedChecklist()

      } catch (error) {
        console.error('❌ 체크리스트 결과 파싱 실패:', error)
        console.error('❌ 원본 데이터:', result)
        return this.getDefaultReviewBasedChecklist()
      }
    },

    // 리뷰 기반 체크리스트 기본 구조 (새로운 점수+등급 체계)
    getDefaultReviewBasedChecklist() {
      return {
        checklistItems: [
          {
            category: "장점 유지",
            title: "품질 만족도",
            description: "고객들이 품질에 대해 긍정적으로 평가",
            score: 85,
            priority: "critical",
            mentionCount: 25,
            source: "리뷰 25건에서 긍정 언급",
            actionRequired: "현재 품질 수준 유지 필수"
          },
          {
            category: "단점 개선",
            title: "배송 품질 개선",
            description: "배송 속도나 포장에 대한 불만",
            score: 55,
            priority: "recommended",
            mentionCount: 10,
            source: "리뷰 10건에서 불만 언급",
            actionRequired: "포장 품질 개선 및 배송 추적 강화"
          }
        ],
        enhancedUSP: {
          currentUSP: "현재 제품의 핵심 가치",
          improvedUSP: "개선된 핵심 가치",
          oneLiner: "한줄 요약"
        },
        summary: {
          totalItems: 2,
          criticalCount: 1,
          recommendedCount: 1,
          optionalCount: 0,
          topPriority: "품질 유지와 배송 품질 개선이 핵심"
        }
      }
    },

    // 기존 체크리스트 구조를 새 구조로 변환 (하위 호환성)
    convertLegacyChecklistToNew(legacyData) {
      const checklistItems = []
      let itemIndex = 0

      // 장점(advantages) 변환
      if (legacyData.reviewAnalysis?.advantages?.items) {
        legacyData.reviewAnalysis.advantages.items.forEach(item => {
          const frequencyScore = item.frequency === '높음' ? 85 : item.frequency === '중간' ? 60 : 35
          checklistItems.push({
            category: '장점 유지',
            title: item.point,
            description: item.description,
            score: frequencyScore + (itemIndex * -5),
            priority: frequencyScore >= 80 ? 'critical' : frequencyScore >= 50 ? 'recommended' : 'optional',
            mentionCount: frequencyScore >= 80 ? 20 : frequencyScore >= 50 ? 10 : 5,
            source: item.customerFeedback,
            actionRequired: `${item.point} 기능 유지 필수`
          })
          itemIndex++
        })
      }

      // 단점(disadvantages) 변환
      if (legacyData.reviewAnalysis?.disadvantages?.items) {
        legacyData.reviewAnalysis.disadvantages.items.forEach(item => {
          const frequencyScore = item.frequency === '높음' ? 85 : item.frequency === '중간' ? 60 : 35
          checklistItems.push({
            category: '단점 개선',
            title: item.point,
            description: item.description,
            score: frequencyScore,
            priority: frequencyScore >= 80 ? 'critical' : frequencyScore >= 50 ? 'recommended' : 'optional',
            mentionCount: frequencyScore >= 80 ? 20 : frequencyScore >= 50 ? 10 : 5,
            source: item.customerFeedback,
            actionRequired: `${item.point} 개선 필요`
          })
        })
      }

      // 개선 방향(improvementDirections) 변환
      if (legacyData.improvementDirections?.items) {
        legacyData.improvementDirections.items.forEach(item => {
          const priorityScore = item.priority === 'high' ? 85 : item.priority === 'medium' ? 60 : 35
          checklistItems.push({
            category: '차별화 요소',
            title: item.category,
            description: item.description,
            score: priorityScore,
            priority: priorityScore >= 80 ? 'critical' : priorityScore >= 50 ? 'recommended' : 'optional',
            mentionCount: 0,
            source: '개선 방향 분석',
            actionRequired: item.solution
          })
        })
      }

      // 점수 순으로 정렬
      checklistItems.sort((a, b) => b.score - a.score)

      // 통계 계산
      const criticalCount = checklistItems.filter(i => i.priority === 'critical').length
      const recommendedCount = checklistItems.filter(i => i.priority === 'recommended').length
      const optionalCount = checklistItems.filter(i => i.priority === 'optional').length

      return {
        checklistItems,
        enhancedUSP: legacyData.enhancedUSP || {
          currentUSP: '',
          improvedUSP: '',
          oneLiner: ''
        },
        summary: {
          totalItems: checklistItems.length,
          criticalCount,
          recommendedCount,
          optionalCount,
          topPriority: checklistItems[0]?.title || '분석 결과 없음'
        }
      }
    },

    // IndexedDB에 체크리스트 저장
    async saveChecklistToIndexedDB(checklistData) {
      try {
        // console.log('💾 IndexedDB에 체크리스트 저장 시작')

        // 현재 그룹 데이터 로드
        const existingData = await keywordAnalysisDB.getKeywordGroup(this.currentGroupId)

        if (existingData) {
          // 체크리스트 데이터 추가 (analysisResults 보존)
          const updatedData = {
            ...existingData,
            analysisResults: this.analysisResults, // 현재 메모리 데이터 보존
            sourcingChecklist: checklistData,
            checklistGeneratedAt: new Date().toISOString()
          }

          // 업데이트된 데이터 저장
          await keywordAnalysisDB.saveKeywordGroup(this.currentGroupId, updatedData)

          // console.log('✅ IndexedDB에 체크리스트 저장 완료')
        } else {
          console.log('⚠️ 기존 그룹 데이터를 찾을 수 없음')
        }
      } catch (error) {
        console.error('❌ IndexedDB 체크리스트 저장 실패:', error)
      }
    },

    // IndexedDB에서 체크리스트 로드
    async loadChecklistFromIndexedDB() {
      try {
        const existingData = await keywordAnalysisDB.getKeywordGroup(this.currentGroupId)

        if (existingData && existingData.sourcingChecklist) {
          this.sourcingChecklist = existingData.sourcingChecklist
          // console.log('✅ IndexedDB에서 체크리스트 로드 완료:', existingData.checklistGeneratedAt)
          return true
        }

        return false
      } catch (error) {
        console.error('❌ IndexedDB 체크리스트 로드 실패:', error)
        return false
      }
    },

    // 기존 체크리스트 구조 (하위 호환성)
    getDefaultChecklist() {
      return {
        checklist: {
          productAnalysis: [{
            category: "상품 품질",
            items: [{
              title: "상품 이미지 품질 확인",
              description: "고해상도 이미지, 다양한 각도 촬영 여부 확인",
              priority: "high",
              benchmark: "최소 5장 이상, 1000px 이상 해상도"
            }]
          }]
        },
        summary: {
          totalItems: 1,
          highPriorityItems: 1,
          keyInsights: ["상품 이미지와 가격이 핵심 성공 요인"],
          recommendations: ["고품질 이미지 확보"]
        }
      }
    },

    // 예상 최소 CVR 계산 (popup.js의 calculateMinCVR와 동일)
    calculateExpectedMinCVR() {
      const inputPrice = parseFloat(this.priceInput)

      if (isNaN(inputPrice) || inputPrice <= 0 || this.priceVsCvrData.length === 0) {
        this.expectedMinCVR = '-'
        return
      }

      const minCVR = this.calculateMinCVR(inputPrice, this.priceVsCvrData)
      this.expectedMinCVR = minCVR.toFixed(2) + '%'
    },

    // 최소 CVR 계산 함수 (popup.js와 동일)
    calculateMinCVR(targetPrice, validData) {
      if (validData.length === 0) return 0

      // 가격순으로 정렬된 데이터에서 위치 찾기
      const prices = validData.map(d => d.x)
      const cvrs = validData.map(d => d.y)

      // 가격이 제일 싸다면 전체 CVR 중 최대값
      if (targetPrice <= Math.min(...prices)) {
        return Math.max(...cvrs)
      }

      // 가격이 제일 비싸다면 0을 제외한 전체 CVR 중 최소값
      if (targetPrice >= Math.max(...prices)) {
        const nonZeroCVRs = cvrs.filter(cvr => cvr > 0)
        return nonZeroCVRs.length > 0 ? Math.min(...nonZeroCVRs) : 0
      }

      // 두 가격 사이에 있다면 작은 가격의 CVR을 최소값으로
      for (let i = 0; i < prices.length - 1; i++) {
        if (targetPrice >= prices[i] && targetPrice <= prices[i + 1]) {
          return cvrs[i] // 작은 가격의 CVR
        }
      }

      return 0
    },





    // 기본 summaryMetrics 반환
    getDefaultSummaryMetrics() {
      return {
        medianCTR: '0.00',
        medianCVR: '0.00',
        totalProducts: 0,
        totalImpressions: 0,
        totalClicks: 0,
        totalSales: 0,
        totalAdImpressions: 0,
        totalOrganicImpressions: 0
      }
    },

    // 데이터 크기 포맷팅
    formatDataSize(bytes) {
      // 200B 이하는 0B로 표시 (사용자 오해 방지)
      if (bytes <= 200) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
    },

    // 메모리 사용량 업데이트
    updateMemoryUsage() {
      try {
        // 분석 결과 데이터 크기 계산
        const analysisResultsSize = new Blob([JSON.stringify(this.analysisResults)]).size

        // Top20 트래픽 데이터 크기 계산
        const top20TrafficSize = this.rpiTop20Products.length > 0
          ? new Blob([JSON.stringify(this.rpiTop20Products)]).size
          : 0

        // 차트 옵션 데이터 크기 계산
        const chartOptionsSize = new Blob([JSON.stringify({
          impressionChartOptions: this.impressionChartOptions,
          priceVsCvrChartOptions: this.priceVsCvrChartOptions,
          adCompetitivenessChartOptions: this.adCompetitivenessChartOptions,
          priceRangeSalesChartOptions: this.priceRangeSalesChartOptions,
          dailyTrafficChartOptions: this.dailyTrafficChartOptions
        })]).size

        const totalSize = analysisResultsSize + top20TrafficSize + chartOptionsSize

        this.memoryUsage = {
          totalProducts: this.analysisResults.length,
          estimatedSize: totalSize,
          breakdown: {
            analysisResults: analysisResultsSize,
            top20Traffic: top20TrafficSize,
            chartOptions: chartOptionsSize
          },
          lastUpdated: new Date().toLocaleTimeString()
        }

        // console.log('📊 [메모리 사용량]', {
        //   총크기: this.formatDataSize(totalSize),
        //   분석결과: this.formatDataSize(analysisResultsSize),
        //   Top20트래픽: this.formatDataSize(top20TrafficSize),
        //   차트옵션: this.formatDataSize(chartOptionsSize)
        // })

        // 메모리 사용량이 30MB를 초과하면 경고
        if (totalSize > 30 * 1024 * 1024) {
          console.warn('⚠️ 메모리 사용량이 30MB를 초과했습니다:', this.formatDataSize(totalSize))
        }
      } catch (error) {
        console.error('메모리 사용량 계산 실패:', error)
      }
    },

    // 차트용 최적화된 데이터 추출 (필요한 필드만)
    extractChartData(products, fields) {
      return products.map(product => {
        const extracted = {}
        fields.forEach(field => {
          if (field === 'calculatedRPI') {
            extracted[field] = parseFloat(this.calculateRPI(product))
          } else if (field === 'calculatedCTR') {
            extracted[field] = parseFloat(this.calculateCTR(product))
          } else if (field === 'calculatedCVR') {
            extracted[field] = parseFloat(this.calculateCVR(product))
          } else if (field === 'calculatedImpressions') {
            extracted[field] = this.calculateImpressions(product)
          } else {
            extracted[field] = product[field]
          }
        })
        return extracted
      })
    },

    // Top20 상품의 일별 트래픽 데이터만 별도 저장 (메모리 최적화)
    async saveTop20DailyTrafficData() {
      try {
        if (this.rpiTop20Products.length === 0) return

        // Top20 상품의 일별 트래픽 데이터만 추출
        const top20DailyTrafficData = this.rpiTop20Products.map(product => ({
          vendorItemId: product.vendorItemId,
          productName: product.productName,
          dailyTraffic: product.dailyTraffic || {},
          calculatedRPI: product.calculatedRPI
        }))

        // IndexedDB에 별도 저장 (analysisResults는 건드리지 않음)
        const groupData = await keywordAnalysisDB.getKeywordGroup(this.currentGroupId) || {}

        // 현재 메모리의 analysisResults를 보존
        const updatedGroupData = {
          ...groupData,
          analysisResults: this.analysisResults, // 현재 메모리 데이터 보존
          top20DailyTrafficData: top20DailyTrafficData,
          top20LastUpdated: new Date().toISOString()
        }

        await keywordAnalysisDB.saveKeywordGroup(this.currentGroupId, updatedGroupData)
        // console.log('✅ Top20 일별 트래픽 데이터 저장 완료:', top20DailyTrafficData.length, '개')
      } catch (error) {
        console.error('❌ Top20 일별 트래픽 데이터 저장 실패:', error)
      }
    },

    // 데이터 저장 최적화 제거 - 원본 데이터 그대로 저장
    // (Out of Memory는 차트 렌더링이 원인, 데이터 자체는 문제없음)

    // 메모리 정리 (사용하지 않는 데이터 해제)
    cleanupMemory() {
      try {
        // 차트 옵션 초기화 (메모리 해제)
        this.impressionChartOptions = {}
        this.priceVsCvrChartOptions = {}
        this.adCompetitivenessChartOptions = {}
        this.priceRangeSalesChartOptions = {}
        this.dailyTrafficChartOptions = {}

        // 차트 표시 상태 초기화
        this.showImpressionChart = false
        this.showPriceVsCvrChart = false
        this.showAdCompetitivenessChart = false
        this.showPriceRangeSalesChart = false
        this.showDailyTrafficChart = false

        // 임시 데이터 정리
        this.adCompetitivenessData = []
        this.priceVsCvrData = []

        // RPI Top20 데이터 초기화 (메모리 사용량 문제 해결)
        this.rpiTop20Products = []

        // 가비지 컬렉션 힌트
        if (window.gc) {
          window.gc()
        }

        // console.log('🧹 메모리 정리 완료')
      } catch (error) {
        console.error('메모리 정리 실패:', error)
      }
    },

    // 지연 로딩 제거 - 일별 트래픽 차트가 이미 Top20으로 최적화되어 메모리 문제 없음



    // RPI 경쟁력 차트 (이상치제외 RPI Top20)
    createRpiCompetitivenessChart() {
      this.showRpiCompetitivenessChart = false

      // 미리 계산된 RPI Top20 상품 사용 (이상치제외)
      const sortedProducts = this.top20ByRPI

      if (sortedProducts.length === 0) return

      // RPI Top20 데이터 저장 (다운로드용 + 일별 트래픽 최적화용) - 이미 계산된 값 사용
      this.rpiTop20Products = sortedProducts.map(product => ({
        ...product,
        calculatedRPI: product.calculatedRPI,
        calculatedCTR: product.calculatedCTR,
        calculatedCVR: product.calculatedCVR,
        calculatedImpressions: product.calculatedImpressions
      }))

      // Top20 상품의 일별 트래픽 데이터만 별도 저장 (메모리 최적화)
      this.saveTop20DailyTrafficData()

      const chartData = sortedProducts.map((product, index) => ({
        x: index + 1,
        y: product.calculatedRPI,
        productName: product.productName,
        salePrice: product.price,
        productId: product.productId,
        vendorItemId: product.vendorItemId,
        isComparisonProduct: this.comparisonProduct && product.vendorItemId === this.comparisonProduct.vendorItemId
      }))

      // 비교상품이 TOP20에 없으면 추가
      let comparisonAddedData = null
      if (this.comparisonProduct && !chartData.some(d => d.isComparisonProduct)) {
        // enrichedAnalysisResults에서 먼저 찾고, 없으면 comparisonProduct 자체 데이터 사용
        const comparisonFromResults = this.enrichedAnalysisResults.find(
          p => p.vendorItemId === this.comparisonProduct.vendorItemId
        )
        const comparisonData = comparisonFromResults || this.comparisonProduct

        comparisonAddedData = {
          x: chartData.length + 1,
          y: parseFloat(comparisonData.calculatedRPI || comparisonData.rpi || comparisonData.overall || 0).toFixed(2),
          productName: comparisonData.productName || this.comparisonProduct.productName,
          salePrice: comparisonData.price || this.comparisonProduct.price,
          productId: comparisonData.productId,
          vendorItemId: comparisonData.vendorItemId || this.comparisonProduct.vendorItemId,
          isComparisonProduct: true
        }
        chartData.push(comparisonAddedData)
        console.log('📊 [RPI 차트] 비교상품 추가:', comparisonAddedData)
      }

      this.rpiCompetitivenessChartOptions = {
        series: [{
          name: 'RPI',
          data: chartData
        }],
        chart: {
          type: 'bar',
          height: 400,
          events: {
            dataPointSelection: (event, chartContext, config) => { // eslint-disable-line no-unused-vars
              const dataPointIndex = config.dataPointIndex
              const data = chartData[dataPointIndex]

              if (data && (data.productId || data.vendorItemId)) {
                // 쿠팡 상품 링크 생성
                let productUrl = ''
                if (data.productId) {
                  productUrl = `https://www.coupang.com/vp/products/${data.productId}`
                } else if (data.vendorItemId) {
                  productUrl = `https://www.coupang.com/vp/products/${data.vendorItemId}`
                }

                if (productUrl) {
                  // console.log('🔗 상품 링크 열기:', {
                  //   productName: data.productName,
                  //   url: productUrl
                  // })

                  // 새 탭에서 상품 링크 열기
                  window.open(productUrl, '_blank')
                }
              }
            }
          }
        },
        xaxis: {
          type: 'category',
          categories: chartData.map(item => `${item.x}위`),
          title: {
            text: '순위'
          },
          labels: {
            rotate: -45
          }
        },
        yaxis: {
          title: {
            text: 'RPI 점수'
          }
        },
        tooltip: {
          custom: function({seriesIndex, dataPointIndex, w}) {
            const data = w.globals.initialSeries[seriesIndex].data[dataPointIndex]
            const comparisonBadge = data.isComparisonProduct ? '<span class="badge bg-warning text-dark mb-1">⚖️ 비교상품</span><br>' : ''
            return `<div class="p-2">
              ${comparisonBadge}
              <strong>${data.productName}</strong><br>
              순위: ${data.x}위<br>
              RPI: ${data.y}<br>
              판매가: ${parseInt(data.salePrice).toLocaleString()}원<br>
              <small class="text-primary">🔗 클릭하면 상품 페이지로 이동합니다</small>
            </div>`
          }
        },
        colors: [function({ dataPointIndex }) {
          return chartData[dataPointIndex]?.isComparisonProduct ? '#ffc107' : '#28a745'
        }],
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '70%',
            endingShape: 'rounded',
            distributed: true
          }
        },
        legend: {
          show: false // distributed: true일 때 범례 숨김
        },
        dataLabels: {
          enabled: false
        },
        states: {
          hover: {
            filter: {
              type: 'lighten',
              value: 0.1
            }
          },
          active: {
            allowMultipleDataPointsSelection: false,
            filter: {
              type: 'darken',
              value: 0.1
            }
          }
        }
      }

      setTimeout(() => {
        this.showRpiCompetitivenessChart = true
      }, 100)
    },

    // 광고경쟁력 vs 광고노출비율 차트 (이상치포함 판매가×CVR Top300)
    createAdCompetitivenessChart() {
      this.showAdCompetitivenessChart = false

      // 미리 계산된 판매가×CVR Top300 상품 사용
      const products = this.top300ByPriceCvr

      if (products.length === 0) {
        // console.log('광고경쟁력 분석을 위한 데이터가 없습니다.')
        return
      }

      // 차트 데이터 생성 (미리 계산된 값 사용)
      this.adCompetitivenessData = products.map(product => {
        // 광고경쟁력 계산 (가격 × CVR × 0.01)
        const price = product.price || 0
        const cvr = product.calculatedCVR
        const competitiveness = price * cvr * 0.01

        // 광고노출비율 계산 (미리 계산된 값 사용)
        const adRatio = product.calculatedAdRatio

        // 최소ROAS 기준선 아래인지 확인
        const isBelowMinROAS = competitiveness < this.minROAS

        // 비교상품 여부 확인
        const isComparisonProduct = this.comparisonProduct && product.vendorItemId === this.comparisonProduct.vendorItemId

        return {
          productName: product.productName.length > 15 ?
            product.productName.substring(0, 15) + '...' :
            product.productName,
          competitiveness: competitiveness,
          adRatio: adRatio,
          price: price,
          cvr: cvr,
          impressions: this.calculateImpressions(product),
          isBelowMinROAS: isBelowMinROAS,
          isComparisonProduct: isComparisonProduct,
          vendorItemId: product.vendorItemId
        }
      })

      // 비교상품이 TOP300에 없으면 추가 (enrichedAnalysisResults에 없어도 comparisonProduct 사용)
      if (this.comparisonProduct && !this.adCompetitivenessData.some(d => d.isComparisonProduct)) {
        const comparisonFromResults = this.enrichedAnalysisResults.find(
          p => p.vendorItemId === this.comparisonProduct.vendorItemId
        )
        const comparisonData = comparisonFromResults || this.comparisonProduct

        const price = comparisonData.price || this.comparisonProduct.price || 0
        const cvr = parseFloat(comparisonData.cvr || this.comparisonProduct.cvr) || 0
        const competitiveness = price * cvr * 0.01
        const adRatio = comparisonData.calculatedAdRatio || 0
        const productName = comparisonData.productName || this.comparisonProduct.productName || ''

        this.adCompetitivenessData.push({
          productName: productName.length > 15 ? productName.substring(0, 15) + '...' : productName,
          competitiveness: competitiveness,
          adRatio: adRatio,
          price: price,
          cvr: cvr,
          impressions: this.calculateImpressions(comparisonData) || this.comparisonProduct._debug?.totalImpressions || 0,
          isBelowMinROAS: competitiveness < this.minROAS,
          isComparisonProduct: true,
          vendorItemId: comparisonData.vendorItemId || this.comparisonProduct.vendorItemId
        })
        console.log('📊 [광고경쟁력 차트] 비교상품 추가:', productName)
      }

      // 정렬 (노출수 기준 또는 광고경쟁력 기준)
      if (this.sortByImpression) {
        this.adCompetitivenessData.sort((a, b) => b.impressions - a.impressions)
      } else {
        this.adCompetitivenessData.sort((a, b) => b.competitiveness - a.competitiveness)
      }

      this.updateAdCompetitivenessChartOptions()

      setTimeout(() => {
        this.showAdCompetitivenessChart = true
      }, 100)
    },

    // 광고경쟁력 차트 옵션 업데이트 (popup.js와 동일)
    updateAdCompetitivenessChartOptions() {
      if (this.adCompetitivenessData.length === 0) return

      this.adCompetitivenessChartOptions = {
        series: [
          {
            name: '광고경쟁력 (ROAS %)',
            type: 'line',
            data: this.adCompetitivenessData.map(d => d.competitiveness),
            yAxisIndex: 0,
            markers: {
              size: this.adCompetitivenessData.map(d => d.isComparisonProduct ? 8 : 3),
              strokeWidth: 1,
              hover: {
                size: 7
              },
              colors: this.adCompetitivenessData.map(d => {
                if (d.isComparisonProduct) return '#ffc107' // 비교상품: 노란색
                if (d.isBelowMinROAS) return '#DC3545' // 최소ROAS 미만: 빨간색
                return '#007BFF' // 기본: 파란색
              })
            }
          },
          {
            name: '광고노출비율 (%)',
            type: 'line',
            data: this.adCompetitivenessData.map(d => d.adRatio),
            yAxisIndex: 1,
            markers: {
              size: 0
            }
          }
        ],
        chart: {
          type: 'line',
          height: 400,
          zoom: {
            enabled: true,
            type: 'xy'
          }
        },
        colors: ['#007BFF', '#00E396'], // 광고경쟁력: 파란색, 광고노출비율: 녹색
        stroke: {
          curve: 'smooth',
          width: [2, 2]
        },
        // 최소ROAS 기준선 추가
        annotations: {
          yaxis: [
            {
              y: this.minROAS,
              yAxisIndex: 0, // 광고경쟁력 축에 표시
              borderColor: '#28a745',
              borderWidth: 2,
              strokeDashArray: 0,
              opacity: 0.7,
              label: {
                text: `최소ROAS: ${this.minROAS}%`,
                style: {
                  color: '#28a745',
                  background: '#ffffff',
                  fontSize: '11px',
                  fontWeight: 'bold'
                },
                position: 'left',
                offsetX: 10
              }
            }
          ]
        },
        xaxis: {
          categories: this.adCompetitivenessData.map(d =>
            d.productName.length > 8 ? d.productName.substring(0, 8) + '...' : d.productName
          ),
          labels: {
            rotate: -90,
            style: {
              fontSize: '8px'
            }
          }
        },
        yaxis: [
          {
            title: {
              text: '광고경쟁력 (ROAS %)',
              style: {
                color: '#007BFF'
              }
            },
            labels: {
              formatter: function(value) {
                return value !== null && value !== undefined ? value.toFixed(1) + '%' : ''
              },
              style: {
                colors: '#007BFF'
              }
            },
            min: 0,
            forceNiceScale: true
          },
          {
            opposite: true,
            title: {
              text: '광고노출비율 (%)',
              style: {
                color: '#00E396'
              }
            },
            labels: {
              formatter: function(value) {
                return value !== null && value !== undefined ? value.toFixed(1) + '%' : ''
              },
              style: {
                colors: '#00E396'
              }
            },
            min: 0,
            max: 100,
            forceNiceScale: false
          }
        ],
        tooltip: {
          custom: ({ series, seriesIndex, dataPointIndex }) => {
            const data = this.adCompetitivenessData[dataPointIndex]
            const productName = data.productName

            if (seriesIndex === 0) {
              // 광고경쟁력 툴팁
              const competitivenessValue = series[0][dataPointIndex]
              const warningText = data.isBelowMinROAS ?
                '<div style="color: #DC3545; font-weight: bold; margin-top: 5px;">⚠️ 최소ROAS 기준 미달</div>' : ''

              return `
                <div class="p-2" style="background: white; border: 1px solid #ccc; border-radius: 4px; min-width: 200px;">
                  <div><strong>${productName}</strong></div>
                  <div>광고경쟁력: <strong>${competitivenessValue.toFixed(2)}%</strong></div>
                  <div>가격: ${data.price.toLocaleString()}원</div>
                  <div>CVR: ${data.cvr.toFixed(2)}%</div>
                  ${warningText}
                </div>
              `
            } else if (seriesIndex === 1) {
              // 광고노출비율 툴팁
              return `
                <div class="p-2" style="background: white; border: 1px solid #ccc; border-radius: 4px; min-width: 200px;">
                  <div><strong>${productName}</strong></div>
                  <div>광고노출비율: <strong>${series[1][dataPointIndex].toFixed(1)}%</strong></div>
                  <div>총 노출수: ${data.impressions.toLocaleString()}</div>
                </div>
              `
            }
          }
        },
        legend: {
          position: 'top'
        }
      }
    },

    // 차트 업데이트 (정렬 변경 시)
    updateAdCompetitivenessChart() {
      if (this.adCompetitivenessData.length === 0) return

      // 정렬 다시 적용
      if (this.sortByImpression) {
        this.adCompetitivenessData.sort((a, b) => b.impressions - a.impressions)
      } else {
        this.adCompetitivenessData.sort((a, b) => b.competitiveness - a.competitiveness)
      }

      this.updateAdCompetitivenessChartOptions()
    },

    // 마진율에서 최소ROAS 계산 (popup.js와 동일)
    updateMinROASFromMargin() {
      const marginRate = parseFloat(this.marginRate) || 30

      if (marginRate > 0 && marginRate <= 100) {
        const calculatedMinROAS = Math.round((100 / marginRate) * 100) // (100 / 마진율) × 100
        this.minROAS = calculatedMinROAS

        // 차트 업데이트
        this.updateAdCompetitivenessChartIfExists()
      }
    },

    // 최소ROAS에서 마진율 계산 (popup.js와 동일)
    updateMarginFromROAS() {
      const minROAS = parseFloat(this.minROAS) || 333

      if (minROAS > 0) {
        const calculatedMarginRate = Math.round((10000 / minROAS) * 100) / 100 // (100 / (ROAS/100)) = 10000/ROAS

        if (calculatedMarginRate > 0 && calculatedMarginRate <= 100) {
          this.marginRate = calculatedMarginRate
        }

        // 차트 업데이트
        this.updateAdCompetitivenessChartIfExists()
      }
    },

    // 차트가 존재할 때만 업데이트
    updateAdCompetitivenessChartIfExists() {
      if (this.showAdCompetitivenessChart && this.adCompetitivenessData.length > 0) {
        // isBelowMinROAS 재계산
        this.adCompetitivenessData.forEach(item => {
          item.isBelowMinROAS = item.competitiveness < this.minROAS
        })

        this.updateAdCompetitivenessChartOptions()
      }
    },

    // 가격대별 매출추이 차트 (popup.js와 동일)
    createPriceRangeSalesChart() {
      this.showPriceRangeSalesChart = false

      // popup.js와 동일한 필터링: 가격, 판매량, CVR이 모두 0보다 큰 경우
      const validProducts = this.analysisResults.filter(product =>
        product.price > 0 &&
        product.salesLast28d > 0 &&
        parseFloat(product.cvr) > 0 &&
        !isNaN(product.price) &&
        !isNaN(product.salesLast28d) &&
        !isNaN(parseFloat(product.cvr))
      )

      if (validProducts.length === 0) {
        // console.log('가격대별 분석을 위한 데이터가 없습니다.')
        return
      }

      // popup.js와 동일한 가격대별 데이터 집계 (1000원 단위)
      const priceRangeData = new Map()

      validProducts.forEach(product => {
        const priceRange = Math.floor(product.price / 1000) * 1000 // 1000원 단위로 그룹화
        const sales = product.salesLast28d * product.price // 매출 = 판매량 × 가격

        if (!priceRangeData.has(priceRange)) {
          priceRangeData.set(priceRange, {
            totalSales: 0,
            productCount: 0,
            totalCVR: 0
          })
        }

        const current = priceRangeData.get(priceRange)
        current.totalSales += sales
        current.productCount += 1
        current.totalCVR += parseFloat(product.cvr)
      })

      // popup.js와 동일한 데이터 정렬 및 변환
      const sortedData = Array.from(priceRangeData.entries())
        .map(([priceRange, data]) => ({
          priceRange: priceRange,
          priceRangeLabel: `${priceRange.toLocaleString()}원~${(priceRange + 999).toLocaleString()}원`,
          totalSales: data.totalSales,
          productCount: data.productCount,
          avgSales: data.totalSales / data.productCount,
          avgCVR: data.totalCVR / data.productCount
        }))
        .sort((a, b) => a.priceRange - b.priceRange)

      // console.log('가격대별 분석 데이터:', sortedData)

      // 비교상품 가격대 인덱스 찾기
      let comparisonPriceRangeIndex = -1
      if (this.comparisonProduct && this.comparisonProduct.price > 0) {
        const comparisonPriceRange = Math.floor(this.comparisonProduct.price / 1000) * 1000
        comparisonPriceRangeIndex = sortedData.findIndex(d => d.priceRange === comparisonPriceRange)
      }

      // 비교상품 annotation 설정
      const annotations = comparisonPriceRangeIndex >= 0 ? {
        xaxis: [{
          x: sortedData[comparisonPriceRangeIndex].priceRangeLabel,
          borderColor: '#ffc107',
          strokeDashArray: 0,
          label: {
            borderColor: '#ffc107',
            style: {
              color: '#333',
              background: '#ffc107'
            },
            text: '⚖️ 비교상품'
          }
        }]
      } : {}

      this.priceRangeSalesChartOptions = {
        series: [
          {
            name: '총 매출',
            type: 'line',
            yAxisIndex: 0,
            data: sortedData.map(d => d.totalSales)
          },
          {
            name: '평균 매출',
            type: 'line',
            yAxisIndex: 0,
            data: sortedData.map(d => d.avgSales)
          },
          {
            name: '평균 CVR',
            type: 'line',
            yAxisIndex: 1,
            data: sortedData.map(d => d.avgCVR)
          }
        ],
        chart: {
          type: 'line',
          height: 400,
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: false,
              zoom: true,
              zoomin: true,
              zoomout: true,
              pan: false,
              reset: true
            }
          }
        },
        annotations: annotations,
        colors: ['#FF6B35', '#FFA726', '#00E396'],
        stroke: {
          curve: 'smooth',
          width: 3
        },
        markers: {
          size: 5,
          hover: {
            size: 7
          }
        },
        xaxis: {
          categories: sortedData.map(d => d.priceRangeLabel),
          title: {
            text: '가격대'
          },
          labels: {
            rotate: -45,
            style: {
              fontSize: '10px'
            }
          }
        },
        yaxis: [
          {
            title: {
              text: '총 매출 (원)',
              style: {
                color: '#FF6B35'
              }
            },
            labels: {
              formatter: function(value) {
                return value.toLocaleString() + '원'
              },
              style: {
                colors: '#FF6B35'
              }
            }
          },
          {
            opposite: true,
            title: {
              text: '평균 CVR (%)',
              style: {
                color: '#00E396'
              }
            },
            labels: {
              formatter: function(value) {
                return value.toFixed(1) + '%'
              },
              style: {
                colors: '#00E396'
              }
            }
          }
        ],
        tooltip: {
          shared: true,
          intersect: false,
          custom: function({ series, dataPointIndex }) {
            const priceRangeLabel = sortedData[dataPointIndex].priceRangeLabel
            const totalSales = series[0][dataPointIndex]
            const avgSales = series[1][dataPointIndex]
            const avgCVR = series[2][dataPointIndex]
            const productCount = sortedData[dataPointIndex].productCount

            return `
              <div class="apexcharts-tooltip-custom" style="padding: 10px; background: white; border: 1px solid #e0e0e0; border-radius: 4px;">
                <div style="font-weight: bold; margin-bottom: 5px;">${priceRangeLabel}</div>
                <div style="color: #FF6B35; margin-bottom: 3px;">
                  <span style="display: inline-block; width: 10px; height: 10px; background: #FF6B35; border-radius: 50%; margin-right: 5px;"></span>
                  총 매출: ${totalSales.toLocaleString()}원
                </div>
                <div style="color: #FFA726; margin-bottom: 3px;">
                  <span style="display: inline-block; width: 10px; height: 10px; background: #FFA726; border-radius: 50%; margin-right: 5px;"></span>
                  평균 매출: ${avgSales.toLocaleString()}원
                </div>
                <div style="color: #00E396; margin-bottom: 3px;">
                  <span style="display: inline-block; width: 10px; height: 10px; background: #00E396; border-radius: 50%; margin-right: 5px;"></span>
                  평균 CVR: ${avgCVR.toFixed(2)}%
                </div>
                <div style="color: #666; margin-bottom: 3px;">
                  <span style="display: inline-block; width: 10px; height: 10px; background: #666; border-radius: 50%; margin-right: 5px;"></span>
                  상품수: ${productCount}개
                </div>
              </div>
            `
          }
        },
        legend: {
          position: 'top'
        }
      }

      setTimeout(() => {
        this.showPriceRangeSalesChart = true
      }, 100)
    },

    // 일별 트래픽 차트 (이상치포함 총노출수 Top20)
    createDailyTrafficChart() {
      this.showDailyTrafficChart = false

      // 미리 계산된 총노출수 Top20 상품 사용
      const topProducts = this.top20ByImpressions.filter(p => p.dailyTraffic)

      if (topProducts.length === 0) {
        // console.log('📊 [일별 트래픽 차트] 일별 트래픽 데이터가 있는 상품이 없습니다.')
        return
      }

      // console.log(`📊 [일별 트래픽 차트] ${topProducts.length}개 상품으로 차트 생성 (메모리 최적화)`)

      // Top20 상품의 일별 트래픽 데이터 처리
      const productsWithDailyData = topProducts.map(product => {
        // dailyTraffic이 없거나 유효하지 않은 경우 빈 객체로 처리
        if (!product.dailyTraffic || typeof product.dailyTraffic !== 'object') {
          return {
            ...product,
            dailyTraffic: {},
            hasLimitedData: true
          }
        }

        // 유효한 날짜 데이터만 필터링
        const validDailyTraffic = {}
        let hasValidData = false

        Object.entries(product.dailyTraffic).forEach(([date, data]) => {
          if (data !== null && data !== undefined && (
            (Array.isArray(data) && data.length > 0) ||
            (typeof data === 'object' && data.impression !== undefined)
          )) {
            validDailyTraffic[date] = data
            hasValidData = true
          }
        })

        return {
          ...product,
          dailyTraffic: validDailyTraffic,
          hasLimitedData: !hasValidData
        }
      })

      // 전체적으로 일별 트래픽 데이터가 없는 경우에만 차트 생성 중단
      const productsWithValidData = productsWithDailyData.filter(p => !p.hasLimitedData)
      if (productsWithValidData.length === 0) {
        // console.log('📊 [차트 생성] 모든 상품의 일별 트래픽 데이터가 부족하여 차트를 생성하지 않습니다.')
        return
      }

      // 데이터가 제한적인 상품 수 확인 및 로깅
      // const productsWithLimitedData = productsWithDailyData.filter(p => p.hasLimitedData).length
      // if (productsWithLimitedData > 0) {
      //   console.log(`📊 [차트 생성] ${productsWithLimitedData}개 상품은 일별 트래픽 데이터가 제한적입니다. (최근 등록 상품일 가능성, 데이터가 있는 날짜만 표시됩니다)`)
      // }

      // popup.js와 동일: 모든 날짜 수집 및 정렬 (null 값 제외)
      const allDates = new Set()
      productsWithDailyData.forEach(product => {
        Object.entries(product.dailyTraffic).forEach(([date, data]) => {
          // 유효한 데이터가 있는 날짜만 추가
          if (data !== null && data !== undefined) {
            allDates.add(date)
          }
        })
      })
      const sortedDates = Array.from(allDates).sort((a, b) => new Date(a) - new Date(b))

      // popup.js와 동일: 상품별 시리즈 데이터 생성 (총 노출수 기준 정렬)
      const sortedProductsByImpressions = productsWithDailyData
        .map(product => {
          const totalImpressions = Object.entries(product.dailyTraffic).reduce((sum, [, dailyDataArray]) => {
            // null 또는 undefined 체크 추가
            if (!dailyDataArray) {
              return sum
            }
            if (Array.isArray(dailyDataArray)) {
              return sum + dailyDataArray.reduce((daySum, item) => daySum + (item?.impression || 0), 0)
            }
            return sum + (dailyDataArray.impression || 0)
          }, 0)
          return { ...product, totalImpressions }
        })
        .sort((a, b) => b.totalImpressions - a.totalImpressions)

      const series = sortedProductsByImpressions.map(product => {
        const data = sortedDates.map(date => {
          const dailyData = product.dailyTraffic[date]
          // null 또는 undefined 체크 강화
          if (!dailyData) {
            return 0
          }
          if (Array.isArray(dailyData)) {
            return dailyData.reduce((sum, item) => sum + (item?.impression || 0), 0)
          } else if (dailyData && typeof dailyData === 'object') {
            return dailyData.impression || 0
          }
          return 0
        })

        // 데이터가 제한적인 상품은 이름에 표시
        const productName = product.productName.length > 20 ?
          product.productName.substring(0, 20) + '...' :
          product.productName

        const displayName = product.hasLimitedData ?
          `${productName} (제한적)` :
          productName

        return {
          name: displayName,
          data: data
        }
      })

      this.dailyTrafficChartOptions = {
        series: series,
        chart: {
          type: 'line',
          height: 700,
          zoom: {
            enabled: true
          }
        },
        markers: {
          size: 0  // 표식 제거
        },
        stroke: {
          width: 2,  // 라인 두께
          curve: 'smooth'  // 부드러운 곡선
        },
        xaxis: {
          categories: sortedDates,
          title: {
            text: '날짜'
          },
          labels: {
            rotate: -45,
            style: {
              fontSize: '10px'
            }
          }
        },
        yaxis: {
          title: {
            text: '일별 노출수'
          },
          labels: {
            formatter: function(val) {
              return parseInt(val).toLocaleString()
            }
          }
        },
        tooltip: {
          x: {
            format: 'yyyy-MM-dd'
          },
          y: {
            formatter: function(val) {
              return parseInt(val).toLocaleString() + ' 노출'
            }
          }
        },
        legend: {
          position: 'top'
        },
        colors: ['#008FFB', '#00E396', '#FEB019', '#FF4560', '#775DD0']
      }

      setTimeout(() => {
        this.showDailyTrafficChart = true
      }, 100)
    },





    // 통합 키워드 분석 테이블 CSV 다운로드
    downloadSummaryCSV() {
      // 현재 활성화된 테이블 데이터 사용 (필터링 적용된 데이터)
      const currentTableData = this.integratedKeywordsForTable

      const optimalCategoryName = this.optimizedCategoryInfo?.categoryName

      // console.log('🔍 CSV 다운로드 시도:', {
      //   originalLength: this.integratedKeywords.length,
      //   filteredLength: currentTableData.length,
      //   showOptimalCategoryOnly: this.showOptimalCategoryOnly,
      //   optimalCategoryName: optimalCategoryName,
      //   summaryDataLength: this.summaryData.length
      // })

      if (currentTableData.length === 0) {
        alert('다운로드할 키워드 분석 데이터가 없습니다.')
        return
      }

      // CSV 헤더 (현재 테이블의 모든 컬럼 포함)
      const headers = [
        '키워드', '총 노출수', '자연노출수', '광고노출수', '총 클릭수', '노출 상품수',
        '주요 카테고리', '카테고리적합도', '총 판매량', '총매출', '최대매출', '평균매출', '중간매출',
        '매출점유율', '일매출 가이드라인', '가격대(25%~75%)', '접근성점수',
        '최대 RPI', '평균 RPI', '중간 RPI', '최소 RPI',
        '상위랭킹 최대RPI', '상위랭킹 평균RPI', '상위랭킹 중간RPI', '상위랭킹 최소RPI',
        '최대 CTR', '평균 CTR', '중간 CTR', '최소 CTR',
        '최대 CVR', '평균 CVR', '중간 CVR', '최소 CVR'
      ]

      // CSV 데이터 생성 (현재 활성화된 테이블 데이터 사용)
      const csvData = currentTableData.map(item => [
        `"${item.keyword || ''}"`,
        item.totalVolume || 0,
        item.organicImpressions === null || item.organicImpressions === undefined ? '-' : item.organicImpressions,
        item.adImpressions === null || item.adImpressions === undefined ? '-' : item.adImpressions,
        item.totalClicks || 0,
        item.productCount || 0,
        `"${item.mainCategory || ''}"`,
        `${item.categoryFitness || 0}%`,
        item.totalSalesCount || 0,
        item.totalGmv || 0,
        item.maxSalesAmount || 0,
        item.avgSalesAmount || 0,
        item.medianSalesAmount || 0,
        `${item.revenueShare || 0}%`,
        `"${item.revenueShareBasedRevenueRange || ''}"`,
        `"${item.priceRange25to75 || ''}"`,
        item.accessibilityScore || 0,
        item.maxRpi || 0,
        item.avgRpi || 0,
        item.medianRpi || 0,
        item.minRpi || 0,
        item.topRankMaxRpi || 0,
        item.topRankAvgRpi || 0,
        item.topRankMedianRpi || 0,
        item.topRankMinRpi || 0,
        item.maxCtr || 0,
        item.avgCtr || 0,
        item.medianCtr || 0,
        item.minCtr || 0,
        item.maxCvr || 0,
        item.avgCvr || 0,
        item.medianCvr || 0,
        item.minCvr || 0
      ])

      // 요약 통계 추가 (구분선 포함)
      const summarySection = [
        [''], // 빈 줄
        ['=== 요약 통계 ==='],
        ['지표', '값'],
        ...this.summaryData.map(item => [
          `"${item.metric}"`,
          `"${item.value}"`
        ])
      ]

      // CSV 문자열 생성
      const csvContent = [
        headers.join(','),
        ...csvData.map(row => row.join(',')),
        ...summarySection.map(row => row.join(','))
      ].join('\n')

      // 파일 다운로드
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)

      // 파일명에 필터링 상태 반영
      const filterSuffix = this.showOptimalCategoryOnly && optimalCategoryName ?
        `_최적카테고리(${optimalCategoryName})` : ''
      const fileName = `통합키워드분석_${this.currentGroupId}${filterSuffix}_${new Date().toISOString().slice(0, 10)}.csv`

      link.setAttribute('href', url)
      link.setAttribute('download', fileName)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },

    // 상품 데이터 CSV 다운로드 (전체 데이터 + 이상치 분석 포함)
    downloadCSV() {
      console.log(this.analysisResults)
      if (this.analysisResults.length === 0) {
        alert('다운로드할 데이터가 없습니다.')
        return
      }

      // 이상치 분석을 위해 전체 데이터에 이상치 플래그 추가
      const dataWithOutlierAnalysis = this.addOutlierAnalysisToAllData()

      // 모든 상품의 공통 날짜 찾기
      const allDates = new Set()
      dataWithOutlierAnalysis.forEach(item => {
        if (item.dailyTraffic && typeof item.dailyTraffic === 'object') {
          Object.keys(item.dailyTraffic).forEach(date => {
            allDates.add(date)
          })
        }
      })

      // 날짜 정렬 (오름차순)
      const sortedDates = Array.from(allDates).sort()
      // console.log(`📅 일별 트래픽 데이터: ${sortedDates.length}개 날짜 (${sortedDates[0]} ~ ${sortedDates[sortedDates.length - 1]})`)

      // 트래픽 키워드 헤더 생성 (최대 10개)
      const trafficKeywordHeaders = []
      for (let i = 1; i <= 10; i++) {
        trafficKeywordHeaders.push(
          `키워드${i}`,
          `키워드${i}(전체노출수)`,
          `키워드${i}(자연노출수)`,
          `키워드${i}(광고노출수)`,
          `키워드${i}(클릭수)`,
          `키워드${i}(CTR)`,
          `키워드${i}(CTR이상치)`
        )
      }

      // 일별 트래픽 헤더 생성
      const dailyTrafficHeaders = sortedDates.map(date => `${date}(노출수)`)

      // console.log(`📊 총 컬럼 수: 기본(23) + 키워드(60) + 일별트래픽(${dailyTrafficHeaders.length}) = ${23 + 60 + dailyTrafficHeaders.length}개`)

      // CSV 헤더 (inputData.json 구조 참고하여 확장)
      const headers = [
        // 기본 상품 정보
        '순위', '타입', '상품명', '노출ID', '옵션ID', '판매가', '평점', '리뷰수', '브랜드명',
        // 카테고리 정보
        '카테고리명',
        // 성과 지표
        'CTR', 'CVR', 'RPI', '월판매량', '월조회수', '광고비율',
        // 수집 정보
        '수집키워드', '수집페이지', '수집일시',
        // 이상치 분석
        '이상치여부', '가격CVR이상치', '가격CTR이상치', '트래픽변화이상치', 'CTR이상치', 'RPI이상치', '사용자이상치적용', '이상치사유',
        // 키워드 수준 CTR 이상치
        '이상키워드수', '이상키워드사유',
        // 추가 분석 지표
        '시선집중도', '총노출수', '총클릭수',

        // 트래픽 키워드 (10개 × 6컬럼 = 60개 컬럼)
        ...trafficKeywordHeaders,
        // 일별 트래픽 (날짜별 노출수)
        ...dailyTrafficHeaders,
      ]

      // CSV 데이터 생성
      const csvData = dataWithOutlierAnalysis.map(item => {
        // 이상치 사유 조합
        const outlierReasons = []
        if (item.isPriceXCvrOutlier) outlierReasons.push('가격×CVR 이상치')
        if (item.isPriceXCtrOutlier) outlierReasons.push('가격×CTR 이상치')
        if (item.isTrafficVariationOutlier) outlierReasons.push('트래픽변화 이상치')
        if (item.isCTROutlier) outlierReasons.push('CTR 이상치')
        if (item.isRPIOutlier) outlierReasons.push('RPI 이상치')
        if (item.allKeywordsOutlier) outlierReasons.push('전키워드이상치(신뢰데이터없음)')
        if (item.isUserOutlierApplied) outlierReasons.push('사용자이상치적용')

        const isOutlier = outlierReasons.length > 0
        const outlierReason = outlierReasons.join('; ')

        // 트래픽 키워드 데이터 추출 (최대 10개)
        const trafficKeywordData = []
        const trafficKeywords = item.trafficKeyword || []
        const outlierKeywordIndices = item.outlierKeywordIndices || []

        for (let i = 0; i < 10; i++) {
          if (i < trafficKeywords.length) {
            const keyword = trafficKeywords[i]
            const keywordDetail = keyword.keywordDetail || {}
            const keywordMetric = keyword.keywordMetric || {}

            // 키워드명 (keywordDetail에서)
            const keywordName = keywordDetail.keyword || ''

            // 노출수 계산 (keywordMetric에서)
            const totalImpressions = keywordMetric.impression || 0
            const organicImpressions = keywordMetric.organicImpression || 0
            const adImpressions = keywordMetric.adImpression || 0

            // 클릭수 계산 (keywordMetric의 srpClick 사용)
            const clicks = keywordMetric.srpClick || 0

            // CTR 계산 (keywordMetric에 이미 있는 값 사용)
            const ctr = keywordMetric.ctr ? keywordMetric.ctr.toFixed(2) : '0.00'

            // 키워드 CTR 이상치 여부
            const isKeywordOutlier = outlierKeywordIndices.includes(i)

            trafficKeywordData.push(
              `"${keywordName}"`,                     // 키워드명
              totalImpressions,                       // 전체노출수
              organicImpressions,                     // 자연노출수
              adImpressions,                          // 광고노출수
              clicks,                                 // 클릭수
              ctr,                                    // CTR
              isKeywordOutlier ? 'Y' : 'N'            // CTR이상치
            )
          } else {
            // 빈 키워드 슬롯
            trafficKeywordData.push('', 0, 0, 0, 0, '0.00', '')
          }
        }

        // 일별 트래픽 데이터 추출
        const dailyTrafficData = []
        const dailyTraffic = item.dailyTraffic || {}

        sortedDates.forEach(date => {
          const dayData = dailyTraffic[date]
          let impressions = 0

          if (Array.isArray(dayData) && dayData.length > 0) {
            // 해당 날짜의 첫 번째 데이터에서 impression 추출
            impressions = dayData[0].impression || 0
          }

          dailyTrafficData.push(impressions)
        })

        return [
          // 기본 상품 정보
          item.rank || '',
          item.type == 'jet' ? '로켓그로스' : (item.type == 'rocket' ? '로켓배송' : '마켓플레이스'),
          `"${item.productName || ''}"`,
          item.productId || '',
          (item.vendorItemIdMissing ? '' : item.vendorItemId) || '',
          // 판매가 없음(price·salePrice 둘 다 null) = 빈 칸 — 0원이 아님(keywordCollect 엑셀과 같은 규칙)
          item.price || item.salePrice || (item.price == null && item.salePrice == null ? '' : 0),
          item.rating || 0,
          item.ratingcount || 0,
          `"${item.brandName || ''}"`,
          // 카테고리 정보
          `"${item.categoryName || ''}"`,
          // 성과 지표
          this.calculateCTR(item),
          this.calculateCVR(item),
          this.calculateRPI(item),
          item.salesLast28d || 0,
          item.pvLast28Day || 0,
          this.calculateAdRatio(item),
          // 수집 정보
          `"${item.sourceKeyword || ''}"`,
          item.sourcePage || '',
          item.collectedAt || new Date().toISOString(),
          // 이상치 분석
          isOutlier ? 'Y' : 'N',
          item.isPriceXCvrOutlier ? 'Y' : 'N',
          item.isPriceXCtrOutlier ? 'Y' : 'N',
          item.isTrafficVariationOutlier ? 'Y' : 'N',
          item.isCTROutlier ? 'Y' : 'N',
          item.isRPIOutlier ? 'Y' : 'N',
          item.isUserOutlierApplied ? 'Y' : 'N',
          `"${outlierReason}"`,
          // 키워드 수준 CTR 이상치
          item.keywordCTROutlierCount || 0,
          `"${item.keywordCTROutlierReason || ''}"`,
          // 추가 분석 지표
          this.calculateAttentionScore(item),
          this.calculateImpressions(item),
          this.calculateClicks(item),

          // 트래픽 키워드 (10개 × 6컬럼)
          ...trafficKeywordData,
          // 일별 트래픽 (날짜별 노출수)
          ...dailyTrafficData,
        ]
      })

      // CSV 문자열 생성
      const csvContent = [headers.join(','), ...csvData.map(row => row.join(','))].join('\n')

      // 파일 다운로드
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', `키워드_상품분석_전체데이터_${this.activeKeywordGroup}_${new Date().toISOString().slice(0, 10)}.csv`)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },

      // const outlierCount = dataWithOutlierAnalysis.filter(item =>
      //   item.isPriceXCvrOutlier || item.isTrafficVariationOutlier || item.isRPIOutlier
      // ).length

      // console.log(`✅ CSV 다운로드 완료: ${dataWithOutlierAnalysis.length}개 상품 (이상치: ${outlierCount}개)`)

    // 수동 광고 키워드 CSV 다운로드 (전체 상품의 키워드 집계: 노출수>=1)
    downloadManualAdKeywordsCSV() {
      if (!this.analysisResults || this.analysisResults.length === 0) {
        alert('다운로드할 데이터가 없습니다.')
        return
      }

      // 키워드별 집계: 총노출수(impression), 총클릭수(srpClick), 카테고리별 노출 합계
      const keywordMap = new Map()

      this.analysisResults.forEach(item => {
        const tks = Array.isArray(item.trafficKeyword) ? item.trafficKeyword : []
        const category = item.categoryName || '기타'
        tks.forEach(tk => {
          const name = (tk.keywordDetail && tk.keywordDetail.keyword) ? tk.keywordDetail.keyword : (tk.keyword || '')
          if (!name) return
          const metric = tk.keywordMetric || {}
          const imp = Number(metric.impression || 0)
          const clk = Number(metric.srpClick || 0)

          if (!keywordMap.has(name)) {
            keywordMap.set(name, { keyword: name, impressions: 0, clicks: 0, catImpressions: {} })
          }
          const agg = keywordMap.get(name)
          agg.impressions += imp
          agg.clicks += clk
          // 카테고리별 노출 합산
          if (!agg.catImpressions[category]) agg.catImpressions[category] = 0
          agg.catImpressions[category] += imp
        })
      })

      // 필터링: 총노출수 >= 1, 정렬: 총노출수 desc, 클릭수 desc
      const rows = Array.from(keywordMap.values())
        .filter(r => (r.impressions || 0) >= 1)
        .map(r => {
          // 대표 카테고리: 노출 합계가 가장 큰 카테고리
          const entries = Object.entries(r.catImpressions || {})
          const topCategory = entries.length > 0 ? entries.sort((a,b) => b[1]-a[1])[0][0] : '-'
          return { ...r, categoryName: topCategory }
        })
        .sort((a, b) => (b.impressions - a.impressions) || (b.clicks - a.clicks))

      if (rows.length === 0) {
        alert('노출수 1 이상인 키워드가 없습니다.')
        return
      }

      const headers = ['키워드', '카테고리명', '총노출수', '총클릭수']
      const csvData = rows.map(r => [
        `"${r.keyword}"`,
        `"${r.categoryName || '-'}"`,
        r.impressions,
        r.clicks
      ])

      const csvContent = [headers.join(','), ...csvData.map(row => row.join(','))].join('\n')

      // 파일명: 수동광고키워드_{그룹ID}_{KST-YYYY-MM-DD}.csv
      const kstDate = new Date(Date.now() + 9 * 60 * 60 * 1000).toISOString().slice(0, 10)
      const groupId = this.currentGroupId || this.activeKeywordGroup || 'Default'
      const fileName = `수동광고키워드_${groupId}_${kstDate}.csv`

      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', fileName)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },

      // console.log(`📊 트래픽키워드 상세: 10개 키워드 × 6컬럼 = 60개 컬럼`)
      // console.log(`📅 일별 트래픽 데이터: ${sortedDates.length}개 날짜 컬럼`)
      // console.log(`🎯 총 컬럼 수: ${headers.length}개`)
    // },

    // 전체 데이터에 이상치 분석 추가
    addOutlierAnalysisToAllData() {
      // 기본 유효성 검사를 통과한 상품들
      let validProducts = this.analysisResults.filter(p => {
        const rpi = this.calculateRPI(p)
        return rpi > 0 && p.price > 0
      })

      // 0단계: 키워드 수준 CTR 이상치 분석 (가장 먼저 실행 → cleanCTR 생성)
      const productsWithKeywordAnalysis = this.analyzeKeywordCTROutliers(validProducts)

      // 이상치 분석 적용 (가격×CVR, 가격×CTR, 트래픽변화, CTR, RPI)
      // 이후 분석에서 cleanCTR이 calculateCTR()/calculateRPI()에 자동 반영됨
      const productsWithPriceXCvrAnalysis = this.analyzeOutliersByPriceXCvr(productsWithKeywordAnalysis)
      const productsWithPriceXCtrAnalysis = this.analyzeOutliersByPriceXCtr(productsWithPriceXCvrAnalysis)
      const productsWithTrafficAnalysis = this.analyzeOutliersByTrafficVariation(productsWithPriceXCtrAnalysis)
      const productsWithCTRAnalysis = this.analyzeOutliersByCTR(productsWithTrafficAnalysis)
      const productsWithAllAnalysis = this.analyzeOutliersByRPI(productsWithCTRAnalysis)

      // vendorItemId를 키로 하는 이상치 정보 맵 생성
      const outlierMap = new Map()
      productsWithAllAnalysis.forEach(product => {
        outlierMap.set(product.vendorItemId, {
          isPriceXCvrOutlier: product.isPriceXCvrOutlier || false,
          isPriceXCtrOutlier: product.isPriceXCtrOutlier || false,
          isTrafficVariationOutlier: product.isTrafficVariationOutlier || false,
          isCTROutlier: product.isCTROutlier || false,
          isRPIOutlier: product.isRPIOutlier || false,
          priceXCvrOutlierReason: product.priceXCvrOutlierReason || '',
          priceXCtrOutlierReason: product.priceXCtrOutlierReason || '',
          trafficVariationOutlierReason: product.trafficVariationOutlierReason || '',
          ctrOutlierReason: product.ctrOutlierReason || '',
          rpiOutlierReason: product.rpiOutlierReason || '',
          // 키워드 수준 CTR 이상치 정보
          cleanCTR: product.cleanCTR,
          allKeywordsOutlier: product.allKeywordsOutlier || false,
          outlierKeywordIndices: product.outlierKeywordIndices || [],
          keywordCTROutlierCount: product.keywordCTROutlierCount || 0,
          keywordCTROutlierReason: product.keywordCTROutlierReason || ''
        })
      })

      // 전체 데이터에 이상치 정보 추가 (+ 사용자 제외 반영)
      return this.analysisResults.map(item => {
        const baseInfo = outlierMap.get(item.vendorItemId) || {
          isPriceXCvrOutlier: false,
          isPriceXCtrOutlier: false,
          isTrafficVariationOutlier: false,
          isCTROutlier: false,
          isRPIOutlier: false,
          priceXCvrOutlierReason: '',
          priceXCtrOutlierReason: '',
          trafficVariationOutlierReason: '',
          ctrOutlierReason: '',
          rpiOutlierReason: '',
          cleanCTR: undefined,
          allKeywordsOutlier: false,
          outlierKeywordIndices: [],
          keywordCTROutlierCount: 0,
          keywordCTROutlierReason: ''
        }

        const isUserApplied = Array.isArray(this.userExcludedVendorItemIds) && this.userExcludedVendorItemIds.includes(item.vendorItemId)
        const userOutlierReason = isUserApplied ? '사용자이상치적용' : ''

        return {
          ...item,
          ...baseInfo,
          isUserOutlierApplied: isUserApplied,
          userOutlierReason
        }
      })
    },

    // 종합 지표 Excel 다운로드
    exportSummaryData() {
      if (this.analysisResults.length === 0) {
        alert('다운로드할 데이터가 없습니다.')
        return
      }

      const headers = [
        '순위', '상품명', '판매가', 'CTR', 'CVR', 'RPI', '노출수', '클릭수', '매출', '카테고리'
      ]

      // overall = 정본 RPI 가 붙은 enrichedAnalysisResults 기준 (화면과 같은 값)
      const exportSource = (this.enrichedAnalysisResults && this.enrichedAnalysisResults.length) ? this.enrichedAnalysisResults : this.analysisResults
      const sortedProducts = exportSource
        .filter(p => p.overall && parseFloat(p.overall) > 0)
        .sort((a, b) => parseFloat(b.overall) - parseFloat(a.overall))

      const csvData = sortedProducts.map((item, index) => [
        index + 1,
        `"${item.productName || ''}"`,
        item.price || item.salePrice || 0,
        this.calculateCTR(item),
        this.calculateCVR(item) === null ? '-' : this.calculateCVR(item),
        item.overall || 0,
        item.impressions || 0,
        item.clicks || 0,
        item.salesLast28d || 0,
        `"${item.categoryName || ''}"`
      ])

      const csvContent = [headers.join(','), ...csvData.map(row => row.join(','))].join('\n')

      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', `종합지표_${this.targetKeyword}_${new Date().toISOString().slice(0, 10)}.csv`)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },

    // 베스트 상품 Excel 다운로드
    exportBestProducts() {
      if (this.bestProducts.length === 0) {
        alert('다운로드할 베스트 상품 데이터가 없습니다.')
        return
      }

      const headers = [
        '순위', '상품명', '브랜드', '판매가', '현재노출순위', '총노출수', 'CTR', 'CVR', 'RPI', '트래픽키워드'
      ]

      const csvData = this.bestProducts.map((item, index) => [
        index + 1,
        `"${item.productName || ''}"`,
        `"${item.brandName || '-'}"`,
        item.price || 0,
        item.currentRank || '-',
        this.calculateImpressions(item) || 0,
        this.calculateCTR(item) || 0,
        this.calculateCVR(item) === null ? '-' : (this.calculateCVR(item) || 0),
        this.calculateRPI(item) === null ? '-' : (this.calculateRPI(item) || 0),
        `"${item.trafficKeyword ? item.trafficKeyword.map(k => k.keyword).join(', ') : ''}"`
      ])

      const csvContent = [headers.join(','), ...csvData.map(row => row.join(','))].join('\n')

      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', `베스트상품_${this.targetKeyword}_${new Date().toISOString().slice(0, 10)}.csv`)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },

    // RPI Top20 상품 Excel 다운로드
    exportRpiTop20Products() {
      if (this.rpiTop20Products.length === 0) {
        alert('다운로드할 RPI Top20 상품 데이터가 없습니다.')
        return
      }

      const headers = [
        '순위', '상품명', '브랜드', '판매가', '총검색노출수', 'CTR', 'CVR', 'RPI', '카테고리'
      ]

      const csvData = this.rpiTop20Products.map((item, index) => [
        index + 1,
        `"${item.productName || ''}"`,
        `"${item.brandName || '-'}"`,
        item.price || 0,
        item.calculatedImpressions || 0,
        `${item.calculatedCTR || 0}%`,
        `${item.calculatedCVR || 0}%`,
        item.calculatedRPI || 0,
        `"${item.categoryName || ''}"`,
      ])

      const csvContent = [headers.join(','), ...csvData.map(row => row.join(','))].join('\n')

      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', `RPI_Top20_상품_${this.currentGroupId}_${new Date().toISOString().slice(0, 10)}.csv`)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },

    // ===== 키워드그룹 관리 메서드 =====

    // 현재 그룹의 키워드 설정 저장
    async saveCurrentGroupKeywords() {
      try {
        // 저장 전 autoList 정리
        this.cleanAutoListFields()

        // 현재 그룹의 기존 데이터 로드
        const existingData = await keywordAnalysisDB.getKeywordGroup(this.currentGroupId) || {}

        // 키워드 설정만 업데이트 (분석 결과는 유지)
        const updatedData = {
          ...existingData,
          groupId: this.currentGroupId,
          groupName: this.activeKeywordGroup,
          autoList: this.autoList || [],
          maxProducts: this.maxProducts, // 최대 수집 상품 수 설정 저장
          lastUpdated: new Date().toISOString()
          // 기존 analysisResults는 유지
        }

        // console.log('💾 [키워드 설정 저장] 그룹:', this.currentGroupId, '키워드 수:', this.autoList.length)

        // 저장할 데이터 구조 상세 로깅
        // console.log('📊 [키워드 설정 저장 데이터 구조]')
        // console.log('  - groupId:', updatedData.groupId)
        // console.log('  - groupName:', updatedData.groupName)
        // console.log('  - autoList 길이:', updatedData.autoList?.length || 0)
        // console.log('  - 기존 analysisResults 길이:', updatedData.analysisResults?.length || 0)

        // autoList 전체 데이터 로깅
        // if (updatedData.autoList && updatedData.autoList.length > 0) {
        //   // console.log('📝 [저장할 autoList 전체 데이터]:')
        //   updatedData.autoList.forEach((keyword, index) => {
        //     console.log(`  키워드 ${index + 1}: ${keyword.keyword}`, {
        //       pageRange: keyword.pageRange,
        //       startPage: keyword.startPage,
        //       endPage: keyword.endPage,
        //       completedPages: keyword.completedPages,
        //       source: keyword.source,
        //       analysisData: keyword.analysisData ? '있음' : '없음',
        //       collectedAt: keyword.collectedAt
        //     })
        //   })
        // }

        await keywordAnalysisDB.saveKeywordGroup(this.currentGroupId, updatedData)
        // console.log('✅ [키워드 설정 저장] 완료:', this.currentGroupId, this.autoList.length, '개')
      } catch (error) {
        console.error('❌ 현재 그룹 키워드 설정 저장 실패:', error)
      }
    },

    async createKeywordGroup() {
      try {
        // 현재 그룹의 키워드 설정을 먼저 저장
        await this.saveCurrentGroupKeywords()

        const groupName = prompt('키워드 그룹명을 입력해주세요:')
        if (!groupName) return

        if (this.keywordGroups.includes(groupName)) {
          alert('이미 존재하는 그룹명입니다.')
          return
        }

        this.keywordGroups.push(groupName)
        this.activeKeywordGroup = groupName
        this.currentGroupId = groupName.toLowerCase()

        // 새 그룹은 빈 상태로 시작 (기존 그룹 데이터는 그대로 유지)
        this.autoList = []
        this.resetAnalysisData() // resetData() 대신 분석 데이터만 초기화
        this.sourcingChecklist = null // 체크리스트도 초기화

        // 새 그룹 초기 데이터 저장
        await this.saveCurrentGroupKeywords()

        // console.log('새 키워드그룹 생성:', groupName)
      } catch (error) {
        console.error('키워드그룹 생성 실패:', error)
        alert('키워드그룹 생성 중 오류가 발생했습니다.')
      }
    },

    async deleteKeywordGroup(groupName) {
      if (groupName === 'Default') {
        alert('Default 그룹은 삭제할 수 없습니다.')
        return
      }

      if (!confirm(`"${groupName}" 그룹을 삭제하시겠습니까?`)) {
        return
      }

      try {
        // 현재 그룹이 삭제될 그룹이 아니라면 현재 설정 저장
        if (this.activeKeywordGroup !== groupName) {
          await this.saveCurrentGroupKeywords()
        }

        // IndexedDB에서 실제 그룹 데이터 삭제
        const groupIdToDelete = groupName.toLowerCase()
        console.log('🗑️ [키워드그룹 삭제] IndexedDB에서 삭제 시작:', groupIdToDelete)
        await keywordAnalysisDB.deleteKeywordGroup(groupIdToDelete)
        console.log('✅ [키워드그룹 삭제] IndexedDB 삭제 완료:', groupIdToDelete)

        // 그룹 목록에서 제거
        this.keywordGroups = this.keywordGroups.filter(g => g !== groupName)

        // 현재 그룹이 삭제된 그룹이면 Default로 변경
        if (this.activeKeywordGroup === groupName) {
          this.activeKeywordGroup = 'Default'
          this.currentGroupId = 'default'
          this.autoList = []

          // Default 그룹의 기존 데이터 로딩 (resetData 대신)
          this.resetAnalysisData()
          this.sourcingChecklist = null
          await this.loadExistingData()
        }

        alert('그룹이 삭제되었습니다.')
      } catch (error) {
        console.error('키워드그룹 삭제 실패:', error)
        alert('키워드그룹 삭제 중 오류가 발생했습니다.')
      }
    },

    async switchKeywordGroup(groupName) {
      if (this.activeKeywordGroup === groupName) return
      if (this.isAnalyzing) {
        alert('분석 중에는 그룹을 변경할 수 없습니다.')
        return
      }

      try {
        // console.log('🔄 [키워드그룹 변경] 시작:', this.activeKeywordGroup, '->', groupName)

        // 로딩 상태 시작 - 분석 내용 숨김
        this.isGroupSwitching = true

        // 현재 그룹의 분석 결과가 있으면 먼저 저장
        if (this.analysisResults.length > 0) {
          // console.log('💾 [키워드그룹 변경] 현재 그룹 데이터 저장 중...', this.analysisResults.length, '개 상품')
          await this.saveAllAnalysisResults()
        }

        // 현재 그룹의 키워드 설정을 먼저 저장
        await this.saveCurrentGroupKeywords()

        // 저장 완료 후 즉시 데이터 초기화 (잘못된 데이터 저장 방지)
        // console.log('🔄 [키워드그룹 변경] 이전 그룹 데이터 초기화 시작...')
        this.resetAnalysisData() // 이미 analysisResults = [] 포함됨
        this.sourcingChecklist = null
        this.autoList = []

        this.activeKeywordGroup = groupName
        this.currentGroupId = groupName.toLowerCase()

        console.log('🔄 [키워드그룹 변경] 그룹 ID 설정:', this.currentGroupId)

        // console.log('🔄 [키워드그룹 변경] 데이터 초기화 완료, 기존 데이터 로딩 시작...')

        // 새 그룹의 기존 데이터 로딩
        await this.loadExistingData()

        // UI 업데이트 보장
        await this.$nextTick()

        // 로딩 상태 완료 - 분석 내용 표시
        this.isGroupSwitching = false

        // 키워드그룹 변경 완료 후 메모리 사용량 업데이트
        this.updateMemoryUsage()

        console.log('✅ [키워드그룹 변경] 완료:', groupName, '키워드 수:', this.autoList.length, '상품 수:', this.analysisResults.length)
        // console.log('🔍 [키워드그룹 변경] 최종 상태 확인:', {
        //   그룹: groupName,
        //   analysisResults길이: this.analysisResults.length,
        //   analysisResults타입: typeof this.analysisResults,
        //   isArray: Array.isArray(this.analysisResults),
        //   첫번째상품: this.analysisResults[0]?.productName || '없음',
        //   메모리상품수: this.memoryUsage.totalProducts,
        //   메모리크기: this.formatDataSize(this.memoryUsage.estimatedSize)
        // })
      } catch (error) {
        console.error('❌ [키워드그룹 변경] 실패:', error)
        this.isGroupSwitching = false // 에러 시에도 로딩 상태 해제
        alert('키워드그룹 전환 중 오류가 발생했습니다.')
      }
    },

    // ===== 대량수집 관리 메서드 =====

    // 개별 키워드의 연관+자동완성 수집
    addRelKeyword(item) {
      if (this.isAnalyzing) {
        alert('분석 중에는 연관 키워드 수집이 불가능합니다.')
        return
      }

      window.postMessage({
        greeting: "getadkeyword",
        data: [item.keyword],
        cnt: 1,
        relKeywordChecked: true,
        autoCompleteChecked: true
      }, "*")
    },

    // 네이버 데이터랩 키워드 수집
    collectKeywordRank() {
      if (this.startRank > this.endRank) {
        alert('시작순위가 종료순위보다 클 수 없습니다.')
        return
      }

      window.postMessage({
        greeting: "collectKeyword",
        startRank: this.startRank,
        endRank: this.endRank
      }, "*")
    },

    // 키워드 목록에 추가하는 공통 메서드
    addKeywordsToList(keywords, source) {
      // console.log('🔍 addKeywordsToList 호출:', { keywords, source, autoListLength: this.autoList.length })

      if (!keywords || keywords.length === 0) {
        alert('수집된 키워드가 없습니다.')
        return
      }

      // 키워드 앞뒤 공백 제거 + 입력 배열 내 중복 제거
      const trimmedKeywords = [...new Set(
        keywords
          .map(kw => (kw || '').trim())
          .filter(kw => kw.length > 0)
      )]

      // 기존 키워드들도 trim하여 비교
      const existingKeywords = new Set(
        this.autoList.map(item => (item.keyword || '').trim())
      )

      const newKeywords = trimmedKeywords.filter(keyword =>
        !existingKeywords.has(keyword)
      )

      // console.log('🔍 중복 제거 후 새 키워드:', newKeywords)

      if (newKeywords.length === 0) {
        alert('새로 추가할 키워드가 없습니다. (모두 중복)')
        return
      }

      if (this.autoList.length + newKeywords.length > 100) {
        alert(`키워드는 최대 100개까지 가능합니다. (현재: ${this.autoList.length}개)`)
        return
      }

      // 키워드 추가 - 직접 입력과 동일한 구조로 생성
      newKeywords.forEach(keyword => {
        this.autoList.push({
          keyword: keyword, // targetKeyword 대신 keyword 사용
          pageRange: `${this.defaultStartPage}-${this.defaultEndPage}`, // 기본 페이지 범위 사용
          startPage: this.defaultStartPage,
          endPage: this.defaultEndPage,
          source: source, // 수집 방법 표시
          analysisData: null,
          collectedAt: null
        })
      })

      // console.log('🔍 키워드 추가 후 autoList 길이:', this.autoList.length)

      // 키워드 추가 즉시 IndexedDB에 저장
      this.saveCurrentGroupKeywords()

      alert(`${source}에서 ${newKeywords.length}개 키워드가 추가되었습니다.`)
    },

    downloadTemplate() {
      var arr = [];
      var myHeader = ['키워드'];

      var dataWS = XLSX.utils.json_to_sheet(arr, { header: myHeader });

      const range = XLSX.utils.decode_range(dataWS['!ref']);
      range.e['c'] = myHeader.length - 1;
      dataWS['!ref'] = XLSX.utils.encode_range(range);

      var wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, dataWS, '키워드업로드양식');
      XLSX.writeFile(wb, '키워드업로드양식.xlsx');
    },

    async uploadBulkFile(event) {
      const file = event.target.files[0]
      if (!file) return

      this.uploadstatus = '파일 처리 중...'

      try {
        const data = await this.readFileAsync(file);
        const workbook = XLSX.read(data, { type: 'binary' });

        const keywords = [];
        for (const sheetName of workbook.SheetNames) {
          const roa = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName]);
          // console.log(roa);

          for (const row of roa) {
            if (row.키워드 && row.키워드.length > 0) {
              const keyword = row.키워드.trim();
              if (keyword) {
                keywords.push(keyword);
              }
            }
          }
        }

        // 공통 메서드 사용
        this.addKeywordsToList(keywords, '엑셀업로드')

        this.uploadstatus = 'Excel 파일 업로드'

      } catch (error) {
        console.error('파일 업로드 실패:', error)
        alert('파일 업로드에 실패했습니다.')
        this.uploadstatus = 'Excel 파일 업로드'
      }
    },

    async readFileAsync(file) {
			return new Promise((resolve, reject) => {
				const reader = new FileReader();
				reader.onload = (e) => resolve(e.target.result);
				reader.onerror = (error) => reject(error);
				reader.readAsBinaryString(file);
			});
		},





    deleteKeywordFromList(index) {
      const keywordToDelete = this.autoList[index]
      const keywordName = keywordToDelete.keyword || keywordToDelete.targetKeyword

      // 해당 키워드의 수집된 데이터 개수 확인
      const relatedDataCount = this.analysisResults.filter(result =>
        result.sourceKeyword === keywordName ||
        (result.collectionKey && result.collectionKey.startsWith(`${keywordName}:`))
      ).length

      const confirmMessage = relatedDataCount > 0
        ? `"${keywordName}" 키워드를 삭제하시겠습니까?\n\n⚠️ 이 키워드로 수집된 ${relatedDataCount}개의 상품 데이터도 함께 삭제됩니다.`
        : `"${keywordName}" 키워드를 삭제하시겠습니까?`

      if (confirm(confirmMessage)) {
        // 1. 해당 키워드의 모든 분석 데이터 제거
        if (relatedDataCount > 0) {
          // console.log(`🗑️ [키워드 삭제] ${keywordName}: ${relatedDataCount}개 상품 데이터 삭제 시작`)

          // 해당 키워드의 모든 페이지 데이터 제거
          // const initialCount = this.analysisResults.length
          this.analysisResults = this.analysisResults.filter(result => {
            // collectionKey 기반 필터링 (우선)
            if (result.collectionKey) {
              return !result.collectionKey.startsWith(`${keywordName}:`)
            }
            // 기존 방식 필터링 (하위 호환성)
            return result.sourceKeyword !== keywordName
          })

          // const removedCount = initialCount - this.analysisResults.length
          // console.log(`🗑️ [키워드 삭제] ${keywordName}: ${removedCount}개 상품 데이터 제거 완료`)

          // IndexedDB에서도 해당 키워드 데이터 제거
          this.removeKeywordDataFromIndexedDB(keywordName)

          // 분석 데이터 재계산
          this.performComprehensiveAnalysis()
          this.updateMemoryUsage()
        }

        // 2. autoList에서 키워드 제거
        this.autoList.splice(index, 1)

        // 3. 키워드 설정 저장
        this.saveCurrentGroupKeywords()

        // console.log(`✅ [키워드 삭제 완료] ${keywordName}`)
      }
    },

    // 선택된 키워드들의 페이지범위 일괄 변경
    applyBulkPageRange() {
      if (this.selectedKeywordItems.length === 0) {
        alert('선택된 키워드가 없습니다.')
        return
      }

      // 페이지 범위 유효성 검사
      const startPage = parseInt(this.bulkStartPage) || 1
      const endPage = parseInt(this.bulkEndPage) || startPage

      if (startPage > endPage) {
        alert('시작페이지가 끝페이지보다 클 수 없습니다.')
        return
      }

      const newPageRange = startPage === endPage ? startPage.toString() : `${startPage}-${endPage}`

      // 선택된 키워드들의 페이지범위 업데이트
      this.selectedKeywordItems.forEach(selectedItem => {
        const index = this.autoList.findIndex(item =>
          (item.keyword || item.targetKeyword) === (selectedItem.keyword || selectedItem.targetKeyword)
        )
        if (index !== -1) {
          this.$set(this.autoList[index], 'startPage', startPage)
          this.$set(this.autoList[index], 'endPage', endPage)
          this.$set(this.autoList[index], 'pageRange', newPageRange)
        }
      })

      // 키워드 설정 저장
      this.saveCurrentGroupKeywords()

      alert(`${this.selectedKeywordItems.length}개 키워드의 페이지범위가 ${newPageRange}로 변경되었습니다.`)

      // 선택 초기화
      this.selectedKeywordItems = []
    },

    // 선택된 키워드들 일괄 삭제
    deleteSelectedKeywords() {
      if (this.selectedKeywordItems.length === 0) {
        alert('선택된 키워드가 없습니다.')
        return
      }

      // 삭제될 키워드들의 수집 데이터 개수 확인
      let totalRelatedDataCount = 0
      const keywordNames = this.selectedKeywordItems.map(item => item.keyword || item.targetKeyword)

      keywordNames.forEach(keywordName => {
        const relatedDataCount = this.analysisResults.filter(result =>
          result.sourceKeyword === keywordName ||
          (result.collectionKey && result.collectionKey.startsWith(`${keywordName}:`))
        ).length
        totalRelatedDataCount += relatedDataCount
      })

      const confirmMessage = totalRelatedDataCount > 0
        ? `선택된 ${this.selectedKeywordItems.length}개 키워드를 삭제하시겠습니까?\n\n⚠️ 이 키워드들로 수집된 ${totalRelatedDataCount}개의 상품 데이터도 함께 삭제됩니다.`
        : `선택된 ${this.selectedKeywordItems.length}개 키워드를 삭제하시겠습니까?`

      if (!confirm(confirmMessage)) {
        return
      }

      // 1. 해당 키워드들의 모든 분석 데이터 제거
      if (totalRelatedDataCount > 0) {
        this.analysisResults = this.analysisResults.filter(result => {
          if (result.collectionKey) {
            return !keywordNames.some(kw => result.collectionKey.startsWith(`${kw}:`))
          }
          return !keywordNames.includes(result.sourceKeyword)
        })

        // 통합 분석 재계산
        this.performComprehensiveAnalysis()
        this.updateMemoryUsage()
      }

      // 2. autoList에서 키워드들 제거 (역순으로 삭제하여 인덱스 문제 방지)
      const indicesToRemove = this.selectedKeywordItems
        .map(selectedItem => this.autoList.findIndex(item =>
          (item.keyword || item.targetKeyword) === (selectedItem.keyword || selectedItem.targetKeyword)
        ))
        .filter(index => index !== -1)
        .sort((a, b) => b - a) // 역순 정렬

      indicesToRemove.forEach(index => {
        this.autoList.splice(index, 1)
      })

      // 3. 키워드 설정 저장
      this.saveCurrentGroupKeywords()

      alert(`${this.selectedKeywordItems.length}개 키워드가 삭제되었습니다.`)

      // 4. 선택 초기화
      this.selectedKeywordItems = []
    },

    async clearAutoList() {
      if (!confirm('모든 키워드를 삭제하시겠습니까?')) return

      // 키워드 삭제 전에 페이지 상태도 확실히 초기화
      this.autoList.forEach(item => {
        item.completedPages = []
        item.failedPages = []
        item.skippedPages = []
        item.analysisData = null
        item.newAnalysisData = null
        item.collectedAt = null
      })

      this.autoList = []
      // 모든 키워드 삭제 즉시 IndexedDB에 저장
      this.saveCurrentGroupKeywords()
    },

    formatDate(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleString('ko-KR')
    },

    async collectNewKeywords() {
      // console.log('🚨 [collectNewKeywords 호출] 호출 스택:', new Error().stack)
      // console.log('🔄 [신규 수집] 시작 - 현재 UI 상태를 먼저 저장')

      // 최대 수집 제한 체크
      if (this.analysisResults.length >= this.maxProducts) {
        alert(`현재 ${this.analysisResults.length}개 상품으로 최대 제한(${this.maxProducts}개)에 도달했습니다.\n초기화 후 다시 수집해주세요.`)
        return
      }

      // 1단계: 현재 UI 상태를 IndexedDB에 저장 (중요!)
      await this.saveCurrentGroupKeywords()
      // console.log('🔄 [신규 수집] 현재 상태 저장 완료')

      // 2단계: 수집 안된 키워드나 페이지가 있는 키워드 찾기
      const incompleteItems = this.autoList.filter(item => {
        if (!item.analysisData || !item.collectedAt) return true // 완전히 미수집

        // 페이지 범위 내에서 완료되지 않은 페이지가 있는지 확인
        const pageRange = this.parsePageRange(item.pageRange)
        const completedPages = item.completedPages || []

        for (let page = pageRange.startPage; page <= pageRange.endPage; page++) {
          if (!completedPages.includes(page)) {
            return true // 미완료 페이지 존재
          }
        }
        return false // 모든 페이지 완료
      })

      if (incompleteItems.length === 0) {
        alert('수집할 새 키워드나 페이지가 없습니다.')
        return
      }

      // console.log('🔄 [신규 수집] 수집 대상:', incompleteItems.length, '개 키워드')

      // 신규 수집 대상 키워드들 상세 로깅
      // incompleteItems.forEach((keyword, index) => {
      //   console.log(`🔄 [신규 수집 대상 ${index + 1}] ${keyword.keyword}:`, {
      //     pageRange: keyword.pageRange,
      //     startPage: keyword.startPage,
      //     endPage: keyword.endPage,
      //     startPageType: typeof keyword.startPage,
      //     endPageType: typeof keyword.endPage,
      //     completedPages: keyword.completedPages
      //   })
      // })

      // 3단계: 신규 수집만 분석하도록 임시로 autoList 백업 후 교체
      this.originalAutoList = [...this.autoList]
      this.autoList = incompleteItems.map(item => ({ ...item, originalIndex: this.originalAutoList.indexOf(item) }))

      // console.log('🔄 [신규 수집] autoList 교체 완료, 새 autoList 길이:', this.autoList.length)

      this.showBulkModal = false
      this.startKeywordAnalysis()
    },

    async collectAllKeywords() {
      if (this.autoList.length === 0) {
        alert('수집할 키워드가 없습니다.')
        return
      }

      // 최대 수집 제한 체크 (전체수집은 초기화 후 진행하므로 기존 데이터가 있을 때만 체크)
      if (this.analysisResults.length >= this.maxProducts && this.analysisResults.length > 0) {
        alert(`현재 ${this.analysisResults.length}개 상품으로 최대 제한(${this.maxProducts}개)에 도달했습니다.\n전체수집은 데이터를 초기화한 후 진행됩니다.`)
      }

      // 기존 데이터가 있는 경우 초기화 확인
      if (this.analysisResults.length > 0) {
        if (!confirm('전체 데이터를 다시 수집합니다.\n\n기존의 모든 분석 데이터를 삭제하고 새로 수집하시겠습니까?\n\n- 현재 수집된 모든 상품 데이터가 삭제됩니다\n- 키워드 설정은 유지되지만 모두 대기 상태로 변경됩니다')) {
          return
        }

        try {
          // 데이터 초기화
          await this.clearIndexedDBData()

          this.analysisResults = []
          this.summaryData = []
          this.progressPercent = 0
          this.progressPrefix = ''
          this.progressSuffix = ''
          this.currentKeywordIndex = 0

          // autoList의 분석 데이터 초기화 (키워드는 유지, 상태만 대기로 변경)
          this.autoList.forEach(item => {
            item.analysisData = null
            item.newAnalysisData = null
            item.collectedAt = null
            item.completedPages = [] // 페이지별 완료 상태도 초기화
            item.failedPages = []
            item.skippedPages = []
          })

          // console.log('🔄 [전체 수집] 키워드 설정 유지, 분석 데이터 및 완료 상태 초기화 완료')

          // 모든 분석 데이터 초기화
          this.resetAnalysisData()

          // console.log('✅ 전체수집을 위한 데이터 초기화 완료')
        } catch (error) {
          console.error('❌ 데이터 초기화 실패:', error)
          alert('데이터 초기화 중 오류가 발생했습니다.')
          return
        }
      }

      this.showBulkModal = false
      this.startKeywordAnalysis()
    },

    async collectKeywordsBatch(keywords) {
      this.isAnalyzing = true

      for (let i = 0; i < keywords.length; i++) {
        const keyword = keywords[i]

        try {
          this.progressPrefix = `키워드 분석 중: ${keyword.targetKeyword} (${i + 1}/${keywords.length}) `
          this.progressSuffix = ''
          this.progressPercent = Math.round((i / keywords.length) * 100)

          // 개별 키워드 분석
          this.targetKeyword = keyword.targetKeyword
          await this.performSingleKeywordAnalysis(keyword.targetKeyword, keyword.pageRange || this.pageRange)

          // 결과 저장
          keyword.analysisData = {
            analysisResults: [...this.analysisResults],
            summaryData: [...this.summaryData],
            collectedAt: new Date().toISOString()
          }
          keyword.collectedAt = new Date().toISOString()

          // 딜레이
          if (i < keywords.length - 1) {
            await new Promise(resolve => setTimeout(resolve, this.delayTime))
          }

        } catch (error) {
          console.error(`키워드 "${keyword.targetKeyword}" 분석 실패:`, error)
        }
      }



      this.isAnalyzing = false
      this.progressPrefix = ''
      this.progressSuffix = '대량 수집 완료'
      this.progressPercent = 100

      alert(`${keywords.length}개 키워드 수집이 완료되었습니다.`)
    },

    async performSingleKeywordAnalysis(keyword, pageRange) {
      return new Promise((resolve, reject) => {
        // Extension에 분석 요청
        const handleMessage = (event) => {
          if (event.data.response === 'keywordSeoAnalysisComplete') {
            window.removeEventListener('message', handleMessage)
            resolve()
          } else if (event.data.response === 'keywordSeoAnalysisError') {
            window.removeEventListener('message', handleMessage)
            // 카테고리 에러인 경우 특별한 에러 객체 생성
            if (event.data.error === 'CATEGORY_NOT_UNDER_SELLER') {
              const error = new Error(event.data.message)
              error.type = 'CATEGORY_NOT_UNDER_SELLER'
              reject(error)
            } else {
              reject(new Error(event.data.message))
            }
          }
        }

        window.addEventListener('message', handleMessage)

        window.postMessage({
          greeting: 'startKeywordSeoAnalysis',
          keyword: keyword,
          pageRange: pageRange || this.pageRange,
          fastCollect: this.collectMode === 'fast'
        }, "*")
      })
    }
  }
}
</script>

<style scoped>
.progress {
  background-color: #e9ecef;
}

.progress-bar {
  background-color: #007bff;
  transition: width 0.3s ease;
}

.badge {
  font-size: 0.75em;
}

.table-responsive {
  border-radius: 0.375rem;
}

/* AI 소싱 분석 체크리스트 카드 스타일 */
.checklist-card {
  padding: 10px 12px;
  border-radius: 8px;
  border-left: 4px solid;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  transition: box-shadow 0.2s ease;
}

.checklist-card:hover {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
}

.checklist-card.critical {
  border-left-color: #dc3545;
  background: linear-gradient(to right, rgba(220, 53, 69, 0.05), transparent);
}

.checklist-card.recommended {
  border-left-color: #ffc107;
  background: linear-gradient(to right, rgba(255, 193, 7, 0.05), transparent);
}

.checklist-card.optional {
  border-left-color: #28a745;
  background: linear-gradient(to right, rgba(40, 167, 69, 0.05), transparent);
}

.score-badge {
  min-width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 0.85em;
  flex-shrink: 0;
  margin-left: 10px;
}

.score-badge.critical {
  background: #dc3545;
  color: white;
}

.score-badge.recommended {
  background: #ffc107;
  color: #212529;
}

.score-badge.optional {
  background: #28a745;
  color: white;
}

.action-text {
  color: #495057;
  line-height: 1.4;
}

.checklist-container::-webkit-scrollbar {
  width: 6px;
}

.checklist-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.checklist-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.checklist-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* 1688 검색 키워드 링크 스타일 */
.keyword-link {
  transition: all 0.2s ease;
}

.keyword-link:hover {
  transform: scale(1.05);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  opacity: 0.9;
}

/* 비교상품 행 강조 스타일 (::v-deep으로 CDataTable 내부 적용) */
::v-deep .comparison-product-row {
  background-color: rgba(255, 193, 7, 0.2) !important;
  border-left: 4px solid #ffc107 !important;
  font-weight: 500;
}

::v-deep .comparison-product-row:hover {
  background-color: rgba(255, 193, 7, 0.35) !important;
}

::v-deep .comparison-product-row td {
  background-color: transparent !important;
}
</style>